import React, { useRef, useEffect, useCallback, useState, useMemo, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import "./NodeEditor.scss";
import Timeline from "./Timeline";
import LeftSidebar from "../../components/TimelineEditor/LeftSidebar";
import NodeEditorCanvas from "../../components/TimelineEditor/NodeEditorCanvas";
import RightSidebar from "../../components/TimelineEditor/RightSidebar";
import { useNodeEditorState } from "../../hooks/useNodeEditorState";
import { usePanningAndZoom } from "../../hooks/usePanningAndZoom";
import { useGridCalculations } from "../../hooks/useGridCalculations";
import axios from "../../api/axios";
import config from "../../config";
import { getDisruptorListRoute, updatedFeatureListRoute, removeCampaignFeatureRoute, getCampaignOverviewRoute, updateCampaignRoute, getCampaignCustomisationVarsRoute, getCampaignFeatureVarsRoute, updateCampaignFeatureVarsRoute, updateCampaignCustomisationVarsRoute, deleteFeatureCampaignCustomisationsRoute, addFeatureFromTemplateRoute, getAllTemplatesCategoriesRoute, importFeaturePackageRoute, exportCampaignPackageRoute } from "../../api/routes";
import ApplyTemplatePopup from "../../components/TimelineEditor/ApplyTemplatePopup";
import CreateFeatureTemplatePopup from "../../components/TimelineEditor/CreateFeatureTemplatePopup";
import { CHAINS_HELPER } from "../../helpers/ChainsHelper";
import { addVal, convertUniqueFilenameToOriginal, customisationSaverHook, findFeatureData, fixSrcUrls, getPreviewCustomisationVars, removeVal, triggerSaveFeatureData, undoRedoHook } from "../../components/v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import { WorkspaceConfig } from "../../components/v2/CustomisationWorkspace/DisruptorConfigFiles/WorkspaceConfig";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import EditablePreview, { findSubNode, getCustomisationFieldData, getGroupData, updateCustomisationValue } from "../../components/v2/CustomisationWorkspace/WorkspaceComponents/EditablePreview/EditablePreview";
import { findDynamicNodeName, getSubNodes, processGroupsToSubNodes, workingVisualEditorData } from "../../components/v2/CustomisationWorkspace/v2IntegrationHelpers/VisualEditorDataFormatHelperFunctions";
import { NODE_LIST_CONTROLLER } from "../../components/NodeList/NodeList";
import { RIGHT_CLICK_MENU_CONTROLLER } from "../../components/RightClickMenu/RightClickMenu";
import { WORKSPACE_LEFT_LIST_ICONS } from "../../components/v2/CustomisationWorkspace/WorkspaceAssets/Assets";
import { stripTemplateAssetSourceMetadata } from "../../helpers/FeatureAssets";
import { initGoogleFonts } from "../../helpers/Fonts";
import FeatureTimeline from "../../components/v2/CustomisationWorkspace/WorkspaceComponents/Timeline/Timeline";
import FontSelectorPopup from "../../components/forms/FontSelector/FontSelectorPopup";
import CustomFontsPopup from "../CustomiseFeature/CustomFontsPopup";
import TemporaryImageUploadPopup from "../../components/v2/CustomisationWorkspace/TemporaryImageUploadPopup/TemporaryImageUploadPopup";
import TemporaryAudioUploadPopup from "../../components/v2/CustomisationWorkspace/TemporaryAudioUploadPopup/TemporaryAudioUploadPopup";
import RestrictedCustomisationPopup from "../CustomiseFeature/RestrictedCustomisationPopup";
import FloatingToolbar from "./floating-toolbar/FloatingToolbar";
import { useFeatureCustomisationSaver } from "../../hooks/useFeatureCustomisationSaver";
import { useUndoSystem } from "../../hooks/useUndoSystem";
import SlideoutPanel, { slideOutPanelControl } from "./slideout-panel/SlideoutPanel";
import LegacyCustomisation from "./legacyCustomisation/LegacyCustomisation";
import FeatureFlowPage from "../../components/FeatureFlow/FeatureFlowPage";
import { getDisruptorItemsFromDataLink, resolveDisruptorSelectDataLink } from "../../components/FeatureFlow/flowCanvas.constants";
import { WORKING_ON_NODE_DATA } from "../../components/v2/CustomisationWorkspace/WorkspaceComponents/EditablePreview/VisualisedComponents/VisualisedNode";
import FeaturePreview, { postMessageToPreviewFrame } from "../../components/FeaturePreview/FeaturePreview";
import FunctionUnavailableInPreviewPopup from "../CustomiseFeature/themes/FunctionUnavailableInPreviewPopup";
import { deepObjectMerge, POST_MESSAGE_OUT_TYPES, syncCustomisationBackedFeatureVars, syncVideoDisruptorControlFeatureVars } from "../../helpers/FeatureControlsHelper";
import { LIVE_AUDIO_PREVIEW } from "../../helpers/LivePreviews";
import { featureIcons } from "../../assets/assets";
import { CampaignContext } from "../../contexts/CampaignContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ACCOUNT_TYPES } from "../../helpers/AccountTypes";
import { applyCampaignCustomFontsCss } from "../../helpers/CustomFonts";
import ProfilePic, { PROFILE_PIC_SIZES } from "../../components/ProfilePic/ProfilePic";

export const MODES = {
    FLOW_EDITOR: 'flow_editor',
    CANVAS_EDITOR: 'canvas_editor',
    EXPANDED_FLOW: 'expanded_flow',
};

export const ACTION_STATE = {
    IDLE: 'idle',
    PANNING: 'panning',
    MOVING_STEP: 'moving_step',
    MOVING_COMPONENT: 'moving_component',
    EDITING_STEP_NAME: 'editing_step_name',
    EDITING_COMPONENT_NAME: 'editing_component_name',
};

const FLOW_STEP_SPACING = 450;
const FLOW_PANEL_WIDTH = 260;
const FLOW_PANEL_HALF_WIDTH = FLOW_PANEL_WIDTH / 2;
const LEFT_SIDEBAR_MIN_WIDTH = 250;
const LEFT_SIDEBAR_MAX_WIDTH = 560;
const RIGHT_SIDEBAR_WIDTH = 250;
const MIN_FLOW_WORKSPACE_WIDTH = 420;
const CAMPAIGN_EDITOR_SOCKET_ACTIONS = {
  join: 'campaignEditorJoin',
  leave: 'campaignEditorLeave',
  activeEditors: 'campaignEditorActiveEditors',
  customisationSaved: 'campaignEditorCustomisationSaved',
  campaignUpdated: 'campaignEditorCampaignUpdated',
};

const NODE_EDITOR_HISTORY_KEY = '__nodeEditorView';

const createCampaignEditorSocket = () => {
  if (process.env.REACT_APP_NODE_ENV == 'local') {
    return io(process.env.REACT_APP_SOCKET_IO_BASE_URL, {
      path: '/socket.io/',
    });
  }

  const hostedSocketBaseUrl = window.location.hostname === 'dev.campaignstudio.tv'
    || window.location.hostname === 'api-dev.campaignstudio.tv'
      ? 'https://api-dev.campaignstudio.tv'
      : config.socketIoBaseUrl;

  return io(hostedSocketBaseUrl, {
    path: '/socket.io/',
    transports: ['polling'],
    upgrade: true,
    secure: window.location.protocol === 'https:',
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    timeout: 20000
  });
};

const createCustomisationSnapshot = (value) => {
  if (!value) {
    return { current: {}, default: {} };
  }

  return JSON.parse(JSON.stringify(value));
};

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';
const CLIENT_ONLY_CUSTOMISATION_KEYS = new Set(['srcUrl', 'originalFile']);

const getComparableObjectKeys = (value) => Object.keys(value || {}).filter(
  (key) => !CLIENT_ONLY_CUSTOMISATION_KEYS.has(key)
);

const cloneComparableValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => cloneComparableValue(item));
  }

  if (isPlainObject(value)) {
    return getComparableObjectKeys(value).reduce((acc, key) => {
      acc[key] = cloneComparableValue(value[key]);
      return acc;
    }, {});
  }

  return value;
};

const areComparableValuesEqual = (left, right) => {
  if (left === right) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }

    for (let index = 0; index < left.length; index++) {
      if (!areComparableValuesEqual(left[index], right[index])) {
        return false;
      }
    }

    return true;
  }

  if (isPlainObject(left) || isPlainObject(right)) {
    if (!isPlainObject(left) || !isPlainObject(right)) {
      return false;
    }

    const leftKeys = getComparableObjectKeys(left);
    const rightKeys = getComparableObjectKeys(right);
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    for (let index = 0; index < leftKeys.length; index++) {
      const key = leftKeys[index];
      if (!Object.prototype.hasOwnProperty.call(right, key) || !areComparableValuesEqual(left[key], right[key])) {
        return false;
      }
    }

    return true;
  }

  return false;
};

const rebaseUnsavedLocalChanges = (remoteValue, baselineValue, localValue) => {
  if (typeof localValue === 'undefined') {
    return typeof baselineValue === 'undefined' ? cloneComparableValue(remoteValue) : undefined;
  }

  if (areComparableValuesEqual(localValue, baselineValue)) {
    return cloneComparableValue(remoteValue);
  }

  if (Array.isArray(localValue)) {
    if (!Array.isArray(remoteValue) || !Array.isArray(baselineValue)) {
      return localValue;
    }

    if (localValue.length !== baselineValue.length || localValue.length !== remoteValue.length) {
      return localValue;
    }

    return localValue.map((item, index) => rebaseUnsavedLocalChanges(remoteValue[index], baselineValue[index], item));
  }

  if (isPlainObject(localValue)) {
    if (!isPlainObject(remoteValue) || !isPlainObject(baselineValue)) {
      return localValue;
    }

    const mergedValue = {};
    const keys = new Set([
      ...getComparableObjectKeys(remoteValue || {}),
      ...getComparableObjectKeys(baselineValue || {}),
      ...getComparableObjectKeys(localValue || {}),
    ]);

    keys.forEach((key) => {
      const rebasedValue = rebaseUnsavedLocalChanges(remoteValue?.[key], baselineValue?.[key], localValue?.[key]);
      if (typeof rebasedValue !== 'undefined') {
        mergedValue[key] = rebasedValue;
      }
    });

    return mergedValue;
  }

  return localValue;
};

const getCampaignEditorDisplayName = (editor) => {
  const fullName = `${editor?.firstName || ''} ${editor?.lastName || ''}`.trim();
  return editor?.displayName || fullName || editor?.name || 'Unknown user';
};

class PreviewErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    this.props.onError?.(error);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}

const normalizeFlowPanelWidth = (width) => {
  const parsedWidth = Number(width);

  if (!Number.isFinite(parsedWidth) || parsedWidth <= 0) {
    return FLOW_PANEL_WIDTH;
  }

  return Math.max(parsedWidth, FLOW_PANEL_WIDTH);
};

const normalizeFlowStepWidth = (step) => ({
  ...step,
  width: normalizeFlowPanelWidth(step?.width),
});

const normalizeFlowItemWidth = (item) => {
  if (item?.type !== 'panel') {
    return item;
  }

  return {
    ...item,
    width: normalizeFlowPanelWidth(item?.width),
  };
};

const MEDIA_DROP_FEATURE_CONFIG = {
  images: {
    featureVarKey: 'imageOverlay_simple',
    collectionDataLink: 'customImageOverlay.imageFiles.customImageFile',
    secondaryCollectionDataLinks: ['customImageOverlay.overlayImageFiles.customImageFile'],
    fileDataLink: 'customImageOverlay.imageFiles.customImageFile.filename',
    nameDataLink: 'customImageOverlay.imageFiles.customImageFile.name',
    assetValueType: 'images',
  },
  audio: {
    featureVarKey: 'disruptorAudio',
    collectionDataLink: 'customAudio.audioFiles.customAudioFile',
    fileDataLink: 'customAudio.audioFiles.customAudioFile.filename',
    nameDataLink: 'customAudio.audioFiles.customAudioFile.name',
    assetValueType: 'audio',
  },
  videos: {
    featureVarKey: 'disruptorVideo',
    collectionDataLink: 'customVideo.videoFiles.customVideoFile',
    fileDataLink: 'customVideo.videoFiles.customVideoFile.filename',
    nameDataLink: 'customVideo.videoFiles.customVideoFile.name',
    assetValueType: 'videos',
  },
};

const MEDIA_DROP_CORE_VAR_KEYS = new Set(
  Object.values(MEDIA_DROP_FEATURE_CONFIG).map((config) => config.featureVarKey)
);

const resolveComponentIconCategory = (iconCategory, varKey) => {
  if (MEDIA_DROP_CORE_VAR_KEYS.has(varKey)) {
    return 'core';
  }

  return iconCategory || null;
};

const normalizeDroppedMediaAssetUrl = (mediaType, assetUrl) => {
  if (typeof assetUrl !== 'string' || assetUrl.length === 0) {
    return assetUrl || null;
  }

  if (mediaType === 'images') {
    return assetUrl.replace('/images/', '/image/');
  }

  return assetUrl;
};

function TimelineEditor({ campaignId: campaignIdProp, campaignDataProp, customisationVarsProp }) {
  const { campaignId: campaignIdParam } = useParams();
  const campaignId = campaignIdProp || campaignIdParam;
  const lastFetchedCampaignData = useRef(null);
  if (lastFetchedCampaignData.current) {
    campaignDataProp = lastFetchedCampaignData.current;
  }

  const [, setCampaignContext] = useContext(CampaignContext);
  const [authContext, setAuthContext] = useContext(AuthContext);
  const canCreateFeatureTemplate = (authContext?.userData?.userLevel || 0) >= ACCOUNT_TYPES.twitchSuperUser.level;

  const [showingTooltip, setShowingTooltip] = useState(false);

  const getNormalizedCampaignOwnerAccountData = useCallback((incomingCampaignData, fallbackCampaignData) => {
    if (!incomingCampaignData) {
      return incomingCampaignData;
    }

    const incomingOwner = incomingCampaignData.campaignOwnerAccount;
    const fallbackOwner = fallbackCampaignData?.campaignOwnerAccount;
    const fallbackOwnerIsObject = fallbackOwner && typeof fallbackOwner === 'object';

    if (!fallbackOwnerIsObject) {
      return incomingCampaignData;
    }

    const fallbackOwnerId = fallbackOwner?._id;
    if (!fallbackOwnerId) {
      return incomingCampaignData;
    }

    const incomingOwnerIsString = typeof incomingOwner === 'string';
    const incomingOwnerId = incomingOwnerIsString
      ? incomingOwner
      : (incomingOwner && typeof incomingOwner === 'object' ? incomingOwner._id : null);

    if (incomingOwnerId && incomingOwnerId !== fallbackOwnerId) {
      return incomingCampaignData;
    }

    const incomingOwnerIsSparseObject = incomingOwner && typeof incomingOwner === 'object'
      && !incomingOwner.acType
      && !incomingOwner.accountType
      && !incomingOwner.firstName
      && !incomingOwner.lastName
      && !incomingOwner.name
      && !incomingOwner.email;

    if (incomingOwnerIsString || !incomingOwner || incomingOwnerIsSparseObject) {
      return {
        ...incomingCampaignData,
        campaignOwnerAccount: fallbackOwner,
      };
    }

    return incomingCampaignData;
  }, []);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipPos, setTooltipPos] = useState({left: 0, bottom: 0});
  const tooltipTimeout = useRef(null);
  const showTooltip = (text, e) => {
      setTooltipPosToMouse(e, true);
      setTooltipText(text);
      if (text.length > 0 && !showingTooltip && tooltipTimeout.current === null) {
          tooltipTimeout.current = setTimeout(() => setShowingTooltip(true), 300);
      }
  }
  const hideTooltip = (e) => {
      clearTimeout(tooltipTimeout.current);
      tooltipTimeout.current = null;
      setShowingTooltip(false);
  }
    const getFlowLoopSettingsFromChain = (chain) => {
      if (!Array.isArray(chain) || chain.length === 0) {
        return { loop: false, loopDelay: 0 };
      }

      const lastChainItem = CHAINS_HELPER.parseChainItem(chain[chain.length - 1]);
      return {
        loop: lastChainItem?.loop === true,
        loopDelay: Math.max(0, Number(lastChainItem?.loopDelay) || 0),
      };
    }
  const setTooltipPosToMouse = (e, force = false) => {
      if (showingTooltip || force) {
          const toolTipPos = {
              left: e.pageX + 'px',
              bottom: (window.innerHeight - e.pageY + 10) + 'px'
          }
          setTooltipPos(toolTipPos);
      }
  }
  
  // State for library items loaded from database
  const [libraryItems, setLibraryItems] = useState({});
  const [libraryLoading, setLibraryLoading] = useState(true);
  // Cache of templates keyed by _id and name for resolving fromTemplateImage fallback
  const [templatesCache, setTemplatesCache] = useState({});
  
  // State for campaign features (features already added to this campaign)
  const [campaignFeatures, setCampaignFeatures] = useState([]);
  const [fullFeaturesList, setFullFeaturesList] = useState([]); // Store full feature data for lookups
  const [campaignData, setCampaignData] = useState(campaignDataProp || null); // Full campaign data including chains;
  const isFeatureTemplateCampaign = campaignData?.isTemplate === true && campaignData?.templateType === 'feature';
  const currentModeRef = useRef(MODES.FLOW_EDITOR);
  applyCampaignCustomFontsCss(campaignData);
  useEffect(() => {
    WORKING_ON_NODE_DATA.campaignData = campaignData;
    applyCampaignCustomFontsCss(campaignData);
  }, [campaignData]);
  const [campaignChains, setCampaignChains] = useState([]); // Feature chains from campaign
  const chainsLoadedRef = useRef(false); // Track if chains have been loaded to prevent re-loading
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(LEFT_SIDEBAR_MIN_WIDTH);
  const [isResizingLeftSidebar, setIsResizingLeftSidebar] = useState(false);

  const [customisationVars, setCustomisationVars] = useState({});
  const customisationVarsRef = useRef(null);
  const syncedCustomisationSnapshotRef = useRef({ current: {}, default: {} });
  const campaignDataRef = useRef(campaignDataProp || null);
  const editorSocketRef = useRef(null);
  const editorSessionIdRef = useRef(`campaign-editor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const suppressTimelineAutosaveUntilRef = useRef(0);
  const handleRemoteCustomisationSavedRef = useRef(null);
  const [activeEditors, setActiveEditors] = useState([]);
  const [customisationVarsFetched, setCustomisationVarsFetched] = useState(false);
  const updateSyncedSnapshotFeature = useCallback((featureId, currentFeatureData, defaultFeatureData) => {
    if (!featureId) {
      return;
    }

    syncedCustomisationSnapshotRef.current = {
      current: {
        ...(syncedCustomisationSnapshotRef.current?.current || {}),
        [featureId]: cloneComparableValue(currentFeatureData),
      },
      default: {
        ...(syncedCustomisationSnapshotRef.current?.default || {}),
        [featureId]: cloneComparableValue(defaultFeatureData),
      },
    };
  }, []);
  const getCustomisationVars = async (campaignData_arg, updateThisIdOnly = null, callback = null, options = {}) => {
    // console.log('Get Campaign / Stream Customisation Vars', campaignId, campaignData_arg, updateThisIdOnly);
    return await axios
        .get(/* streamId ? getStreamCustomisationVarsRoute :  */getCampaignCustomisationVarsRoute, {
            params: { campaignID: campaignId, streamID: null/*streamId*/ },
            withCredentials: true
        })
        .then(function (response) {
            if (response.data.customisationVars) {
                const incomingCustomisationVars = response.data.customisationVars;
                if (updateThisIdOnly) {
                    if (!customisationVarsRef.current) {
                        customisationVarsRef.current = { current: {}, default: {} };
                    }

                    const incomingCurrentFeature = incomingCustomisationVars?.current?.[updateThisIdOnly];
                    const incomingDefaultFeature = incomingCustomisationVars?.default?.[updateThisIdOnly];
                    const nextFeatureCurrent = options?.preserveUnsavedLocal
                        ? rebaseUnsavedLocalChanges(
                            incomingCurrentFeature,
                            syncedCustomisationSnapshotRef.current?.current?.[updateThisIdOnly],
                            customisationVarsRef.current?.current?.[updateThisIdOnly]
                          )
                        : cloneComparableValue(incomingCurrentFeature);

                    // Only add/update vars for this specific feature, preserving all existing vars (including any pending mutations)
                    customisationVarsRef.current.current = {
                        ...(customisationVarsRef.current.current || {}),
                        [updateThisIdOnly]: nextFeatureCurrent,
                    };
                    customisationVarsRef.current.default = {
                        ...(customisationVarsRef.current.default || {}),
                        [updateThisIdOnly]: cloneComparableValue(incomingDefaultFeature),
                    };
                    updateSyncedSnapshotFeature(updateThisIdOnly, incomingCurrentFeature, incomingDefaultFeature);
                    // Fix src URLs for the new feature
                    const newFeatureData = findFeatureData(campaignData, updateThisIdOnly);
                    if (newFeatureData) {
                        fixSrcUrls(newFeatureData, updateThisIdOnly, campaignId, null, customisationVarsRef.current.current[updateThisIdOnly]);
                    }
                    // Spread to create a new reference so React re-renders and picks up the new feature's vars,
                    // while preserving the inner .current object (so existing mutations are not lost)
                    setCustomisationVars({
                        ...customisationVarsRef.current,
                        current: { ...(customisationVarsRef.current.current || {}) },
                        default: { ...(customisationVarsRef.current.default || {}) },
                    });
                } else {
                    customisationVarsRef.current = incomingCustomisationVars;
                    syncedCustomisationSnapshotRef.current = createCustomisationSnapshot(incomingCustomisationVars);
                    // Step through all the features and fix the srcUrls for each one
                    for (let i = 0; i < campaignData_arg.features.length; i++) {
                        const featureData = findFeatureData(campaignData_arg, campaignData_arg.features[i]._id);
                        if (!featureData || !featureData.feature) continue; // guard: findFeatureData may return null, or feature library ref may be unpopulated
                        //
                        fixSrcUrls(featureData, featureData._id, campaignId, null, customisationVarsRef.current.current[featureData._id]);
                    }
                }

                setCustomisationVars(customisationVarsRef.current);
                setCustomisationVarsFetched(true);
                //
            }

            if (callback) callback();
        })
        .catch(function (error) {
            toast.error('Error - ' + error);
            console.error(error);
        });
  }
  const getCustomisationVarsForFeature = (featureId) => {
    if (customisationVarsRef.current && customisationVarsRef.current.current && customisationVarsRef.current.current[featureId]) {
        return customisationVarsRef.current.current[featureId];
    }
    return null;
  }

  const buildCampaignEditorUser = useCallback(() => {
    const user = authContext?.userData;

    if (!user?._id) {
      return null;
    }

    return {
      _id: user._id,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      name: user.name || '',
      displayName: getCampaignEditorDisplayName(user),
      profilePic: user.profilePic || '',
      accountType: user.accountType || user.acType || '',
      acType: user.acType || user.accountType || '',
      authStrategy: user.authStrategy || '',
      isTwitchLogin: user.isTwitchLogin === true,
      isTwitch: user.isTwitch === true,
    };
  }, [authContext?.userData]);

  const handleLocalCustomisationSaveSuccess = useCallback(({ featureId, savedCurrentData }) => {
    if (!featureId) {
      return;
    }

    updateSyncedSnapshotFeature(featureId, savedCurrentData, syncedCustomisationSnapshotRef.current?.default?.[featureId]);

    const featureData = findFeatureData(campaignDataRef.current, featureId);
    const bridgeSource = { current: { [featureId]: savedCurrentData } };
    const didSyncCustomisationBackedVars = featureData && featureVarsRef.current?.current
      ? syncCustomisationBackedFeatureVars(featureVarsRef.current, featureData, bridgeSource)
      : false;
    const didSyncVideoControlVars = featureData && featureVarsRef.current?.current
      ? syncVideoDisruptorControlFeatureVars(featureVarsRef.current, featureData, bridgeSource)
      : false;
    const didSyncLiveFeatureVars = didSyncCustomisationBackedVars || didSyncVideoControlVars;
    if (didSyncLiveFeatureVars) {
      featureVarsRef.current = {
        ...featureVarsRef.current,
        current: {
          ...featureVarsRef.current.current,
        },
      };
      setFeatureVars(featureVarsRef.current);

      axios
        .post(updateCampaignFeatureVarsRoute, {
          campaignID: campaignId,
          updatedVars: featureVarsRef.current.current,
          withCredentials: true,
        })
        .catch(function (error) {
          toast.error('Error - ' + error);
        });
    }

    const editorSocket = editorSocketRef.current;
    const editorUser = buildCampaignEditorUser();

    if (editorSocket?.connected && editorUser) {
      editorSocket.emit(CAMPAIGN_EDITOR_SOCKET_ACTIONS.customisationSaved, {
        campaignID: campaignId,
        featureID: featureId,
        editorSessionId: editorSessionIdRef.current,
        savedAt: Date.now(),
        user: editorUser,
      });
    }
  }, [buildCampaignEditorUser, campaignId, updateSyncedSnapshotFeature]);

  const emitCampaignEditorCampaignUpdated = useCallback((extraData = {}) => {
    const editorSocket = editorSocketRef.current;
    const editorUser = buildCampaignEditorUser();

    if (!editorSocket?.connected || !editorUser || !campaignId) {
      return;
    }

    editorSocket.emit(CAMPAIGN_EDITOR_SOCKET_ACTIONS.campaignUpdated, {
      campaignID: campaignId,
      editorSessionId: editorSessionIdRef.current,
      savedAt: Date.now(),
      user: editorUser,
      ...extraData,
    });
  }, [buildCampaignEditorUser, campaignId]);

  // Fetch campaign data when campaignId is available
  const fetchCampaignData = async (forceFetch = false) => {
    if (!campaignId) return;
    
    // If campaign data is provided via props, use it instead of fetching
    if (!forceFetch && campaignDataProp) {
      setCampaignData(campaignDataProp);
      setCampaignFeatures(campaignDataProp.features || []);
      setCampaignChains(campaignDataProp.chains || []);
      if (customisationVarsProp) {
        setCustomisationVars(customisationVarsProp);
        customisationVarsRef.current = customisationVarsProp;
        syncedCustomisationSnapshotRef.current = createCustomisationSnapshot(customisationVarsProp);
        for (let i = 0; i < campaignDataProp.features.length; i++) {
            const featureData = findFeatureData(campaignDataProp, campaignDataProp.features[i]._id);
            if (!featureData || !featureData.feature) continue; // guard: may be unpopulated
            //
            fixSrcUrls(featureData, featureData._id, campaignId, null, customisationVarsRef.current.current[featureData._id]);
        }
        generateLeftListNewCustomisationItems(customisationVarsRef.current.current, campaignDataProp);
      } else {
        await getCustomisationVars(campaignDataProp, null, () => generateLeftListNewCustomisationItems(customisationVarsRef.current.current, campaignDataProp));
      }
      
      return;
    }
    
    try {
      const response = await axios.get(getCampaignOverviewRoute, {
        params: { campaignID: campaignId },
        withCredentials: true
      });

      if (response.data && response.data.campaignData) {
        const data = getNormalizedCampaignOwnerAccountData(response.data.campaignData, campaignDataRef.current);
        lastFetchedCampaignData.current = data; // Store the last fetched campaign data for potential reuse
        setCampaignData(data);
        setCampaignFeatures(data.features || []);
        setCampaignChains(data.chains || []);
        if (forceFetch) {
          setCampaignContext(oldValues => ({
            ...oldValues,
            campaignData: getNormalizedCampaignOwnerAccountData(data, oldValues?.campaignData),
          }));
        }
        if (!forceFetch && customisationVarsProp) {
          setCustomisationVars(customisationVarsProp);
          customisationVarsRef.current = customisationVarsProp;
          syncedCustomisationSnapshotRef.current = createCustomisationSnapshot(customisationVarsProp);
          for (let i = 0; i < data.features.length; i++) {
            const featureData = findFeatureData(data, data.features[i]._id);
            if (!featureData || !featureData.feature) continue; // guard: may be unpopulated
            //
            fixSrcUrls(featureData, featureData._id, campaignId, null, customisationVarsRef.current.current[featureData._id]);
          }
          generateLeftListNewCustomisationItems(customisationVarsRef.current.current, data);
        } else {
          await getCustomisationVars(data, null, () => generateLeftListNewCustomisationItems(customisationVarsRef.current.current, data));
        }
        
      }
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      toast.error('Failed to load campaign data');
    }
  };

  useEffect(() => {
    fetchCampaignData();
  }, [campaignId, campaignDataProp]);

  handleRemoteCustomisationSavedRef.current = async (data) => {
    if (!data?.featureID || !campaignDataRef.current) {
      return;
    }

    await getCustomisationVars(
      campaignDataRef.current,
      data.featureID,
      () => generateLeftListNewCustomisationItems(customisationVarsRef.current?.current || {}, campaignDataRef.current),
      { preserveUnsavedLocal: true }
    );

    forceUpdateComponent();
  };

  useEffect(() => {
    const editorUser = buildCampaignEditorUser();

    if (!campaignId || !editorUser?._id) {
      setActiveEditors([]);
      return undefined;
    }

    const editorSocket = createCampaignEditorSocket();

    editorSocketRef.current = editorSocket;

    const joinCampaignEditorRoom = () => {
      editorSocket.emit(CAMPAIGN_EDITOR_SOCKET_ACTIONS.join, {
        campaignID: campaignId,
        editorSessionId: editorSessionIdRef.current,
        user: editorUser,
      });
    };

    const handleActiveEditors = (payload) => {
      if (payload?.campaignID === campaignId) {
        setActiveEditors(Array.isArray(payload?.editors) ? payload.editors : []);
      }
    };

    const handleRemoteSave = (payload) => {
      if (payload?.campaignID !== campaignId || payload?.editorSessionId === editorSessionIdRef.current) {
        return;
      }

      handleRemoteCustomisationSavedRef.current?.(payload);
    };

    const handleRemoteCampaignUpdated = async (payload) => {
      if (payload?.campaignID !== campaignId || payload?.editorSessionId === editorSessionIdRef.current) {
        return;
      }

      // Only react to editor-aware campaign update events.
      // Some non-editor API writes can emit campaignUpdated without an editorSessionId,
      // which would otherwise suppress local autosave during first-time feature drops.
      if (!payload?.editorSessionId) {
        return;
      }

      suppressTimelineAutosaveUntilRef.current = Date.now() + 5000;
      chainsLoadedRef.current = false;
      await fetchCampaignData(true);
    };

    const handleConnectError = (error) => {
      console.error('Campaign editor socket connection error:', error);
      console.error('Campaign editor transport options:', editorSocket.io?.opts?.transports);
    };

    editorSocket.on('connect', joinCampaignEditorRoom);
    editorSocket.on('connect_error', handleConnectError);
    editorSocket.on(CAMPAIGN_EDITOR_SOCKET_ACTIONS.activeEditors, handleActiveEditors);
    editorSocket.on(CAMPAIGN_EDITOR_SOCKET_ACTIONS.customisationSaved, handleRemoteSave);
    editorSocket.on(CAMPAIGN_EDITOR_SOCKET_ACTIONS.campaignUpdated, handleRemoteCampaignUpdated);

    if (editorSocket.connected) {
      joinCampaignEditorRoom();
    }

    return () => {
      editorSocket.emit(CAMPAIGN_EDITOR_SOCKET_ACTIONS.leave, { campaignID: campaignId });
      editorSocket.off('connect', joinCampaignEditorRoom);
      editorSocket.off('connect_error', handleConnectError);
      editorSocket.off(CAMPAIGN_EDITOR_SOCKET_ACTIONS.activeEditors, handleActiveEditors);
      editorSocket.off(CAMPAIGN_EDITOR_SOCKET_ACTIONS.customisationSaved, handleRemoteSave);
      editorSocket.off(CAMPAIGN_EDITOR_SOCKET_ACTIONS.campaignUpdated, handleRemoteCampaignUpdated);
      editorSocket.disconnect();

      if (editorSocketRef.current === editorSocket) {
        editorSocketRef.current = null;
      }

      setActiveEditors([]);
    };
  }, [buildCampaignEditorUser, campaignId]);

  // Fetch features from database on mount
  useEffect(() => {
    const fetchLibraryItems = async () => {
      try {
        setLibraryLoading(true);
        
        // Fetch all features and disruptors from the database
        const response = await axios.get(getDisruptorListRoute, {
          params: { includeFeatures: true },
          withCredentials: true
        });

        if (response.data && response.data.disruptorList) {
          // Store full feature data for later lookups
          setFullFeaturesList(response.data.disruptorList);
          
          // Organize features by featureCategory
          const categorizedItems = {};
          const miscItems = [];

          // Build a map of featureKey → [{configKey, featureName}] from WorkspaceConfig
          // so features with multiple config variants (e.g. particleEffects_fireworks) each
          // get their own library item.
          const configsByFeatureKey = {};
          Object.entries(WorkspaceConfig).forEach(([configKey, config]) => {
            const fk = config?.featureKey || configKey;
            if (!configsByFeatureKey[fk]) configsByFeatureKey[fk] = [];
            configsByFeatureKey[fk].push({ configKey, featureName: config?.featureName });
          });
          
          response.data.disruptorList.forEach(feature => {
            // Skip features with 'Hidden' category
            const rawCategory = feature.featureCategory;
            const categoryArr = Array.isArray(rawCategory)
              ? rawCategory
              : (rawCategory ? [rawCategory] : []);

            if (categoryArr.includes('Hidden')) {
              return;
            }

            // Determine which config variants exist for this feature's varKey
            const variants = configsByFeatureKey[feature.varKey];
            const itemsToAdd = (variants && variants.length > 1)
              ? variants.map(v => ({
                  id: v.configKey ? v.configKey : feature.varKey, // Use configKey as ID if it exists, otherwise fallback to varKey
                  label: v.featureName || feature.featureName || feature.disruptorName,
                  icon: getFeatureIcon(v.configKey ? v.configKey : feature.varKey, feature.featureType),
                  category: categoryArr[0] || null,
                  featureType: feature.featureType,
                  varKey: v.configKey ? v.configKey : feature.varKey, // store the config-specific key so the right config loads
                  dummyData: {},
                  _id: feature._id
                }))
              : [{
                  id: feature.varKey,
                  label: variants?.[0]?.featureName || feature.featureName || feature.disruptorName,
                  icon: getFeatureIcon(feature.varKey, feature.featureType),
                  category: categoryArr[0] || null,
                  featureType: feature.featureType,
                  varKey: feature.varKey,
                  dummyData: {},
                  _id: feature._id
                }];

            itemsToAdd.forEach(categorizedItem => {
              if (categoryArr.length === 0) {
                miscItems.push(categorizedItem);
                return;
              }

              // Add to every category in the array so multi-category features appear in each group
              categoryArr.forEach(cat => {
                if (!categorizedItems[cat]) {
                  categorizedItems[cat] = [];
                }
                categorizedItems[cat].push({ ...categorizedItem, category: cat });
              });
            });
          });

          if (miscItems.length > 0) {
            categorizedItems['Misc'] = miscItems;
          }
          setLibraryItems(categorizedItems);
        }
        
        setLibraryLoading(false);
      } catch (error) {
        console.error('Error fetching library items:', error);
        setLibraryLoading(false);
        // Fallback to empty library
        setLibraryItems({ Misc: [] });
      }
    };

    fetchLibraryItems();

    // Also fetch templates so we can resolve templateImage for legacy features
    // (features added before fromTemplateImage was stored in the DB)
    axios.get(getAllTemplatesCategoriesRoute, { withCredentials: true })
      .then(res => {
        if (res.data?.data?.templates) {
          const cache = {};
          res.data.data.templates.forEach(t => {
            if (t._id) cache[t._id.toString()] = t;
            if (t.id)  cache[t.id]              = t;
            if (t.name) cache[t.name]           = t;
          });
          setTemplatesCache(cache);
        }
      })
      .catch(() => {/* non-critical — template images just won't appear for legacy items */});
  }, []);

  // Update library items to include campaign features when they change
  useEffect(() => {

    if (campaignFeatures.length === 0) {
      setLibraryItems(prevItems => {
        const { Campaign: oldCampaign, ...otherCategories } = prevItems;
        return otherCategories;
      });
      return;
    }

    setLibraryItems(prevItems => {
      // Remove existing Campaign category from previous items to avoid stale data
      const { Campaign: oldCampaign, ...otherCategories } = prevItems;
      
      // Create Campaign category with existing campaign features
      const campaignCategory = campaignFeatures.map(cf => {
        if (!cf?._id) {
          console.warn('Skipping invalid campaign feature entry:', cf);
          return null;
        }

        // Get the feature object (might be populated, just an ID, or missing after a merge)
        const feature = cf.feature && typeof cf.feature === 'object'
          ? cf.feature
          : fullFeaturesList.find(f => f._id === cf.feature || f.varKey === cf.varKey || (cf.configKey && f.varKey === cf.configKey));
        
        if (!feature) {
          console.warn('Feature not found for campaign feature:', cf._id);
          return null;
        }

        const baseLabel = cf.contentLabel || feature.featureName || feature.disruptorName;
        const libraryItem = {
          id: cf._id, // Use the campaign feature's unique _id
          label: baseLabel,
          icon: getFeatureIcon((cf.configKey || cf.varKey) || feature.varKey, feature.featureType),
          category: 'Campaign',
          iconCategory: resolveComponentIconCategory(feature.featureCategory, (cf.configKey || cf.varKey) || feature.varKey),
          featureType: feature.featureType,
          varKey: (cf.configKey || cf.varKey) || feature.varKey,
          dummyData: {},
          _id: feature._id,
          campaignFeatureId: cf._id, // Mark this as an existing campaign feature
          isExistingCampaignFeature: true, // Flag to indicate this is from the campaign
          fromTemplateName: cf.fromTemplateName || null,
          fromTemplateImage: cf.fromTemplateImage
            || (cf.fromTemplateId ? templatesCache[cf.fromTemplateId]?.templateImage : null)
            || (cf.fromTemplateName ? templatesCache[cf.fromTemplateName]?.templateImage : null)
            || null,
        };

        return libraryItem;
      }).filter(item => item !== null);

      // Add Campaign category at the beginning with completely fresh data
      return {
        Campaign: campaignCategory,
        ...otherCategories
      };
    });
  }, [campaignFeatures, fullFeaturesList, templatesCache]);

  const resolveCampaignFeatureObject = useCallback((campaignFeature) => {
    if (!campaignFeature) return null;

    if (campaignFeature.feature && typeof campaignFeature.feature === 'object') {
      return campaignFeature.feature;
    }

    return fullFeaturesList.find(f =>
      f._id === campaignFeature.feature ||
      f.varKey === campaignFeature.varKey ||
      (campaignFeature.configKey && f.varKey === campaignFeature.configKey)
    ) || null;
  }, [fullFeaturesList]);

  const resolveCampaignFeatureId = useCallback((campaignFeature) => {
    if (!campaignFeature) return null;

    if (campaignFeature.feature && typeof campaignFeature.feature === 'object') {
      return campaignFeature.feature._id || null;
    }

    if (typeof campaignFeature.feature === 'string' && campaignFeature.feature) {
      return campaignFeature.feature;
    }

    return resolveCampaignFeatureObject(campaignFeature)?._id || null;
  }, [resolveCampaignFeatureObject]);

  const serializeCampaignFeatureForUpdate = useCallback((campaignFeature, overrides = {}) => {
    if (!campaignFeature?._id) {
      console.warn('Skipping invalid campaign feature during update serialization:', campaignFeature);
      return null;
    }

    const featureId = resolveCampaignFeatureId(campaignFeature);
    if (!featureId) {
      console.warn('Unable to resolve feature ID for campaign feature:', campaignFeature);
      return null;
    }

    const resolvedFeature = resolveCampaignFeatureObject(campaignFeature);

    return {
      _id: campaignFeature._id,
      feature: featureId,
      contentLabel: overrides.contentLabel ?? campaignFeature.contentLabel ?? resolvedFeature?.featureName ?? resolvedFeature?.disruptorName ?? '',
      varKey: campaignFeature.varKey,
      configKey: campaignFeature.configKey || null,
      countIn: campaignFeature.countIn || false,
      countInTime: campaignFeature.countInTime || 10,
      isChained: campaignFeature.isChained || false,
      stackingOrder: overrides.stackingOrder ?? campaignFeature.stackingOrder ?? 0
    };
  }, [resolveCampaignFeatureId, resolveCampaignFeatureObject]);

  const normalizeFeatureReferenceId = useCallback((value) => {
    if (value === undefined || value === null) {
      return null;
    }

    return String(value);
  }, []);

  // Helper function to assign icons based on feature type/varKey
  const getFeatureIcon = useCallback((varKey, featureType) => {
    return featureIcons[varKey] || featureIcons[featureType] || null;
  }, []);

  const buildCampaignWideStackingOrder = useCallback((prevOrder = []) => {
    const normalizedPrevOrder = [];
    const prevByFeatureId = new Map();

    prevOrder.forEach((item) => {
      const featureId = normalizeFeatureReferenceId(item?.featureId || item?.id);
      if (!featureId || prevByFeatureId.has(featureId)) {
        return;
      }

      const normalizedItem = {
        ...item,
        id: featureId,
        featureId,
      };

      normalizedPrevOrder.push(normalizedItem);
      prevByFeatureId.set(featureId, normalizedItem);
    });

    const nextItems = (campaignFeatures || [])
      .map((campaignFeature, campaignIndex) => {
        const featureId = normalizeFeatureReferenceId(campaignFeature?._id);
        if (!featureId) {
          return null;
        }

        const prevItem = prevByFeatureId.get(featureId);
        const resolvedFeature = resolveCampaignFeatureObject(campaignFeature);
        const varKey = campaignFeature?.varKey || resolvedFeature?.varKey || campaignFeature?.configKey || prevItem?.varKey || null;
        const featureType = resolvedFeature?.featureType || resolvedFeature?.type || null;
        const name = campaignFeature?.contentLabel || prevItem?.name || resolvedFeature?.featureName || resolvedFeature?.disruptorName || 'Untitled';

        return {
          ...prevItem,
          id: featureId,
          featureId,
          name,
          label: name,
          icon: prevItem?.icon || getFeatureIcon(varKey, featureType),
          iconCategory: prevItem?.iconCategory || resolveComponentIconCategory(resolvedFeature?.featureCategory, varKey),
          varKey,
          _campaignStackingOrder: typeof campaignFeature?.stackingOrder === 'number' ? campaignFeature.stackingOrder : Number.MAX_SAFE_INTEGER,
          _campaignIndex: campaignIndex,
        };
      })
      .filter(Boolean);

    nextItems.sort((a, b) => {
      const prevIndexA = normalizedPrevOrder.findIndex((item) => item.featureId === a.featureId);
      const prevIndexB = normalizedPrevOrder.findIndex((item) => item.featureId === b.featureId);
      const hasPrevA = prevIndexA !== -1;
      const hasPrevB = prevIndexB !== -1;

      if (hasPrevA && hasPrevB) {
        return prevIndexA - prevIndexB;
      }

      if (hasPrevA) {
        return -1;
      }

      if (hasPrevB) {
        return 1;
      }

      if (a._campaignStackingOrder !== b._campaignStackingOrder) {
        return a._campaignStackingOrder - b._campaignStackingOrder;
      }

      return a._campaignIndex - b._campaignIndex;
    });

    return nextItems.map((item, index) => {
      const { _campaignStackingOrder, _campaignIndex, ...cleanItem } = item;
      return {
        ...cleanItem,
        zIndex: index,
      };
    });
  }, [campaignFeatures, getFeatureIcon, normalizeFeatureReferenceId, resolveCampaignFeatureObject]);

  // Use custom hooks for state and logic
  const editorState = useNodeEditorState();
  const {
    steps, setSteps,
    flowItems, setFlowItems,
    draggedItem, setDraggedItem,
    draggedPanel, setDraggedPanel,
    dragOverPosition, setDragOverPosition,
    dragOverStep, setDragOverStep,
    animatingPanels, setAnimatingPanels,
    panOffset, setPanOffset,
    zoom, setZoom,
    isPanning, setIsPanning,
    lastPanPoint, setLastPanPoint,
    lockedZoomCenter, setLockedZoomCenter,
    selectedItem, setSelectedItem,
    expandedComponents, setExpandedComponents,
    activeTab, setActiveTab,
    layers, setLayers,
    expandedLayers, setExpandedLayers,
    timelineHeight, setTimelineHeight,
    isResizingTimeline, setIsResizingTimeline,
    isStackingMode, setIsStackingMode,
    stackingOrder, setStackingOrder,
    draggedStackItem, setDraggedStackItem,
    dragOverStackItem, setDragOverStackItem,
    hoveredStackingItem, setHoveredStackingItem,
    draggedComponent, setDraggedComponent,
    draggedComponentSourceStep, setDraggedComponentSourceStep,
    isEditingName, setIsEditingName,
    showExpansionLines, setShowExpansionLines,
    flows, setFlows,
    activeFlowId, setActiveFlowId,
    showFlowNameModal, setShowFlowNameModal,
  } = editorState;

  const [hoveredTimelineComponentId, setHoveredTimelineComponentId] = useState(null);

  // Track current action state
  const [currentAction, setCurrentAction] = useState(ACTION_STATE.IDLE);

  // Refs for tracking drag state to avoid closure staleness in event handlers
  const draggedComponentRef = useRef(null);
  const draggedItemRef = useRef(null);
  const draggedPanelRef = useRef(null);
  const dragOverStepRef = useRef(null);
  const draggedComponentSourceStepRef = useRef(null);
  const lastActivatedStepIdRef = useRef(null);

  const flowAreaRef = useRef(null);
  
  // Refs to prevent cascade re-renders
  const isUpdatingFlowItemsRef = useRef(false);
  const lastStepsHashRef = useRef('');
  const lastStackingHashRef = useRef('');
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const hoverThrottleRef = useRef(null);
  const hoverEffectsEnabled = true; // Enable hover/rollover effects for stacking highlight
  const hoveredStackingItemSafe = hoverEffectsEnabled ? hoveredStackingItem : null;
  const [isScrollingState, setIsScrollingState] = useState(false);
  const autoFramedFlowIdsRef = useRef(new Set());
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuStep, setContextMenuStep] = useState(null);
  const [contextMenuComponent, setContextMenuComponent] = useState(null);
  const [currentFlowIndex, setCurrentFlowIndex] = useState(0);
  const [copiedStep, setCopiedStep] = useState(null);
  const [copiedComponent, setCopiedComponent] = useState(null);
  const [activeNode, setActiveNode] = useState(null);

  // Apply-template popup state
  const [showApplyTemplatePopup, setShowApplyTemplatePopup] = useState(false);
  const [applyTemplateTarget, setApplyTemplateTarget] = useState(null); // { campaignFeatureId, featureVarKey, featureLabel, hasCustomisations }
  const [showCreateFeatureTemplatePopup, setShowCreateFeatureTemplatePopup] = useState(false);
  const [createFeatureTemplateTarget, setCreateFeatureTemplateTarget] = useState(null);

  // Use panning and zoom hook
  const panningZoomHandlers = usePanningAndZoom({
    isPanning,
    setIsPanning,
    lastPanPoint,
    setLastPanPoint,
    panOffset,
    setPanOffset,
    zoom,
    setZoom,
    lockedZoomCenter,
    setLockedZoomCenter,
    flowAreaRef,
    currentAction,
    setCurrentAction,
    ACTION_STATE
  });

  // Use grid calculations hook
  const {
    gridDots,
    safeGridWidth,
    safeGridHeight,
    minX,
    minY,
    connections,
    GRID_SIZE,
    CONTAINER_WIDTH,
    CONTAINER_HEIGHT
  } = useGridCalculations(flowItems, showExpansionLines);
  
  // Debug: Log connections when they change
  useEffect(() => {
    //
  }, [connections]);
  
  // Debug: Log flowItems when they change
  useEffect(() => {
    //
    // });
  }, [flowItems, activeFlowId]);
  
  // Layer management functions
  const handleLayerReorder = (dragIndex, hoverIndex) => {
    setLayers(prevLayers => {
      const newLayers = [...prevLayers];
      const draggedLayer = newLayers[dragIndex];
      newLayers.splice(dragIndex, 1);
      newLayers.splice(hoverIndex, 0, draggedLayer);
      return newLayers;
    });
  };
  
  // Stacking order management
  const draggedStackItemRef = useRef(null);
  const draggedStackItemIdRef = useRef(null);
  const targetDropIndexRef = useRef(null);
  const stackingOrderRef = useRef(stackingOrder);

  useEffect(() => {
    stackingOrderRef.current = stackingOrder;
  }, [stackingOrder]);

  const getStackIdentity = (item) => normalizeFeatureReferenceId(item?.featureId || item?.id);

  const getReorderedStackingOrder = (currentOrder, dragItemId, targetIndex) => {
    const dragIdentity = normalizeFeatureReferenceId(dragItemId);
    const dragIndex = currentOrder.findIndex(item => getStackIdentity(item) === dragIdentity);
    if (dragIndex === -1 || dragIndex === targetIndex) return null;

    const newOrder = [...currentOrder];
    const draggedItem = newOrder[dragIndex];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    return newOrder.map((item, idx) => ({
      ...item,
      zIndex: idx
    }));
  };
  
  const handleStackingReorder = (dragItemId, targetIndex) => {
    const nextOrder = getReorderedStackingOrder(stackingOrder, dragItemId, targetIndex);
    if (!nextOrder) return null;

    setStackingOrder(nextOrder);

    // Update the dragged item index to track its new position
    setDraggedStackItem(targetIndex);
    draggedStackItemRef.current = targetIndex;

    return nextOrder;
  };
  
  const handleStackDragStart = (index, itemId) => {
    setDraggedStackItem(index);
    draggedStackItemRef.current = index;
    draggedStackItemIdRef.current = itemId;
    targetDropIndexRef.current = index;
  };
  
  const handleStackDragEnd = async () => {
    // Perform the reorder using item ID instead of index
    const draggedId = draggedStackItemIdRef.current;
    let targetIdx = targetDropIndexRef.current;
    const draggedIdx = draggedStackItemRef.current;
    
    if (targetIdx !== null && targetIdx !== draggedIdx && draggedId) {
      // When dragging down, adjust the target index because removal happens first
      // The drop indicator shows above the target item, so we need to account for the removal
      if (targetIdx > draggedIdx) {
        targetIdx = targetIdx - 1;
      }
      const nextStackingOrder = handleStackingReorder(draggedId, targetIdx);
      if (nextStackingOrder) {
        const persisted = await persistCampaignFeatureStackingOrder(nextStackingOrder);
        if (persisted) {
          await saveTimelineAsChain(activeFlowId, steps, { successMessage: 'Saved stacking order' }, nextStackingOrder);
        } else {
          toast.error('Failed to save stacking order');
        }
      }
    }
    
    // Keep the dragging state active longer to hide the ghost animation (300ms matches typical browser animation)
    setTimeout(() => {
      setDraggedStackItem(null);
      setDragOverStackItem(null);
      draggedStackItemRef.current = null;
      draggedStackItemIdRef.current = null;
      targetDropIndexRef.current = null;
    }, 300);
  };
  
  const handleStackDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedStackItemRef.current === null) return;
    
    setDragOverStackItem(index);
    targetDropIndexRef.current = index;
  };

  const handleStackingContextAction = async (itemId, action) => {
    if (!itemId || !action) {
      return;
    }

    const currentOrder = stackingOrderRef.current || [];
    if (currentOrder.length === 0) {
      return;
    }

    const itemIdentity = normalizeFeatureReferenceId(itemId);
    const currentIndex = currentOrder.findIndex((item) => getStackIdentity(item) === itemIdentity);
    if (currentIndex === -1) {
      return;
    }

    let targetIndex = currentIndex;

    switch (action) {
      case 'move-to-front':
        targetIndex = 0;
        break;
      case 'move-forward':
        targetIndex = Math.max(0, currentIndex - 1);
        break;
      case 'move-back':
        targetIndex = Math.min(currentOrder.length - 1, currentIndex + 1);
        break;
      case 'move-to-back':
        targetIndex = currentOrder.length - 1;
        break;
      default:
        return;
    }

    if (targetIndex === currentIndex) {
      return;
    }

    const nextStackingOrder = getReorderedStackingOrder(currentOrder, itemIdentity, targetIndex);
    if (!nextStackingOrder) {
      return;
    }

    setStackingOrder(nextStackingOrder);

    const persisted = await persistCampaignFeatureStackingOrder(nextStackingOrder);
    if (persisted) {
      await saveTimelineAsChain(activeFlowId, steps, { successMessage: 'Saved stacking order' }, nextStackingOrder);
    } else {
      toast.error('Failed to save stacking order');
    }
  };

  const getStackingContextMenuState = useCallback((itemId) => {
    const currentOrder = stackingOrderRef.current || [];
    const itemIdentity = normalizeFeatureReferenceId(itemId);

    if (!itemIdentity || currentOrder.length === 0) {
      return {
        currentIndex: -1,
        canMoveToFront: false,
        canMoveForward: false,
        canMoveBack: false,
        canMoveToBack: false,
      };
    }

    const currentIndex = currentOrder.findIndex((item) => getStackIdentity(item) === itemIdentity);
    const hasItem = currentIndex !== -1;

    return {
      currentIndex,
      canMoveToFront: hasItem && currentIndex > 0,
      canMoveForward: hasItem && currentIndex > 0,
      canMoveBack: hasItem && currentIndex < currentOrder.length - 1,
      canMoveToBack: hasItem && currentIndex < currentOrder.length - 1,
    };
  }, [normalizeFeatureReferenceId]);
  
  const handleLayerToggle = (layerId) => {
    setExpandedLayers(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(layerId)) {
        newExpanded.delete(layerId);
      } else {
        newExpanded.add(layerId);
      }
      return newExpanded;
    });
  };

  const persistCampaignFeatureStackingOrder = useCallback(async (nextStackingOrder) => {
    if (!campaignId || !nextStackingOrder || nextStackingOrder.length === 0) {
      return false;
    }

    const stackingByFeatureId = new Map(
      nextStackingOrder
        .filter((item) => item?.featureId)
        .map((item, index) => [normalizeFeatureReferenceId(item.featureId), index])
    );

    const updatedFeaturesList = campaignFeatures
      .map((campaignFeature) => {
        const campaignFeatureId = normalizeFeatureReferenceId(campaignFeature?._id);

        return serializeCampaignFeatureForUpdate(campaignFeature, {
          stackingOrder: campaignFeatureId && stackingByFeatureId.has(campaignFeatureId)
            ? stackingByFeatureId.get(campaignFeatureId)
            : (campaignFeature?.stackingOrder || 0),
        });
      })
      .filter(Boolean);

    try {
      const response = await axios.post(updatedFeatureListRoute, {
        campaignID: campaignId,
        features: updatedFeaturesList,
        addingFeature: false,
      }, {
        withCredentials: true,
      });

      if (response.data?.campaignData?.features) {
        const updatedFeatures = response.data.campaignData.features || [];
        setCampaignFeatures(updatedFeatures);
        setCampaignData((prev) => prev ? { ...prev, features: updatedFeatures } : prev);
        setCampaignContext((prev) => ({
          ...prev,
          campaignData: prev?.campaignData
            ? { ...prev.campaignData, features: updatedFeatures }
            : prev.campaignData,
        }));
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error updating campaign feature stacking order:', error);
      return false;
    }
  }, [campaignId, campaignFeatures, normalizeFeatureReferenceId, serializeCampaignFeatureForUpdate, setCampaignContext]);

  // Flow management functions
  const handleFlowChange = (flowId) => {
    
    // First, save current flow state
    const updatedFlows = flows.map(flow => 
      flow.id === activeFlowId 
        ? { ...flow, flowItems, steps, stackingOrder }
        : flow
    );
    
    // Find the new flow
    const newFlow = updatedFlows.find(f => f.id === flowId);
    if (!newFlow) {
      console.warn('Flow not found:', flowId);
      return;
    }
    
    // Get the flow index to access locked states
    const newFlowIndex = updatedFlows.findIndex(f => f.id === flowId);
    const stepLockedStates = campaignData?.chainMetadata?.[newFlowIndex]?.stepLockedStates || {};
    
    // Apply locked states to steps using index
    const stepsWithLockedStates = (newFlow.steps || []).map((step, idx) => ({
      ...normalizeFlowStepWidth(step),
      locked: stepLockedStates[idx] || false
    }));
    const normalizedFlowItems = (newFlow.flowItems || []).map(normalizeFlowItemWidth);
    const nextStackingOrder = Array.isArray(newFlow.stackingOrder) ? newFlow.stackingOrder : [];

    // Flow preview state is flow-specific. Clear it on switch so the next flow
    // does not inherit selected/visible items or playhead position from the previous one.
    handleFlowBackgroundClick();
    setActiveVisualisedPreviewNode(null);
    setVisibleTriggeredDisruptorId(null);
    setVisibleTriggeredDisruptorParamValue(null);
    selectedFeaturePlacementContextRef.current = null;
    setSelectedFeaturePlacementContext(null);
    setTimelinePlayheadStep(0);

    const nextVisibleStepId = [...stepsWithLockedStates].sort((left, right) => left.x - right.x)[0]?.id || null;
    if (nextVisibleStepId) {
      syncStepComponentVisibility(nextVisibleStepId, stepsWithLockedStates, true);
    } else if (leftListNewCustomisationItemsRef.current) {
      Object.values(leftListNewCustomisationItemsRef.current).forEach((item) => {
        if (!item) {
          return;
        }

        item.visible = false;
        if (item.topNode) {
          item.topNode.visible = false;
        }
      });
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
    }
    
    // Update all states
    setFlows(updatedFlows);
    setActiveFlowId(flowId);
    setCurrentFlowIndex(newFlowIndex);
    setFlowItems(normalizedFlowItems);
    setSteps(stepsWithLockedStates);
    setStackingOrder(nextStackingOrder);
  };

  const handleCreateFlow = async (flowName) => {
    if (isFeatureTemplateCampaign) {
      toast.error('Feature templates are limited to a single flow.');
      return;
    }

    const newFlowId = `flow-${Date.now()}`;
    const newFlow = {
      id: newFlowId,
      name: flowName,
      flowItems: [],
      steps: [],
      stackingOrder: [],
      loop: false,
      loopDelay: 0,
      stepNames: [] // Initialize with empty step names array
    };
    
    setFlows(prev => [...prev, newFlow]);
    
    // Create a new empty feature chain for this flow
    if (campaignId && campaignData) {
      const updatedChains = [...campaignChains, []]; // Add empty chain for new flow
      const updatedChainMetadata = [...(campaignData.chainMetadata || []), { flowName, stepNames: [] }];
      
      try {
        await axios.post(updateCampaignRoute, {
          campaignID: campaignId,
          campaignName: campaignData.campaignName,
          campaignTier: campaignData.campaignTier,
          selectedRegion: campaignData.region,
          clientName: campaignData.client,
          adBugCopy: campaignData.adBugCopy,
          campaignOwner: campaignData.campaignOwnerAccount ? campaignData.campaignOwnerAccount._id : null,
          sfLink: campaignData.sfLink,
          sfNumber: campaignData.sfNumber,
          chains: updatedChains,
          chainMetadata: updatedChainMetadata,
        }, {
          withCredentials: true
        });
        
        setCampaignChains(updatedChains);
        toast.success(`Created new feature chain: ${flowName}`);
      } catch (error) {
        console.error('Error creating feature chain:', error);
        toast.error('Failed to create feature chain');
      }
    }
    
    handleFlowChange(newFlowId);
  };

  // Context menu handlers
  const handleStepContextMenu = (e, step) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
    setContextMenuStep(step);
    setContextMenuComponent(null);
  };

  const handleComponentContextMenu = (e, component, step) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
    setContextMenuComponent(component);
    setContextMenuStep(step);
  };

  const closeContextMenu = () => {
    setContextMenu(null);
    setContextMenuStep(null);
    setContextMenuComponent(null);
  };

  const handleOpenApplyTemplate = () => {
    const component = contextMenuComponent;
    if (!component) return;
    closeContextMenu();

    // Resolve the campaign feature subdoc ID and varKey from the component
    const featureId = normalizeFeatureReferenceId(component.featureId || component.id);
    const campaignFeature = campaignFeatures.find(cf => cf._id?.toString() === featureId || cf._id === featureId);
    if (!campaignFeature) return;

    const varKey = campaignFeature.varKey;
    const label = campaignFeature.contentLabel || component.label || varKey;

    // Check whether this feature has any saved customisations in campaignData
    const featureIdStr = String(featureId);
    const hasCust = (campaignData?.customisations || []).some(
      c => c.var && c.var.startsWith(`${featureIdStr}.`)
    );

    setApplyTemplateTarget({ campaignFeatureId: featureIdStr, featureVarKey: varKey, featureLabel: label, hasCustomisations: hasCust });
    setShowApplyTemplatePopup(true);
  };

  const handleConvertComponentToFeatureTemplate = () => {
    const component = contextMenuComponent;
    if (!component || !campaignId) return;

    const featureId = normalizeFeatureReferenceId(component.featureId || component.id);
    const campaignFeature = campaignFeatures.find(cf => cf._id?.toString() === featureId || cf._id === featureId);

    closeContextMenu();

    if (!campaignFeature) {
      toast.error('Failed to resolve the selected component.');
      return;
    }

    setCreateFeatureTemplateTarget({
      campaignFeatureId: featureId,
      featureLabel: campaignFeature.contentLabel || component.label || campaignFeature.varKey || 'Feature',
    });
    setShowCreateFeatureTemplatePopup(true);
  };

  const ensureSinglePrimaryComponent = useCallback((components = []) => {
    if (!components.length) {
      return components;
    }

    const existingPrimary = components.find(component => component.isPrimaryStepItem);
    const primaryId = existingPrimary ? existingPrimary.id : components[0].id;

    return components.map(component => ({
      ...component,
      isPrimaryStepItem: component.id === primaryId,
    }));
  }, []);

  const setPrimaryComponentForStep = useCallback((stepId, componentId) => {
    setSteps(prevSteps => prevSteps.map(step => {
      if (step.id !== stepId) {
        return step;
      }

      return {
        ...step,
        components: step.components.map(component => ({
          ...component,
          isPrimaryStepItem: component.id === componentId,
        })),
      };
    }));
  }, []);

  const handleRenameStep = () => {
    if (contextMenuStep) {
      setIsEditingName(contextMenuStep.id);
    }
    closeContextMenu();
  };

  const handleSetPrimaryComponent = () => {
    if (!contextMenuStep || !contextMenuComponent) return;

    const stepId = contextMenuStep.id || contextMenuStep.stepId;

    const updatedSteps = steps.map(step => {
      if (step.id !== stepId) {
        return step;
      }

      return {
        ...step,
        components: step.components.map(component => ({
          ...component,
          isPrimaryStepItem: component.id === contextMenuComponent.id,
        })),
      };
    });

    setSteps(updatedSteps);

    // Directly update flowItems for immediate visual feedback without waiting for the sync effect
    setFlowItems(prevItems => prevItems.map(item => {
      if (item.type !== 'panel') return item;
      if (item.id !== stepId && item.stepId !== stepId) return item;
      return {
        ...item,
        components: (item.components || []).map(comp => ({
          ...comp,
          isPrimaryStepItem: comp.id === contextMenuComponent.id,
        })),
      };
    }));

    saveTimelineAsChain(activeFlowId, updatedSteps, { showSuccessToast: false });
    toast.success('Primary feature updated');
    closeContextMenu();
  };

  const handleDuplicateStep = async () => {
    if (!contextMenuStep) return;
    
    // If a component is selected, duplicate the component within the same step
    if (contextMenuComponent) {
      const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
      if (stepIndex === -1) return;

      const duplicateComponent = await duplicateCampaignFeatureFromComponent(contextMenuComponent);
      if (!duplicateComponent) {
        return;
      }
      
      const updatedSteps = [...steps];
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        components: ensureSinglePrimaryComponent([...updatedSteps[stepIndex].components, { ...duplicateComponent, isPrimaryStepItem: false }])
      };
      
      setSteps(updatedSteps);
      saveTimelineAsChain(activeFlowId, updatedSteps, { showSuccessToast: false, forceSave: true });
      toast.success('Component duplicated');
      closeContextMenu();
      return;
    }
    
    // Otherwise, duplicate the entire step
    const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
    if (stepIndex === -1) return;
    
    // Create a duplicate step
    const duplicateStep = {
      ...contextMenuStep,
      id: `step-${Date.now()}`,
      x: contextMenuStep.x + FLOW_STEP_SPACING, // Move to next position
      components: contextMenuStep.components.map(comp => ({
        ...comp,
        id: `${comp.featureId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }))
    };
    
    // Insert after the current step and shift others
    const updatedSteps = [...steps];
    updatedSteps.splice(stepIndex + 1, 0, duplicateStep);
    
    // Shift subsequent steps to the right
    for (let i = stepIndex + 2; i < updatedSteps.length; i++) {
      updatedSteps[i] = {
        ...updatedSteps[i],
        x: updatedSteps[i].x + FLOW_STEP_SPACING
      };
    }
    
    setSteps(updatedSteps);
    toast.success('Step duplicated');
    closeContextMenu();
  };

  const handleMoveToStart = () => {
    if (!contextMenuStep) return;
    
    const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
    if (stepIndex === -1 || stepIndex === 0) return; // Already at start
    
    // Remove the step from its current position
    const updatedSteps = [...steps];
    const [movedStep] = updatedSteps.splice(stepIndex, 1);
    
    // Insert at the beginning
    updatedSteps.unshift(movedStep);
    
    // Recalculate x positions for all steps
    const repositionedSteps = updatedSteps.map((step, index) => ({
      ...step,
      x: 50 + (index * FLOW_STEP_SPACING)
    }));
    
    setSteps(repositionedSteps);
    toast.success('Step moved to start');
    closeContextMenu();
  };

  const handleMoveToEnd = () => {
    if (!contextMenuStep) return;
    
    const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
    if (stepIndex === -1 || stepIndex === steps.length - 1) return; // Already at end
    
    // Remove the step from its current position
    const updatedSteps = [...steps];
    const [movedStep] = updatedSteps.splice(stepIndex, 1);
    
    // Insert at the end
    updatedSteps.push(movedStep);
    
    // Recalculate x positions for all steps
    const repositionedSteps = updatedSteps.map((step, index) => ({
      ...step,
      x: 50 + (index * FLOW_STEP_SPACING)
    }));
    
    setSteps(repositionedSteps);
    toast.success('Step moved to end');
    closeContextMenu();
  };

  const handleToggleLockStep = async () => {
    if (!contextMenuStep) return;
    
    const newLockedState = !contextMenuStep.locked;
    
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === contextMenuStep.id 
          ? { ...step, locked: newLockedState }
          : step
      )
    );
    
    // Save locked state to database
    if (campaignId && campaignData) {
      try {
        // Find the step index in the current steps array
        const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
        if (stepIndex === -1) {
          console.error('Step not found in steps array');
          return;
        }
        
        // Get current flow's chain and metadata
        const currentChainMetadata = campaignData.chainMetadata?.[currentFlowIndex] || {};
        const stepLockedStates = currentChainMetadata.stepLockedStates || {};
        
        // Update the locked state for this step using its index
        stepLockedStates[stepIndex] = newLockedState;
        
        // Update chain metadata with new locked states
        const updatedChainMetadata = [...(campaignData.chainMetadata || [])];
        updatedChainMetadata[currentFlowIndex] = {
          ...currentChainMetadata,
          stepLockedStates
        };
        
        const response = await axios.post(updateCampaignRoute, {
          campaignID: campaignId,
          campaignName: campaignData.campaignName,
          campaignTier: campaignData.campaignTier,
          selectedRegion: campaignData.region,
          clientName: campaignData.client,
          adBugCopy: campaignData.adBugCopy,
          campaignOwner: campaignData.campaignOwnerAccount ? campaignData.campaignOwnerAccount._id : null,
          sfLink: campaignData.sfLink,
          sfNumber: campaignData.sfNumber,
          chains: campaignChains,
          chainMetadata: updatedChainMetadata,
        }, {
          withCredentials: true
        });
        
        // Update local campaign data
        setCampaignData(prev => ({
          ...prev,
          chainMetadata: updatedChainMetadata
        }));
      } catch (error) {
        console.error('Error saving locked state:', error);
        console.error('Error response:', error.response?.data);
        toast.error('Failed to save locked state');
        return;
      }
    }
    
    toast.success(newLockedState ? 'Step locked' : 'Step unlocked');
    closeContextMenu();
  };

  const handleCopyStep = () => {
    // If a component is selected, copy the component
    if (contextMenuComponent) {
      setCopiedComponent(JSON.parse(JSON.stringify(contextMenuComponent)));
      setCopiedStep(null);
      toast.success('Component copied');
      closeContextMenu();
      return;
    }
    
    // Otherwise, copy the entire step
    if (!contextMenuStep) return;
    
    // Store a deep copy of the step
    setCopiedStep(JSON.parse(JSON.stringify(contextMenuStep)));
    setCopiedComponent(null);
    toast.success('Step copied');
    closeContextMenu();
  };

  const handlePasteStep = async () => {
    // If a component is copied, paste it into the current step
    if (copiedComponent) {
      if (!contextMenuStep) {
        toast.error('Select a step to paste the component into');
        return;
      }
      
      const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
      if (stepIndex === -1) return;
      
      // Keep the same featureId and label to maintain linkage with the original component
      // Only generate a new unique id for this instance
      const newComponentId = `${copiedComponent.featureId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const newComponent = {
        ...copiedComponent,
        id: newComponentId
        // label stays the same - do NOT add (COPY) as they should be linked
      };
      
      const updatedSteps = [...steps];
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        components: ensureSinglePrimaryComponent([...updatedSteps[stepIndex].components, { ...newComponent, isPrimaryStepItem: false }])
      };
      
      setSteps(updatedSteps);
      toast.success('Component pasted');
      closeContextMenu();
      return;
    }
    
    // If a step is copied, paste it as a new step
    if (!copiedStep) {
      toast.error('Nothing copied');
      return;
    }
    
    // Create a new step ID with timestamp
    const newStepId = `step-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // Get the original step label and add (COPY)
    const originalLabel = copiedStep.label || copiedStep.name || `Step ${steps.length + 1}`;
    const newStepLabel = `${originalLabel} (COPY)`;
    
    // Find the rightmost step position to place the new step after it
    const rightmostX = steps.length > 0 
      ? Math.max(...steps.map(s => s.x || 0))
      : 0;
    
    // Create new step with copied data, positioned at the end
    const newStep = {
      ...copiedStep,
      id: newStepId,
      label: newStepLabel,
      name: newStepLabel,
      x: rightmostX + FLOW_STEP_SPACING, // Position after the rightmost step
      locked: false // Pasted steps are not locked
    };
    
    // Add to steps array
    const updatedSteps = [...steps, newStep];
    setSteps(updatedSteps);
    
    // Update flows state
    setFlows(prev => prev.map(flow => 
      flow.id === activeFlowId 
        ? { ...flow, steps: updatedSteps }
        : flow
    ));
    
    toast.success('Step pasted');
    closeContextMenu();
  };

  const handleExpandFlow = () => {
    if (!contextMenuStep) {
      closeContextMenu();
      return;
    }

    // Prefer the specific component that was right-clicked (contextMenuComponent) over
    // components[0] — the step may contain multiple features and the user may have
    // right-clicked one that is not the first in the list.
    const component = contextMenuComponent || (contextMenuStep.components && contextMenuStep.components[0]);
    if (!component || !component.varKey) {
      toast.error('No feature found for this step');
      closeContextMenu();
      return;
    }

    const featureVarKey = component.varKey;
    const featureInstanceId = component.featureId || null;

    // Let the backend (FeatureFlowPage API) be the source of truth for whether a flow
    // exists. Checking fullFeaturesList here is unreliable because it is loaded at
    // page-mount and may be stale (e.g. a flowStructure was seeded after the page loaded).
    // If the API returns no flow, FeatureFlowPage will display the appropriate error.
    setExpandedFlowFeature(featureVarKey);
    setExpandedFlowFeatureId(featureInstanceId);
    setTimelinePlayheadStep(0);
    setCurrentMode(MODES.EXPANDED_FLOW);
    closeContextMenu();
  };

  const expandedFlowVarKeyAliases = {
    particleEffects_fireworks: 'particleEffects',
    particleEffects_confetti: 'particleEffects',
    randomiser_slots: 'randomiser',
  };

  const canOpenExpandedFlowForComponent = (component) => {
    if (!component?.varKey) return false;

    const resolvedVarKey = expandedFlowVarKeyAliases[component.varKey] || component.varKey;
    const feature = fullFeaturesList.find(f => f.varKey === component.varKey)
      || fullFeaturesList.find(f => f.varKey === resolvedVarKey);

    // If the feature list is stale or doesn't include this config variant, don't block the
    // action here. `handleExpandFlow()`/FeatureFlowPage will defer to the backend as the
    // source of truth and show an error only if no flow actually exists.
    if (!feature) return true;

    return Array.isArray(feature.flowStructure) && feature.flowStructure.length > 0;
  };

  const handleDeleteStep = () => {
    if (!contextMenuStep) return;
    
    // Prevent deletion of locked steps
    if (contextMenuStep.locked) {
      toast.error('Cannot delete a locked step. Unlock it first.');
      closeContextMenu();
      return;
    }
    
    const stepIndex = steps.findIndex(s => s.id === contextMenuStep.id);
    if (stepIndex === -1) return;
    
    // Remove the step
    const updatedSteps = steps.filter(s => s.id !== contextMenuStep.id);
    
    // Shift subsequent steps to the left
    const adjustedSteps = updatedSteps.map((step, idx) => {
      if (idx >= stepIndex) {
        return {
          ...step,
          x: step.x - FLOW_STEP_SPACING
        };
      }
      return step;
    });
    
    setSteps(adjustedSteps);
    toast.success('Step deleted');
    closeContextMenu();
  };

  const deletePlacedComponent = (targetStepId, targetComponentId) => {
    if (!targetStepId || !targetComponentId) {
      return;
    }

    const targetStep = steps.find(step => step.id === targetStepId);
    if (!targetStep) {
      toast.error('Unable to find the component step.');
      return;
    }

    if (targetStep.locked) {
      toast.error('Cannot delete component from a locked step. Unlock it first.');
      return;
    }

    const targetComponent = targetStep.components?.find(component => component.id === targetComponentId) || null;
    const deletedFeatureId = normalizeFeatureReferenceId(
      targetComponent?.featureId || targetComponent?.campaignFeatureId || null
    );

    setSteps(prevSteps => {
      const mappedSteps = prevSteps.map(step => {
        if (step.id === targetStepId) {
          const updatedComponents = step.components.filter(c => c.id !== targetComponentId);
          return {
            ...step,
            components: ensureSinglePrimaryComponent(updatedComponents),
            height: calculateStepHeight(updatedComponents.length)
          };
        }
        return step;
      });

      // If deleting the last component from a step, remove that step entirely.
      const stepsWithoutEmptyTarget = mappedSteps.filter((step) => !(step.id === targetStepId && (!step.components || step.components.length === 0)));

      const updatedSteps = stepsWithoutEmptyTarget.map((step, index) => {
        const redistributedStep = {
          ...step,
          x: Math.round((CONTAINER_WIDTH / 2 - (stepsWithoutEmptyTarget.length - 1) * FLOW_STEP_SPACING / 2 + index * FLOW_STEP_SPACING - FLOW_PANEL_HALF_WIDTH) / GRID_SIZE) * GRID_SIZE,
        };

        if (step.isCustomName === false) {
          redistributedStep.name = `Step ${index + 1}`;
        }

        return redistributedStep;
      });

      setFlows(prevFlows => prevFlows.map(flow => (
        flow.id === activeFlowId
          ? { ...flow, steps: updatedSteps }
          : flow
      )));

      syncStepComponentVisibility(targetStepId, updatedSteps, true);

      const selectedComponentId = selectedItem?.id || null;
      const selectedFeature = normalizeFeatureReferenceId(
        selectedItem?.featureId || selectedItem?.campaignFeatureId || selectedFeatureId
      );

      if (selectedComponentId === targetComponentId || (deletedFeatureId && selectedFeature === deletedFeatureId)) {
        setSelectedItem(null);
        setSelectedFeatureId(null);
        setSelectedFeatureNode(null);
        setActiveVisualisedPreviewNode(null);
        clearActiveNode();
      }
      
      // Update the chain to persist the deletion
      setTimeout(async () => {
        const currentFlow = flows.find(f => f.id === activeFlowId);
        if (currentFlow && campaignId && campaignData) {
          const { chain: newChain, metadata } = convertTimelineToChain(
            updatedSteps,
            currentFlow.name,
            updatedSteps.map(s => s.label)
          );
          
          // Update the campaign chains
          const updatedChains = [...campaignChains];
          const flowIndex = flows.findIndex(f => f.id === activeFlowId);
          if (flowIndex !== -1) {
            updatedChains[flowIndex] = newChain;
            
            try {
              await axios.post(updateCampaignRoute, {
                campaignID: campaignId,
                campaignName: campaignData.campaignName,
                campaignTier: campaignData.campaignTier,
                selectedRegion: campaignData.region,
                clientName: campaignData.client,
                adBugCopy: campaignData.adBugCopy,
                campaignOwner: campaignData.campaignOwnerAccount ? campaignData.campaignOwnerAccount._id : null,
                sfLink: campaignData.sfLink,
                sfNumber: campaignData.sfNumber,
                chains: updatedChains,
                chainMetadata: campaignData.chainMetadata?.map((meta, idx) => 
                  idx === flowIndex ? metadata : meta
                ) || [],
              }, {
                withCredentials: true
              });
              
              setCampaignChains(updatedChains);
            } catch (error) {
              console.error('Error updating campaign after component deletion:', error);
              toast.error('Failed to save component deletion');
            }
          }
        }
      }, 0);
      
      return updatedSteps;
    });

    toast.success('Component deleted');
  };

  const handleDeleteComponent = () => {
    if (!contextMenuComponent || !contextMenuStep) return;

    deletePlacedComponent(contextMenuStep.id, contextMenuComponent.id);
    closeContextMenu();
  };

  // Timeline resize handlers
  const handleTimelineResizeStart = (e) => {
    e.preventDefault();
    setIsResizingTimeline(true);
  };

  const handleTimelineResizeMove = useCallback((e) => {
    if (!isResizingTimeline) return;
    
    // Calculate the new height based on the distance from the bottom of the viewport
    const viewportHeight = window.innerHeight;
    const newHeight = viewportHeight - e.clientY;
    
    // Clamp between 150px and 600px
    const clampedHeight = Math.max(150, Math.min(600, newHeight));
    setTimelineHeight(clampedHeight);
  }, [isResizingTimeline]);

  const handleTimelineResizeEnd = useCallback(() => {
    setIsResizingTimeline(false);
  }, []);

  const clampLeftSidebarWidth = useCallback((nextWidth) => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const maxWidthFromViewport = viewportWidth > 0
      ? viewportWidth - RIGHT_SIDEBAR_WIDTH - MIN_FLOW_WORKSPACE_WIDTH
      : LEFT_SIDEBAR_MAX_WIDTH;
    const maxWidth = Math.max(LEFT_SIDEBAR_MIN_WIDTH, Math.min(LEFT_SIDEBAR_MAX_WIDTH, maxWidthFromViewport));

    return Math.max(LEFT_SIDEBAR_MIN_WIDTH, Math.min(maxWidth, Number(nextWidth) || LEFT_SIDEBAR_MIN_WIDTH));
  }, []);

  const handleLeftSidebarResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizingLeftSidebar(true);
  }, []);

  const handleLeftSidebarResizeMove = useCallback((e) => {
    if (!isResizingLeftSidebar) return;

    setLeftSidebarWidth(clampLeftSidebarWidth(e.clientX));
  }, [clampLeftSidebarWidth, isResizingLeftSidebar]);

  const handleLeftSidebarResizeEnd = useCallback(() => {
    setIsResizingLeftSidebar(false);
  }, []);

  useEffect(() => {
    if (isResizingTimeline) {
      document.addEventListener('mousemove', handleTimelineResizeMove);
      document.addEventListener('mouseup', handleTimelineResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleTimelineResizeMove);
        document.removeEventListener('mouseup', handleTimelineResizeEnd);
      };
    }
  }, [isResizingTimeline, handleTimelineResizeMove, handleTimelineResizeEnd]);

  useEffect(() => {
    if (!isResizingLeftSidebar) {
      return undefined;
    }

    document.addEventListener('mousemove', handleLeftSidebarResizeMove);
    document.addEventListener('mouseup', handleLeftSidebarResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleLeftSidebarResizeMove);
      document.removeEventListener('mouseup', handleLeftSidebarResizeEnd);
    };
  }, [handleLeftSidebarResizeEnd, handleLeftSidebarResizeMove, isResizingLeftSidebar]);

  useEffect(() => {
    const handleWindowResize = () => {
      setLeftSidebarWidth(prevWidth => clampLeftSidebarWidth(prevWidth));
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [clampLeftSidebarWidth]);

  // Handle step name change
  const handleStepNameChange = (stepId, newName, shouldUpdateCustomFlag = false) => {
    setSteps(prevSteps => {
      return prevSteps.map(step => {
        if (step.id === stepId) {
          // Only update isCustomName flag when explicitly requested (on blur)
          if (shouldUpdateCustomFlag) {
            const sortedSteps = [...prevSteps].sort((a, b) => a.x - b.x);
            const stepIndex = sortedSteps.findIndex(s => s.id === stepId);
            const defaultName = `Step ${stepIndex + 1}`;
            
            // If name is empty on blur, revert to default
            if (newName === '' || newName.trim() === '') {
              return { ...step, label: defaultName, isCustomName: false };
            }
            
            const isCustom = newName !== defaultName;
            return { ...step, label: newName, isCustomName: isCustom };
          }
          
          // Otherwise just update the label
          return { ...step, label: newName };
        }
        return step;
      });
    });
  };

  const handleComponentNameChange = (componentId, newName, shouldUpdateCustomFlag = false) => {
    let targetComponent = null;
    for (const step of steps) {
      if (step.components) {
        const matchedComponent = step.components.find(c => c.id === componentId);
        if (matchedComponent) {
          targetComponent = matchedComponent;
          break;
        }
      }
    }

    const getDefaultComponentName = (component) => {
      if (!component) return '';

      const libraryItem = Object.values(libraryItems).flat().find(item => 
        component.type === item.label.toLowerCase() || component.type === item.id
      );

      return libraryItem ? libraryItem.label : component.type;
    };

    const getComponentFeatureLinkId = (component) => component?.featureId || component?.campaignFeatureId || null;

    const resolvedSidebarName = shouldUpdateCustomFlag && (newName === '' || newName.trim() === '')
      ? getDefaultComponentName(targetComponent)
      : newName;

    const targetFeatureLinkId = getComponentFeatureLinkId(targetComponent);

    if (targetFeatureLinkId) {
      setLeftListNewCustomisationItems(prevItems => {
        if (!prevItems || !prevItems[targetFeatureLinkId]) {
          return prevItems;
        }

        const currentNode = prevItems[targetFeatureLinkId];
        return {
          ...prevItems,
          [targetFeatureLinkId]: {
            ...currentNode,
            name: resolvedSidebarName,
            featureData: currentNode.featureData
              ? {
                  ...currentNode.featureData,
                  contentLabel: resolvedSidebarName,
                }
              : currentNode.featureData,
          },
        };
      });

      setSelectedFeatureNode(prevNode => {
        if (!prevNode || prevNode.id !== targetFeatureLinkId) {
          return prevNode;
        }

        return {
          ...prevNode,
          name: resolvedSidebarName,
          featureData: prevNode.featureData
            ? {
                ...prevNode.featureData,
                contentLabel: resolvedSidebarName,
              }
            : prevNode.featureData,
        };
      });
    }

    setSelectedItem(prevSelectedItem => {
      if (!prevSelectedItem || prevSelectedItem.id !== componentId) {
        return prevSelectedItem;
      }

      return {
        ...prevSelectedItem,
        label: resolvedSidebarName,
        customName: resolvedSidebarName,
      };
    });

    setSteps(prevSteps => {
      // During typing (shouldUpdateCustomFlag = false), only update the specific component
      if (!shouldUpdateCustomFlag) {
        return prevSteps.map(step => {
          if (step.components) {
            const updatedComponents = step.components.map(comp => {
              if (comp.id === componentId) {
                return { ...comp, label: newName };
              }
              return comp;
            });
            return { ...step, components: updatedComponents };
          }
          return step;
        });
      }
      
      // On blur (shouldUpdateCustomFlag = true), update ALL components with the same featureId
      // First, find the component to get its featureId
      let targetFeatureId = null;
      for (const step of prevSteps) {
        if (step.components) {
          const comp = step.components.find(c => c.id === componentId);
          if (comp) {
            targetFeatureId = getComponentFeatureLinkId(comp);
            break;
          }
        }
      }
      
      if (!targetFeatureId) return prevSteps;
      
      // Update ALL components with the same featureId across all steps
      return prevSteps.map(step => {
        if (step.components) {
          const updatedComponents = step.components.map(comp => {
            // Update all components with matching featureId
            if (getComponentFeatureLinkId(comp) === targetFeatureId) {
              // Get the component's type/original label from library
              const defaultName = getDefaultComponentName(comp);
              
              // If name is empty on blur, revert to default
              if (newName === '' || newName.trim() === '') {
                return { ...comp, label: defaultName, isCustomName: false };
              }
              
              const isCustom = newName !== defaultName;
              
              // Update customName field for chain serialization
              const updatedComp = { 
                ...comp, 
                label: newName, 
                customName: isCustom ? newName : null,
                isCustomName: isCustom 
              };
              
              // Update the database - either with campaign feature ID or by varKey + old label
              // Only do this once for the component that was actually edited
              if (comp.id === componentId) {
                if (comp.isExistingCampaignFeature && comp.campaignFeatureId) {
                  // This is an existing campaign feature with known ID
                  updateCampaignFeatureName(comp.campaignFeatureId, newName);
                } else if (comp.varKey && comp.label) {
                  // This is a newly added feature - find it by varKey and current label
                  updateCampaignFeatureNameByVarKey(comp.varKey, comp.label, newName);
                }
              }
              
              return updatedComp;
            }
            return comp;
          });
          
          return { ...step, components: updatedComponents };
        }
        return step;
      });
    });
  };

  const applyFeatureNameToVisibleState = useCallback((featureId, nextName) => {
    const normalizedFeatureId = normalizeFeatureReferenceId(featureId);
    if (!normalizedFeatureId || !nextName) {
      return;
    }

    const syncComponentLabel = (component) => {
      const componentFeatureId = normalizeFeatureReferenceId(component?.featureId || component?.campaignFeatureId);
      if (componentFeatureId !== normalizedFeatureId) {
        return component;
      }

      return {
        ...component,
        label: nextName,
        customName: nextName,
      };
    };

    setSteps(prevSteps => prevSteps.map(step => ({
      ...step,
      components: (step.components || []).map(syncComponentLabel),
    })));

    setFlows(prevFlows => prevFlows.map(flow => ({
      ...flow,
      steps: (flow.steps || []).map(step => ({
        ...step,
        components: (step.components || []).map(syncComponentLabel),
      })),
    })));

    setLibraryItems(prevItems => {
      if (!prevItems?.Campaign?.length) {
        return prevItems;
      }

      let changed = false;
      const updatedCampaignItems = prevItems.Campaign.map(item => {
        const itemFeatureId = normalizeFeatureReferenceId(item?.campaignFeatureId || item?.id);
        if (itemFeatureId !== normalizedFeatureId) {
          return item;
        }

        changed = true;
        return {
          ...item,
          label: nextName,
        };
      });

      return changed
        ? { ...prevItems, Campaign: updatedCampaignItems }
        : prevItems;
    });

    setLeftListNewCustomisationItems(prevItems => {
      if (!prevItems?.[normalizedFeatureId]) {
        return prevItems;
      }

      const currentNode = prevItems[normalizedFeatureId];
      const updatedNode = {
        ...currentNode,
        name: nextName,
        featureData: currentNode.featureData
          ? {
              ...currentNode.featureData,
              contentLabel: nextName,
            }
          : currentNode.featureData,
      };
      updatedNode.topNode = updatedNode;

      return {
        ...prevItems,
        [normalizedFeatureId]: updatedNode,
      };
    });

    setSelectedFeatureNode(prevNode => {
      if (!prevNode || normalizeFeatureReferenceId(prevNode.id) !== normalizedFeatureId) {
        return prevNode;
      }

      const updatedNode = {
        ...prevNode,
        name: nextName,
        featureData: prevNode.featureData
          ? {
              ...prevNode.featureData,
              contentLabel: nextName,
            }
          : prevNode.featureData,
      };
      updatedNode.topNode = updatedNode;
      return updatedNode;
    });

    setActiveNode(prevNode => {
      if (!prevNode) {
        return prevNode;
      }

      const activeFeatureId = normalizeFeatureReferenceId(
        prevNode?.topNode?.id || prevNode?.featureData?._id || prevNode?.featureData?.id || prevNode?.id
      );

      if (activeFeatureId !== normalizedFeatureId) {
        return prevNode;
      }

      if (normalizeFeatureReferenceId(prevNode.id) === normalizedFeatureId) {
        const updatedNode = {
          ...prevNode,
          name: nextName,
          featureData: prevNode.featureData
            ? {
                ...prevNode.featureData,
                contentLabel: nextName,
              }
            : prevNode.featureData,
        };
        updatedNode.topNode = updatedNode;
        return updatedNode;
      }

      return {
        ...prevNode,
        topNode: prevNode.topNode
          ? {
              ...prevNode.topNode,
              name: nextName,
              featureData: prevNode.topNode.featureData
                ? {
                    ...prevNode.topNode.featureData,
                    contentLabel: nextName,
                  }
                : prevNode.topNode.featureData,
            }
          : prevNode.topNode,
      };
    });

    setSelectedItem(prevSelectedItem => {
      if (!prevSelectedItem) {
        return prevSelectedItem;
      }

      const selectedFeatureId = normalizeFeatureReferenceId(
        prevSelectedItem.featureId || prevSelectedItem.campaignFeatureId
      );

      if (selectedFeatureId !== normalizedFeatureId) {
        return prevSelectedItem;
      }

      return {
        ...prevSelectedItem,
        label: nextName,
        customName: nextName,
      };
    });

    setStackingOrder(prevOrder => prevOrder.map(item => {
      const itemFeatureId = normalizeFeatureReferenceId(item?.featureId || item?.id);
      if (itemFeatureId !== normalizedFeatureId) {
        return item;
      }

      return {
        ...item,
        name: nextName,
        label: nextName,
      };
    }));
  }, [normalizeFeatureReferenceId, setActiveNode, setLibraryItems, setSelectedItem]);

  const updateComponentTiming = useCallback((componentId, updates) => {
    if (!componentId || !updates) {
      return;
    }

    const normalizedUpdates = {};

    if (Object.prototype.hasOwnProperty.call(updates, 'autoStart')) {
      normalizedUpdates.autoStart = Boolean(updates.autoStart);
    }

    if (Object.prototype.hasOwnProperty.call(updates, 'startDelay')) {
      const parsedDelay = Number(updates.startDelay);
      normalizedUpdates.startDelay = Number.isFinite(parsedDelay)
        ? Math.max(0, Math.min(1799, parsedDelay))
        : 0;
    }

    if (Object.prototype.hasOwnProperty.call(updates, 'commandParam')) {
      normalizedUpdates.commandParam = updates.commandParam;
    }

    setSteps(prevSteps => prevSteps.map(step => ({
      ...step,
      components: (step.components || []).map(component => (
        component.id === componentId
          ? { ...component, ...normalizedUpdates }
          : component
      ))
    })));

    setSelectedItem(prevSelectedItem => {
      if (!prevSelectedItem || prevSelectedItem.id !== componentId) {
        return prevSelectedItem;
      }

      return {
        ...prevSelectedItem,
        ...normalizedUpdates,
      };
    });
  }, [setSelectedItem, setSteps]);

  // Handle component expansion from timeline
  const handleComponentExpansion = (componentId, originalStepId, newStepLength, newHomeStepId) => {
    setSteps(prevSteps => {
      // Sort steps by x position to match timeline's view
      let sortedSteps = [...prevSteps].sort((a, b) => a.x - b.x);
      
      // Find the step containing this component (current home)
      const currentStepIndex = sortedSteps.findIndex(step => 
        step.components && step.components.some(comp => comp.id === componentId)
      );
      
      if (currentStepIndex === -1) return prevSteps;
      
      const currentStep = sortedSteps[currentStepIndex];
      const component = currentStep.components.find(comp => comp.id === componentId);
      
      if (!component) return prevSteps;
      
      // Calculate how many steps are needed for this expansion
      const requiredSteps = currentStepIndex + newStepLength;
      
      // If expansion goes beyond current steps, create placeholder steps
      if (requiredSteps > sortedSteps.length) {
        const stepsToAdd = requiredSteps - sortedSteps.length;
        
        const STEP_SPACING = FLOW_STEP_SPACING;
        const baseX = sortedSteps[0]?.x || 0;
        const baseY = sortedSteps[0]?.y || 0;
        
        for (let i = 0; i < stepsToAdd; i++) {
          const newIndex = sortedSteps.length;
          const placeholderStep = {
            id: `step-${Date.now()}-${i}`,
            type: "step",
            name: `Step ${newIndex + 1}`,
            x: baseX + (newIndex * STEP_SPACING),
            y: baseY,
            width: FLOW_PANEL_WIDTH,
            height: calculateStepHeight(0),
            components: []
          };
          sortedSteps.push(placeholderStep);
        }
      }
      
      // Find the new home step by ID
      const newHomeStepIndex = sortedSteps.findIndex(step => step.id === newHomeStepId);
      
      if (newHomeStepIndex === -1) {
        console.error('🔧 Could not find new home step with ID:', newHomeStepId);
        return prevSteps;
      }
      
      // Check if component is alone in its step
      const isAloneInStep = currentStep.components.length === 1;
      
      // Special case: If component is alone and new home is after current position,
      // this is a "shrink from left" - keep component in current step with new stepLength,
      // and shift all subsequent steps down by the number of positions we shrank
      if (isAloneInStep && newHomeStepIndex > currentStepIndex) {
        const stepsToShift = newHomeStepIndex - currentStepIndex;
        
        const updatedSteps = sortedSteps.map((step, idx) => {
          if (idx === currentStepIndex) {
            // Update the component's stepLength in current step
            const updatedComponents = step.components.map(comp => 
              comp.id === componentId ? { ...comp, stepLength: newStepLength } : comp
            );
            
            // If there's a step right after current, merge its components into current
            const nextStep = sortedSteps[currentStepIndex + 1];
            const componentsToAdd = nextStep ? nextStep.components : [];
            
            return {
              ...step,
              components: [...updatedComponents, ...componentsToAdd],
              height: calculateStepHeight(updatedComponents.length + componentsToAdd.length)
            };
          } else if (idx > currentStepIndex && idx < sortedSteps.length - stepsToShift) {
            // For steps after current, take components from the step `stepsToShift` positions ahead
            const sourceStep = sortedSteps[idx + stepsToShift];
            return {
              ...step,
              components: sourceStep ? sourceStep.components : [],
              height: sourceStep ? calculateStepHeight(sourceStep.components.length) : step.height
            };
          }
          return step;
        });
        
        // Remove the last `stepsToShift` steps as they're now empty (their content was shifted down)
        const finalSteps = updatedSteps.slice(0, updatedSteps.length - stepsToShift).filter(step => 
          step.components && step.components.length > 0
        );
        
        // CRITICAL: Update x positions to match new sequential order!
        // Steps are spaced using the shared timeline spacing
        const STEP_SPACING = FLOW_STEP_SPACING;
        const reorderedSteps = finalSteps.map((step, idx) => ({
          ...step,
          x: sortedSteps[0].x + (idx * STEP_SPACING) // Maintain original starting position, then space evenly
        }));
        
        return reorderedSteps;
      }
      
      // If the component is staying in the same step (just changing stepLength)
      if (currentStepIndex === newHomeStepIndex) {
        return sortedSteps.map((step, idx) => {
          if (idx === currentStepIndex) {
            return {
              ...step,
              components: ensureSinglePrimaryComponent(step.components.map(comp => 
                comp.id === componentId ? { ...comp, stepLength: newStepLength } : comp
              ))
            };
          }
          return step;
        });
      }
      
      // Component is moving to a different step
      
      const updatedSteps = sortedSteps.map((step, idx) => {
        if (idx === currentStepIndex) {
          // Remove from current step
          const newComponents = step.components.filter(comp => comp.id !== componentId);
          return {
            ...step,
            components: ensureSinglePrimaryComponent(newComponents),
            height: calculateStepHeight(newComponents.length)
          };
        } else if (idx === newHomeStepIndex) {
          // Add to new home step with updated stepLength
          const updatedComponent = { ...component, stepLength: newStepLength, isPrimaryStepItem: false };
          return {
            ...step,
            components: ensureSinglePrimaryComponent([...step.components, updatedComponent]),
            height: calculateStepHeight(step.components.length + 1)
          };
        }
        return step;
      });
      
      // Keep all steps including empty ones (they're placeholders for expanded components)
      return updatedSteps;
    });
  };

  // Calculate dynamic height for step panels based on component count
  const calculateStepHeight = useCallback((componentCount) => {
    const baseHeight = 120; // Base height for header and minimal content
    const componentHeight = 40; // Height per component
    const padding = 20; // Extra padding
    return baseHeight + (componentCount * componentHeight) + padding;
  }, []);

  const getSharedConnectorCenterY = useCallback((panels) => {
    if (!panels || panels.length === 0) {
      return 0;
    }

    return Math.min(...panels.map((panel) => panel.y + panel.height / 2));
  }, []);

  const fitViewportToFirstStep = useCallback((options = {}) => {
    const flowArea = flowAreaRef.current;
    if (!flowArea) {
      return false;
    }

    const panels = flowItems
      .filter((item) => item.type === 'panel')
      .sort((a, b) => a.x - b.x);

    const firstPanel = panels[0];
    if (!firstPanel) {
      return false;
    }

    const rect = flowArea.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return false;
    }

    const horizontalMargin = options.horizontalMargin ?? 72;
    const verticalMargin = options.verticalMargin ?? 48;
    const maxZoom = options.maxZoom ?? 1;
    const minZoom = options.minZoom ?? 0.5;

    const availableWidth = Math.max(1, rect.width - (horizontalMargin * 2));
    const availableHeight = Math.max(1, rect.height - (verticalMargin * 2));
    const fittedZoom = Math.min(
      maxZoom,
      availableWidth / Math.max(firstPanel.width, 1),
      availableHeight / Math.max(firstPanel.height, 1)
    );
    const nextZoom = Math.min(maxZoom, Math.max(minZoom, fittedZoom));

    const panelLeft = firstPanel.x - minX;
    const panelTop = firstPanel.y - minY;
    const panelScreenHeight = firstPanel.height * nextZoom;
    const desiredLeft = horizontalMargin;
    const desiredTop = Math.max(verticalMargin, (rect.height - panelScreenHeight) / 2);

    setZoom(nextZoom);
    setPanOffset({
      x: desiredLeft - (panelLeft * nextZoom),
      y: desiredTop - (panelTop * nextZoom)
    });

    return true;
  }, [flowItems, minX, minY, setPanOffset, setZoom]);

  // Remove truly empty steps (no components AND no expanded copies from other steps)
  const removeEmptySteps = useCallback((steps) => {
    if (steps.length === 0) return steps;
    
    const sortedSteps = [...steps].sort((a, b) => a.x - b.x);
    
    return sortedSteps.filter((step, stepIdx) => {
      // If step has components, keep it
      if (step.components && step.components.length > 0) {
        return true;
      }
      
      // Check if this position is covered by any spanning component from previous steps
      for (let i = 0; i < stepIdx; i++) {
        const prevStep = sortedSteps[i];
        if (prevStep.components) {
          for (const comp of prevStep.components) {
            const compSpan = comp.stepLength || 1;
            const compEnd = i + compSpan - 1;
            if (stepIdx <= compEnd) {
              // This step is covered by an expanded component
              return true;
            }
          }
        }
      }
      
      // Truly empty - not covered by any spanning component
      return false;
    });
  }, []);

  const removeFeatureFromSteps = useCallback((stepsToClean, targetFeatureId) => {
    const normalizedTargetFeatureId = normalizeFeatureReferenceId(targetFeatureId);
    if (!normalizedTargetFeatureId) {
      return stepsToClean;
    }

    const cleanedSteps = (stepsToClean || []).map(step => ({
      ...step,
      components: (step.components || []).filter(component => {
        const componentFeatureId = normalizeFeatureReferenceId(component.featureId || component.campaignFeatureId);
        return componentFeatureId !== normalizedTargetFeatureId;
      })
    }));

    return removeEmptySteps(cleanedSteps);
  }, [normalizeFeatureReferenceId, removeEmptySteps]);

  const pruneFeatureReferenceValue = useCallback((value, targetFeatureId) => {
    const normalizedTargetFeatureId = normalizeFeatureReferenceId(targetFeatureId);

    if (Array.isArray(value)) {
      return value
        .map(item => pruneFeatureReferenceValue(item, normalizedTargetFeatureId))
        .filter(item => item !== undefined);
    }

    if (value && typeof value === 'object') {
      const cleanedObject = Object.entries(value).reduce((accumulator, [key, childValue]) => {
        const cleanedChildValue = pruneFeatureReferenceValue(childValue, normalizedTargetFeatureId);
        if (cleanedChildValue !== undefined) {
          accumulator[key] = cleanedChildValue;
        }
        return accumulator;
      }, {});

      return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
    }

    const normalizedValue = normalizeFeatureReferenceId(value);
    if (
      normalizedValue &&
      normalizedTargetFeatureId &&
      (normalizedValue === normalizedTargetFeatureId || normalizedValue.startsWith(`${normalizedTargetFeatureId}:`))
    ) {
      return undefined;
    }

    return value;
  }, [normalizeFeatureReferenceId]);

  const buildCleanCustomisationVarsBundle = useCallback((varsBundle, targetFeatureId) => {
    const normalizedTargetFeatureId = normalizeFeatureReferenceId(targetFeatureId);
    const sourceBundle = varsBundle || {};
    const sourceCurrent = sourceBundle.current || {};
    const sourceDefault = sourceBundle.default || {};

    const cleanVarsMap = (varsMap) => Object.entries(varsMap).reduce((accumulator, [featureId, featureVars]) => {
      if (normalizeFeatureReferenceId(featureId) === normalizedTargetFeatureId) {
        return accumulator;
      }

      accumulator[featureId] = pruneFeatureReferenceValue(featureVars, normalizedTargetFeatureId) || {};
      return accumulator;
    }, {});

    return {
      ...sourceBundle,
      current: cleanVarsMap(sourceCurrent),
      default: cleanVarsMap(sourceDefault),
    };
  }, [normalizeFeatureReferenceId, pruneFeatureReferenceValue]);

  const buildCustomisationOverrideData = useCallback((currentVars, defaultVars) => {
    const overrideValues = [];

    const collectOverrides = (currentValue, defaultValue, previousKeys = []) => {
      if (!currentValue || typeof currentValue !== 'object') {
        return;
      }

      Object.keys(currentValue).forEach(key => {
        const nextCurrentValue = currentValue[key];
        const nextDefaultValue = defaultValue?.[key];

        if (
          typeof nextCurrentValue === 'object' &&
          nextCurrentValue !== null &&
          !Array.isArray(nextCurrentValue) &&
          nextDefaultValue !== undefined
        ) {
          collectOverrides(nextCurrentValue, nextDefaultValue, [...previousKeys, key]);
          return;
        }

        if (nextCurrentValue != nextDefaultValue) {
          overrideValues.push({
            var: [...previousKeys, key].join('.'),
            value: nextCurrentValue,
          });
        }
      });
    };

    collectOverrides(currentVars || {}, defaultVars || {});
    return overrideValues;
  }, []);

  const removeFeatureFromCampaignChains = useCallback((chains, chainMetadata, targetFeatureId) => {
    const normalizedTargetFeatureId = normalizeFeatureReferenceId(targetFeatureId);
    const cleanedChains = [];
    const cleanedChainMetadata = [];

    (chains || []).forEach((chain, index) => {
      const cleanedChain = (chain || []).filter(chainItem => {
        if (typeof chainItem === 'string') {
          const [featureId] = chainItem.split(':');
          return normalizeFeatureReferenceId(featureId) !== normalizedTargetFeatureId;
        }

        if (chainItem && typeof chainItem === 'object' && chainItem.featureId) {
          return normalizeFeatureReferenceId(chainItem.featureId) !== normalizedTargetFeatureId;
        }

        return true;
      });

      if (cleanedChain.length > 0) {
        cleanedChains.push(cleanedChain);
        cleanedChainMetadata.push(chainMetadata?.[index] || { flowName: `Flow ${cleanedChains.length}`, stepNames: [] });
      }
    });

    return { cleanedChains, cleanedChainMetadata };
  }, [normalizeFeatureReferenceId]);

  const handleDeleteCampaignFeature = async (libraryItem) => {
    const targetFeatureId = libraryItem?.campaignFeatureId || libraryItem?.id;
    const normalizedTargetFeatureId = normalizeFeatureReferenceId(targetFeatureId);

    if (!normalizedTargetFeatureId || !campaignId || !campaignData) {
      toast.error('Unable to delete this component right now.');
      return false;
    }

    const normalizedSelectedFeatureId = normalizeFeatureReferenceId(selectedFeatureId);
    const normalizedExpandedFlowFeatureId = normalizeFeatureReferenceId(expandedFlowFeatureId);
    const normalizedSelectedItemFeatureId = normalizeFeatureReferenceId(
      selectedItem?.featureId || selectedItem?.campaignFeatureId
    );
    const wasDeletedFeatureSelected = (
      normalizedSelectedFeatureId === normalizedTargetFeatureId
      || normalizedExpandedFlowFeatureId === normalizedTargetFeatureId
      || normalizedSelectedItemFeatureId === normalizedTargetFeatureId
    );
    const shouldReturnToMainFlow = wasDeletedFeatureSelected
      && (currentMode === MODES.CANVAS_EDITOR || currentMode === MODES.EXPANDED_FLOW);

    const remainingCampaignFeatures = campaignFeatures.filter(feature => (
      normalizeFeatureReferenceId(feature?._id) !== normalizedTargetFeatureId
    ));

    const nextCustomisationVars = buildCleanCustomisationVarsBundle(
      customisationVarsRef.current || customisationVars,
      normalizedTargetFeatureId,
    );

    const { cleanedChains, cleanedChainMetadata } = removeFeatureFromCampaignChains(
      campaignChains,
      campaignData.chainMetadata || [],
      normalizedTargetFeatureId,
    );

    const cleanedHashtagCommands = (campaignData.hashtagCommands || []).filter(command => {
      const commandFeatureId = normalizeFeatureReferenceId(command?.featureID || command?.featureId);
      return commandFeatureId !== normalizedTargetFeatureId;
    });

    const nextCampaignData = {
      ...campaignData,
      features: remainingCampaignFeatures,
      chains: cleanedChains,
      chainMetadata: cleanedChainMetadata,
      hashtagCommands: cleanedHashtagCommands,
    };

    const nextSteps = removeFeatureFromSteps(steps, normalizedTargetFeatureId);
    const nextStackingOrder = stackingOrder.filter(item => (
      normalizeFeatureReferenceId(item.featureId || item.id) !== normalizedTargetFeatureId
    ));

    try {
      await axios.post(deleteFeatureCampaignCustomisationsRoute, {
        campaignID: campaignId,
        featureID: normalizedTargetFeatureId,
      }, {
        withCredentials: true,
      });

      await axios.post(updateCampaignRoute, {
        campaignID: campaignId,
        campaignName: campaignData.campaignName,
        campaignTier: campaignData.campaignTier,
        selectedRegion: campaignData.region,
        clientName: campaignData.client,
        adBugCopy: campaignData.adBugCopy,
        campaignOwner: campaignData.campaignOwnerAccount ? campaignData.campaignOwnerAccount._id : null,
        sfLink: campaignData.sfLink,
        sfNumber: campaignData.sfNumber,
        chains: cleanedChains,
        chainMetadata: cleanedChainMetadata,
        hashtagCommands: cleanedHashtagCommands,
      }, {
        withCredentials: true,
      });

      await axios.post(removeCampaignFeatureRoute, {
        campaignID: campaignId,
        featureID: normalizedTargetFeatureId,
      });

      customisationVarsRef.current = nextCustomisationVars;
      setCustomisationVars(nextCustomisationVars);
      setCampaignData(nextCampaignData);
      setCampaignFeatures(remainingCampaignFeatures);
      setCampaignChains(cleanedChains);
      setSteps(nextSteps);
      setStackingOrder(nextStackingOrder);
      setFlows(prevFlows => prevFlows.map(flow => ({
        ...flow,
        steps: removeFeatureFromSteps(flow.steps || [], normalizedTargetFeatureId),
        stackingOrder: (flow.stackingOrder || []).filter(item => (
          normalizeFeatureReferenceId(item.featureId || item.id) !== normalizedTargetFeatureId
        )),
      })));

      setExpandedComponents(prevExpandedComponents => {
        const validComponentIds = new Set(
          nextSteps.flatMap(step => (step.components || []).map(component => component.id))
        );
        return new Map(
          [...prevExpandedComponents.entries()].filter(([componentId]) => validComponentIds.has(componentId))
        );
      });

      setLeftListNewCustomisationItems(prevItems => {
        if (!prevItems) {
          return prevItems;
        }

        const nextItems = { ...prevItems };
        delete nextItems[normalizedTargetFeatureId];
        return nextItems;
      });

      if (wasDeletedFeatureSelected) {
        setSelectedFeatureId(null);
        setSelectedFeatureNode(null);
        setSelectedItem(null);
        clearActiveNode();
        setActiveVisualisedPreviewNode(null);
      }

      if (shouldReturnToMainFlow) {
        setCurrentMode(MODES.FLOW_EDITOR);
        setExpandedFlowFeature(null);
        setExpandedFlowFeatureId(null);
      }

      setCampaignContext(oldValues => ({
        ...oldValues,
        campaignData: nextCampaignData,
      }));

      generateLeftListNewCustomisationItems(nextCustomisationVars.current, nextCampaignData);
      await fetchCampaignData(true);
      toast.success(`Deleted "${libraryItem.label}" from the campaign.`);
      return true;
    } catch (error) {
      console.error('Error deleting campaign feature:', error);
      toast.error(error.response?.data?.message || 'Failed to delete the component from the campaign.');
      await fetchCampaignData(true);
      return false;
    }
  };

  // REMOVED - This effect is redundant with the one below at line 1518

  // Sync layers with steps and components whenever steps change
  useEffect(() => {
    // Skip if no steps
    if (steps.length === 0) {
      return;
    }
    
    setLayers(prevLayers => {
      const newLayers = [];
      
      // Sort steps by x position to get correct order
      const sortedSteps = [...steps].sort((a, b) => a.x - b.x);
      
      // For each step, we need to show both its own components AND expanded copies that appear in this step
      sortedSteps.forEach((step, stepIndex) => {
        // Add step layer
        const stepLayer = {
          id: step.id,
          name: step.label || `Step ${stepIndex + 1}`,
          sequenceLabel: String(stepIndex + 1).padStart(2, '0'),
          type: 'step',
          icon: step.components[0]?.icon || null,
          varKey: step.components[0]?.varKey || null,
          iconCategory: step.components[0]?.iconCategory || null,
          visible: true,
          locked: false,
          isStep: true
        };
        newLayers.push(stepLayer);
        
        // Collect all components that should appear under this step (own + expanded copies)
        const componentsForThisStep = new Map(); // Use Map to avoid duplicates
        
        // First, add this step's own components
        step.components.forEach((component) => {
          const isExpanded = expandedComponents.has(component.id);
          const expansionData = expandedComponents.get(component.id);
          const isOriginalStep = expansionData?.originalStepId === step.id;
          
          componentsForThisStep.set(component.id, {
            id: component.id,
            name: component.label,
            featureId: component.featureId,
            type: component.type,
            icon: component.icon,
            varKey: component.varKey,
            iconCategory: component.iconCategory || null,
            visible: true,
            locked: false,
            isComponent: true,
            parentStep: step.id,
            isExpanded: isExpanded,
            isExpandedCopy: false, // This is the original
            isOriginal: true
          });
        });
        
        // Then, check if any components from OTHER steps expand into this step
        sortedSteps.forEach((otherStep, otherStepIndex) => {
          if (otherStep.id === step.id) return; // Skip same step
          
          otherStep.components.forEach((component) => {
            const stepLength = component.stepLength || 1;
            const componentStartIndex = otherStepIndex;
            const componentEndIndex = otherStepIndex + stepLength - 1;
            
            // Check if this component spans into the current step
            if (stepIndex >= componentStartIndex && stepIndex <= componentEndIndex && stepIndex !== componentStartIndex) {
              // This is an expanded copy in this step
              componentsForThisStep.set(`${component.id}-copy-${stepIndex}`, {
                id: component.id, // Keep original ID for identification
                copyId: `${component.id}-copy-${stepIndex}`, // Unique ID for the layer list
                name: `${component.label} (expanded)`,
                featureId: component.featureId,
                type: component.type,
                icon: component.icon,
                varKey: component.varKey,
                iconCategory: component.iconCategory || null,
                visible: true,
                locked: false,
                isComponent: true,
                parentStep: step.id, // This step is showing the expanded copy
                isExpanded: true,
                isExpandedCopy: true,
                isOriginal: false,
                homeStepId: otherStep.id
              });
            }
          });
        });
        
        const orderedComponentsForThisStep = Array.from(componentsForThisStep.values()).sort((left, right) => {
          const leftIndex = stackingOrder.findIndex(item => item.id === left.id);
          const rightIndex = stackingOrder.findIndex(item => item.id === right.id);

          if (leftIndex !== -1 && rightIndex !== -1) {
            return leftIndex - rightIndex;
          }

          if (leftIndex !== -1) {
            return -1;
          }

          if (rightIndex !== -1) {
            return 1;
          }

          return 0;
        });

        // Add all components (own + expanded copies) to layers
        orderedComponentsForThisStep.forEach(componentLayer => {
          newLayers.push(componentLayer);
        });
      });
      
      return newLayers;
    });
  }, [steps, expandedComponents, stackingOrder]);
  
  // Sync flowItems with steps - remove panels for deleted/empty steps and sync positions
  useEffect(() => {
    // Prevent cascading updates
    if (isUpdatingFlowItemsRef.current) {
      return;
    }
    
    // Create hash to detect actual changes
    const stepsHash = JSON.stringify(steps.map(s => ({
      id: s.id,
      x: s.x,
      label: s.label,
      components: s.components?.map(c => ({
        id: c.id,
        label: c.label,
        customName: c.customName,
        isCustomName: c.isCustomName,
        stepLength: c.stepLength,
        autoStart: c.autoStart,
        startDelay: c.startDelay,
        isPrimaryStepItem: c.isPrimaryStepItem,
        commandParam: c.commandParam
      }))
    })));
    const stackingHash = JSON.stringify(stackingOrder.map(s => s.id));
    
    if (stepsHash === lastStepsHashRef.current && stackingHash === lastStackingHashRef.current) {
      return; // No actual change, skip update
    }
    
    lastStepsHashRef.current = stepsHash;
    lastStackingHashRef.current = stackingHash;
    isUpdatingFlowItemsRef.current = true;
    
    setFlowItems(prevItems => {
      // Sort steps by x position to get correct order
      const sortedSteps = [...steps].sort((a, b) => a.x - b.x);
      
      // Calculate the actual last step position accounting for component spans
      let lastStepIndex = sortedSteps.length - 1;
      sortedSteps.forEach((step, idx) => {
        if (step.components) {
          step.components.forEach(comp => {
            const componentSpan = comp.stepLength || 1;
            const componentLastStep = idx + componentSpan - 1;
            if (componentLastStep > lastStepIndex) {
              lastStepIndex = componentLastStep;
            }
          });
        }
      });
      
      // Build flowItems with expanded components for ALL visual positions
      const updatedPanels = [];
      const STEP_SPACING = FLOW_STEP_SPACING;
      const baseX = sortedSteps[0]?.x || 0;
      
      for (let visualIdx = 0; visualIdx <= lastStepIndex; visualIdx++) {
        const actualStep = sortedSteps[visualIdx];
        
        // Find existing panel to preserve any properties
        const existingPanel = prevItems.find(item => 
          item.type === 'panel' && (item.stepId === actualStep?.id || item.id === actualStep?.id)
        );
        
        // Collect components for this visual position
        const componentsForThisStep = [];
        
        sortedSteps.forEach((sourceStep, sourceIndex) => {
          if (sourceStep.components) {
            sourceStep.components.forEach(component => {
              const stepLength = component.stepLength || 1;
              const componentStartIndex = sourceIndex;
              const componentEndIndex = sourceIndex + stepLength - 1;
              
              // Check if this component spans into the current visual position
              if (visualIdx >= componentStartIndex && visualIdx <= componentEndIndex) {
                const isHomeStep = visualIdx === componentStartIndex;
                componentsForThisStep.push({
                  ...component,
                  isExpansionLinked: stepLength > 1,
                  isExpandedCopy: !isHomeStep,
                  homeStepId: sourceStep.id
                });
              }
            });
          }
        });
        
        // Sort components by stacking order (top of stacking list = top of panel)
        componentsForThisStep.sort((a, b) => {
          const aIndex = stackingOrder.findIndex(item => item.id === a.id);
          const bIndex = stackingOrder.findIndex(item => item.id === b.id);
          
          // If both are in stacking order, sort by their position (lower index = higher in list = appears first)
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only a is in stacking order, it comes first
          if (aIndex !== -1) return -1;
          // If only b is in stacking order, it comes first
          if (bIndex !== -1) return 1;
          // If neither is in stacking order, maintain original order
          return 0;
        });
        
        // Get y position from actual step or use the first step's y position for alignment
        const yPosition = actualStep?.y ?? sortedSteps[0]?.y ?? 0;
        
        // Calculate height based on total components at this position (including expanded copies)
        const panelHeight = calculateStepHeight(componentsForThisStep.length);
        
        updatedPanels.push({
          ...(existingPanel || {}),
          id: actualStep?.id || `virtual-step-${visualIdx}`,
          type: 'panel',
          stepId: actualStep?.id || `virtual-step-${visualIdx}`,
          x: baseX + (visualIdx * STEP_SPACING),
          y: yPosition,
          width: FLOW_PANEL_WIDTH,
          height: panelHeight,
          components: componentsForThisStep,
          label: actualStep?.label ?? `Step ${visualIdx + 1}`,
          sequenceLabel: String(visualIdx + 1).padStart(2, '0'),
          icon: componentsForThisStep[0]?.icon || actualStep?.components?.[0]?.icon || null,
          varKey: componentsForThisStep[0]?.varKey || actualStep?.components?.[0]?.varKey || null,
          isVirtual: !actualStep,
          locked: actualStep?.locked || false
        });
      }
      
      // Keep non-panel items (including plus signs for connections)
      const otherItems = prevItems.filter(item => item.type !== 'panel');
      
      // Add plus buttons between panels if they don't exist.
      // Keep them centered on the connector line (midpoint between panel edges).
      const plusButtons = [];
      const sharedConnectorCenterY = getSharedConnectorCenterY(updatedPanels);

      updatedPanels.forEach((panel, index) => {
        if (index < updatedPanels.length - 1) {
          const nextPanel = updatedPanels[index + 1];
          const existingPlus = otherItems.find(item => item.id === `plus-${index}`);
          if (!existingPlus) {
            const connectorStartX = panel.x + panel.width;
            const connectorEndX = nextPanel.x;
            plusButtons.push({
              id: `plus-${index}`,
              type: 'plus',
              x: ((connectorStartX + connectorEndX) / 2) - 16,
              y: sharedConnectorCenterY - 16,
              insertIndex: index + 1
            });
          }
        }
      });
      
      // Add start and end plus buttons
      if (updatedPanels.length > 0) {
        const existingStartPlus = otherItems.find(item => item.id === 'plus-start');
        if (!existingStartPlus) {
          plusButtons.push({
            id: 'plus-start',
            type: 'plus',
            x: updatedPanels[0].x - 80,
            y: sharedConnectorCenterY - 16,
            insertIndex: 0
          });
        }
        
        const lastPanel = updatedPanels[updatedPanels.length - 1];
        const existingEndPlus = otherItems.find(item => item.id === 'plus-end');
        if (!existingEndPlus) {
          plusButtons.push({
            id: 'plus-end',
            type: 'plus',
            x: lastPanel.x + lastPanel.width + GRID_SIZE,
            y: sharedConnectorCenterY - 16,
            insertIndex: updatedPanels.length
          });
        }
      }
      
      return [...otherItems, ...updatedPanels, ...plusButtons];
    });
    
    // Reset the flag after state update completes
    setTimeout(() => {
      isUpdatingFlowItemsRef.current = false;
    }, 0);
  }, [steps, stackingOrder, calculateStepHeight, getSharedConnectorCenterY]);
  
  // Sync stacking order with campaign features so it includes every feature once,
  // even if it is not in the active flow or only referenced by triggers.
  useEffect(() => {
    if (!campaignFeatures || campaignFeatures.length === 0) {
      return;
    }

    setStackingOrder(prevOrder => buildCampaignWideStackingOrder(prevOrder));
  }, [buildCampaignWideStackingOrder, campaignFeatures]);

  // Clean up truly empty steps (no components AND no expanded copies)
  useEffect(() => {
    setSteps(prevSteps => {
      const cleaned = removeEmptySteps(prevSteps);
      // Only update if something changed
      if (cleaned.length !== prevSteps.length) {
        return cleaned;
      }
      return prevSteps;
    });
  }, [steps, removeEmptySteps]);

  // Save current flow state when flowItems, steps, or stackingOrder change
  // Debounced to prevent excessive updates during drag operations
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      //
      setFlows(prevFlows => 
        prevFlows.map(flow => 
          flow.id === activeFlowId 
            ? { ...flow, flowItems, steps, stackingOrder }
            : flow
        )
      );
    }, 100); // 100ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [flowItems, steps, stackingOrder, activeFlowId]);

  // Auto-save timeline to feature chain when serialized flow state changes
  useEffect(() => {
    // Only save if we have campaign data, steps, and flows have been loaded
    if (!campaignData || !campaignId || flows.length === 0) return;
    
    // Don't auto-save if we just loaded from chains (wait for user changes)
    if (steps.length === 0) return;

    if (Date.now() < suppressTimelineAutosaveUntilRef.current) return;
    
    // Debounce the save to avoid too many API calls
    const timeoutId = setTimeout(() => {
      //
      saveTimelineAsChain(null, null, { showSuccessToast: false });
    }, 2000); // Save after 2 seconds of inactivity
    
    return () => clearTimeout(timeoutId);
  }, [steps, stackingOrder, campaignId, flows.length, campaignFeatures.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prevent default dragover behavior to avoid snap-back animation
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
    };
    
    document.addEventListener('dragover', handleDragOver);
    
    return () => {
      document.removeEventListener('dragover', handleDragOver);
    };
  }, []);
  
  // Detect scrolling to disable hover effects during scroll
  useEffect(() => {
    const handleScroll = () => {
      isScrollingRef.current = true;
      setIsScrollingState(true);
      
      // Clear previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Re-enable hovers 150ms after scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
        setIsScrollingState(false);
      }, 150);
    };
    
    const flowArea = flowAreaRef.current;
    if (flowArea) {
      flowArea.addEventListener('scroll', handleScroll, { passive: true });
      // Also listen on window for wheel events
      window.addEventListener('wheel', handleScroll, { passive: true });
    }
    
    return () => {
      if (flowArea) {
        flowArea.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('wheel', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Prevent any native dragstart within the flow area (pan-only behavior)
  useEffect(() => {
    const flowArea = flowAreaRef.current;
    if (!flowArea) return;
    const blockDragStart = (e) => {
      // e.preventDefault(); // Temporarily disabled to debug if this was causing issues or if it's needed
    };
    flowArea.addEventListener('dragstart', blockDragStart, { capture: true });
    return () => flowArea.removeEventListener('dragstart', blockDragStart, { capture: true });
  }, [flowAreaRef.current]);

  // Prevent native drag interactions while scrolling to avoid cross-cursor and freezes
  useEffect(() => {
    const handleDragStart = (e) => {
      if (isScrollingRef.current || isScrollingState) {
        e.preventDefault();
      }
    };
    window.addEventListener('dragstart', handleDragStart, { capture: true });
    return () => window.removeEventListener('dragstart', handleDragStart, { capture: true });
  }, [isScrollingState]);

  // Clear locked zoom center when Ctrl key is released
  useEffect(() => {
    const handleKeyUp = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setLockedZoomCenter(null);
      }
    };
    document.addEventListener('keyup', handleKeyUp);
    return () => document.removeEventListener('keyup', handleKeyUp);
  }, []);
  
  // These are now provided by useGridCalculations hook - remove old implementations
  // Generate grid dots based on container size
  const generateGridDots_OLD = useCallback(() => {
    const dots = [];
    
    // Calculate bounds based on panel positions
    let minX = 0;
    let minY = 0;
    let maxX = CONTAINER_WIDTH;
    let maxY = CONTAINER_HEIGHT;
    
    if (flowItems.length > 0) {
      const panels = flowItems.filter(item => item.type === 'panel');
      const plusSigns = flowItems.filter(item => item.type === 'plus');
      
      if (panels.length > 0 || plusSigns.length > 0) {
        // Include both panels and plus signs in bounds calculation
        const allItems = [...panels, ...plusSigns];
        minX = Math.min(...allItems.map(item => item.x));
        minY = Math.min(...allItems.map(item => item.y));
        maxX = Math.max(...allItems.map(item => item.type === 'panel' ? item.x + item.width : item.x + 32)); // Plus signs are 32px
        maxY = Math.max(...allItems.map(item => item.type === 'panel' ? item.y + item.height : item.y + 32));
        
        // Add padding around the items
        const padding = 200;
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = maxX + padding;
        maxY = maxY + padding;
      }
    }
    
    const gridWidth = Math.max(CONTAINER_WIDTH, Math.max(0, maxX - minX));
    const gridHeight = Math.max(CONTAINER_HEIGHT, Math.max(0, maxY - minY));
    
    const dotsX = Math.floor(gridWidth / GRID_SIZE);
    const dotsY = Math.floor(gridHeight / GRID_SIZE);
    
    for (let i = 0; i <= dotsX; i++) {
      for (let j = 0; j <= dotsY; j++) {
        dots.push({
          key: `${i}-${j}`,
          x: i * GRID_SIZE,
          y: j * GRID_SIZE,
        });
      }
    }
    
    return { dots, width: gridWidth, height: gridHeight };
  }, [flowItems]);

  // Generate connection lines between panels and plus signs
  const generateConnections = useCallback(() => {
    const connections = [];
    const panels = flowItems.filter(item => item.type === 'panel').sort((a, b) => a.x - b.x);
    const plusSigns = flowItems.filter(item => item.type === 'plus').sort((a, b) => a.x - b.x);
    
    // Connect panels to each other with straight horizontal lines
    for (let i = 0; i < panels.length - 1; i++) {
      const start = panels[i];
      const end = panels[i + 1];
      
      const startY = start.y + start.height / 2;
      const endY = end.y + end.height / 2;
      const midX = start.x + start.width + (end.x - start.x - start.width) / 2;
      
      // Use a consistent Y position (average of both panels' centers)
      const connectionY = (startY + endY) / 2;
      
      // Single horizontal line connecting the panels at the average height
      connections.push({
        key: `connection-panel-${start.id}-${end.id}`,
        x1: start.x + start.width,
        y1: connectionY,
        x2: end.x,
        y2: connectionY,
      });
      
      // Add dot in the middle of connection between panels
      connections.push({
        key: `connection-dot-${start.id}-${end.id}`,
        x1: midX,
        y1: connectionY,
        x2: midX,
        y2: connectionY,
        showDot: true,
        isJustDot: true
      });
    }
    
    // Generate expansion connector lines for expanded components (if enabled)
    if (showExpansionLines) {
      // Group panels by component ID to find expanded copies
      const componentPanelGroups = new Map();
      
      panels.forEach((panel, panelIdx) => {
        if (panel.components) {
          panel.components.forEach(component => {
            const compId = component.id;
            if (!componentPanelGroups.has(compId)) {
              componentPanelGroups.set(compId, []);
            }
            componentPanelGroups.get(compId).push({
              panel,
              component,
              isExpandedCopy: component.isExpandedCopy
            });
          });
        }
      });
      
      // For each component that appears in multiple panels, draw connector lines
      componentPanelGroups.forEach((instances, componentId) => {
        if (instances.length > 1) {
          // Sort instances by panel x position
          instances.sort((a, b) => a.panel.x - b.panel.x);
          
          // Draw lines between consecutive instances
          for (let i = 0; i < instances.length - 1; i++) {
            const startInstance = instances[i];
            const endInstance = instances[i + 1];
            
            // Calculate Y position based on component position within panel
            const startComponentIndex = startInstance.panel.components.indexOf(startInstance.component);
            const endComponentIndex = endInstance.panel.components.indexOf(endInstance.component);
            
            const componentHeight = 60; // Approximate height per component
            const startY = startInstance.panel.y + 40 + (startComponentIndex * componentHeight);
            const endY = endInstance.panel.y + 40 + (endComponentIndex * componentHeight);
            
            // Draw subtle dashed line
            connections.push({
              key: `expansion-line-${componentId}-${i}`,
              x1: startInstance.panel.x + startInstance.panel.width,
              y1: startY,
              x2: endInstance.panel.x,
              y2: endY,
              isExpansionLine: true // Flag for different styling
            });
          }
        }
      });
    }
    
    // Add horizontal extension lines at the ends with dots
    if (panels.length > 0) {
      const firstPanel = panels[0];
      const lastPanel = panels[panels.length - 1];
      const extensionLength = 60; // Length of extension lines
      
      // Calculate consistent Y position for all connections
      const allPanelsCenterY = panels.reduce((sum, panel) => sum + (panel.y + panel.height / 2), 0) / panels.length;
      
      // Extension line from start of first panel with dot at far left
      const leftExtensionStart = Math.max(0, firstPanel.x - extensionLength);
      connections.push({
        key: `extension-start-${firstPanel.id}`,
        x1: leftExtensionStart,
        y1: allPanelsCenterY,
        x2: firstPanel.x,
        y2: allPanelsCenterY,
      });
      
      // Dot at the far left end
      connections.push({
        key: `dot-start-${firstPanel.id}`,
        x1: leftExtensionStart,
        y1: allPanelsCenterY,
        x2: leftExtensionStart,
        y2: allPanelsCenterY,
        showDot: true,
        isJustDot: true
      });
      
      // Extension line from end of last panel with dot at far right
      const rightExtensionEnd = Math.min(CONTAINER_WIDTH, lastPanel.x + lastPanel.width + extensionLength);
      connections.push({
        key: `extension-end-${lastPanel.id}`,
        x1: lastPanel.x + lastPanel.width,
        y1: allPanelsCenterY,
        x2: rightExtensionEnd,
        y2: allPanelsCenterY,
      });
      
      // Dot at the far right end
      connections.push({
        key: `dot-end-${lastPanel.id}`,
        x1: rightExtensionEnd,
        y1: allPanelsCenterY,
        x2: rightExtensionEnd,
        y2: allPanelsCenterY,
        showDot: true,
        isJustDot: true
      });
    }
    
    return connections;
  }, [flowItems, showExpansionLines]);

  // OLD - Now using hooks for these
  // const connections = generateConnections();
  // const { dots: gridDots, width: gridWidth, height: gridHeight } = generateGridDots();
  // const safeGridWidth = Math.max(100, gridWidth || CONTAINER_WIDTH);
  // const safeGridHeight = Math.max(100, gridHeight || CONTAINER_HEIGHT);

  // Pan functionality - OLD, now using usePanningAndZoom hook
  const handleMouseDown_OLD = (e) => {
    if (e.target.classList.contains('flow-container') || e.target.classList.contains('grid-bg')) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  const handleMouseMove_OLD = (e) => {
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Function to zoom to center of viewport (for slider and wheel)
  const handleSliderZoom = useCallback((newZoom) => {
    const flowArea = flowAreaRef.current;
    if (!flowArea) {
      setZoom(newZoom);
      return;
    }

    // Get viewport center
    const rect = flowArea.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Calculate world coordinates at viewport center
    const worldX = (centerX - panOffset.x) / zoom;
    const worldY = (centerY - panOffset.y) / zoom;

    // Calculate new pan offset to keep center point in center
    const newPanX = centerX - worldX * newZoom;
    const newPanY = centerY - worldY * newZoom;

    setZoom(newZoom);
    setPanOffset({ x: newPanX, y: newPanY });
  }, [zoom, panOffset]);

  // Function to zoom with a locked center point (for wheel with session)
  const handleLockedZoom = useCallback((newZoom, lockedCenter) => {
    // Use the locked center point's world coordinates
    const newPanX = lockedCenter.viewportX - lockedCenter.worldX * newZoom;
    const newPanY = lockedCenter.viewportY - lockedCenter.worldY * newZoom;

    setZoom(newZoom);
    setPanOffset({ x: newPanX, y: newPanY });
  }, []);

  // Function to convert timeline steps to feature chain format
  const convertTimelineToChain = useCallback((steps, flowName = null, stepNames = null, flowLoopSettings = null) => {
    if (!steps || steps.length === 0) return { chain: [], metadata: { flowName, stepNames: [] } };

    const chain = [];
    const sortedSteps = [...steps].sort((a, b) => a.x - b.x);
    const extractedStepNames = [];

    sortedSteps.forEach((step, stepIndex) => {
    });

    sortedSteps.forEach((step, stepIndex) => {
      // Extract step name (use custom name or default)
      const stepName = step.label || `Step ${stepIndex + 1}`;
      extractedStepNames.push(stepName);
      
      if (step.components && step.components.length > 0) {
        const visibleStepComponents = [...step.components].sort((a, b) => {
          const aFeatureId = a.featureId || a.campaignFeatureId;
          const bFeatureId = b.featureId || b.campaignFeatureId;
          const aStackItem = stackingOrder?.find(item => item.featureId === aFeatureId);
          const bStackItem = stackingOrder?.find(item => item.featureId === bFeatureId);
          const aIndex = aStackItem?.zIndex ?? 999;
          const bIndex = bStackItem?.zIndex ?? 999;
          return aIndex - bIndex;
        });

        const primaryComponentId = visibleStepComponents.find(component => component.isPrimaryStepItem)?.id || visibleStepComponents[0]?.id;
        const orderedComponents = [
          ...visibleStepComponents.filter(component => component.id === primaryComponentId),
          ...visibleStepComponents.filter(component => component.id !== primaryComponentId),
        ];

        orderedComponents.forEach((component, componentIndex) => {
          const normalizedComponentFeatureId = normalizeFeatureReferenceId(
            component.campaignFeatureId || component.featureId
          );

          // Try to find the campaign feature by campaignFeatureId first (most reliable)
          let campaignFeature = null;
          
          if (component.campaignFeatureId) {
            const componentCampaignFeatureId = normalizeFeatureReferenceId(component.campaignFeatureId);
            campaignFeature = campaignFeatures.find(cf => (
              normalizeFeatureReferenceId(cf?._id) === componentCampaignFeatureId
            ));
          }

          // Secondary match by generic feature link id before falling back to label/varKey.
          if (!campaignFeature) {
            const componentFeatureId = normalizeFeatureReferenceId(component.featureId || component.campaignFeatureId);
            campaignFeature = campaignFeatures.find(cf => (
              normalizeFeatureReferenceId(cf?._id) === componentFeatureId
            ));
          }
          
          // Fallback to finding by label and varKey if no campaignFeatureId
          if (!campaignFeature) {
            campaignFeature = campaignFeatures.find(cf => 
              cf?.contentLabel === component.label && cf?.varKey === component.varKey
            );
          }

          const resolvedFeatureId = normalizeFeatureReferenceId(campaignFeature?._id) || normalizedComponentFeatureId;

          if (resolvedFeatureId) {
            // Determine end type based on the chosen primary component, not display order
            const isLastInStep = componentIndex === orderedComponents.length - 1;
            const endType = isLastInStep 
              ? CHAINS_HELPER.END_TYPE_FEATURE_END 
              : CHAINS_HELPER.END_TYPE_IMMEDIATE;

            // Build chain item with enhanced metadata
            const chainItem = {
              featureId: resolvedFeatureId,
              startType: component.autoStart === false ? CHAINS_HELPER.START_TYPE_MANUAL : CHAINS_HELPER.START_TYPE_AUTO,
              startDelay: component.autoStart === false ? 0 : Math.max(0, Math.min(1799, Number(component.startDelay) || 0)),
              endType: endType,
              stepLength: component.stepLength || 1,
              componentName: component.customName || null, // Custom name if set, otherwise null
              commandParam: component.commandParam || null, // Preserve commandParam if present
            };
            
            // Serialize to most efficient format (string if no extended props, object otherwise)
            const serialized = CHAINS_HELPER.serializeChainItem(chainItem);
            chain.push(serialized);
          } else {
            console.warn(`Component not found in campaign features:`, {
              label: component.label,
              varKey: component.varKey,
              campaignFeatureId: component.campaignFeatureId,
              featureId: component.featureId,
              stepIndex: stepIndex + 1
            });
          }
        });
      }
    });

    const normalizedFlowLoopSettings = {
      loop: flowLoopSettings?.loop === true,
      loopDelay: Math.max(0, Number(flowLoopSettings?.loopDelay) || 0),
    };

    if (chain.length > 0) {
      const lastSerializedChainItem = CHAINS_HELPER.parseChainItem(chain[chain.length - 1]);
      if (lastSerializedChainItem) {
        lastSerializedChainItem.loop = normalizedFlowLoopSettings.loop;
        lastSerializedChainItem.loopDelay = normalizedFlowLoopSettings.loop ? normalizedFlowLoopSettings.loopDelay : 0;
        chain[chain.length - 1] = CHAINS_HELPER.serializeChainItem(lastSerializedChainItem);
      }
    }

    return {
      chain,
      metadata: {
        flowName: flowName || null,
        stepNames: extractedStepNames
      }
    };
  }, [campaignFeatures, stackingOrder]);

  // Function to convert feature chain to timeline steps format
  const convertChainToTimeline = useCallback((chain, chainIndex, flowMetadata = {}) => {
    if (!chain || chain.length === 0) return { steps: [], flowItems: [], stackingOrder: [], metadata: {} };

    const steps = [];
    const GRID_SIZE = 40;
    const STEP_WIDTH = 300; // Actual panel width
    const STEP_SPACING = FLOW_STEP_SPACING; // Shared spacing between panel start positions
    const STEP_HEIGHT = 200;
    const CONTAINER_WIDTH = 2000;
    const CONTAINER_HEIGHT = 2000;
    
    // Extract metadata with defaults
    const flowName = flowMetadata?.flowName || `Flow ${chainIndex + 1}`;
    const stepNames = flowMetadata?.stepNames || [];
    
    // Don't calculate positions yet - wait until we know how many valid steps we have
    const START_Y = (CONTAINER_HEIGHT - STEP_HEIGHT) / 2; // Center vertically
    
    let currentStep = null;
    let currentStepComponents = [];
    let stepCounter = 0;
    let componentCounter = 0;

    chain.forEach((chainItemData, itemIndex) => {
      // Parse chain item (handles both legacy string and new object format)
      const chainItem = CHAINS_HELPER.parseChainItem(chainItemData);
      
      if (!chainItem) {
        console.warn('Failed to parse chain item:', chainItemData);
        return;
      }

      const { featureId, endType, stepLength, componentName } = chainItem;

      // Find the campaign feature
      const normalizedChainFeatureId = normalizeFeatureReferenceId(featureId);
      const campaignFeature = campaignFeatures.find(cf => (
        normalizeFeatureReferenceId(cf?._id) === normalizedChainFeatureId
      ));
      if (!campaignFeature) {
        console.warn('Campaign feature not found for chain item:', featureId);
        return;
      }

      // Find the full feature data
      const fullFeature = resolveCampaignFeatureObject(campaignFeature);

      if (!fullFeature) {
        console.warn('Full feature not found:', campaignFeature.feature);
        return;
      }

      // Create component from feature
      // Use itemIndex to ensure unique IDs even for same feature
      const isFirstComponentInStep = currentStepComponents.length === 0;
      const component = {
        id: `component-${Date.now()}-${itemIndex}-${componentCounter++}`,
        featureId: campaignFeature._id, // Use campaignFeatureId as featureId for linking
        label: campaignFeature.contentLabel || fullFeature.featureName || fullFeature.disruptorName, // Always use contentLabel from campaign, ignore saved componentName
        customName: componentName || null, // Preserve custom name for reference only
        icon: getFeatureIcon((campaignFeature.configKey || campaignFeature.varKey) || fullFeature.varKey, fullFeature.featureType),
        varKey: (campaignFeature.configKey || campaignFeature.varKey) || fullFeature.varKey,
        featureType: fullFeature.featureType,
        campaignFeatureId: campaignFeature._id,
        isExistingCampaignFeature: true, // Mark as existing campaign feature so updates work
        stepLength: stepLength || 1,
        autoStart: chainItem.startType !== CHAINS_HELPER.START_TYPE_MANUAL,
        startDelay: Math.max(0, Math.min(1799, Number(chainItem.startDelay) || 0)),
        commandParam: chainItem.commandParam || null,
        isPrimaryStepItem: isFirstComponentInStep,
        dummyData: {},
        iconCategory: resolveComponentIconCategory(fullFeature.featureCategory, (campaignFeature.configKey || campaignFeature.varKey) || fullFeature.varKey) // For flow view icon colouring
      };

      // Add to current step components
      currentStepComponents.push(component);

      // If this is END_TYPE_FEATURE_END or last item, create the step
      if (endType === CHAINS_HELPER.END_TYPE_FEATURE_END || itemIndex === chain.length - 1) {
        const stepId = `step-${Date.now()}-${stepCounter}`;
        // Don't calculate X position yet - will be done after loop
        const stepX = 0; // Placeholder
        const stepY = START_Y;
        
        // Use custom step name if available, otherwise default
        const stepLabel = stepNames[stepCounter] || `Step ${stepCounter + 1}`;
        
        currentStep = {
          id: stepId,
          stepId: stepId,
          label: stepLabel,
          x: stepX,
          y: stepY,
          width: STEP_WIDTH - GRID_SIZE,
          height: STEP_HEIGHT,
          components: [...currentStepComponents]
        };

        steps.push(currentStep);
        currentStepComponents = [];
        stepCounter++;
      }
    });

    // Now calculate positions based on ACTUAL number of steps created
    const totalStepsWidth = steps.length * STEP_SPACING;
    const START_X = (CONTAINER_WIDTH - totalStepsWidth) / 2;
    
    // Update all step positions
    steps.forEach((step, index) => {
      step.x = START_X + (index * STEP_SPACING);
    });

    // Generate flowItems and stackingOrder from steps
    const flowItems = [];
    const stackingOrder = [];
    
    steps.forEach((step, index) => {
      // Add panel for each step
      flowItems.push({
        id: step.id,
        type: 'panel',
        x: step.x,
        y: step.y,
        width: step.width,
        height: step.height,
        label: step.label,
        stepId: step.stepId,
        locked: step.locked || false
      });

      // Add components to stacking order
      step.components.forEach((component, compIndex) => {
        stackingOrder.push({
          id: component.id,
          featureId: component.featureId,
          label: component.label,
          icon: component.icon,
          iconCategory: component.iconCategory,
          varKey: component.varKey,
          stepId: step.id,
          zIndex: stackingOrder.length
        });
      });

      const sharedConnectorCenterY = getSharedConnectorCenterY(steps);

      // Add plus sign between steps at the geometric midpoint of the connector span
      if (index < steps.length - 1) {
        const nextStep = steps[index + 1];
        const connectorStartX = step.x + step.width;
        const connectorEndX = nextStep.x;
        flowItems.push({
          id: `plus-${index}`,
          type: 'plus',
          x: ((connectorStartX + connectorEndX) / 2) - 16,
          y: sharedConnectorCenterY - 16,
          insertIndex: index + 1
        });
      }
    });

    // Add plus signs at start and end
    if (steps.length > 0) {
      flowItems.unshift({
        id: 'plus-start',
        type: 'plus',
        x: steps[0].x - 80,
        y: getSharedConnectorCenterY(steps) - 16,
        insertIndex: 0
      });

      const lastStep = steps[steps.length - 1];
      flowItems.push({
        id: 'plus-end',
        type: 'plus',
        x: lastStep.x + lastStep.width + GRID_SIZE,
        y: getSharedConnectorCenterY(steps) - 16,
        insertIndex: steps.length
      });
    }

    return { steps, flowItems, stackingOrder, metadata: { flowName, stepNames } };
  }, [campaignFeatures, fullFeaturesList, getFeatureIcon, getSharedConnectorCenterY, resolveCampaignFeatureObject]);

  const getSyncedFlowsSnapshot = useCallback(() => {
    return flows.map((flow) => {
      if (flow.id !== activeFlowId) {
        return flow;
      }

      return {
        ...flow,
        flowItems,
        steps,
        stackingOrder,
      };
    });
  }, [activeFlowId, flowItems, flows, stackingOrder, steps]);

  const persistFlowCollection = useCallback(async (nextFlows) => {
    if (!campaignId || !campaignData) {
      toast.error('Cannot update flows right now.');
      return null;
    }

    const normalizedFlows = (nextFlows || []).map((flow, index) => ({
      ...flow,
      name: (flow?.name || `Flow ${index + 1}`).trim() || `Flow ${index + 1}`,
      flowItems: flow?.flowItems || [],
      steps: flow?.steps || [],
      stackingOrder: flow?.stackingOrder || [],
      stepNames: flow?.stepNames || [],
      loop: flow?.loop === true,
      loopDelay: Math.max(0, Number(flow?.loopDelay) || 0),
    }));

    const nextChains = [];
    const nextChainMetadata = [];

    normalizedFlows.forEach((flow) => {
      const stepLockedStates = {};
      (flow.steps || []).forEach((step, index) => {
        if (step?.locked) {
          stepLockedStates[index] = true;
        }
      });

      const { chain, metadata } = convertTimelineToChain(
        flow.steps || [],
        flow.name,
        flow.stepNames,
        {
          loop: flow.loop === true,
          loopDelay: Math.max(0, Number(flow.loopDelay) || 0),
        }
      );

      nextChains.push(chain);
      nextChainMetadata.push({
        ...metadata,
        stepLockedStates,
        stackingFeatureOrder: (flow.stackingOrder || [])
          .map((item) => item?.featureId)
          .filter(Boolean),
      });
    });

    try {
      await axios.post(updateCampaignRoute, {
        campaignID: campaignId,
        campaignName: campaignData.campaignName,
        campaignTier: campaignData.campaignTier,
        selectedRegion: campaignData.region,
        clientName: campaignData.client,
        adBugCopy: campaignData.adBugCopy,
        campaignOwner: campaignData.campaignOwnerAccount ? campaignData.campaignOwnerAccount._id : null,
        sfLink: campaignData.sfLink,
        sfNumber: campaignData.sfNumber,
        chains: nextChains,
        chainMetadata: nextChainMetadata,
      }, {
        withCredentials: true,
      });

      const nextCampaignData = {
        ...(campaignData || {}),
        chains: nextChains,
        chainMetadata: nextChainMetadata,
      };

      lastFetchedCampaignData.current = nextCampaignData;
      setFlows(normalizedFlows);
      setCampaignChains(nextChains);
      setCampaignData(nextCampaignData);
      setCampaignContext((prev) => ({
        ...prev,
        campaignData: nextCampaignData,
      }));

      return {
        flows: normalizedFlows,
        chains: nextChains,
        chainMetadata: nextChainMetadata,
        campaignData: nextCampaignData,
      };
    } catch (error) {
      console.error('Error updating flow collection:', error);
      toast.error(error.response?.data?.message || 'Failed to update flows');
      return null;
    }
  }, [campaignData, campaignId, convertTimelineToChain, setCampaignContext]);

  const handleRenameFlow = useCallback(async (flowId, newName) => {
    const resolvedName = (newName || '').trim();
    if (!resolvedName) {
      return false;
    }

    const syncedFlows = getSyncedFlowsSnapshot();
    const targetIndex = syncedFlows.findIndex((flow) => flow.id === flowId);

    if (targetIndex === -1) {
      toast.error('Flow not found');
      return false;
    }

    const nextFlows = syncedFlows.map((flow, index) => (
      index === targetIndex ? { ...flow, name: resolvedName } : flow
    ));
    const persisted = await persistFlowCollection(nextFlows);

    if (!persisted) {
      return false;
    }

    if (flowId === activeFlowId) {
      setCurrentFlowIndex(targetIndex);
    }

    toast.success(`Renamed flow to ${resolvedName}`);
    return true;
  }, [activeFlowId, getSyncedFlowsSnapshot, persistFlowCollection]);

  const handleDeleteFlow = useCallback(async (flowId) => {
    const syncedFlows = getSyncedFlowsSnapshot();
    if (syncedFlows.length <= 1) {
      return false;
    }

    const targetIndex = syncedFlows.findIndex((flow) => flow.id === flowId);
    if (targetIndex === -1) {
      toast.error('Flow not found');
      return false;
    }

    const deletedFlow = syncedFlows[targetIndex];
    const nextFlows = syncedFlows.filter((flow) => flow.id !== flowId);
    const fallbackActiveFlow = flowId === activeFlowId
      ? nextFlows[Math.max(0, targetIndex - 1)] || nextFlows[0]
      : nextFlows.find((flow) => flow.id === activeFlowId) || nextFlows[0];
    const persisted = await persistFlowCollection(nextFlows);

    if (!persisted) {
      return false;
    }

    const nextActiveFlow = persisted.flows.find((flow) => flow.id === fallbackActiveFlow?.id)
      || persisted.flows[0];
    const nextActiveIndex = persisted.flows.findIndex((flow) => flow.id === nextActiveFlow?.id);

    if (nextActiveFlow) {
      setActiveFlowId(nextActiveFlow.id);
      setCurrentFlowIndex(Math.max(0, nextActiveIndex));
      setFlowItems((nextActiveFlow.flowItems || []).map(normalizeFlowItemWidth));
      setSteps((nextActiveFlow.steps || []).map((step) => ({
        ...normalizeFlowStepWidth(step),
        locked: step?.locked || false,
      })));
      setStackingOrder(nextActiveFlow.stackingOrder || []);
    }

    toast.success(`Deleted flow: ${deletedFlow?.name || 'Flow'}`);
    return true;
  }, [activeFlowId, getSyncedFlowsSnapshot, persistFlowCollection]);

  // Function to save timeline as feature chain
  const saveTimelineAsChain = useCallback(async (flowId = null, stepsOverride = null, options = {}, stackingOrderOverride = null) => {
    const {
      showSuccessToast = true,
      successMessage = null,
      flowOverride = null,
      forceSave = false,
    } = options;

    if (!campaignId || !campaignData) {
      console.error('Cannot save chain: missing campaign data');
      return false;
    }

    try {
      // Get the flow to save (current or specified)
      const requestedFlowId = flowId || activeFlowId;
      const flowToSave = flowOverride || (requestedFlowId
        ? flows.find(f => f.id === requestedFlowId)
        : null);

      const resolvedFlowToSave = flowToSave || {
        id: requestedFlowId,
        name: 'Flow',
        steps,
        flowItems,
        stackingOrder,
        loop: false,
        loopDelay: 0,
        stepNames: [],
      };

      if (!resolvedFlowToSave?.id) {
        console.error('Flow not found');
        return false;
      }

      // Use current steps from state if this is the active flow (avoids stale data from flows state)
      const stepsToConvert = resolvedFlowToSave.id === activeFlowId ? (stepsOverride || steps) : resolvedFlowToSave.steps;

      const stackingOrderToSave = resolvedFlowToSave.id === activeFlowId ? (stackingOrderOverride || stackingOrder) : (resolvedFlowToSave.stackingOrder || []);

      // Convert timeline steps to chain format with metadata
      const { chain: newChain, metadata } = convertTimelineToChain(
        stepsToConvert,
        resolvedFlowToSave.name,
        resolvedFlowToSave.stepNames,
        {
          loop: resolvedFlowToSave.loop === true,
          loopDelay: Math.max(0, Number(resolvedFlowToSave.loopDelay) || 0),
        }
      );

      // Find the chain index for this flow (flows map to chains by index)
      const flowIndex = Math.max(0, flows.findIndex(f => f.id === resolvedFlowToSave.id));
      
      // Update chains array
      const updatedChains = [...campaignChains];
      
      // Ensure we have enough chain slots
      while (updatedChains.length <= flowIndex) {
        updatedChains.push([]);
      }
      
      updatedChains[flowIndex] = newChain;
      
      // Prepare chain metadata (flow names and step names for each flow)
      const updatedChainMetadata = [...(campaignData.chainMetadata || [])];
      while (updatedChainMetadata.length <= flowIndex) {
        updatedChainMetadata.push({});
      }
      
      // Preserve existing stepLockedStates when updating metadata
      const existingStepLockedStates = updatedChainMetadata[flowIndex]?.stepLockedStates || {};
      updatedChainMetadata[flowIndex] = {
        ...metadata,
        stepLockedStates: existingStepLockedStates,
        stackingFeatureOrder: stackingOrderToSave
          .map((item) => item.featureId)
          .filter(Boolean),
      };

      const currentChain = campaignChains[flowIndex] || [];
      const currentMetadata = campaignData.chainMetadata?.[flowIndex] || {};
      const nextMetadata = updatedChainMetadata[flowIndex] || {};
      const chainChanged = JSON.stringify(currentChain) !== JSON.stringify(newChain);
      const metadataChanged = JSON.stringify(currentMetadata) !== JSON.stringify(nextMetadata);

      if (!forceSave && !chainChanged && !metadataChanged) {
        return true;
      }

      // Save to backend
      const requestData = {
        campaignID: campaignId,
        campaignName: campaignData.campaignName,
        campaignTier: campaignData.campaignTier,
        selectedRegion: campaignData.region,
        clientName: campaignData.client,
        adBugCopy: campaignData.adBugCopy,
        campaignOwner: campaignData.campaignOwnerAccount ? campaignData.campaignOwnerAccount._id : null,
        sfLink: campaignData.sfLink,
        sfNumber: campaignData.sfNumber,
        chains: updatedChains,
        chainMetadata: updatedChainMetadata, // NEW: Save metadata
      };

      const response = await axios.post(updateCampaignRoute, requestData, {
        withCredentials: true
      });

      if (response.data) {
        const nextCampaignData = {
          ...(campaignData || {}),
          chains: updatedChains,
          chainMetadata: updatedChainMetadata,
        };

        setCampaignChains(updatedChains);
        setCampaignData(nextCampaignData);
        setCampaignContext((prev) => ({
          ...prev,
          campaignData: nextCampaignData,
        }));
        if (showSuccessToast) {
          toast.success(successMessage || `Saved timeline as feature chain: ${flowToSave.name}`);
        }
        emitCampaignEditorCampaignUpdated({
          updateType: 'timeline-chain',
          flowId: resolvedFlowToSave.id,
          flowIndex,
        });
        return true;
      } else {
        toast.error('Failed to save feature chain');
        return false;
      }
    } catch (error) {
      console.error('Error saving feature chain:', error);
      toast.error(error.response?.data?.message || 'Failed to save feature chain');
      return false;
    }
  }, [campaignId, campaignData, flows, activeFlowId, campaignChains, convertTimelineToChain, steps, stackingOrder, emitCampaignEditorCampaignUpdated]);

  const updateActiveFlowLoopSettings = useCallback(async (updates) => {
    const currentFlow = flows.find((flow) => flow.id === activeFlowId);
    if (!currentFlow) {
      return false;
    }

    const nextLoop = updates.loop === undefined ? currentFlow.loop === true : updates.loop === true;
    const nextLoopDelay = nextLoop ? Math.max(0, Number(updates.loopDelay ?? currentFlow.loopDelay) || 0) : 0;
    const nextFlow = {
      ...currentFlow,
      flowItems,
      steps,
      stackingOrder,
      loop: nextLoop,
      loopDelay: nextLoopDelay,
    };

    setFlows((prevFlows) => prevFlows.map((flow) => (
      flow.id === activeFlowId ? nextFlow : flow
    )));

    return saveTimelineAsChain(
      activeFlowId,
      steps,
      { showSuccessToast: false, flowOverride: nextFlow },
      stackingOrder
    );
  }, [activeFlowId, flowItems, flows, saveTimelineAsChain, stackingOrder, steps]);

  // Load chains as flows when campaign data is available
  useEffect(() => {
    // Prevent loading chains multiple times
    if (chainsLoadedRef.current) {
      return;
    }

    // Need campaign data and features loaded
    if (!campaignData || !campaignFeatures || campaignFeatures.length === 0 || !fullFeaturesList || fullFeaturesList.length === 0) {
      return;
    }

    // Mark as loaded
    chainsLoadedRef.current = true;

    // If no chains, create default empty flow
    if (!campaignChains || campaignChains.length === 0) {
      const defaultFlow = {
        id: 'flow-default',
        name: 'Flow 1',
        flowItems: [],
        steps: [],
        stackingOrder: []
      };
      setFlows([defaultFlow]);
      setActiveFlowId(defaultFlow.id);
      return;
    }

    // Get chain metadata (flow names, step names) with defaults
    const chainMetadata = campaignData.chainMetadata || [];

    // Convert each chain to a flow
    const loadedFlows = campaignChains.map((chain, index) => {
      // Get metadata for this chain (with defaults if not present)
      const flowMetadata = chainMetadata[index] || {};
      const flowName = flowMetadata.flowName || `Flow ${index + 1}`;
      const stepNames = flowMetadata.stepNames || [];
      const stepLockedStates = flowMetadata.stepLockedStates || {};
      const stackingFeatureOrder = flowMetadata.stackingFeatureOrder || [];
      
      const { steps, flowItems, stackingOrder, metadata } = convertChainToTimeline(chain, index, flowMetadata);

      const restoredStackingOrder = stackingOrder.length > 0
        ? [
            ...stackingOrder.slice().sort((a, b) => {
              const aFeatureId = normalizeFeatureReferenceId(a.featureId);
              const bFeatureId = normalizeFeatureReferenceId(b.featureId);
              const aCampaignStack = campaignFeatures.find((feature) => normalizeFeatureReferenceId(feature?._id) === aFeatureId)?.stackingOrder;
              const bCampaignStack = campaignFeatures.find((feature) => normalizeFeatureReferenceId(feature?._id) === bFeatureId)?.stackingOrder;
              const hasCampaignStack = typeof aCampaignStack === 'number' || typeof bCampaignStack === 'number';

              if (hasCampaignStack) {
                const normalizedACampaignStack = typeof aCampaignStack === 'number' ? aCampaignStack : Number.MAX_SAFE_INTEGER;
                const normalizedBCampaignStack = typeof bCampaignStack === 'number' ? bCampaignStack : Number.MAX_SAFE_INTEGER;
                if (normalizedACampaignStack !== normalizedBCampaignStack) {
                  return normalizedACampaignStack - normalizedBCampaignStack;
                }
              }

              const aIndex = stackingFeatureOrder.indexOf(aFeatureId);
              const bIndex = stackingFeatureOrder.indexOf(bFeatureId);
              const normalizedAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
              const normalizedBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
              return normalizedAIndex - normalizedBIndex;
            }).map((item, orderIndex) => ({
              ...item,
              zIndex: orderIndex,
            }))
          ]
        : stackingOrder;

      // Apply locked states to steps using index
      const stepsWithLockedStates = steps.map((step, idx) => ({
        ...step,
        locked: stepLockedStates[idx] || false
      }));

      const loopSettings = getFlowLoopSettingsFromChain(chain);
      
      return {
        id: `flow-chain-${index}`,
        name: flowName,
        flowItems,
        steps: stepsWithLockedStates,
        stackingOrder: restoredStackingOrder,
        loop: loopSettings.loop,
        loopDelay: loopSettings.loopDelay,
        stepNames // Store step names in flow for later saving
      };
    });

    // Include empty flows too
    if (loadedFlows.length > 0) {
      
      // Set the first flow as active
      const firstFlow = loadedFlows[0];
      setFlows(loadedFlows);
      setActiveFlowId(firstFlow.id);
      setCurrentFlowIndex(0);
      setFlowItems((firstFlow.flowItems || []).map(normalizeFlowItemWidth));
      setSteps((firstFlow.steps || []).map(normalizeFlowStepWidth));
      setStackingOrder(prevOrder => buildCampaignWideStackingOrder(prevOrder));
    }
  }, [buildCampaignWideStackingOrder, campaignData, campaignChains, campaignFeatures, fullFeaturesList]); // Removed convertChainToTimeline from deps

  // Update component labels when campaign features change (e.g., after renaming)
  useEffect(() => {
    if (!campaignFeatures || campaignFeatures.length === 0) {
      return;
    }

    // Update all flows with new labels from campaignFeatures
    if (flows && flows.length > 0) {
      const updatedFlows = flows.map(flow => ({
        ...flow,
        steps: (flow.steps || []).map(step => ({
          ...step,
          components: (step.components || []).map(component => {
            // Find the campaign feature for this component
            const campaignFeature = campaignFeatures.find(cf => 
              cf?._id === component.featureId || cf?._id === component.campaignFeatureId
            );
            
            if (campaignFeature && campaignFeature.contentLabel !== component.label) {
              return {
                ...component,
                label: campaignFeature.contentLabel
              };
            }
            
            return component;
          })
        }))
      }));
      
      setFlows(updatedFlows);
    }

    // Also update labels in the active steps without changing their structure
    setSteps(prevSteps => {
      if (!prevSteps || prevSteps.length === 0) {
        return prevSteps;
      }

      return prevSteps.map(step => ({
        ...step,
        components: (step.components || []).map(component => {
          const campaignFeature = campaignFeatures.find(cf => 
            cf?._id === component.featureId || cf?._id === component.campaignFeatureId
          );

          if (campaignFeature && campaignFeature.contentLabel !== component.label) {
            return {
              ...component,
              label: campaignFeature.contentLabel
            };
          }

          return component;
        })
      }));
    });
  }, [campaignFeatures/* , flows */]);

  // Function to add a feature to the campaign
  const addFeatureToCampaign = useCallback(async (varKey, contentLabel) => {
    if (!campaignId) {
      toast.error('No campaign ID available');
      return false;
    }

    // Find the full feature data - handle config variant keys (e.g. particleEffects_fireworks → particleEffects)
    const baseVarKey = WorkspaceConfig[varKey]?.featureKey || varKey;
    const featureData = fullFeaturesList.find(f => f.varKey === baseVarKey);
    
    if (!featureData) {
      toast.error(`Feature with varKey "${varKey}" not found`);
      return false;
    }

    // Note: We don't check for duplicates here because users should be able to add
    // the same feature type multiple times with different names/configurations.
    // The Campaign category items (isExistingCampaignFeature) skip calling this function entirely.

    // Create the feature data structure expected by the backend
    const newFeatureData = {
      feature: {
        _id: featureData._id,
        featureName: featureData.featureName || featureData.disruptorName,
        featureType: featureData.featureType
      },
      contentLabel: contentLabel,
      varKey: featureData.varKey,
      configKey: varKey !== featureData.varKey ? varKey : null,
      countIn: false,
      countInTime: 10,
      isChained: false,
      stackingOrder: campaignFeatures.length
    };

    // Prepare the full features array for the update
    const updatedFeaturesList = [
      ...campaignFeatures
        .map(cf => serializeCampaignFeatureForUpdate(cf))
        .filter(Boolean),
      {
        feature: newFeatureData.feature._id,
        contentLabel: newFeatureData.contentLabel,
        varKey: newFeatureData.varKey,
        configKey: newFeatureData.configKey,
        countIn: newFeatureData.countIn,
        countInTime: newFeatureData.countInTime,
        isChained: newFeatureData.isChained,
        stackingOrder: newFeatureData.stackingOrder
      }
    ];

    try {
      const previousFeatureIds = new Set(
        (campaignFeatures || [])
          .map(feature => feature?._id)
          .filter(Boolean)
      );

      const response = await axios.post(updatedFeatureListRoute, {
        campaignID: campaignId,
        features: updatedFeaturesList,
        addingFeature: true
      }, {
        withCredentials: true
      });

      if (response.data && response.data.campaignData) {
        const nextCampaignData = getNormalizedCampaignOwnerAccountData(response.data.campaignData, campaignDataRef.current);
        // Update local campaign features state
        const updatedFeatures = nextCampaignData.features || [];
        setCampaignFeatures(updatedFeatures);
        setCampaignData(nextCampaignData);
        generateLeftListNewCustomisationItems(customisationVarsRef.current.current, nextCampaignData);
        
        const addedFeatures = updatedFeatures.filter(feature => (
          feature?._id && !previousFeatureIds.has(feature._id)
        ));

        const newFeature = addedFeatures.find(feature => feature?.configKey === varKey)
          || addedFeatures.find(feature => (
            feature?.varKey === featureData.varKey
            && feature?.contentLabel === contentLabel
          ))
          || addedFeatures.find(feature => feature?.varKey === featureData.varKey)
          || updatedFeatures[updatedFeatures.length - 1];
        
        toast.success(`Added "${contentLabel}" to campaign!`);

        // Make sure our left list items are updated
        // setTimeout(() => {
        await fetchCampaignData(true); // Force refetch to update all related data and ensure consistency
        await getFeatureVars();
        // }, 100);
        
        // Return the new feature's ID
        return newFeature?._id || null;
      } else {
        toast.error('Failed to add feature to campaign');
        return null;
      }
    } catch (error) {
      console.error('Error adding feature to campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to add feature to campaign');
      return null;
    }
  }, [campaignId, fullFeaturesList, campaignFeatures, serializeCampaignFeatureForUpdate]);

  // Add a feature from a template and return the first newly created campaign feature ID,
  // so it can immediately be placed in a step after a drag-and-drop.
  const addFeatureFromTemplateForDrop = useCallback(async (templateId) => {
    try {
      const oldFeaturesLength = campaignFeatures.length;
      const response = await axios.post(addFeatureFromTemplateRoute, { campaignId, templateId }, { withCredentials: true });
      if (response.data && response.data.campaignData) {
        const data = getNormalizedCampaignOwnerAccountData(response.data.campaignData, campaignDataRef.current);
        setCampaignData(data);
        setCampaignFeatures(data.features || []);
        setCampaignChains(data.chains || []);
        await getFeatureVars();
        await getCustomisationVars(data);
        generateLeftListNewCustomisationItems(customisationVarsRef.current.current, data);
        const newFeature = (data.features || [])[oldFeaturesLength];
        if (newFeature) {
          toast.success('Template applied!');
          return newFeature._id;
        }
        toast.success('Template applied!');
        return null;
      }
      return null;
    } catch (err) {
      console.error('add-feature-from-template (drop) error:', err);
      toast.error('Failed to apply template');
      return null;
    }
  }, [campaignId, campaignFeatures]);

  const duplicateCampaignFeatureFromComponent = useCallback(async (sourceComponent) => {
    if (!sourceComponent || !campaignId) {
      return null;
    }

    const sourceFeatureId = normalizeFeatureReferenceId(
      sourceComponent.featureId || sourceComponent.campaignFeatureId || sourceComponent.id
    );
    const sourceCampaignFeature = (campaignFeatures || []).find((feature) => (
      normalizeFeatureReferenceId(feature?._id) === sourceFeatureId
    ));

    if (!sourceFeatureId || !sourceCampaignFeature) {
      toast.error('Failed to resolve the component to duplicate.');
      return null;
    }

    const duplicatedCampaignFeatureId = await addFeatureToCampaign(
      sourceCampaignFeature.configKey || sourceCampaignFeature.varKey || sourceComponent.varKey,
      sourceCampaignFeature.contentLabel || sourceComponent.label || 'Feature'
    );

    if (!duplicatedCampaignFeatureId) {
      return null;
    }

    const sourceCurrentVars = customisationVarsRef.current?.current?.[sourceFeatureId];
    if (sourceCurrentVars && Object.keys(sourceCurrentVars).length > 0) {
      const clonedCurrentVars = cloneComparableValue(sourceCurrentVars);
      const baseCustomisationVars = customisationVarsRef.current || customisationVars || { current: {}, default: {} };
      const nextCustomisationVars = {
        ...baseCustomisationVars,
        current: {
          ...(baseCustomisationVars.current || {}),
          [duplicatedCampaignFeatureId]: clonedCurrentVars,
        },
      };
      const latestCampaignData = lastFetchedCampaignData.current || campaignDataRef.current;
      const duplicatedFeatureData = findFeatureData(latestCampaignData, duplicatedCampaignFeatureId);

      if (duplicatedFeatureData?.feature) {
        fixSrcUrls(duplicatedFeatureData, duplicatedCampaignFeatureId, campaignId, null, clonedCurrentVars);
      }

      customisationVarsRef.current = nextCustomisationVars;
      setCustomisationVars(nextCustomisationVars);
      generateLeftListNewCustomisationItems(nextCustomisationVars.current || {}, latestCampaignData);

      try {
        await axios.post(updateCampaignCustomisationVarsRoute, {
          campaignID: campaignId,
          featureID: duplicatedCampaignFeatureId,
          updatedVars: {
            [duplicatedCampaignFeatureId]: cloneComparableValue(clonedCurrentVars),
          },
          oldFiles: [],
        }, {
          withCredentials: true,
        });

        updateSyncedSnapshotFeature(
          duplicatedCampaignFeatureId,
          clonedCurrentVars,
          nextCustomisationVars.default?.[duplicatedCampaignFeatureId]
        );
      } catch (error) {
        console.error('Error copying duplicated component settings:', error);
        toast.error(error.response?.data?.message || 'Component duplicated, but copying its settings failed.');
      }
    }

    return {
      ...sourceComponent,
      id: `component-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      featureId: duplicatedCampaignFeatureId,
      campaignFeatureId: duplicatedCampaignFeatureId,
      isExistingCampaignFeature: true,
    };
  }, [addFeatureToCampaign, campaignFeatures, campaignId, customisationVars, normalizeFeatureReferenceId, updateSyncedSnapshotFeature]);

  // Function to update a campaign feature's name (contentLabel)
  const updateCampaignFeatureName = useCallback(async (campaignFeatureId, newName) => {
    if (!campaignId) {
      console.error('No campaign ID available');
      return false;
    }

    // Find the feature in the campaign features list
    const featureToUpdate = campaignFeatures.find(cf => cf?._id === campaignFeatureId);
    
    if (!featureToUpdate) {
      console.error(`Campaign feature with ID "${campaignFeatureId}" not found`);
      return false;
    }

    // If the name hasn't changed, skip the update
    if (featureToUpdate.contentLabel === newName) {
      return true;
    }

    // Prepare the full features array with the updated name
    const updatedFeaturesList = campaignFeatures.map(cf => {
      const isTargetFeature = cf?._id === campaignFeatureId;

      return serializeCampaignFeatureForUpdate(cf, {
        contentLabel: isTargetFeature ? newName : undefined
      });
    }).filter(Boolean);

    try {
      const response = await axios.post(updatedFeatureListRoute, {
        campaignID: campaignId,
        features: updatedFeaturesList,
        addingFeature: false
      }, {
        withCredentials: true
      });

      if (response.data && response.data.campaignData) {
        const updatedCampaignData = response.data.campaignData;
        const updatedFeatures = updatedCampaignData.features || [];

        // Keep all local campaign snapshots aligned so regenerated nodes don't restore stale labels.
        setCampaignFeatures(updatedFeatures);
        setCampaignData(prev => prev ? { ...prev, features: updatedFeatures } : updatedCampaignData);
        setCampaignContext(prev => ({
          ...prev,
          campaignData: prev?.campaignData
            ? { ...prev.campaignData, features: updatedFeatures }
            : updatedCampaignData,
        }));
        toast.success(`Updated feature name to "${newName}"`);
        return true;
      } else {
        toast.error('Failed to update feature name');
        return false;
      }
    } catch (error) {
      console.error('Error updating feature name:', error);
      toast.error(error.response?.data?.message || 'Failed to update feature name');
      return false;
    }
  }, [campaignId, campaignFeatures, serializeCampaignFeatureForUpdate]);

  // Function to update a campaign feature's name by finding it via varKey and old name
  const updateCampaignFeatureNameByVarKey = useCallback(async (varKey, oldName, newName) => {
    if (!campaignId) {
      console.error('No campaign ID available');
      return false;
    }

    // Find the feature in the campaign features list by varKey and contentLabel
    const featureToUpdate = campaignFeatures.find(cf => 
      cf?.varKey === varKey && cf?.contentLabel === oldName
    );
    
    if (!featureToUpdate) {
      console.error(`Campaign feature with varKey "${varKey}" and label "${oldName}" not found`);
      return false;
    }

    // If the name hasn't changed, skip the update
    if (featureToUpdate.contentLabel === newName) {
      return true;
    }

    // Prepare the full features array with the updated name
    const updatedFeaturesList = campaignFeatures.map(cf => {
      const isTargetFeature = cf?._id === featureToUpdate._id;

      return serializeCampaignFeatureForUpdate(cf, {
        contentLabel: isTargetFeature ? newName : undefined
      });
    }).filter(Boolean);

    try {
      const response = await axios.post(updatedFeatureListRoute, {
        campaignID: campaignId,
        features: updatedFeaturesList,
        addingFeature: false
      }, {
        withCredentials: true
      });

      if (response.data && response.data.campaignData) {
        const updatedCampaignData = response.data.campaignData;
        const updatedFeatures = updatedCampaignData.features || [];

        // Keep all local campaign snapshots aligned so regenerated nodes don't restore stale labels.
        setCampaignFeatures(updatedFeatures);
        setCampaignData(prev => prev ? { ...prev, features: updatedFeatures } : updatedCampaignData);
        setCampaignContext(prev => ({
          ...prev,
          campaignData: prev?.campaignData
            ? { ...prev.campaignData, features: updatedFeatures }
            : updatedCampaignData,
        }));
        toast.success(`Updated feature name to "${newName}"`);
        return true;
      } else {
        toast.error('Failed to update feature name');
        return false;
      }
    } catch (error) {
      console.error('Error updating feature name:', error);
      toast.error(error.response?.data?.message || 'Failed to update feature name');
      return false;
    }
  }, [campaignId, campaignFeatures, serializeCampaignFeatureForUpdate]);

  const handleFeatureNameChange = useCallback(async (featureId, newName) => {
    const normalizedFeatureId = normalizeFeatureReferenceId(featureId);
    if (!normalizedFeatureId) {
      return false;
    }

    const featureToRename = campaignFeatures.find(cf => normalizeFeatureReferenceId(cf?._id) === normalizedFeatureId);
    if (!featureToRename) {
      return false;
    }

    const resolvedName = (newName || '').trim() || featureToRename.contentLabel;

    applyFeatureNameToVisibleState(normalizedFeatureId, resolvedName);

    if (resolvedName === featureToRename.contentLabel) {
      return true;
    }

    const updated = await updateCampaignFeatureName(normalizedFeatureId, resolvedName);

    if (!updated) {
      applyFeatureNameToVisibleState(normalizedFeatureId, featureToRename.contentLabel);
    }

    return updated;
  }, [applyFeatureNameToVisibleState, campaignFeatures, normalizeFeatureReferenceId, updateCampaignFeatureName]);

  const handleWheel = useCallback((e) => {
    // Check if Ctrl/Cmd is held down first
    if (e.ctrlKey || e.metaKey) {
      // Immediately prevent default behavior to stop scrolling
      e.preventDefault();
      e.stopPropagation();

      // Calculate zoom factor
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(Math.max(0.5, zoom * zoomFactor), 2);

      // If no locked center, establish one (like pressing down on slider)
      if (!lockedZoomCenter) {
        const flowArea = flowAreaRef.current;
        if (!flowArea) return;

        // Lock to viewport center
        const rect = flowArea.getBoundingClientRect();
        const viewportX = rect.width / 2;
        const viewportY = rect.height / 2;
        
        // Calculate what world point is at viewport center right now
        const worldX = (viewportX - panOffset.x) / zoom;
        const worldY = (viewportY - panOffset.y) / zoom;
        
        const newLockedCenter = { viewportX, viewportY, worldX, worldY };
        setLockedZoomCenter(newLockedCenter);
        
        // Use locked zoom for this first scroll
        handleLockedZoom(newZoom, newLockedCenter);
      } else {
        // Use existing locked center (like dragging slider)
        handleLockedZoom(newZoom, lockedZoomCenter);
      }
    }
  }, [zoom, panOffset, lockedZoomCenter, handleLockedZoom]);

  // Mouse event listeners are now handled in usePanningAndZoom hook

  // Handle drop on plus sign or step
  const handleDrop = async (e, plusItem, targetStep = null) => {
    e.preventDefault();
    if (isScrollingRef.current || isScrollingState) return;
    
    const dc = draggedComponentRef.current || draggedComponent;
    const di = draggedItemRef.current || draggedItem;
    const dp = draggedPanelRef.current || draggedPanel;
    const sourceStepId = draggedComponentSourceStepRef.current || draggedComponentSourceStep;
    const queueTimelineSave = (stepsOverride = null) => {
      if (!activeFlowId) {
        return;
      }

      // Save shortly after state updates settle so first-time drops persist.
      setTimeout(() => {
        saveTimelineAsChain(activeFlowId, stepsOverride, { showSuccessToast: false, forceSave: true });
      }, 400);
    };

    if (isFeatureTemplateCampaign) {
      const isLibraryDrop = !!di;
      const createsNewStep = !!dc && (!targetStep || targetStep.isVirtual || plusItem?.insertIndex !== undefined);

      if (isLibraryDrop || createsNewStep) {
        setDraggedItem(null);
        draggedItemRef.current = null;
        setDragOverStep(null);
        dragOverStepRef.current = null;
        setDragOverPosition(null);
        setCurrentAction(ACTION_STATE.IDLE);
        return;
      }
    }
    
    if (!di && !dp && !dc) {
      return;
    }

    // Snap to grid
    const snap = (val) => Math.round(val / GRID_SIZE) * GRID_SIZE;

    // If dropping a component from one step to another
    if (targetStep && dc) {
      const targetStepId = targetStep.id;
      
      // If dropping to the same step, do nothing
      if (sourceStepId === targetStepId && !targetStep.isVirtual) {
        setDraggedComponent(null);
        setDraggedComponentSourceStep(null);
        setDragOverStep(null);
        return;
      }
      
      // Remove component from source step and add to target step
      setSteps(prevSteps => {
        let updatedSteps = prevSteps.map(step => {
          // Remove from source step
          if (step.id === sourceStepId) {
            const newComponents = step.components.filter(c => c.id !== dc.id);
            return {
              ...step,
              components: newComponents,
              height: calculateStepHeight(newComponents.length)
            };
          }
          // Add to target step (if it exists)
          if (step.id === targetStepId && !targetStep.isVirtual) {
            const newComponents = [...step.components, dc];
            return {
              ...step,
              components: newComponents,
              height: calculateStepHeight(newComponents.length)
            };
          }
          return step;
        });
        
        // If target is virtual, create a new actual step
        if (targetStep.isVirtual) {
          // Determine step index for default naming
          const stepIndex = updatedSteps.length + 1;
          
          const newStep = {
            id: `step-${Date.now()}`,
            type: "step",
            name: `Step ${stepIndex}`, // Default to "Step N"
            isCustomName: false, // Track if user has customized the name
            x: targetStep.x,
            y: targetStep.y,
            width: targetStep.width,
            height: calculateStepHeight(1),
            components: [dc]
          };
          updatedSteps.push(newStep);
        }
        
        // Keep empty steps as placeholders (they may contain expanded components from other steps)
        // Empty component arrays [] are valid - they represent positions with only expanded copies
        
        // Redistribute remaining steps to close the gap
        if (updatedSteps.length > 0) {
          const PANEL_SPACING = FLOW_STEP_SPACING;
          updatedSteps = updatedSteps.map((step, index) => {
            const newStep = {
              ...step,
              x: snap(CONTAINER_WIDTH / 2 - (updatedSteps.length - 1) * PANEL_SPACING / 2 + index * PANEL_SPACING - FLOW_PANEL_HALF_WIDTH)
            };
            
            // Only update name if isCustomName is explicitly false (not undefined)
            if (step.isCustomName === false) {
              newStep.name = `Step ${index + 1}`;
            }
            
            return newStep;
          });
        }
        
        return updatedSteps;
      });
      
      // Clean up drag state after moving component
      setDraggedComponent(null);
      draggedComponentRef.current = null;
      setDraggedComponentSourceStep(null);
      draggedComponentSourceStepRef.current = null;
      setDragOverStep(null);
      dragOverStepRef.current = null;
      setDraggedItem(null);
      draggedItemRef.current = null;
      // Ensure we leave drag state clean even if dragend doesn't fire
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    // If dropping into an existing step, add component to that step
    if (targetStep && di) {
      // Check if this is an existing campaign feature
      const isExistingFeature = di.isExistingCampaignFeature;
      
      let newCampaignFeatureId = null;
      
      if (!isExistingFeature) {
        // Only add to campaign if it's a new feature from the library
        const contentLabel = di.label; // Use the label as the content label
        
        // Call the API to add feature to campaign and wait for the new feature ID
        try {
          if (di.isMediaAsset) {
            newCampaignFeatureId = await createFeatureForDraggedMediaItem(di);
          } else if (di.isTemplate) {
            newCampaignFeatureId = await addFeatureFromTemplateForDrop(di.templateId);
          } else {
            newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, contentLabel);
          }
          
          if (!newCampaignFeatureId) {
            console.warn('Failed to add feature to campaign');
            toast.error('Failed to add feature to campaign');
            return;
          }
          
        } catch (error) {
          console.error('Error adding feature to campaign:', error);
          toast.error('Failed to add feature to campaign');
          return;
        }
      } else {
        newCampaignFeatureId = di.campaignFeatureId;
      }
      
      const newComponent = {
        id: `component-${Date.now()}`,
        featureId: newCampaignFeatureId, // Use the new or existing campaign feature ID
        label: di.label,
        icon: di.icon,
        dummyData: di.dummyData,
        type: di.label.toLowerCase(),
        stepLength: 1,
        autoStart: true,
        startDelay: 0,
        commandParam: di.commandParam || null,
        isPrimaryStepItem: false,
        varKey: di.varKey || di.id, // Store varKey for later reference
        campaignFeatureId: newCampaignFeatureId, // Reference to campaign feature
        isExistingCampaignFeature: true, // Mark as existing campaign feature
        iconCategory: resolveComponentIconCategory(di.iconCategory || di.category, di.varKey || di.id) // For flow view icon colouring
      };
      const selectionStepId = targetStep.isVirtual ? `step-${Date.now()}` : targetStep.id;
      
      const baseSteps = Array.isArray(steps) ? steps : [];
      let updatedSteps;

      // If dropping on a virtual step, we need to create an actual step
      if (targetStep.isVirtual) {
        const newStep = {
          id: selectionStepId,
          type: "step",
          name: newComponent.label,
          x: targetStep.x,
          y: targetStep.y,
          width: targetStep.width,
          height: calculateStepHeight(1),
          components: [{ ...newComponent, isPrimaryStepItem: true }]
        };
        updatedSteps = [...baseSteps, newStep];

        // Redistribute steps to prevent stacking
        const PANEL_SPACING = FLOW_STEP_SPACING;
        updatedSteps = updatedSteps.map((step, index) => ({
          ...step,
          x: snap(CONTAINER_WIDTH / 2 - (updatedSteps.length - 1) * PANEL_SPACING / 2 + index * PANEL_SPACING - FLOW_PANEL_HALF_WIDTH)
        }));
      } else {
        // Otherwise, add to existing step
        updatedSteps = baseSteps.map(step => {
          if (step.id === targetStep.id) {
            const newComponents = ensureSinglePrimaryComponent([...step.components, newComponent]);
            return {
              ...step,
              components: newComponents,
              height: calculateStepHeight(newComponents.length) // Recalculate height
            };
          }
          return step;
        });
      }

      setSteps(updatedSteps);

      pendingDroppedCanvasSelectionRef.current = {
        source: 'library-drop',
        featureId: normalizeFeatureReferenceId(newCampaignFeatureId),
        componentId: newComponent.id,
        homeStepId: selectionStepId,
        selectedStepId: selectionStepId,
      };

      setDraggedItem(null);
      draggedItemRef.current = null;
      setDragOverStep(null);
      dragOverStepRef.current = null;
      // Reset action in case dragend is missed
      setCurrentAction(ACTION_STATE.IDLE);
      queueTimelineSave(updatedSteps);
      return;
    }

    // Check if this is an insertion between panels
    const insertIndex = plusItem.insertIndex;

    // Handle dropping an existing panel (reordering)
    if (dp) {
      setFlowItems((items) => {
        // Get current positions before reordering
        const currentPanels = items.filter(item => item.type === 'panel').sort((a, b) => a.x - b.x);
        const draggedPanelIndex = currentPanels.findIndex(p => p.id === dp.id);
        
        // Determine target index
        let targetIndex = insertIndex;
        
        // If no insertIndex, we're adding to the end
        if (targetIndex === undefined) {
          targetIndex = currentPanels.length - 1;
        }
        
        // The insertIndex comes from handleDragOver which already excludes the dragged panel
        // So we can use it directly without further adjustment
        let adjustedTargetIndex = targetIndex;
        
        // If dragging to the same position, don't do anything
        // But we need to check if they're ACTUALLY the same after adjustment
        if (adjustedTargetIndex === draggedPanelIndex) {
          return items; // No change needed
        }
        
        // Get all panels except the dragged one
        let allPanels = items.filter(item => item.type === 'panel' && item.id !== dp.id).sort((a, b) => a.x - b.x);
        
        // Get the CURRENT state of the dragged panel (not the cached version)
        const currentDraggedPanel = items.find(item => item.type === 'panel' && item.id === dp.id);
        if (!currentDraggedPanel) {
          console.warn('Dragged panel not found in current items');
          return items;
        }
        
        // Insert the current dragged panel at the correct position
        allPanels.splice(adjustedTargetIndex, 0, currentDraggedPanel);
        
        // Calculate new positions
        const PANEL_SPACING = FLOW_STEP_SPACING; // Shared spacing between panel centers
        const redistributedPanels = allPanels.map((panel, index) => ({
          ...panel,
          x: snap(CONTAINER_WIDTH / 2 - (allPanels.length - 1) * PANEL_SPACING / 2 + index * PANEL_SPACING - FLOW_PANEL_HALF_WIDTH)
        }));
        
        // Determine which panels need to animate
        const panelsToAnimate = [];
        if (draggedPanelIndex !== -1 && adjustedTargetIndex !== draggedPanelIndex) {
          if (draggedPanelIndex < adjustedTargetIndex) {
            // Dragged from left to right - animate panels that were between original and new position
            for (let i = draggedPanelIndex + 1; i <= adjustedTargetIndex; i++) {
              if (i < currentPanels.length) {
                panelsToAnimate.push(currentPanels[i].id);
              }
            }
          } else if (draggedPanelIndex > adjustedTargetIndex) {
            // Dragged from right to left - animate panels that were between new and original position
            for (let i = adjustedTargetIndex; i < draggedPanelIndex; i++) {
              if (i < currentPanels.length) {
                panelsToAnimate.push(currentPanels[i].id);
              }
            }
          }
        }
        
        // Set panels to animate
        setAnimatingPanels(panelsToAnimate);
        
        // Clear animation after 300ms
        setTimeout(() => {
          setAnimatingPanels([]);
        }, 300);
        
        // Also reorder the steps array to match the new panel order (inside setFlowItems to ensure it happens)
        setSteps(prevSteps => {
          // Get panel IDs in the new order
          const panelIds = redistributedPanels.map(p => p.stepId || p.id);
          
          // Reorder steps to match panel order
          const reorderedSteps = [];
          panelIds.forEach(panelId => {
            const step = prevSteps.find(s => s.id === panelId);
            if (step) {
              reorderedSteps.push(step);
            }
          });
          
          // Add any steps that weren't found (shouldn't happen, but safety)
          prevSteps.forEach(step => {
            if (!reorderedSteps.find(s => s.id === step.id)) {
              reorderedSteps.push(step);
            }
          });
          
          // Update x positions to match flowItems
          const finalSteps = reorderedSteps.map((step, index) => {
            const correspondingPanel = redistributedPanels[index];
            const newStep = {
              ...step,
              x: correspondingPanel ? correspondingPanel.x : step.x
            };
            
            // Only update name if isCustomName is explicitly false (not undefined)
            if (step.isCustomName === false) {
              newStep.name = `Step ${index + 1}`;
            }
            
            return newStep;
          });
          
          return finalSteps;
        });
        
        // Remove all existing plus signs and panels, then add redistributed panels
        const otherItems = items.filter(item => item.type !== 'plus' && item.type !== 'panel');
        const finalItems = [...otherItems, ...redistributedPanels];
        
        // No plus signs added
        
        return finalItems;
      });
      
      setDraggedPanel(null);
      setDragOverPosition(null);
      return;
    }

    // Handle dropping a new item from library OR dragging a component to create a new step
    if (di || dc) {
      // If we're dragging a component, we should NOT add a new campaign feature;
      // instead, we reuse the existing component's campaign feature reference.
      const isDraggingComponent = !!dc;
      let newCampaignFeatureId = null;

      if (!isDraggingComponent && di) {
        // For library items only, decide whether this is an existing campaign feature
        const isExistingFeature = di.isExistingCampaignFeature;
        
        if (!isExistingFeature) {
          // Only add to campaign if it's a new feature from the library
          const contentLabel = di.label; // Use the label as the content label
          
          // Call the API to add feature to campaign and wait for the new feature ID
          try {
            if (di.isMediaAsset) {
              newCampaignFeatureId = await createFeatureForDraggedMediaItem(di);
            } else if (di.isTemplate) {
              newCampaignFeatureId = await addFeatureFromTemplateForDrop(di.templateId);
            } else {
              newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, contentLabel);
            }
            
            if (!newCampaignFeatureId) {
              console.warn('Failed to add feature to campaign');
              toast.error('Failed to add feature to campaign');
              return;
            }
            
          } catch (error) {
            console.error('Error adding feature to campaign:', error);
            toast.error('Failed to add feature to campaign');
            return;
          }
        } else {
          newCampaignFeatureId = di.campaignFeatureId;
        }
      }
      
      // Determine the Y position - use existing steps' Y or center if first step
      const existingSteps = steps;
      const baseY = existingSteps.length > 0 
        ? existingSteps[0].y 
        : CONTAINER_HEIGHT / 2 - 30;

      // Determine panel dimensions based on component count
      const panelWidth = FLOW_PANEL_WIDTH; // Wider cards
      const newComponentId = `component-${Date.now()}`;
      const initialComponents = isDraggingComponent ? [{ ...dc, stepLength: dc.stepLength || 1 }] : [{
        id: newComponentId,
        featureId: newCampaignFeatureId, // Use the new or existing campaign feature ID
        label: di.label,
        icon: di.icon,
        dummyData: di.dummyData,
        type: di.label.toLowerCase(),
        stepLength: 1, // Default to single step
        autoStart: true,
        startDelay: 0,
        commandParam: di.commandParam || null,
        isCustomName: false, // Track if user has customized the component name
        varKey: di.varKey || di.id, // Store varKey for later reference
        campaignFeatureId: newCampaignFeatureId, // Reference to campaign feature
        isExistingCampaignFeature: true, // Mark as existing campaign feature now that it's been added
        iconCategory: resolveComponentIconCategory(di.iconCategory || di.category, di.varKey || di.id) // For flow view icon colouring
      }];
      const panelHeight = calculateStepHeight(initialComponents.length);
      
      // Create new step with the dragged component
      // Determine step index for default naming
      const stepIndex = existingSteps.length + 1;
      
      const newStep = {
        id: `step-${Date.now()}`,
        type: "step",
        name: `Step ${stepIndex}`, // Default to "Step N"
        isCustomName: false, // Track if user has customized the name
        x: snap(plusItem.x - FLOW_PANEL_HALF_WIDTH), // Center panel on plus sign
        y: snap(baseY), // Align with other panels
        width: panelWidth,
        height: panelHeight,
        components: initialComponents
      };

      // Also create flowItem for backwards compatibility
      const newFlowItem = {
        id: newStep.id,
        type: "panel",
        label: newStep.name,
        icon: di?.icon || dc?.icon,
        dummyData: di?.dummyData || dc?.dummyData,
        x: newStep.x,
        y: newStep.y,
        width: newStep.width,
        height: newStep.height,
        stepId: newStep.id,
        components: newStep.components
      };

      // Add step to steps array
      let nextSteps = [...existingSteps];

      // If dragging a component, remove it from source step
      if (isDraggingComponent && sourceStepId) {
        nextSteps = existingSteps.map(step => {
          if (step.id === sourceStepId) {
            const newComponents = step.components.filter(c => c.id !== dc.id);
            return {
              ...step,
              components: newComponents,
              height: calculateStepHeight(newComponents.length)
            };
          }
          return step;
        });
      }

      // Add the new step
      nextSteps = [...nextSteps, newStep];

      // If this is an insertion, reorder steps
      if (insertIndex !== undefined) {
        const stepWithoutNew = nextSteps.filter(s => s.id !== newStep.id);
        nextSteps = [...stepWithoutNew];

        // Check for components that span across the insertion point - increase their stepLength
        nextSteps = nextSteps.map((step, stepIdx) => {
          if (stepIdx < insertIndex && step.components) {
            const updatedComponents = step.components.map(comp => {
              const compSpan = comp.stepLength || 1;
              const compEnd = stepIdx + compSpan - 1;

              // If this component's span reaches or goes beyond the insertion point, extend it by 1
              if (compEnd >= insertIndex) {
                return { ...comp, stepLength: compSpan + 1 };
              }
              return comp;
            });

            return { ...step, components: updatedComponents };
          }
          return step;
        });

        nextSteps.splice(insertIndex, 0, newStep);

        // Redistribute ALL steps with proper spacing only when inserting
        const STEP_SPACING = FLOW_STEP_SPACING;
        nextSteps = nextSteps.map((step, index) => {
          const nextStep = {
            ...step,
            x: snap(CONTAINER_WIDTH / 2 - (nextSteps.length - 1) * STEP_SPACING / 2 + index * STEP_SPACING - FLOW_PANEL_HALF_WIDTH)
          };

          // Only update name if isCustomName is explicitly false (not undefined)
          if (step.isCustomName === false) {
            nextStep.name = `Step ${index + 1}`;
          }

          return nextStep;
        });
      }

      setSteps(nextSteps);

      // Fit the first panel into the viewport if this is the first step
      const isFirstStep = steps.length === 0;
      if (isFirstStep && flowAreaRef.current) {
        // Delay centering to allow flowItems to sync
        setTimeout(() => {
          fitViewportToFirstStep();
        }, 50);
      }

      if (!isDraggingComponent && newCampaignFeatureId) {
        pendingDroppedCanvasSelectionRef.current = {
          source: 'library-drop',
          featureId: normalizeFeatureReferenceId(newCampaignFeatureId),
          componentId: newComponentId,
          homeStepId: newStep.id,
          selectedStepId: newStep.id,
        };
      }

      setDraggedItem(null);
      draggedItemRef.current = null;
      setDragOverPosition(null);
      setDraggedComponent(null);
      draggedComponentRef.current = null;
      setDraggedComponentSourceStep(null);
      draggedComponentSourceStepRef.current = null;
      setCurrentAction(ACTION_STATE.IDLE);

      if (!isDraggingComponent && di) {
        queueTimelineSave(nextSteps);
      }
    }
  };

  // Handle drag over the flow area to detect insertion points
  const handleFlowAreaDragOver = (e) => {
    e.preventDefault();
    //
    // Debug logging for freeze investigation
    //
    
    if (isScrollingRef.current || isScrollingState) {
        //
        return;
    }
    // IMPORTANT: Check draggedComponent directly from state/ref because local variable binding might be stale in callback
    const dc = draggedComponentRef.current || draggedComponent;
    const di = draggedItemRef.current || draggedItem;
    const dp = draggedPanelRef.current || draggedPanel;
    if (!di && !dp && !dc) {
      return;
    }

    const flowArea = flowAreaRef.current;
    if (!flowArea) return;

    const rect = flowArea.getBoundingClientRect();
    // Calculate mouse position - since panels are rendered at (panel.x - minX), 
    // we need to add minX back to get the original coordinate space for comparison
    const x = (e.clientX - rect.left - panOffset.x) / zoom + minX;
    const y = (e.clientY - rect.top - panOffset.y) / zoom + minY;
    
    // Log every 10th event to avoid total spam, but enough to see activity
    // if (Math.random() < 0.1) 
    //

    // Check if we're hovering over an existing step (for library items OR dragged components)
    if (di || dc) {
      // Prefer DOM hit-testing so drag target remains active anywhere inside
      // the rendered panel, even if panel model height is stale.
      const hoveredPanelElement = e.target?.closest?.('.flow-panel');
      if (hoveredPanelElement) {
        const hoveredStepId = hoveredPanelElement.dataset?.stepId;
        if (hoveredStepId) {
          const correspondingStep = steps.find(
            (step) => String(step.id) === String(hoveredStepId)
          );

          if (correspondingStep) {
            if (correspondingStep.locked) {
              dragOverStepRef.current = null;
              setDragOverStep(null);
              setDragOverPosition(null);
              return;
            }

            dragOverStepRef.current = correspondingStep;
            setDragOverStep(correspondingStep);
            setDragOverPosition(null);
            return;
          }

          const correspondingPanel = flowItems.find((item) => (
            item.type === 'panel' && String(item.stepId || item.id) === String(hoveredStepId)
          ));

          if (correspondingPanel?.isVirtual) {
            const virtualStep = {
              id: correspondingPanel.id,
              name: correspondingPanel.label,
              x: correspondingPanel.x,
              y: correspondingPanel.y,
              width: correspondingPanel.width,
              height: correspondingPanel.height,
              components: correspondingPanel.components || [],
              isVirtual: true,
            };
            dragOverStepRef.current = virtualStep;
            setDragOverStep(virtualStep);
            setDragOverPosition(null);
            return;
          }
        }
      }

      //
      const panels = flowItems.filter(item => item.type === 'panel');
      for (const panel of panels) {
        const within = x >= panel.x && x <= panel.x + panel.width && 
                       y >= panel.y && y <= panel.y + panel.height;
        if (within) {
          //
          const correspondingStep = steps.find(step => step.id === panel.stepId || step.id === panel.id);
          
          if (correspondingStep) {
            // Don't allow drop on locked steps
            if (correspondingStep.locked) {
              //
              dragOverStepRef.current = null;
              setDragOverStep(null);
              setDragOverPosition(null);
              return;
            }
            // Real step exists
            //
            dragOverStepRef.current = correspondingStep;
            setDragOverStep(correspondingStep);
            setDragOverPosition(null);
            return;
          } else if (panel.isVirtual) {
            // Virtual step - create a temporary step object for drag-over
            //
            const virtualStep = {
              id: panel.id,
              name: panel.label,
              x: panel.x,
              y: panel.y,
              width: panel.width,
              height: panel.height,
              components: panel.components || [],
              isVirtual: true
            };
            dragOverStepRef.current = virtualStep;
            setDragOverStep(virtualStep);
            setDragOverPosition(null);
            return;
          }
        }
      }
      // Not hovering over any step
      //
      dragOverStepRef.current = null;
      setDragOverStep(null);
    }

    // Filter out the dragged panel when checking for insertion points
    const panels = flowItems.filter(item => 
      item.type === 'panel' && 
      (!draggedPanel || item.id !== draggedPanel.id)
    ).sort((a, b) => a.x - b.x);
    
    if (panels.length < 1) {
      // Allow dropping the first panel anywhere on the empty stage
      setDragOverPosition({
        x: x,
        y: y,
        insertIndex: 0
      });
      return;
    }

    // If we only have one panel (or dragging the only other panel), allow insertion at start/end
    if (panels.length === 1) {
      const panel = panels[0];
      const panelCenterY = getSharedConnectorCenterY(panels);
      const topBound = panel.y - 20;
      const bottomBound = panel.y + panel.height + 20;
      
      if (y >= topBound && y <= bottomBound) {
        if (x < panel.x) {
          // Insert before the panel
          setDragOverPosition({
            x: panel.x - 60,
            y: panelCenterY - 16,
            insertIndex: 0,
            leftPanelId: null,
            rightPanelId: panel.id
          });
          return;
        } else if (x > panel.x + panel.width) {
          // Insert after the panel
          setDragOverPosition({
            x: panel.x + panel.width + 60,
            y: panelCenterY - 16,
            insertIndex: 1,
            leftPanelId: panel.id,
            rightPanelId: null
          });
          return;
        }
      }
    }

    const sharedConnectorCenterY = getSharedConnectorCenterY(panels);

    // Check if we're between any two panels
    for (let i = 0; i < panels.length - 1; i++) {
      const leftPanel = panels[i];
      const rightPanel = panels[i + 1];
      
      // Make drop zone span the area between panel centers (wider, more intuitive)
      const leftBound = leftPanel.x + leftPanel.width / 2; // From left panel center
      const rightBound = rightPanel.x + rightPanel.width / 2; // To right panel center  
      const topBound = Math.min(leftPanel.y, rightPanel.y) - 20;
      const bottomBound = Math.max(leftPanel.y + leftPanel.height, rightPanel.y + rightPanel.height) + 20;
      
      if (x >= leftBound && x <= rightBound && y >= topBound && y <= bottomBound) {
        const connectorStartX = leftPanel.x + leftPanel.width;
        const connectorEndX = rightPanel.x;
        const insertionX = (connectorStartX + connectorEndX) / 2;
        const insertionY = sharedConnectorCenterY;
        
        // Calculate the correct insert index based on the original panel positions
        let adjustedInsertIndex = i + 1;
        if (draggedPanel) {
          // If we're moving an existing panel, we need to account for its removal
          const draggedPanelOriginalIndex = flowItems.filter(item => item.type === 'panel')
            .sort((a, b) => a.x - b.x)
            .findIndex(panel => panel.id === draggedPanel.id);
          
          // When dragging from left to right, the insertion index should be i + 1
          // When dragging from right to left, we need to account for the removal
          if (draggedPanelOriginalIndex < i + 1) {
            // Dragging from left to right - insert at i + 1
            adjustedInsertIndex = i + 1;
          } else {
            // Dragging from right to left - insert at i + 1 (no adjustment needed)
            adjustedInsertIndex = i + 1;
          }
        }
        
        setDragOverPosition({
          x: insertionX - 16,
          y: insertionY - 16,
          insertIndex: adjustedInsertIndex,
          leftPanelId: leftPanel.id,
          rightPanelId: rightPanel.id
        });
        return;
      }
    }

    // Check for insertion at the very beginning (before first panel)
    const firstPanel = panels[0];
    if (firstPanel && x < firstPanel.x && x >= firstPanel.x - 100) {
      const panelCenterY = sharedConnectorCenterY;
      const topBound = firstPanel.y - 20;
      const bottomBound = firstPanel.y + firstPanel.height + 20;
      
      if (y >= topBound && y <= bottomBound) {
        setDragOverPosition({
          x: firstPanel.x - 60,
          y: panelCenterY - 16,
          insertIndex: 0,
          leftPanelId: null,
          rightPanelId: firstPanel.id
        });
        return;
      }
    }

    // Check for insertion at the very end (after last panel)
    const lastPanel = panels[panels.length - 1];
    if (lastPanel && x > lastPanel.x + lastPanel.width && x <= lastPanel.x + lastPanel.width + 100) {
      const panelCenterY = sharedConnectorCenterY;
      const topBound = lastPanel.y - 20;
      const bottomBound = lastPanel.y + lastPanel.height + 20;
      
      if (y >= topBound && y <= bottomBound) {
        setDragOverPosition({
          x: lastPanel.x + lastPanel.width + 60,
          y: panelCenterY - 16,
          insertIndex: panels.length,
          leftPanelId: lastPanel.id,
          rightPanelId: null
        });
        return;
      }
    }
    
    setDragOverPosition(null);
  };

  // Handle drag leave to reset positions
  const handleFlowAreaDragLeave = (e) => {
    if (isScrollingRef.current || isScrollingState) return;
    // Only reset if we're actually leaving the flow area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverPosition(null);
      setDragOverStep(null);
    }
  };

  // Handle drop on insertion point
  const handleInsertionDrop = (e) => {
    e.preventDefault();
    const dc = draggedComponentRef.current || draggedComponent;
    const di = draggedItemRef.current || draggedItem;
    const dp = draggedPanelRef.current || draggedPanel;
    let targetStep = dragOverStepRef.current || dragOverStep;
    if (isScrollingRef.current || isScrollingState) return;

    // Fallback: if we don't have a dragOverStep yet, try to infer the step
    // from the element we dropped on (FlowPanel exposes data-step-id).
    if (!targetStep && (dc || di)) {
      const stepElement = e.currentTarget?.closest?.('.flow-panel');
      const stepId = stepElement?.dataset?.stepId;
      if (stepId) {
        const inferredStep = steps.find(step => step.id === stepId);
        if (inferredStep) {
          targetStep = inferredStep;
        }
      }
    }
    
    // Handle dropping a component into a step
    if (targetStep && dc) {
      handleDrop(e, null, targetStep);
      return;
    }
    
    // Handle dropping a library item into a step
    if (targetStep && di) {
      handleDrop(e, null, targetStep);
      return;
    }

    // If no dragOverPosition but we have a dragged item, drop it at cursor position
    if (!dragOverPosition && di) {
      const flowArea = flowAreaRef.current;
      if (!flowArea) return;
      
      const rect = flowArea.getBoundingClientRect();
      const x = (e.clientX - rect.left - panOffset.x) / zoom;
      const y = (e.clientY - rect.top - panOffset.y) / zoom;
      
      const tempPlusItem = {
        id: 'temp-insertion-plus',
        x: x,
        y: y,
        insertIndex: steps.length // Add to end
      };
      
      handleDrop(e, tempPlusItem);
      return;
    }

    if ((!di && !dp) || !dragOverPosition) {
      return;
    }

    // Create a temporary plus item for the drop handler
    const tempPlusItem = {
      id: 'temp-insertion-plus',
      x: dragOverPosition.x,
      y: dragOverPosition.y,
      insertIndex: dragOverPosition.insertIndex
    };

    handleDrop(e, tempPlusItem);
  };

  const resolveCanvasDropTargetStep = useCallback(() => {
    const placementContext = selectedFeaturePlacementContextRef.current;
    const candidateStepId = placementContext?.selectedStepId
      || selectedItem?.selectedStepId
      || selectedItem?.homeStepId
      || null;

    if (candidateStepId) {
      const matchedStep = steps.find((step) => step.id === candidateStepId);
      if (matchedStep) {
        return matchedStep;
      }
    }

    const sortedSteps = [...(steps || [])].sort((a, b) => a.x - b.x);
    return sortedSteps[0] || null;
  }, [selectedItem, steps]);

  const handleCanvasAreaDragOver = useCallback((e) => {
    const di = draggedItemRef.current || draggedItem;
    const dc = draggedComponentRef.current || draggedComponent;

    if (!di && !dc) {
      return;
    }

    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [draggedComponent, draggedItem]);

  const handleCanvasAreaDrop = useCallback((e) => {
    e.preventDefault();

    const targetStep = resolveCanvasDropTargetStep();
    if (!targetStep) {
      toast.error('Select a step before dropping a feature into canvas view.');
      return;
    }

    handleDrop(e, null, targetStep);
  }, [handleDrop, resolveCanvasDropTargetStep]);

  const handleAddFeatureFromTemplate = async (templateId, callback) => {
    try {
      const oldFeaturesLength = campaignFeatures.length;
      const response = await axios.post(addFeatureFromTemplateRoute, { campaignId, templateId }, { withCredentials: true });
      if (response.data && response.data.campaignData) {
        const data = getNormalizedCampaignOwnerAccountData(response.data.campaignData, campaignDataRef.current);
        const newFeature = (data.features || [])[oldFeaturesLength];
        const newFeatureId = newFeature?._id || null;
        setCampaignData(data);
        setCampaignFeatures(data.features || []);
        setCampaignChains(data.chains || []);
        // Keep CampaignContext in sync so DisruptorField.getDisruptorName can find the new feature
        setCampaignContext((prev) => ({
          ...prev,
          campaignData: getNormalizedCampaignOwnerAccountData(data, prev?.campaignData),
        }));
        toast.success('Template applied!');
        // Fire callback BEFORE fetching new customisation vars so the disruptor mutation
        // is applied to the current (unmodified) customisationVarsRef.current, not a fresh copy
        if (callback) callback(true, newFeatureId);
        // Now add only the new feature's vars without replacing existing vars (preserves the mutation above)
        await getFeatureVars();
        await getCustomisationVars(data, newFeatureId);
        generateLeftListNewCustomisationItems(customisationVarsRef.current.current, data);
      }
    } catch (err) {
      console.error('add-feature-from-template error:', err);
      toast.error('Failed to apply template');
      if (callback) callback(false, null);
    }
  };

  const handleAddFeatureFromLocalFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append('campaignPackage', file);
      formData.append('campaignId', campaignId);
      await axios.post(importFeaturePackageRoute, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchCampaignData(true);
      await getFeatureVars();
      toast.success('Feature package added!');
    } catch (err) {
      console.error('feature package import error:', err);
      toast.error(err.response?.data?.error || 'Failed to add feature package');
    }
  };

  const showPackageDownloadMenu = (event, feature = null) => {
    RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(event, [{
      label: feature ? 'Download Feature Package' : 'Download Campaign Package',
      action: async () => {
        RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
        try {
          await downloadCampaignPackage(feature?._id || feature?.campaignFeatureId || null);
        } catch (err) {
          console.error('campaign package download error:', err);
          toast.error('Failed to download package');
        }
      },
    }]);
  };

  const downloadCampaignPackage = async (featureId = null) => {
    const url = `${exportCampaignPackageRoute}/${campaignId}/export${featureId ? `?featureId=${encodeURIComponent(featureId)}` : ''}`;
    const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
    const disposition = response.headers['content-disposition'] || '';
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `${featureId ? 'feature' : 'campaign'}.zip`;
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadContextMenuFeaturePackage = async () => {
    const component = contextMenuComponent;
    // A flow component can contain both the feature-definition id and the
    // campaign-feature subdocument id. The package endpoint requires the
    // latter, because it owns this campaign's customisations and triggers.
    const candidateIds = [
      component?.campaignFeatureId,
      component?.featureId,
      component?.id,
    ]
      .map(normalizeFeatureReferenceId)
      .filter(Boolean);
    const featureId = candidateIds.find((candidateId) => (
      campaignFeatures.some((campaignFeature) => normalizeFeatureReferenceId(campaignFeature?._id) === candidateId)
    )) || candidateIds[0] || null;
    closeContextMenu();
    if (!featureId) {
      toast.error('Failed to resolve the selected component.');
      return;
    }
    try {
      await downloadCampaignPackage(featureId);
    } catch (err) {
      console.error('feature package download error:', err);
      toast.error('Failed to download feature package');
    }
  };

  const handleLibraryDragStart = useCallback((item, event) => {
    if (!item) {
      return;
    }
    if (isFeatureTemplateCampaign && currentModeRef.current === MODES.FLOW_EDITOR) {
      event?.preventDefault?.();
      return;
    }
    if (isScrollingRef.current || isScrollingState) {
      event?.preventDefault?.();
      return;
    }
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
      try {
        event.dataTransfer.setData('text/plain', item.id || item.varKey || item.label || 'library-item');
      } catch (_error) {
        // Some browsers throw when setting data on custom drags; ignore.
      }
    }
    setCurrentAction(ACTION_STATE.MOVING_COMPONENT);
    draggedItemRef.current = item;
    setDraggedItem(item);
    dragOverStepRef.current = null;
    setDragOverStep(null);
    setDragOverPosition(null);
  }, [isFeatureTemplateCampaign, isScrollingState, setDraggedItem, setDragOverStep, setDragOverPosition, setCurrentAction]);

  const handleLibraryDragEnd = useCallback(() => {

    setTimeout(() => {
      draggedItemRef.current = null;
      setDraggedItem(null);
      dragOverStepRef.current = null;
      setDragOverStep(null);
      setDragOverPosition(null);
      setCurrentAction(ACTION_STATE.IDLE);
    }, 100);
  }, [setDraggedItem, setDragOverStep, setDragOverPosition, setCurrentAction]);

  const pendingDroppedMediaSeedRef = useRef(null);

  const seedDroppedMediaFeatureAsset = useCallback((featureId, mediaItem) => {
    const mediaType = mediaItem?.mediaType;
    const mediaAsset = mediaItem?.asset;
    const config = MEDIA_DROP_FEATURE_CONFIG[mediaType];

    if (!featureId || !config || !mediaAsset?.filename) {
      return false;
    }

    const currentVarsRoot = customisationVarsRef.current?.current;
    const defaultVarsRoot = customisationVarsRef.current?.default;
    if (!currentVarsRoot) {
      return false;
    }

    if (!currentVarsRoot[featureId]) {
      currentVarsRoot[featureId] = JSON.parse(JSON.stringify(defaultVarsRoot?.[featureId] || {}));
    }

    const featureVars = currentVarsRoot[featureId];
    const displayName = mediaAsset.displayName || convertUniqueFilenameToOriginal(mediaAsset.name || mediaAsset.filename || 'Media Asset');
    const existingMediaEntries = Array.isArray(getGroupData(featureVars, config.collectionDataLink, -1))
      ? getGroupData(featureVars, config.collectionDataLink, -1)
      : [];
    const baseAssetValue = existingMediaEntries[0] && typeof existingMediaEntries[0] === 'object'
      ? existingMediaEntries[0]
      : {};
    const assetValue = {
      ...baseAssetValue,
      name: displayName,
      filename: mediaAsset.filename,
      isDefault: false,
      originalFile: mediaAsset.filename,
      srcUrl: normalizeDroppedMediaAssetUrl(mediaType, mediaAsset.url),
      type: mediaAsset.type || config.assetValueType,
    };
    stripTemplateAssetSourceMetadata(assetValue);

    updateCustomisationValue(featureVars, config.collectionDataLink, [assetValue], 0, null, null);

    if (Array.isArray(config.secondaryCollectionDataLinks)) {
      config.secondaryCollectionDataLinks.forEach((secondaryPath) => {
        updateCustomisationValue(featureVars, secondaryPath, [assetValue], 0, null, null);
      });
    }

    const featureData = campaignData?.features?.find((feature) => normalizeFeatureReferenceId(feature?._id) === normalizeFeatureReferenceId(featureId));
    if (featureData) {
      fixSrcUrls(featureData, featureId, campaignId, null, featureVars);
    }

    triggerSaveFeatureData(featureId);
    return true;
  }, [campaignData, campaignId, normalizeFeatureReferenceId]);

  const createFeatureForDraggedMediaItem = useCallback(async (mediaItem) => {
    const mediaType = mediaItem?.mediaType;
    const config = MEDIA_DROP_FEATURE_CONFIG[mediaType];
    if (!config) {
      return null;
    }

    const contentLabel = mediaItem?.label || mediaItem?.asset?.displayName || 'Media Asset';
    const newCampaignFeatureId = await addFeatureToCampaign(config.featureVarKey, contentLabel);
    if (!newCampaignFeatureId) {
      return null;
    }

    pendingDroppedMediaSeedRef.current = {
      featureId: normalizeFeatureReferenceId(newCampaignFeatureId),
      mediaItem,
    };

    return newCampaignFeatureId;
  }, [addFeatureToCampaign, normalizeFeatureReferenceId]);

  // Handle panel drag start
  const handlePanelDragStart = (panel) => {
    if (isScrollingRef.current || isScrollingState) {
        return;
    }
    setCurrentAction(ACTION_STATE.MOVING_STEP);
    setDraggedPanel(panel);
    draggedPanelRef.current = panel;
  };

  // Handle panel drag end
  const handlePanelDragEnd = () => {

    // Delay cleanup AND action state reset to allow drop event to complete first
    setTimeout(() => {
      setCurrentAction(ACTION_STATE.IDLE);
      setDraggedPanel(null);
      draggedPanelRef.current = null;
      setDragOverPosition(null);
    }, 100);
  };

  // Handle component drag start
  const handleComponentDragStart = (component, sourceStepId) => {

    if (isScrollingRef.current || isScrollingState) {

        return;
    }

    setCurrentAction(ACTION_STATE.MOVING_COMPONENT);
    setDraggedComponent(component);
    draggedComponentRef.current = component;
    setDraggedComponentSourceStep(sourceStepId);
    draggedComponentSourceStepRef.current = sourceStepId;
    // Treat dragged component like a library item for drop targets, but preserve
    // campaign feature metadata so disruptor/trigger slots can link to it.
    const dragItem = {
      ...component,
      label: component.label,
      icon: component.icon,
      dummyData: component.dummyData,
      varKey: component.varKey,
      campaignFeatureId: component.campaignFeatureId || component.featureId || null,
      isExistingCampaignFeature: !!(component.campaignFeatureId || component.featureId),
    };
    setDraggedItem(dragItem);
    draggedItemRef.current = dragItem;

  };

  // Handle component drag end
  const handleComponentDragEnd = () => {

    // Delay cleanup AND action state reset to allow drop event to complete first
    setTimeout(() => {
      setCurrentAction(ACTION_STATE.IDLE);
      setDraggedComponent(null);
      draggedComponentRef.current = null;
      setDraggedComponentSourceStep(null);
      draggedComponentSourceStepRef.current = null;
      setDragOverStep(null);
      dragOverStepRef.current = null;
      setDraggedItem(null);
      draggedItemRef.current = null;
    }, 100);
  };

  // Fit the initial viewport to the first step once each flow has rendered panels.
  useEffect(() => {
    const hasPanels = flowItems.some((item) => item.type === 'panel');
    if (!hasPanels || autoFramedFlowIdsRef.current.has(activeFlowId)) {
      return;
    }

    const frameViewport = () => {
      if (fitViewportToFirstStep()) {
        autoFramedFlowIdsRef.current.add(activeFlowId);
      }
    };

    requestAnimationFrame(frameViewport);
  }, [activeFlowId, flowItems, fitViewportToFirstStep]);
  
  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return;
    
    const handleClick = () => closeContextMenu();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu]);

  // Throttled hover setter to prevent rapid re-renders
  const setHoveredStackingItemThrottled = useCallback((featureId) => {
    const normalizedId = featureId === undefined || featureId === null ? null : String(featureId);
    
    if (!hoverEffectsEnabled) {
      return;
    }
    
    if (isScrollingRef.current) {

      return;
    }
    
    if (hoverThrottleRef.current) {
      clearTimeout(hoverThrottleRef.current);
    }
    
    hoverThrottleRef.current = setTimeout(() => {

      setHoveredStackingItem(normalizedId);
    }, 16);
  }, [hoverEffectsEnabled, setHoveredStackingItem]);

  // Direct setter for FlowPanel component hover — bypasses isScrollingRef so
  // canvas panning doesn't leave stale hover state stuck on previous item.
  const handleFlowPanelComponentHover = useCallback((featureId) => {
    if (!hoverEffectsEnabled) return;
    const normalizedId = featureId === undefined || featureId === null ? null : String(featureId);
    setHoveredStackingItem(normalizedId);
  }, [hoverEffectsEnabled, setHoveredStackingItem]);

  const handleTimelineClipHover = useCallback((componentId) => {
    if (componentId === undefined || componentId === null) {
      setHoveredTimelineComponentId(null);
    } else {
      setHoveredTimelineComponentId(String(componentId));
    }
  }, []);

  const handleCampaignLibraryFeatureHover = useCallback((libraryItem) => {
    const featureId = normalizeFeatureReferenceId(libraryItem?.campaignFeatureId || libraryItem?.id);
    if (!featureId) {
      setHoveredStackingItemThrottled(null);
      return;
    }

    setHoveredStackingItemThrottled(featureId);
  }, [normalizeFeatureReferenceId, setHoveredStackingItemThrottled]);
  
  // Prepare props for child components
  const layersPanelProps = {
    layers,
    flowItems,
    expandedLayers,
    onLayerToggle: handleLayerToggle,
    isStackingMode,
    setIsStackingMode,
    stackingOrder,
    draggedStackItem,
    dragOverStackItem,
    onStackDragStart: handleStackDragStart,
    onStackDragEnd: handleStackDragEnd,
    onStackDragOver: handleStackDragOver,
    onStackingContextAction: handleStackingContextAction,
    getStackingContextMenuState,
    hoveredStackingItem: hoveredStackingItemSafe,
    setHoveredStackingItem: setHoveredStackingItemThrottled,
    isScrollingRef,
    flows,
    activeFlowId,
    onFlowChange: handleFlowChange,
    onCreateFlow: handleCreateFlow,
    onRenameFlow: handleRenameFlow,
    onDeleteFlow: handleDeleteFlow,
    canCreateFlow: !isFeatureTemplateCampaign,
    showExpansionLines,
    setShowExpansionLines,
    onDeletePlacedComponent: deletePlacedComponent,
  };

  // If you pass in a step number, then you will see all the components used on that step, regardless of visiblity, this is used for thumbnails on flow panels
  const isVisibleFeatureListItem = (item) => item && item.visible !== false && item?.topNode?.visible !== false;
  const isFeatureListItemExplicitlyVisible = (item) => item?.visible === true || item?.topNode?.visible === true;

  const applyStepVisibilityToItems = useCallback((itemsMap, stepId, stepSource = steps) => {
    if (!itemsMap || !stepId) {
      return false;
    }

    const targetStep = (stepSource || []).find((step) => step.id === stepId);
    if (!targetStep) {
      return false;
    }

    const targetFeatureIds = new Set(
      (targetStep.components || [])
        .map((component) => normalizeFeatureReferenceId(component?.featureId || component?.campaignFeatureId || component?.id))
        .filter(Boolean)
    );

    let changed = false;
    Object.entries(itemsMap).forEach(([featureId, item]) => {
      if (!item) {
        return;
      }

      const shouldBeVisible = targetFeatureIds.has(String(featureId));

      if (item.visible !== shouldBeVisible) {
        item.visible = shouldBeVisible;
        changed = true;
      }

      if (item.topNode && item.topNode.visible !== shouldBeVisible) {
        item.topNode.visible = shouldBeVisible;
        changed = true;
      }
    });

    return changed;
  }, [normalizeFeatureReferenceId, steps]);

  const getFlowStepListNodesForPreview = (forceDisplayStepLayers = -1) => {
    const flowStepListNodesIdsForPreview = [];
    const flows_loc = flows;
    if (Array.isArray(flows_loc) && activeFlowId) {
      // Find the active flowand get its steps
      const activeFlow = flows_loc.find(flow => flow.id === activeFlowId);
      const previewStackingOrder = Array.isArray(stackingOrder) && stackingOrder.length > 0
        ? stackingOrder
        : activeFlow?.stackingOrder;

      if (activeFlow && Array.isArray(previewStackingOrder)) {
        if (forceDisplayStepLayers === -1) {
          previewStackingOrder.forEach(stackComponentData => {
              // Find the component in the flow's steps
              const componentId = stackComponentData.featureId;
              if (!componentId) return;
              if (flowStepListNodesIdsForPreview.includes(componentId)) return; // Avoid duplicates
              if (componentId === selectedFeatureId || componentId === visibleTriggeredDisruptorId || isVisibleFeatureListItem(leftListNewCustomisationItemsRef.current?.[componentId])) {
                flowStepListNodesIdsForPreview.push(componentId);
              }
          });
        } else {
          const activeStepData = activeFlow.steps[forceDisplayStepLayers];
          // Include all components from any step where startStep <= targetStepIndex <= endStep
          const targetStepIndex = forceDisplayStepLayers;
          activeFlow.steps.forEach((stepData, sIdx) => {
            previewStackingOrder.forEach(stackComponentData => {
              const componentId = stackComponentData.featureId;
              if (!componentId) return;
              if (flowStepListNodesIdsForPreview.includes(componentId)) return; // Avoid duplicates
              if (componentId === visibleTriggeredDisruptorId) {
                flowStepListNodesIdsForPreview.push(componentId);
                return;
              }
              const componentData = stepData?.components?.find(comp => comp.featureId === componentId);
              if (componentData) {
                const stepLength = componentData.stepLength || 1;
                const endStep = sIdx + stepLength - 1;
                if (targetStepIndex >= sIdx && targetStepIndex <= endStep) {
                  flowStepListNodesIdsForPreview.push(componentId);
                }
              }
            });
          });
        }
      }
    }
    const flowStepListNodesForPreview = [];
    for (const componentId of flowStepListNodesIdsForPreview) {
      const nodeListItemData = leftListNewCustomisationItemsRef.current?.[componentId];
      if (nodeListItemData) {
        flowStepListNodesForPreview.push(nodeListItemData);
        if (componentId === visibleTriggeredDisruptorId) {
          nodeListItemData.useParamValue = visibleTriggeredDisruptorParamValue;
          if (nodeListItemData.topNode) {
            nodeListItemData.topNode.useParamValue = visibleTriggeredDisruptorParamValue;
          }
        } else {
          nodeListItemData.useParamValue = null;
          if (nodeListItemData.topNode) {
            nodeListItemData.topNode.useParamValue = null;
          }
        }
      }
    }

    return flowStepListNodesForPreview;
  };

  const findNodeInListContainingPropValue = (propValue = null, nodeListId = null) => {
    if (propValue === null) {
      return null; 
    }
    if (nodeListId) {
      const nodeList = leftListNewCustomisationItemsRef.current?.[nodeListId];
      return searchNodeListForPropValue(nodeList, propValue);
    } else {
      for (const nodeId in leftListNewCustomisationItemsRef.current) {
        const nodeList = leftListNewCustomisationItemsRef.current[nodeId];
        const foundNode = searchNodeListForPropValue(nodeList, propValue);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }
  const searchNodeListForPropValue = (nodeList, propValue) => {
    const timeline = nodeList?.config?.timeline;
    if (timeline) {
      const audioGroups = timeline.audioGroups;
      if (audioGroups && Array.isArray(audioGroups)) {
        for (const audioGroup of audioGroups) {
          const audios = audioGroup.audios;
          if (audios && Array.isArray(audios)) {
            for (const audio of audios) {
              if (doesObjectContainPropValue(audio, propValue)) {
                return audio;
              }
            }
          }
        }
      }
      if (timeline.disruptorGroups && Array.isArray(timeline.disruptorGroups)) {
        for (const disruptorGroup of timeline.disruptorGroups) {
          const disruptors = disruptorGroup.disruptors;
          if (disruptors && Array.isArray(disruptors)) {
            for (const disruptor of disruptors) {
              if (doesObjectContainPropValue(disruptor, propValue)) {
                return disruptor;
              }
            }
          }
        }
      }
      // We should expand this to search subnodes, for for my purposes, I only need timeline audio and disruptor groups, so I'm leaving it as is for now.
    }
    return null;
  }
  const doesObjectContainPropValue = (obj, propValue) => {
    // recursively check if the object or any of its nested objects contain the specified propValue
    if (obj === null || typeof obj !== 'object') {
      return false;
    }
    for (const key in obj) {
      if (obj[key] === propValue) {
        return true;
      }
      if (doesObjectContainPropValue(obj[key], propValue)) {
        return true;
      }
    }
    return false;
  }

  const activateStep = (stepId) => {
    if (!stepId) {
      return;
    }

    lastActivatedStepIdRef.current = stepId;

    const itemsMap = leftListNewCustomisationItemsRef.current;
    if (!itemsMap) {
      return;
    }

    applyStepVisibilityToItems(itemsMap, stepId);

    // Finally reset visible triggered disruptor (if any) since we're switching steps
    setVisibleTriggeredDisruptorId(null);
    setVisibleTriggeredDisruptorParamValue(null);
    NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
    forceUpdateComponent();
  }

  const syncStepComponentVisibility = useCallback((stepId, stepSource = steps, resetToStep = false) => {
    if (!stepId || !leftListNewCustomisationItemsRef.current) {
      return;
    }

    const targetStep = (stepSource || []).find((step) => step.id === stepId);
    if (!targetStep) {
      return;
    }

    const targetFeatureIds = new Set(
      (targetStep.components || [])
        .map((component) => normalizeFeatureReferenceId(component?.featureId || component?.campaignFeatureId))
        .filter(Boolean)
    );

    let changed = false;
    Object.entries(leftListNewCustomisationItemsRef.current || {}).forEach(([featureId, item]) => {
      if (!item) {
        return;
      }

      const shouldBeVisible = targetFeatureIds.has(String(featureId));
      if (resetToStep) {
        if (item.visible !== shouldBeVisible) {
          item.visible = shouldBeVisible;
          changed = true;
        }

        if (item.topNode && item.topNode.visible !== shouldBeVisible) {
          item.topNode.visible = shouldBeVisible;
          changed = true;
        }
        return;
      }

      if (!shouldBeVisible) {
        return;
      }

      if (item.visible !== true) {
        item.visible = true;
        changed = true;
      }

      if (item.topNode && item.topNode.visible !== true) {
        item.topNode.visible = true;
        changed = true;
      }
    });

    if (changed) {
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
      forceUpdateComponent();
    }
  }, [normalizeFeatureReferenceId, steps]);

  useEffect(() => {

  }, [activeNode]);

  /* NEW VISUAL EDITOR INTEGRATION STUFF
    ================================================== 
    SOME OF THIS MAY BE REDUNDANT AND CAN BE CLEANED UP LATER OR MOVED OUT OF THIS COMPONENT
    ==================================================  
  */
  const workspaceConfig = WorkspaceConfig;
  const navigate = useNavigate();

  const [leftListNewCustomisationItems, setLeftListNewCustomisationItems] = useState(null);
  const leftListNewCustomisationItemsRef = useRef(null);
  useEffect(() => {
    leftListNewCustomisationItemsRef.current = leftListNewCustomisationItems;
    console.log('leftListNewCustomisationItems updated:', leftListNewCustomisationItems);
  }, [leftListNewCustomisationItems]);

  useEffect(() => {
    if (!leftListNewCustomisationItems) {
      return;
    }

    NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
  }, [leftListNewCustomisationItems]);
  
  const [selectedSidebarFieldDataLink, setSelectedSidebarFieldDataLink] = useState(null);
  const pendingSidebarFieldDataLinkRef = useRef(null);
  const [focusEditingOnNode, setFocusEditingOnNode] = useState(null);
  useEffect(() => {
    slideOutPanelControl?.closePanel?.();
  }, [activeNode]);
  const [activeTimelineAudioGroup, setActiveTimelineAudioGroup] = useState(null);
  const [activeTimelineDisruptorGroup, setActiveTimelineDisruptorGroup] = useState(null);
  const [visibleTriggeredDisruptorId, setVisibleTriggeredDisruptorId] = useState(null);
  const [visibleTriggeredDisruptorParamValue, setVisibleTriggeredDisruptorParamValue] = useState(null);
  const [activeVisualisedPreviewNode, setActiveVisualisedPreviewNode] = useState(null);
  const activeVisualisedPreviewNode_ref = useRef(activeVisualisedPreviewNode);
  useEffect(() => {
    activeVisualisedPreviewNode_ref.current = activeVisualisedPreviewNode;
  }, [activeVisualisedPreviewNode]);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [selectedFeatureNode, setSelectedFeatureNode] = useState(null);
  const [selectedLayersNodeState, setSelectedLayersNodeState] = useState(null);
  const normalizedSelectedFeatureId = normalizeFeatureReferenceId(selectedFeatureId);

  const getLayersNodeFeatureScope = useCallback((node) => (
    node?.topNode?.id
    || node?.visualisedTopNode?.id
    || node?.featureData?._id
    || node?.featureData?.id
    || node?.parent?.topNode?.id
    || node?.parent?.visualisedTopNode?.id
    || node?.id
    || null
  ), []);

  const getLayersNodeDataLink = useCallback((node) => String(
    node?.config?.groupSettings?.dataLink
    || node?.config?.dataLink
    || ''
  ).trim(), []);

  const getLayersNodeDataIndex = useCallback((node) => (
    Number.isInteger(node?.dataIndex) && node.dataIndex >= 0
      ? String(node.dataIndex)
      : ''
  ), []);

  const getLayersNodePathTail = useCallback((rawPath) => {
    const value = String(rawPath || '').trim();
    if (!value) return '';

    const parts = value.split('.').filter(Boolean);
    return parts.slice(-2).join('.');
  }, []);

  const getNodeTreeMatchKey = useCallback((node) => JSON.stringify({
    id: String(node?.id || '').trim(),
    path: String(node?.path || '').trim(),
    name: String(node?.name || '').trim().toLowerCase(),
    dataLink: String(node?.config?.groupSettings?.dataLink || node?.config?.dataLink || '').trim(),
    dataIndex: Number.isInteger(node?.dataIndex) ? String(node.dataIndex) : '',
    depth: Number.isInteger(node?.depth) ? String(node.depth) : '',
  }), []);

  const forEachNodeInTree = useCallback((node, visitor) => {
    if (!node) {
      return;
    }

    visitor(node);
    (node.subNodes || []).forEach((subNode) => forEachNodeInTree(subNode, visitor));
  }, []);

  const copyTransientNodeState = useCallback((previousNode, nextNode) => {
    if (!previousNode || !nextNode) {
      return;
    }

    nextNode.visible = previousNode.visible === true;
    nextNode.selected = previousNode.selected === true;
    nextNode.expanded = previousNode.expanded === true;
    nextNode.active = previousNode.active === true;

    if (Object.prototype.hasOwnProperty.call(previousNode, 'useParamValue')) {
      nextNode.useParamValue = previousNode.useParamValue;
    }

    if (!Array.isArray(previousNode.subNodes) || !Array.isArray(nextNode.subNodes)) {
      return;
    }

    const nextNodesByKey = new Map(
      nextNode.subNodes.map((subNode) => [getNodeTreeMatchKey(subNode), subNode])
    );

    previousNode.subNodes.forEach((previousSubNode) => {
      const matchingNextSubNode = nextNodesByKey.get(getNodeTreeMatchKey(previousSubNode));
      if (matchingNextSubNode) {
        copyTransientNodeState(previousSubNode, matchingNextSubNode);
      }
    });
  }, [getNodeTreeMatchKey]);

  const resolveNodeFromFreshItems = useCallback((sourceNode, itemsMap) => {
    if (!sourceNode || !itemsMap) {
      return null;
    }

    const featureScope = getLayersNodeFeatureScope(sourceNode);
    const featureNode = itemsMap[featureScope] || itemsMap[sourceNode.id] || null;

    if (!featureNode) {
      return null;
    }

    if (sourceNode === featureNode || sourceNode.id === featureNode.id || sourceNode.depth === 0) {
      return featureNode;
    }

    const sourceKey = getNodeTreeMatchKey(sourceNode);
    let matchingNode = null;

    forEachNodeInTree(featureNode, (candidateNode) => {
      if (!matchingNode && getNodeTreeMatchKey(candidateNode) === sourceKey) {
        matchingNode = candidateNode;
      }
    });

    return matchingNode || featureNode;
  }, [forEachNodeInTree, getLayersNodeFeatureScope, getNodeTreeMatchKey]);

  const rebindEditorNodeReferences = useCallback((itemsMap) => {
    if (!itemsMap) {
      return;
    }

    setSelectedFeatureNode((previousNode) => {
      if (previousNode) {
        return resolveNodeFromFreshItems(previousNode, itemsMap);
      }

      return selectedFeatureId ? (itemsMap[selectedFeatureId] || null) : null;
    });

    setActiveNode((previousNode) => {
      if (!previousNode) {
        return null;
      }

      previousNode.active = false;
      const reboundNode = resolveNodeFromFreshItems(previousNode, itemsMap);
      if (reboundNode) {
        reboundNode.active = true;
      }
      return reboundNode;
    });

    setActiveVisualisedPreviewNode((previousNode) => resolveNodeFromFreshItems(previousNode, itemsMap));
  }, [resolveNodeFromFreshItems, selectedFeatureId]);

  const buildLayersNodeSelectionToken = useCallback((node, fallbackFeatureId = null, placementContext = null) => {
    const featureId = String(getLayersNodeFeatureScope(node) || fallbackFeatureId || '').trim();

    if (!featureId) {
      return null;
    }

    const hierarchy = [];
    let current = node;

    while (current && current.depth > 0) {
      hierarchy.unshift({
        dataLink: getLayersNodeDataLink(current),
        dataIndex: getLayersNodeDataIndex(current),
        pathTail: getLayersNodePathTail(current?.path),
        name: String(current?.name || '').trim().toLowerCase(),
        depth: Number.isInteger(current?.depth) ? String(current.depth) : '',
      });
      current = current.parent || null;
    }

    return {
      featureId,
      hierarchy,
      placementStepId: String(placementContext?.selectedStepId || '').trim(),
      homeStepId: String(placementContext?.homeStepId || '').trim(),
      selectionSource: String(placementContext?.source || '').trim(),
    };
  }, [getLayersNodeDataIndex, getLayersNodeDataLink, getLayersNodeFeatureScope, getLayersNodePathTail]);

  const [selectedFeaturePlacementContext, setSelectedFeaturePlacementContext] = useState(null);
  const selectedFeaturePlacementContextRef = useRef(null);
  const pendingDroppedCanvasSelectionRef = useRef(null);

  const buildFeaturePlacementContext = useCallback((component, options = {}) => {
    const featureId = normalizeFeatureReferenceId(
      component?.campaignFeatureId || component?.featureId || component?.id,
    );

    if (!featureId) {
      return null;
    }

    const sortedSteps = [...(steps || [])].sort((a, b) => a.x - b.x);
    const inferredHomeStepId = sortedSteps.find((step) => (
      step.components || []
    ).some((candidate) => (
      candidate.id === component?.id
      && normalizeFeatureReferenceId(candidate?.campaignFeatureId || candidate?.featureId || candidate?.id) === featureId
    )))?.id || null;

    const homeStepId = component?.homeStepId || component?.parentStep || inferredHomeStepId;
    const clickedStepId = options.clickedStepId || options.selectedStepId || component?.parentStep || homeStepId || null;
    const selectedStepId = options.preferHomeStep ? homeStepId : clickedStepId;
    const homeStepIndex = sortedSteps.findIndex((step) => step.id === homeStepId);
    const selectedStepIndex = sortedSteps.findIndex((step) => step.id === selectedStepId);

    const getVisibleComponentsForStepIndex = (stepIndex) => {
      if (stepIndex < 0) {
        return [];
      }

      return sortedSteps.flatMap((sourceStep, sourceIndex) => (
        (sourceStep.components || []).flatMap((candidate) => {
          const stepLength = candidate.stepLength || 1;
          const endIndex = sourceIndex + stepLength - 1;
          if (stepIndex < sourceIndex || stepIndex > endIndex) {
            return [];
          }

          return [{
            componentId: candidate.id,
            featureId: normalizeFeatureReferenceId(candidate?.campaignFeatureId || candidate?.featureId || candidate?.id),
            label: candidate.label,
            sourceStepId: sourceStep.id,
            visibleStepId: sortedSteps[stepIndex]?.id || null,
            isExpandedCopy: stepIndex !== sourceIndex,
            stepLength,
            startDelay: Number(candidate.startDelay) || 0,
            command: candidate.command || null,
            autoStart: candidate.autoStart !== false,
          }];
        })
      ));
    };

    const componentsInSelectedStep = getVisibleComponentsForStepIndex(selectedStepIndex);

    return {
      source: options.source || 'unknown',
      featureId,
      componentId: component?.id || null,
      componentLabel: component?.label || component?.name || null,
      clickedStepId,
      selectedStepId,
      selectedStepIndex,
      selectedStepLabel: selectedStepIndex >= 0 ? sortedSteps[selectedStepIndex]?.label || null : null,
      homeStepId,
      homeStepIndex,
      homeStepLabel: homeStepIndex >= 0 ? sortedSteps[homeStepIndex]?.label || null : null,
      isExpandedCopy: Boolean(component?.isExpandedCopy),
      componentsInSelectedStep,
      otherComponentsInSelectedStep: componentsInSelectedStep.filter((candidate) => candidate.componentId !== component?.id),
      timestamp: Date.now(),
    };
  }, [normalizeFeatureReferenceId, steps]);

  const publishFeaturePlacementContext = useCallback((component, options = {}) => {
    const nextContext = buildFeaturePlacementContext(component, options);
    selectedFeaturePlacementContextRef.current = nextContext;
    setSelectedFeaturePlacementContext(nextContext);

    if (typeof window !== 'undefined') {
      window.__NODE_EDITOR_SELECTION_CONTEXT__ = nextContext;
    }

    if (nextContext) {

    }

    return nextContext;
  }, [buildFeaturePlacementContext]);

  useEffect(() => {
    selectedFeaturePlacementContextRef.current = selectedFeaturePlacementContext;

    if (typeof window !== 'undefined') {
      window.__NODE_EDITOR_SELECTION_CONTEXT__ = selectedFeaturePlacementContext;
    }
  }, [selectedFeaturePlacementContext]);

  useEffect(() => {

  }, [selectedFeatureId, selectedFeatureNode, activeNode, selectedLayersNodeState, selectedSidebarFieldDataLink, selectedFeaturePlacementContext]);

  useEffect(() => {
    if (!selectedFeatureId) {
      setSelectedLayersNodeState(null);
    }
  }, [selectedFeatureId]);
  
  const handleFlowBackgroundClick = useCallback(() => {
    setSelectedFeatureId(null);
    setSelectedFeatureNode(null);
    setSelectedItem(null);
    setHoveredStackingItem(null);
    setActiveNode((oldVal) => {
      if (oldVal) oldVal.active = false;
      return null;
    });
  }, [setSelectedFeatureId, setSelectedFeatureNode, setSelectedItem, setHoveredStackingItem, setActiveNode]);

  const [selectedFeature, setSelectedFeature] = useState(null);
  useEffect(() => {
    let selectedFeature = null;
    if (selectedFeatureNode) {
      selectedFeature = campaignData.features.find(feature => feature._id === selectedFeatureNode.id);
    }
    setSelectedFeature(selectedFeature);
  }, [selectedFeatureNode]);

  const [currentMode, setCurrentMode] = useState(MODES.FLOW_EDITOR);
  const previousModeRef = useRef(currentMode);
  const historyInitializedRef = useRef(false);
  const suppressHistorySyncRef = useRef(false);
  const lastHistorySignatureRef = useRef('');
  const [expandedFlowFeature, setExpandedFlowFeature] = useState(null);
  const [expandedFlowFeatureId, setExpandedFlowFeatureId] = useState(null);
  const expandedFlowResetViewRef = useRef(null);

  const buildEditorHistoryState = useCallback(() => ({
    campaignId,
    mode: currentMode,
    selectedFeatureId: normalizeFeatureReferenceId(selectedFeatureId),
    expandedFlowFeature,
    expandedFlowFeatureId: normalizeFeatureReferenceId(expandedFlowFeatureId),
    activeNodeId: typeof activeNode?.id === 'string' ? activeNode.id : null,
  }), [campaignId, currentMode, selectedFeatureId, expandedFlowFeature, expandedFlowFeatureId, activeNode, normalizeFeatureReferenceId]);

  useEffect(() => {
    const normalizedSelectedFeatureId = normalizeFeatureReferenceId(selectedFeatureId);
    const normalizedSelectedItemFeatureId = normalizeFeatureReferenceId(selectedItem?.featureId || selectedItem?.campaignFeatureId);

    if (!normalizedSelectedFeatureId) {
      if (selectedItem) {
        setSelectedItem(null);
      }
      return;
    }

    if (normalizedSelectedItemFeatureId === normalizedSelectedFeatureId) {
      return;
    }

    const placementContext = selectedFeaturePlacementContextRef.current;
    const matchingTimelineItem = [...(steps || [])]
      .sort((a, b) => a.x - b.x)
      .flatMap((step) => (step.components || []).map((component) => ({
        ...component,
        __homeStepId: step.id,
      })))
      .find((component) => {
        const componentFeatureId = normalizeFeatureReferenceId(component.featureId || component.campaignFeatureId);
        if (componentFeatureId !== normalizedSelectedFeatureId) {
          return false;
        }

        if (!placementContext || placementContext.featureId !== normalizedSelectedFeatureId) {
          return true;
        }

        if (placementContext.componentId && component.id !== placementContext.componentId) {
          return false;
        }

        if (placementContext.homeStepId && component.__homeStepId !== placementContext.homeStepId) {
          return false;
        }

        return true;
      });

    if (matchingTimelineItem) {
      setSelectedItem({
        ...matchingTimelineItem,
        homeStepId: placementContext?.homeStepId || matchingTimelineItem.__homeStepId,
        selectedStepId: placementContext?.selectedStepId || placementContext?.homeStepId || matchingTimelineItem.__homeStepId,
        selectionSource: placementContext?.source || 'selected-feature-sync',
      });
    }
  }, [selectedFeatureId, selectedItem, setSelectedItem, steps, normalizeFeatureReferenceId]);

  const [timelinePlayheadStep, setTimelinePlayheadStep] = useState(0);
  const [timelinePlayheadLabel, setTimelinePlayheadLabel] = useState('Start');
  const getTimelinePlayheadLabel = (step) => {
    if (selectedFeatureNode && selectedFeature) {
      const timelineStepsConfig = selectedFeatureNode?.config?.timeline?.steps;
      if (timelineStepsConfig && timelineStepsConfig.length > 0) {
        const stepLabel = timelineStepsConfig[step];
        if (stepLabel) {
          return stepLabel;
        }
      }
    }
    return 'Start';
  }
  useEffect(() => {
    setTimelinePlayheadLabel(getTimelinePlayheadLabel(timelinePlayheadStep));
  }, [timelinePlayheadStep, selectedFeatureNode, selectedFeature]);

  // Helper function to expand all parent nodes recursively
  // Helper function to expand and clone a node and all its parents
  const expandAndCloneNodeWithParents = (node, itemsMap) => {
    // Create a completely new map to ensure React sees the change
    const updatedItemsMap = {};
    
    // Collect IDs of nodes in the parent chain that should be expanded
    const nodesToExpand = new Set();
    let current = node;

    while (current) {
      if (current.id) {

        nodesToExpand.add(current.id);
      }
      current = current.parent;
    }

    
    // Clone all nodes in the map, setting expanded: true for nodes in the chain
    Object.keys(itemsMap).forEach(key => {
      const originalNode = itemsMap[key];
      const shouldExpand = nodesToExpand.has(key);
      const isTargetNode = key === node.id;
      
      updatedItemsMap[key] = {
        ...originalNode,
        expanded: shouldExpand ? true : originalNode.expanded,
        selected: isTargetNode ? true : originalNode.selected
      };
      
      if (shouldExpand) {

      }
    });
    
    return { updatedItemsMap, updatedNode: updatedItemsMap[node.id] || node };
  };

  // Helper function for mutating parent nodes (used in activateNode)
  const expandParentNodes = (node) => {
    if (node.parent) {
      node.parent.expanded = true;
      expandParentNodes(node.parent);
    }
  };

  // Handle timeline item selection and sync with feature node selection
  const selectFeatureFromPlacement = useCallback((item, selectionOptions = {}) => {
    const featureId = normalizeFeatureReferenceId(item?.featureId || item?.campaignFeatureId || item?.id);
    if (!featureId) {
      return { context: null, updatedNode: null };
    }

    const context = publishFeaturePlacementContext(item, selectionOptions);

    setActiveNode((oldVal) => {
      if (oldVal) {
        oldVal.active = false;
      }
      return null;
    });
    pendingSidebarFieldDataLinkRef.current = null;
    setSelectedSidebarFieldDataLink(null);
    setActiveTimelineAudioGroup(null);
    setActiveTimelineDisruptorGroup(null);
    setActiveVisualisedPreviewNode(null);

    if (context) {
      setSelectedItem({
        ...item,
        homeStepId: context.homeStepId || item?.homeStepId || item?.parentStep || null,
        selectedStepId: context.selectedStepId || context.homeStepId || item?.homeStepId || item?.parentStep || null,
        selectionSource: context.source,
      });

      if (context.selectedStepId) {
        setExpandedLayers((prev) => {
          const next = new Set(prev);
          next.add(context.selectedStepId);
          return next;
        });
      }

      setSelectedLayersNodeState({
        featureId,
        hierarchy: [],
        placementStepId: String(context.selectedStepId || '').trim(),
        homeStepId: String(context.homeStepId || '').trim(),
        selectionSource: context.source,
      });

      if (Number.isInteger(context.selectedStepIndex) && context.selectedStepIndex >= 0) {
        setTimelinePlayheadStep(context.selectedStepIndex);
      }
    }

    const itemsMap = leftListNewCustomisationItemsRef.current;
    const featureNode = itemsMap?.[featureId] || null;

    if (!featureNode) {
      setSelectedFeatureNode(null);
      setSelectedFeatureId(featureId);
      return { context, updatedNode: null };
    }

    const { updatedItemsMap, updatedNode } = expandAndCloneNodeWithParents(featureNode, itemsMap);
    setLeftListNewCustomisationItems(updatedItemsMap);
    setSelectedFeatureNode(updatedNode);
    setSelectedFeatureId(featureId);

    return { context, updatedNode };
  }, [expandAndCloneNodeWithParents, normalizeFeatureReferenceId, publishFeaturePlacementContext, setExpandedLayers, setTimelinePlayheadStep]);

  const handleFlowComponentSelect = useCallback((item, selectionOptions = {}) => {
    selectFeatureFromPlacement(item, {
      source: selectionOptions.source || 'flow-panel',
      clickedStepId: selectionOptions.clickedStepId || selectionOptions.stepId || null,
      preferHomeStep: false,
    });
  }, [selectFeatureFromPlacement]);

  const handleTimelineItemSelect = useCallback((item) => {
    const { context } = selectFeatureFromPlacement(item, {
      source: 'timeline',
      clickedStepId: item?.homeStepId || item?.parentStep || null,
      preferHomeStep: true,
    });


  }, [selectFeatureFromPlacement]);

  const [forceUpdate, setForceUpdate] = useState(0);
  const forceUpdateComponent = (resetActiveNodeTo) => {
    setForceUpdate((oldVal) => oldVal + 1);
    // We can reset the active node here which helps update our list when we delete nodes,
    // otherwise we can end up with a deleted node still being active
    if (resetActiveNodeTo !== undefined) {
      if (resetActiveNodeTo === null) {
        activateNode(selectedFeatureNode);
      } else {
        activateNode(resetActiveNodeTo);
      }
    }
  }
  const [forceUpdateModuleList, setForceUpdateModuleList] = useState(0);
  const forceUpdateModuleListFunc = () => {
    setForceUpdateModuleList((oldVal) => oldVal + 1);
  }

  const activeSidebarFeatureId = activeNode?.featureData?._id || activeNode?.featureData?.id || selectedFeatureId;
  const _activeSidebarCurrentVars = (activeSidebarFeatureId && customisationVarsRef.current?.current?.[activeSidebarFeatureId])
    || (activeSidebarFeatureId && customisationVars?.current?.[activeSidebarFeatureId]);
  const _activeSidebarDefaultVars = (activeSidebarFeatureId && customisationVarsRef.current?.default?.[activeSidebarFeatureId])
    || (activeSidebarFeatureId && customisationVars?.default?.[activeSidebarFeatureId]);
  const activeSidebarCustomisationVars = (_activeSidebarCurrentVars || _activeSidebarDefaultVars)
    ? { ...(_activeSidebarDefaultVars || {}), ...(_activeSidebarCurrentVars || {}) }
    : (activeNode?.customisationVars
      || customisationVarsRef.current?.current?.[selectedFeatureId]
      || customisationVars?.current?.[selectedFeatureId]
      || {});

  const [showLegacyEditor, setShowLegacyEditor] = useState(false);
  const [legacyEditorFeatureNode, setLegacyEditorFeatureNode] = useState(null);
  const openLegacyEditor = (featureNode) => {
    featureNode.active = true;
    setLegacyEditorFeatureNode(featureNode);
    setShowLegacyEditor(true);
  }
  const closeLegacyEditor = () => {
    if (legacyEditorFeatureNode) {
      legacyEditorFeatureNode.active = false;
    }
    setLegacyEditorFeatureNode(null);
    setShowLegacyEditor(false);
  }

  const generateLeftListNewCustomisationItems = (customisationVars = null, campaignData = null) => {

    if (customisationVars) {
      workingVisualEditorData.customisationVars = customisationVars;
    }
    if (campaignData) {
      workingVisualEditorData.campaignData = campaignData;
    }
  // useEffect(() => {
    // if (leftListNewCustomisationItems === null) {
      // Always create a new object so React detects the reference change and re-renders
      // consumers (LayersPanel). Previously reusing the existing reference caused React to
      // bail out of re-rendering even after new campaign features were added to the map.
      const previousListItems = leftListNewCustomisationItemsRef.current;
      const previouslyVisibleFeatureIds = new Set(
        Object.entries(previousListItems || {})
          .filter(([, item]) => isFeatureListItemExplicitlyVisible(item))
          .map(([featureId]) => featureId)
      );
      const newListItems = {};
      if (!campaignData || !Array.isArray(campaignData.features)) {
        console.warn('[NodeEditor][AudioDebug] generateLeftListNewCustomisationItems-missing-campaignData');
        return newListItems;
      }

      for (let i = 0; i < campaignData.features.length; i++) {
        // if (workspaceConfig[campaignData.features[i].varKey]) {
        const feature = campaignData.features[i];
        const featureConfig = workspaceConfig?.[feature.configKey || feature.varKey];

        if (featureConfig) {
          const isSelectedFeature = normalizedSelectedFeatureId === normalizeFeatureReferenceId(feature._id);
          const newItem = {
            id: feature._id,
            path: feature._id,
            name: feature.contentLabel,
            varKey: feature.configKey || feature.varKey,
            config: featureConfig,
            featureData: feature,
            customisationVars: customisationVars?.[feature._id] ? customisationVars[feature._id] : null,
            hasChildren: false,
            enabled: true,
            selected: isSelectedFeature,
            expanded: isSelectedFeature,
            subNodes: [],
            icon: "feature",
            depth: 0,
          }

          if (featureConfig?.customisation?.groups && featureConfig.customisation.groups.length > 0) {
            newItem.hasChildren = true;
            processGroupsToSubNodes(featureConfig.customisation.groups, newItem, newItem);
          }
          newItem.topNode = newItem;

          // newListItems.push(newItem);
          newListItems[feature._id] = newItem;

          // if (props.isToolPreview === true) {
            // If we find configs with _1, _2 etc, we need to add those as well.
            // These are duplicated configs for the same feature, but may have changes applied, so our workspace needs to deal with that.

          // }

          // If we are a koko dev, add a legacy link to the list
          if (authContext.userData.userLevel >= ACCOUNT_TYPES.koko.level) {
            const legacyItem = {
              id: feature._id + '-legacy-info',
              path: feature._id + '-legacy-info',
              name: <span className="locked-koko-dev-only">[KOKO: Legacy Editor]</span>,
              // openUrl: true,
              // urlToOpen: TOP_LEVEL_NAV_ROUTES.CUSTOMISE_FEATURE.replace(':campaignId', campaignData._id).replace(':featureId', feature._id),
              openLegacyEditor: true,
              hideFromTimeline: true,
              // navigate(TOP_LEVEL_NAV_ROUTES.CUSTOMISE_FEATURE.replace(':campaignId', props._campaignId).replace(':featureId', featureId));
              // urlToOpen: './' + feature._id,
              config: null,
              featureData: feature,
              customisationVars: null,
              hasChildren: false,
              enabled: true,
              selected: false,
              expanded: false,
              subNodes: [],
              icon: "settings",
              depth: 1,
            }
            newItem.subNodes.push(legacyItem);
          }
        } else {
          const isSelectedFeature = normalizedSelectedFeatureId === normalizeFeatureReferenceId(feature._id);
          const newItem = {
            id: feature._id,
            path: feature._id,
            name: feature.contentLabel + ' [Legacy Feature]',
            varKey: feature.varKey,
            config: null,
            featureData: feature,
            customisationVars: customisationVars?.[feature._id] ? customisationVars[feature._id] : null,
            hasChildren: true,
            enabled: true,
            selected: isSelectedFeature,
            expanded: isSelectedFeature,
            subNodes: [
              {
                id: feature._id + '-legacy-info',
                path: feature._id + '-legacy-info',
                name: 'Edit in Legacy Editor',
                // openUrl: true,
                // urlToOpen: TOP_LEVEL_NAV_ROUTES.CUSTOMISE_FEATURE.replace(':campaignId', campaignData._id).replace(':featureId', feature._id),
                openLegacyEditor: true,
                // navigate(TOP_LEVEL_NAV_ROUTES.CUSTOMISE_FEATURE.replace(':campaignId', props._campaignId).replace(':featureId', featureId));
                // urlToOpen: './' + feature._id,
                config: null,
                featureData: feature,
                customisationVars: null,
                hasChildren: false,
                enabled: true,
                selected: false,
                expanded: false,
                subNodes: [],
                icon: "settings",
                depth: 1,
              }
            ],
            icon: "feature",
            depth: 0,
            isLegacy: true,
          }
          if (feature?.feature?.varKey === 'quizPoll') {
            newItem.subNodes.push({
              id: feature._id + '-quizpoll-legacy-info',
              path: feature._id + '-quizpoll-legacy-info',
              name: 'Edit Legacy Quiz/Poll Data',
              openUrl: true,
              urlToOpen: TOP_LEVEL_NAV_ROUTES.QUIZZES,
              config: null,
              featureData: feature,
              customisationVars: null,
              hasChildren: false,
              enabled: true,
              selected: false,
              expanded: false,
              subNodes: [],
              icon: "settings",
              depth: 1,
            })
          }
          // newListItems.push(newItem);
          newListItems[feature._id] = newItem;
        }
        // }
      }
      if (previousListItems) {
        Object.keys(newListItems).forEach((featureId) => {
          copyTransientNodeState(previousListItems[featureId], newListItems[featureId]);

          if (previouslyVisibleFeatureIds.has(featureId)) {
            newListItems[featureId].visible = true;
            if (newListItems[featureId].topNode) {
              newListItems[featureId].topNode.visible = true;
            }
          }
        });
      }

      applyStepVisibilityToItems(newListItems, lastActivatedStepIdRef.current);

      setLeftListNewCustomisationItems(newListItems);
      rebindEditorNodeReferences(newListItems);
      return newListItems;
      //
    // }
  } // , []);

  // Safety net: regenerate the left-panel list whenever campaignData becomes available.
  // This fires if the async callback chain in fetchCampaignData fails to call the function.
  useEffect(() => {
    if (!campaignData || !Array.isArray(campaignData.features) || campaignData.features.length === 0) return;
    generateLeftListNewCustomisationItems(customisationVarsRef.current?.current ?? null, campaignData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData]);

  const clearActiveNode = (clearSidebarField = true) => {
    setActiveNode((oldVal) => {
      if (oldVal) {
        oldVal.active = false;
      }
      return null
    });
    if (clearSidebarField) {
      pendingSidebarFieldDataLinkRef.current = null;
      setSelectedSidebarFieldDataLink(null);
    }
  }

  const handleExpandSelectedFeatureFlow = useCallback(() => {
    if (!selectedFeature) return;

    const featureVarKey = selectedFeature.configKey || selectedFeature.varKey;
    const featureInstanceId = selectedFeature._id?.toString?.() || selectedFeature._id || null;


    setExpandedFlowFeature(featureVarKey);
    setExpandedFlowFeatureId(featureInstanceId);
    setTimelinePlayheadStep(0);
    setCurrentMode(MODES.EXPANDED_FLOW);
    clearActiveNode();
  }, [clearActiveNode, selectedFeature]);

  const canOpenSelectedFeatureFlow = useMemo(() => {
    if (!selectedFeature) {
      return false;
    }

    return canOpenExpandedFlowForComponent({
      varKey: selectedFeature.configKey || selectedFeature.varKey,
    });
  }, [canOpenExpandedFlowForComponent, selectedFeature]);

  const resolveOwningFeatureNode = (node) => {
    if (!node || typeof node !== 'object') return null;

    const visited = new Set();
    let current = node;
    const itemsMap = leftListNewCustomisationItemsRef.current;



    while (current && !visited.has(current)) {
      visited.add(current);

      const featureId = current?.featureData?._id || current?.featureData?.id || current?.id;

      if (featureId && itemsMap?.[featureId]) {

        return itemsMap[featureId];
      }

      if (current?.featureData && !current?.parent) {

        return current;
      }

      current = current?.topNode || current?.visualisedTopNode || current?.parent || null;
    }


    return null;
  };

  // The active node is the one we are going to display modules for in the right panel
  const activateNode = (node) => {

    if (typeof node === 'string') {
      node = leftListNewCustomisationItemsRef.current?.[node] || null;
      if (!node) {
        console.warn("[activateNode] could not resolve node from string id:", node);
        return;
      }

    }

    setFocusEditingOnNode(null);

    if (node.openUrl) {
      navigate(node.urlToOpen);
      return;
    }
    
    if (node.openLegacyEditor) {
      openLegacyEditor(node);
      clearActiveNode();
      setActiveTimelineAudioGroup(null);
      setActiveTimelineDisruptorGroup(null);
      return;
    }
    
    if (showLegacyEditor) {
      closeLegacyEditor();
    }

    const owningFeatureNode = resolveOwningFeatureNode(node);

    if (owningFeatureNode) {
      // Expand and clone the node with all its parents
      const itemsMap = leftListNewCustomisationItemsRef.current;
      if (itemsMap && itemsMap[owningFeatureNode.id]) {
        const { updatedItemsMap, updatedNode } = expandAndCloneNodeWithParents(owningFeatureNode, itemsMap);
        
        setLeftListNewCustomisationItems(updatedItemsMap);
        setSelectedFeatureNode(updatedNode);
        setSelectedFeatureId(updatedNode.id);
        

      }
    }
    
    clearActiveNode(false);
    setActiveTimelineAudioGroup(null);
    setActiveTimelineDisruptorGroup(null);
    node.active = true;
    setActiveNode(node);
    setSelectedLayersNodeState(buildLayersNodeSelectionToken(node, owningFeatureNode?.id));
    setSelectedSidebarFieldDataLink(pendingSidebarFieldDataLinkRef.current || null);
    pendingSidebarFieldDataLinkRef.current = null;

    expandParentNodes(node);

    if ((node.isVisualised && !node.visualisedTopNode) || node.visualisedTopNode || node.config?.visualisationConfig?.restrictSteps) {
      const topVisualisedNode = node.visualisedTopNode || node;
      if ((node.isVisualised && !node.visualisedTopNode) || node.visualisedTopNode) {
        if (topVisualisedNode !== activeVisualisedPreviewNode_ref.current) {
          setActiveVisualisedPreviewNode(topVisualisedNode);

        } else {
          console.warn("activateNode - activeVisualisedPreviewNode already set to:", topVisualisedNode);
        }
      } else {
        console.warn("activateNode - clearing activeVisualisedPreviewNode");
        setActiveVisualisedPreviewNode(null);
      }
      if (/* node.isVisualised && */ node.config?.visualisationConfig?.restrictSteps) {
        // If this node is restricted to certain steps, we need to use it's step settings to move the playhead rather than the topVisualisedNode's step settings
        if (typeof node?.config?.visualisationConfig?.startStep === 'number' && typeof node?.config?.visualisationConfig?.endStep === 'number') {
          setTimelinePlayheadStep(
            (currentVal) => {
              let newVal = currentVal;
              if (newVal < node.config.visualisationConfig.startStep || newVal > node.config.visualisationConfig.endStep) {
                newVal = node.config.visualisationConfig.startStep;
              }
              return newVal;
            }
          )
          // setTimelinePlayheadStep(node.config.visualisationConfig.startStep);
        }
      } else
        if (typeof topVisualisedNode?.config?.visualisationConfig?.startStep === 'number' && typeof topVisualisedNode?.config?.visualisationConfig?.endStep === 'number') {
          setTimelinePlayheadStep(
            (currentVal) => {
              let newVal = currentVal;
              if (newVal < topVisualisedNode.config.visualisationConfig.startStep || newVal > topVisualisedNode.config.visualisationConfig.endStep) {
                newVal = topVisualisedNode.config.visualisationConfig.startStep;
              }
              return newVal;
            }
          )
          // setTimelinePlayheadStep(node.config.visualisationConfig.startStep);
        }
    } else {
      setActiveVisualisedPreviewNode(null);
    }
    /* if (typeof node?.config?.visualisationConfig?.startStep === 'number') {
        setTimelinePlayheadStep(node.config.visualisationConfig.startStep);
    } */
  }

  const restoreEditorViewState = useCallback((viewState) => {
    if (!viewState || viewState.campaignId !== campaignId) {
      return false;
    }

    const itemsMap = leftListNewCustomisationItemsRef.current || {};
    const normalizedFeatureId = normalizeFeatureReferenceId(viewState.selectedFeatureId);
    const featureNode = normalizedFeatureId ? itemsMap[normalizedFeatureId] || null : null;

    suppressHistorySyncRef.current = true;

    if (viewState.mode === MODES.FLOW_EDITOR) {
      handleFlowBackgroundClick();
      setExpandedFlowFeature(null);
      setExpandedFlowFeatureId(null);
      setCurrentMode(MODES.FLOW_EDITOR);
      return true;
    }

    if (normalizedFeatureId) {
      setSelectedFeatureId(normalizedFeatureId);
      setSelectedFeatureNode(featureNode);
    } else {
      setSelectedFeatureId(null);
      setSelectedFeatureNode(null);
    }

    if (viewState.mode === MODES.EXPANDED_FLOW) {
      setExpandedFlowFeature(viewState.expandedFlowFeature || featureNode?.configKey || featureNode?.varKey || null);
      setExpandedFlowFeatureId(normalizeFeatureReferenceId(viewState.expandedFlowFeatureId) || normalizedFeatureId || null);
      clearActiveNode();
      setCurrentMode(MODES.EXPANDED_FLOW);
      return true;
    }

    if (viewState.mode === MODES.CANVAS_EDITOR) {
      setCurrentMode(MODES.CANVAS_EDITOR);
      if (viewState.activeNodeId && itemsMap[viewState.activeNodeId]) {
        activateNode(viewState.activeNodeId);
      } else {
        clearActiveNode();
      }
      return true;
    }

    suppressHistorySyncRef.current = false;
    return false;
  }, [activateNode, campaignId, clearActiveNode, handleFlowBackgroundClick, normalizeFeatureReferenceId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const currentViewState = buildEditorHistoryState();
    const existingHistoryState = window.history.state || {};
    const seededState = existingHistoryState[NODE_EDITOR_HISTORY_KEY] || currentViewState;

    window.history.replaceState({
      ...existingHistoryState,
      [NODE_EDITOR_HISTORY_KEY]: seededState,
    }, '');

    historyInitializedRef.current = true;
    lastHistorySignatureRef.current = JSON.stringify(seededState);

    const handlePopState = (event) => {
      const nextViewState = event.state?.[NODE_EDITOR_HISTORY_KEY];
      if (!nextViewState || nextViewState.campaignId !== campaignId) {
        return;
      }

      restoreEditorViewState(nextViewState);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [buildEditorHistoryState, campaignId, restoreEditorViewState]);

  useEffect(() => {
    if (typeof window === 'undefined' || !historyInitializedRef.current) {
      return;
    }

    const nextViewState = buildEditorHistoryState();
    const nextSignature = JSON.stringify(nextViewState);

    if (suppressHistorySyncRef.current) {
      suppressHistorySyncRef.current = false;
      lastHistorySignatureRef.current = nextSignature;
      return;
    }

    if (nextSignature === lastHistorySignatureRef.current) {
      return;
    }

    const existingHistoryState = window.history.state || {};

    if (!existingHistoryState[NODE_EDITOR_HISTORY_KEY]) {
      window.history.replaceState({
        ...existingHistoryState,
        [NODE_EDITOR_HISTORY_KEY]: nextViewState,
      }, '');
      lastHistorySignatureRef.current = nextSignature;
      return;
    }

    lastHistorySignatureRef.current = nextSignature;
    window.history.pushState({
      ...existingHistoryState,
      [NODE_EDITOR_HISTORY_KEY]: nextViewState,
    }, '');
  }, [buildEditorHistoryState]);

  const handleCampaignLibraryFeatureSelect = useCallback((libraryItem, selectionOptions = {}) => {
    // console.log("[handleCampaignLibraryFeatureSelect] libraryItem:", libraryItem, "selectionOptions:", selectionOptions);
    // const foundNode = findNodeInListContainingPropValue(libraryItem?.step?.customisationConfig?.meta?.disruptorSelectDataLink, libraryItem?.featureInfo?.featureInstanceId);
    // console.log("[handleCampaignLibraryFeatureSelect] foundNode:", foundNode);
    // Carl: I was working on changing the way disruptors were selected so you could edit their trigger settings,
    // but it turns out you can already do that by clicking the outer card on the feassture flow.
    // I've left my changes in because it doesn't affect anything, just passes more data back to this function and as= new function
    // called findNodeInListContainingPropValue has been added above.

    const { context, updatedNode } = selectFeatureFromPlacement(libraryItem, {
      source: selectionOptions.source || 'library',
      clickedStepId: selectionOptions.clickedStepId || null,
      preferHomeStep: Boolean(selectionOptions.preferHomeStep),
    });

    // Dropping from the library should add the feature without navigating away from the flow.
    const isLibraryAddSelection = (selectionOptions.source || 'library') === 'library-drop';
    if (isLibraryAddSelection) {
      setCurrentMode(MODES.FLOW_EDITOR);
      return;
    }

    if (updatedNode?.isLegacy) {
      openLegacyEditor(updatedNode);
      setCurrentMode(MODES.FLOW_EDITOR);
      return;
    }

    if (updatedNode) {
      const visualisedGroupNode = updatedNode?.subNodes?.find(
        (subNode) => (subNode.isVisualised || subNode?.config?.groupSettings?.visualise)
          && subNode.config?.visualisationConfig?.startStep === 0,
      );

      if (visualisedGroupNode) {
        visualisedGroupNode.component = libraryItem;
        activateNode(visualisedGroupNode);
      } else {
        clearActiveNode();
      }
    } else {
      clearActiveNode();
    }

    if (!selectionOptions.preserveTimelinePlayhead) {
      if (Number.isInteger(context?.selectedStepIndex) && context.selectedStepIndex >= 0) {
        setTimelinePlayheadStep(context.selectedStepIndex);
      } else {
        setTimelinePlayheadStep(0);
      }
    }

    setCurrentMode(MODES.CANVAS_EDITOR);
  }, [
    activateNode,
    openLegacyEditor,
    clearActiveNode,
    selectFeatureFromPlacement,
    setTimelinePlayheadStep,
  ]);

  const attachLibraryItemToVisualisedGroupNode = useCallback((visualisedGroupNode) => {
    if (!visualisedGroupNode) {
      return null;
    }

    const nodeFeatureId = normalizeFeatureReferenceId(
      visualisedGroupNode?.featureData?._id
      || visualisedGroupNode?.featureData?.id
      || visualisedGroupNode?.topNode?.id
      || visualisedGroupNode?.visualisedTopNode?.id
      || null
    );

    if (!nodeFeatureId) {
      return null;
    }

    const allLibraryItems = Object.values(libraryItems || {}).flat();
    const libraryItem = allLibraryItems.find((item) => {
      const itemFeatureId = normalizeFeatureReferenceId(
        item?.campaignFeatureId || item?.id || item?._id
      );
      return itemFeatureId === nodeFeatureId;
    });

    if (!libraryItem) {
      return null;
    }

    visualisedGroupNode.component = libraryItem;
    return libraryItem;
  }, [libraryItems, normalizeFeatureReferenceId]);

  useEffect(() => {
    const pendingSelection = pendingDroppedCanvasSelectionRef.current;
    if (!pendingSelection?.featureId) {
      return;
    }

    const hasFeatureCustomisationVars = Boolean(
      customisationVarsRef.current?.current?.[pendingSelection.featureId]
      || customisationVarsRef.current?.default?.[pendingSelection.featureId]
      || customisationVars?.current?.[pendingSelection.featureId]
      || customisationVars?.default?.[pendingSelection.featureId]
    );

    if (!hasFeatureCustomisationVars) {
      return;
    }

    if (!leftListNewCustomisationItemsRef.current?.[pendingSelection.featureId]) {
      return;
    }

    const matchedStep = steps.find((step) => step.id === pendingSelection.homeStepId)
      || steps.find((step) => step.id === pendingSelection.selectedStepId);

    if (!matchedStep) {
      return;
    }

    const matchedComponent = (matchedStep.components || []).find((component) => {
      if (pendingSelection.componentId && component.id === pendingSelection.componentId) {
        return true;
      }

      const componentFeatureId = normalizeFeatureReferenceId(component.featureId || component.campaignFeatureId || component.id);
      return componentFeatureId === pendingSelection.featureId;
    });

    if (!matchedComponent) {
      return;
    }

    pendingDroppedCanvasSelectionRef.current = null;
    handleCampaignLibraryFeatureSelect({
      ...matchedComponent,
      homeStepId: pendingSelection.homeStepId || matchedStep.id,
      parentStep: pendingSelection.selectedStepId || pendingSelection.homeStepId || matchedStep.id,
    }, {
      source: pendingSelection.source || 'library-drop',
      clickedStepId: pendingSelection.selectedStepId || pendingSelection.homeStepId || matchedStep.id,
      preferHomeStep: false,
      preserveTimelinePlayhead: true,
    });

    syncStepComponentVisibility(pendingSelection.selectedStepId || pendingSelection.homeStepId || matchedStep.id);
  }, [customisationVars, handleCampaignLibraryFeatureSelect, leftListNewCustomisationItems, normalizeFeatureReferenceId, steps, syncStepComponentVisibility]);

  useEffect(() => {
    const pendingMediaSeed = pendingDroppedMediaSeedRef.current;
    if (!pendingMediaSeed?.featureId || !pendingMediaSeed?.mediaItem) {
      return;
    }

    const featureId = pendingMediaSeed.featureId;
    const hasFeatureCustomisationVars = Boolean(
      customisationVarsRef.current?.current?.[featureId]
      || customisationVarsRef.current?.default?.[featureId]
      || customisationVars?.current?.[featureId]
      || customisationVars?.default?.[featureId]
    );

    if (!hasFeatureCustomisationVars) {
      return;
    }

    const seeded = seedDroppedMediaFeatureAsset(featureId, pendingMediaSeed.mediaItem);
    if (!seeded) {
      return;
    }

    pendingDroppedMediaSeedRef.current = null;
  }, [customisationVars, campaignData, campaignFeatures, seedDroppedMediaFeatureAsset]);

  const activateTimelineAudioGroup = (audioGroupData) => {

    clearActiveNode();
    setActiveVisualisedPreviewNode(null);
    setActiveTimelineDisruptorGroup(null);
    setActiveTimelineAudioGroup(audioGroupData);
    forceUpdateModuleListFunc();

    if ((typeof audioGroupData?.audioData?.startStep === 'number' || typeof audioGroupData?.audioData?.['trigger-step'] === 'number') && (typeof audioGroupData?.audioData?.endStep === 'number' || typeof audioGroupData?.audioData?.['end-step'] === 'number')) {
      setTimelinePlayheadStep(
        (currentVal) => {
          let newVal = currentVal;
          if (newVal < (audioGroupData?.audioData?.startStep ?? audioGroupData?.audioData?.['trigger-step']) || newVal > (audioGroupData?.audioData?.endStep ?? audioGroupData?.audioData?.['end-step'])) {
            newVal = audioGroupData?.audioData?.startStep ?? audioGroupData?.audioData?.['trigger-step'];
          }
          return newVal;
        }
      )
      // setTimelinePlayheadStep(node.config.visualisationConfig.startStep);
    }
  }

  const activateTimelineDisruptorGroup = (disruptorGroupData) => {

    clearActiveNode();
    setActiveVisualisedPreviewNode(null);
    setActiveTimelineAudioGroup(null);
    setActiveTimelineDisruptorGroup(disruptorGroupData);
    forceUpdateModuleListFunc();

    if (typeof disruptorGroupData?.disruptorData?.startStep === 'number' && typeof disruptorGroupData?.disruptorData?.endStep === 'number') {
      setTimelinePlayheadStep(
        (currentVal) => {
          let newVal = currentVal;
          if (newVal < disruptorGroupData?.disruptorData?.startStep || newVal > disruptorGroupData?.disruptorData?.endStep) {
            newVal = disruptorGroupData?.disruptorData?.startStep;
          }
          return newVal;
        }
      )
    }
  }

  const [showFontSelector, setShowFontSelector] = useState(false);
  const fontSelectedFunc_ref = useRef(null);
  const currentFont_ref = useRef(null);
  const openFontSelector = (currentFont, selectedFunc) => {
    currentFont_ref.current = currentFont;
    fontSelectedFunc_ref.current = selectedFunc;
    setShowFontSelector(true);
  }
  const closeFontSelector = () => {
    setShowFontSelector(false);
  }

  const [showCustomFontSelector, setShowCustomFontSelector] = useState(false);
  const openCustomFontSelector = () => {
    setShowCustomFontSelector(true);
  }
  const closeCustomFontSelector = () => {
    setShowCustomFontSelector(false);
  }

  useEffect(() => {
    initGoogleFonts();
  }, []);

  // Temporary image upload popup
  const [showTemporaryImageUploadPopup, setShowTemporaryImageUploadPopup] = useState(false);
  const [imageFileData, setImageFileData] = useState(null);
  const [imagePopupLabel, setImagePopupLabel] = useState('');
  const [imagePopupLabelNote, setImagePopupLabelNote] = useState('');
  const openTemporaryImageUploadPopup = (fileData, label, labelNote) => {
    setImageFileData(fileData);
    setImagePopupLabel(label);
    setImagePopupLabelNote(labelNote);
    setShowTemporaryImageUploadPopup(true);
  }
  const closeTemporaryImageUploadPopup = () => {
    setShowTemporaryImageUploadPopup(false);
    setImageFileData(null);
    setImagePopupLabel('');
    setImagePopupLabelNote('');
  }

  // Temporary audio upload popup
  const [showTemporaryAudioUploadPopup, setShowTemporaryAudioUploadPopup] = useState(false);
  const [audioFileData, setAudioFileData] = useState(null);
  const [audioPopupLabel, setAudioPopupLabel] = useState('');
  const openTemporaryAudioUploadPopup = (fileData, label) => {
    setAudioFileData(fileData);
    setAudioPopupLabel(label);
    setShowTemporaryAudioUploadPopup(true);
  }
  const closeTemporaryAudioUploadPopup = () => {
    setShowTemporaryAudioUploadPopup(false);
    setAudioFileData(null);
    setAudioPopupLabel('');
  }

  // Classic Customisation Subset Popup
  const [showClassicCustomisationSubsetPopup, setShowClassicCustomisationSubsetPopup] = useState(false);
  const [classCustomisationSubsetRestriction, setClassCustomisationSubsetRestriction] = useState(null);
  const [classicCustomisationSubsetTitle, setClassicCustomisationSubsetTitle] = useState('Edit Data');
  const [classicCustomisationSubsetCompletedCallback, setClassicCustomisationSubsetCompletedCallback] = useState(null);
  const [classicCustomisationSubsetItemData, setClassicCustomisationSubsetItemData] = useState(null);
  const openClassicCustomisationSubsetPopup = (path, dataIndex = null, title = 'Edit Data', completedCallback = null) => {

    const pathSplit = path.split('.');
    const restrictTab = pathSplit?.[0] ?? null;
    const restrictGroup = pathSplit?.[1] ?? null;
    const restrictFamily = pathSplit?.[2] ?? null;
    const restrictVar = pathSplit?.[3] ?? null;
    const restrictItem = typeof dataIndex === 'number' && dataIndex >= 0 ? getGroupData(activeNode?.customisationVars || customisationVars?.current?.[selectedFeatureId] || {}, path, dataIndex, true) : null;
    setClassicCustomisationSubsetItemData(restrictItem);

    setClassCustomisationSubsetRestriction({
      tab: restrictTab,
      group: restrictGroup,
      family: restrictFamily,
      var: restrictVar,
      item: null,
      itemIndex: dataIndex,
    });
    setClassicCustomisationSubsetTitle(title);
    setClassicCustomisationSubsetCompletedCallback(() => completedCallback);
    setShowClassicCustomisationSubsetPopup(true);
  }

  const {saveFeatureCustomisation, forceImmediateCustomisationSave, isSaving, saveError, lastSavedFeatureId, hasUnsavedChanges} = useFeatureCustomisationSaver(campaignData._id, campaignData, customisationVars, toast, 1000, false, {
    onSaveSuccess: handleLocalCustomisationSaveSuccess,
  });
  customisationSaverHook.saveFeatureCustomisation = saveFeatureCustomisation;
  customisationSaverHook.forceImmediateCustomisationSave = forceImmediateCustomisationSave;
  // Trigger and immediate save on unmount/unload
  useEffect(() => {

    const onUnloadForcedSave = async (e) => {
      if (hasUnsavedChanges()) {

        // window.confirm('Unsaved changes will be lost. Are you sure you want to leave?');
        await forceImmediateCustomisationSave(customisationSaverHook.campaignData, customisationSaverHook.customisationVars);
        e.returnValue = true;
      }
    };
    window.addEventListener('beforeunload', 
      onUnloadForcedSave
    );
    return async () => {
      window.removeEventListener('beforeunload', onUnloadForcedSave);
      await forceImmediateCustomisationSave(customisationSaverHook.campaignData, customisationSaverHook.customisationVars);
    }
  }, []);

  const {undoStack, redoStack, undoLastAction, redoLastAction, clearUndoRedoStacks, recordAction, recordCustomisationValueChange, recordCustomisationValueDeleted, recordCustomisationValueAdded, recordCustomisationArrayValuesReplaced} = useUndoSystem(forceUpdateComponent);
  undoRedoHook.recordCustomisationValueChange = recordCustomisationValueChange;
  undoRedoHook.recordCustomisationValueDeleted = recordCustomisationValueDeleted;
  undoRedoHook.recordCustomisationValueAdded = recordCustomisationValueAdded;
  undoRedoHook.recordCustomisationArrayValuesReplaced = recordCustomisationArrayValuesReplaced;

  useEffect(() => {
    workingVisualEditorData.customisationVars = customisationVars.current;
    customisationSaverHook.customisationVars = customisationVars;
  }, [customisationVars]);
  useEffect(() => {
    workingVisualEditorData.campaignData = campaignData;
    campaignDataRef.current = campaignData;
    customisationSaverHook.campaignData = campaignData;
  }, [campaignData]);
  useEffect(() => {
    workingVisualEditorData.activeNode = activeNode;
  }, [activeNode]);
  /* END - New Visual Editor Customisation Workspace Code */


  /* FEATURE PREVIEW INTEGRATION STUFF
    ================================================== 
    BORROWED FROM CustomiseFeature.jsx, MAY BE CLEANED UP LATER
    ==================================================  
  */
  const [featureVars, setFeatureVars] = useState({});
  const featureVarsRef = useRef(null);
  const getFeatureVars = useCallback(async () => {
    if (!campaignId) {
      return false;
    }

    try {
      const response = await axios.get(getCampaignFeatureVarsRoute, {
        params: { campaignID: campaignId },
        withCredentials: true
      });

      if (response.data.streamVars) {
        featureVarsRef.current = response.data.streamVars;
        setFeatureVars(featureVarsRef.current);
        return true;
      }

      return false;
    } catch (error) {
      toast.error('Error - ' + error);
      return false;
    }
  }, [campaignId]);
  const updateFeatureVars = (updatedVars) => {
      axios
      .post(updateCampaignFeatureVarsRoute, {
          campaignID: campaignId, 
          updatedVars: updatedVars,
          withCredentials:true
      })
      .then(function (response) {





      })
      .catch(function (error) {
          toast.error('Error - ' + error);
      });
  }
  useEffect(() => {
    getFeatureVars();
  }, [getFeatureVars]);

  const ensureFeatureVarsForFeature = useCallback(async (featureId) => {
    const normalizedFeatureId = featureId ? String(featureId) : null;
    if (!normalizedFeatureId) {
      return false;
    }

    if (featureVarsRef.current?.current?.[normalizedFeatureId]) {
      return true;
    }

    const fetched = await getFeatureVars();
    if (!fetched) {
      return false;
    }

    return !!featureVarsRef.current?.current?.[normalizedFeatureId];
  }, [getFeatureVars]);

  const [previewMode, setPreviewMode] = useState(false);
  const [previewPostFix, setPreviewPostFix] = useState('');
  const [previewChains, setPreviewChains] = useState(false);
  const [autoStartPreview, setAutoStartPreview] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(null);
  const currentFeatureRef = useRef(null);
  const [previewUpdate, setPreviewUpdate] = useState(0);
    const previewThisCustomisation = async () => {
      const featureData = findFeatureData(campaignData, selectedFeatureId);
        const opened = await editFeatureSettings(featureData, "(Feature Preview)");
        if (!opened) {
          return;
        }
      setAutoStartPreview(true);
      setPreviewChains(false);
  }
  const previewFlow = () => {
        const hasPreviewableFlow = Array.isArray(steps)
          && steps.some((step) => Array.isArray(step?.components) && step.components.length > 0);

        if (!hasPreviewableFlow) {
          return;
        }

        saveTimelineAsChain(activeFlowId, steps, { showSuccessToast: false }).finally(async () => {
        const featureData = findFeatureData(campaignData, selectedFeatureId) || getFirstFeature(campaignData);
          const opened = await editFeatureSettings(featureData, "(Flow Preview)");
          if (!opened) {
            return;
          }
        setAutoStartPreview(false);
        setPreviewChains(true);
      });
  }
    const editFeatureSettings = async (f, postFix = "") => {
      if (!f?._id) {
        toast.warning('Something went wrong opening the preview. Please try again.');
        return false;
      }

      const hasFeatureVars = await ensureFeatureVarsForFeature(f._id);
      if (!hasFeatureVars) {
        toast.warning('Something went wrong opening the preview. Please try again.');
        return false;
      }

      currentFeatureRef.current = f;
      setPreviewPostFix(postFix);
      setCurrentFeature(f);
      setPreviewMode(true);
      return true;
  }

  const getFirstFeature = (campaignData) => {
      if (campaignData.features && campaignData.features.length > 0) {
          return campaignData.features[0];
      }
      return null;
  }

  const previewUpdateTimeoutId = useRef(null);
  const refreshPreview = () => {
      if (previewMode) {
          clearTimeout((previewUpdateTimeoutId.current));
          previewUpdateTimeoutId.current = setTimeout(
              () => setPreviewUpdate((currentVal) => currentVal + 1),
              500
          );
      }
  }

  const reportFeatureVarsChanged = (onlyUpdateDisruptor = false, frameId = null) => {
      if (!onlyUpdateDisruptor) {
          updateFeatureVars(featureVarsRef.current.current);
      }

      postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.SETTINGS_VARS_RECEIVED, featureVarsRef.current.current[(frameId === 'previewFrame' || !frameId) ? currentFeatureRef.current?._id : frameId], frameId);
  }

  const reportCustomisationVarsChanged = (updatedVars, featureID) => {

      postMessageToPreviewFrame(
        POST_MESSAGE_OUT_TYPES.CUSTOMISATION_VARS_RECEIVED,
        getPreviewCustomisationVars(currentFeatureRef.current, featureID, campaignId, updatedVars.current[featureID])
      );
        
    }

  const [showingFunctionUnavilablePopup, setShowingFunctionUnavilablePopup] = useState(false);
  const openFunctionUnavilablePopup = () => {
      setShowingFunctionUnavilablePopup(true);
  }
  const closeFunctionUnavilablePopup = () => {
      setShowingFunctionUnavilablePopup(false);
  }

  const handleFeaturePreviewOpenError = useCallback(() => {
    toast.warning('Something went wrong opening the preview. Please try again.');
  }, []);

    // Special functions that can be triggered by buttons in the feature controls in place of the normal feature commands...
  const BUTTON_FUNCTIONS = {
      reportFeatureVarsChanged: (featureId, param) => reportFeatureVarsChanged(),
      startLivePoll: (featureId, param) => {
          reportFeatureVarsChanged();
          setTimeout(() => {
              sendFeatureCommand(null, 'START_POLL', featureId, param);
              // postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: 'START_POLL', extraData: {username:'CampaignStudio', hashtag:'Manual Press', commandParam: param} })
          }, 100);
      },
      createNewLivePoll: (featureId, param) => {
          // openRestrictedCustomisationPopup(featureId);
          openFunctionUnavilablePopup();
      },
      editRandomizerOptions: (featureId, param) => {
          openFunctionUnavilablePopup();
      },
      editLiveTextPresets: (featureId, param) => {
          // openRestrictedCustomisationPopup(featureId);
          openFunctionUnavilablePopup();
      },
      saveLiveTextPreset: (featureId, param) => {
          // openRestrictedCustomisationPopup(featureId);
          openFunctionUnavilablePopup();
      },
      saveResponseLiveTextPreset: (featureId, param) => {
          // openRestrictedCustomisationPopup(featureId);
          openFunctionUnavilablePopup();
      },
      selectLiveTextPreset: (featureId, param) => {
          const _featureVars = featureVarsRef?.current?.current?.[featureId] ?? featureVarsRef?.current?.[featureId];
          const _customisationVars = customisationVars?.current?.current?.[featureId] ?? customisationVars?.current?.[featureId];


          const presetTextIndex = Number(_featureVars.liveTextControls.presetText);
          const presetTextData = _customisationVars.setup.presetText.presets[presetTextIndex];
          if (presetTextData) {
              _featureVars.liveTextControls.presetName = presetTextData.presetName;
              _featureVars.liveTextControls.headerText = presetTextData.headerText;
              _featureVars.liveTextControls.mainContent = presetTextData.mainContent;
              featureVarsRef.current = {current: {...featureVarsRef.current.current, [featureId]: _featureVars}};
              setFeatureVars(featureVarsRef.current);
              reportFeatureVarsChanged();
          }
      },
      selectResponseLiveTextPreset: (featureId, param) => {
          const _featureVars = featureVarsRef.current.current[featureId];
          const _customisationVars = customisationVars.current.current[featureId];


          const presetTextIndex = Number(_featureVars.responseTextControls.presetText);
          const presetTextData = _customisationVars.setup.presetText.presets[presetTextIndex];
          if (presetTextData) {
              _featureVars.responseTextControls.presetName = presetTextData.presetName;
              _featureVars.responseTextControls.headerText = presetTextData.headerText;
              _featureVars.responseTextControls.mainContent = presetTextData.mainContent;
              featureVarsRef.current = {current: {...featureVarsRef.current.current, [featureId]: _featureVars}};
              setFeatureVars(featureVarsRef.current);
              reportFeatureVarsChanged();
          }
      },
      previewAudio: (featureId, param, frameId) => {
          //
          // Find the srcUrl in the param object. This will be inside another property, which could be called anything.
          let srcUrl = null;
          for (const key in param) {
              if (param[key].srcUrl) {
                  srcUrl = param[key].srcUrl;
                  break;
              }
          }
          if (!srcUrl) {

              return;
          }
          LIVE_AUDIO_PREVIEW.playStopAudio(srcUrl);
      },
  }
  const sendFeatureCommand = (e, commandString, featureId, param = null, frameId = null) => {
      if (e) {
          e.preventDefault();
      }
      if (BUTTON_FUNCTIONS[commandString]) {

          BUTTON_FUNCTIONS[commandString](featureId, param);
      } else {
          postMessageToPreviewFrame(POST_MESSAGE_OUT_TYPES.COMMAND_RECEIVED, { command: commandString, extraData: {username:'CampaignStudio', hashtag:'Manual Press', commandParam: param} }, frameId);
      }
  }
  /* END - Feature Preview Integration Stuff */
  useEffect(() => {
    currentModeRef.current = currentMode;
  }, [currentMode]);

  useEffect(() => {
    setVisibleTriggeredDisruptorId(null);
    setVisibleTriggeredDisruptorParamValue(null);
  }, [activeTimelineDisruptorGroup, activeNode, selectedFeatureNode, currentMode]);
  
  // Log mode changes for debugging
  useEffect(() => {

  }, [currentMode, selectedFeatureId, selectedFeatureNode]);

  useEffect(() => {
    const previousMode = previousModeRef.current;

    if (currentMode === MODES.CANVAS_EDITOR && previousMode !== MODES.CANVAS_EDITOR) {
      setTimelinePlayheadStep(0);
    }

    previousModeRef.current = currentMode;
  }, [currentMode]);

  useEffect(() => {
    if (currentMode !== MODES.EXPANDED_FLOW || !selectedFeature) {
      return;
    }

    const nextExpandedFlowFeature = selectedFeature.configKey || selectedFeature.varKey || null;
    const nextExpandedFlowFeatureId = selectedFeature._id?.toString?.() || selectedFeature._id || null;

    if (!nextExpandedFlowFeature) {
      return;
    }

    const hasSameFeature = expandedFlowFeature === nextExpandedFlowFeature;
    const hasSameFeatureId = normalizeFeatureReferenceId(expandedFlowFeatureId) === normalizeFeatureReferenceId(nextExpandedFlowFeatureId);

    if (hasSameFeature && hasSameFeatureId) {
      return;
    }

    setExpandedFlowFeature(nextExpandedFlowFeature);
    setExpandedFlowFeatureId(nextExpandedFlowFeatureId);
    setTimelinePlayheadStep(0);
  }, [currentMode, selectedFeature, expandedFlowFeature, expandedFlowFeatureId, normalizeFeatureReferenceId]);

  const resolveFlowFeatureNode = (step, sourceLabel) => {
    let itemsMap = leftListNewCustomisationItems;
    if (!itemsMap || Object.keys(itemsMap).length === 0) {
      const rebuilt = generateLeftListNewCustomisationItems(
        customisationVarsRef.current?.current,
        campaignData || campaignDataProp,
      );
      if (rebuilt && Object.keys(rebuilt).length > 0) {
        itemsMap = rebuilt;
      }
    }

    if (!itemsMap || Object.keys(itemsMap).length === 0) {
      console.warn('[NodeEditor][AudioDebug] feature-node-missing-leftList', {
        source: sourceLabel,
        stepId: step?.stepId,
      });
      return null;
    }

    const byExpandedId = expandedFlowFeatureId
      ? itemsMap[expandedFlowFeatureId]
      : null;
    if (byExpandedId) return byExpandedId;

    const byExpandedVarKey = expandedFlowFeature
      ? Object.values(itemsMap).find(n => n.varKey === expandedFlowFeature)
      : null;
    if (byExpandedVarKey) return byExpandedVarKey;

    const bySelectedId = selectedFeatureNode?.id
      ? itemsMap[selectedFeatureNode.id]
      : null;
    if (bySelectedId) return bySelectedId;

    const allNodes = Object.values(itemsMap);
    if (allNodes.length === 1) {
      console.warn('[NodeEditor][AudioDebug] feature-node-fallback-singleton', {
        source: sourceLabel,
        stepId: step?.stepId,
        fallbackFeatureId: allNodes[0]?.id,
      });
      return allNodes[0];
    }

    console.warn('[NodeEditor][AudioDebug] feature-node-unresolved', {
      source: sourceLabel,
      stepId: step?.stepId,
      expandedFlowFeature,
      expandedFlowFeatureId,
      selectedFeatureId: selectedFeatureNode?.id,
      availableFeatureIds: allNodes.map(n => n?.id),
      availableVarKeys: allNodes.map(n => n?.varKey),
    });
    return null;
  };

  // Called from FlowCanvas when a VoteCard graphic is clicked.
  // Finds the matching Wave Graphics sub-node in the left-list and activates it.
  // RightSidebar is always rendered, so activateNode() updates the settings panel
  // without changing mode — the flow canvas stays visible.
  const handleFlowGraphicRemove = (step, absoluteIndex) => {
    const cfg = step?.customisationConfig;

    const featureNode = resolveFlowFeatureNode(step, 'handleFlowGraphicRemove');
    if (!featureNode) return;

    const dataLinkPrefix = cfg ? `${cfg.tabKey}.${cfg.varKey}` : null;
    const primaryVariableName = cfg?.variables?.length === 1 ? cfg.variables[0]?.variableName : null;
    const variableDataLink = (dataLinkPrefix && primaryVariableName) ? `${dataLinkPrefix}.${primaryVariableName}` : null;
    // Detect slot-level removal from pollAudio multi-slot nodes: { slotVarName, fileIndex }
    const removeIsObj = absoluteIndex && typeof absoluteIndex === 'object' && !Array.isArray(absoluteIndex);
    const removeSlotVarName = removeIsObj ? absoluteIndex.slotVarName : null;
    const removeToggleVarName = removeIsObj ? (absoluteIndex.toggleVarName ?? absoluteIndex.slotVarName) : null;
    const fileIdx = removeIsObj ? absoluteIndex.fileIndex : absoluteIndex;
    const targetSlotVarName = removeSlotVarName || primaryVariableName;
    const targetToggleVarName = removeToggleVarName || targetSlotVarName;
    const removeExactDataLink = (dataLinkPrefix && targetSlotVarName) ? `${dataLinkPrefix}.${targetSlotVarName}` : null;
    const findGroupNode = (nodes) => {
      for (const n of nodes) {
        const dl = n.config?.groupSettings?.dataLink;
        if (removeExactDataLink ? dl === removeExactDataLink : (variableDataLink ? dl === variableDataLink : false)) return n;
        if (!removeExactDataLink && variableDataLink && dl?.startsWith(variableDataLink)) return n;
        // Only use the broad prefix fallback when no variableDataLink is available;
        // otherwise sibling groups that share the same tabKey/varKey (e.g. customImages
        // when searching for colours) would be matched first.
        if (!removeExactDataLink && !variableDataLink && dataLinkPrefix && dl?.startsWith(dataLinkPrefix)) return n;
        if (n.subNodes?.length) { const found = findGroupNode(n.subNodes); if (found) return found; }
      }
      return null;
    };
    let groupNode = dataLinkPrefix ? findGroupNode(featureNode.subNodes || []) : null;

    const isAudioRemovalNode = !!cfg?.meta?.pollAudio || !!cfg?.meta?.audioStack;
    // For pollAudio/audioStack steps the audio variables live in a customisation TAB
    // (not in customisation.groups), so findGroupNode can return null or the wrong
    // group. Build a synthetic node that points at the exact slot dataLink so
    // getSubNodes resolves the correct live data array for deletion.
    if (!groupNode && isAudioRemovalNode && dataLinkPrefix) {
      const fId = featureNode?.id;
      const liveCVars = (fId && customisationVars?.current?.[fId]) ?? (fId && customisationVars?.default?.[fId]) ?? {};
      groupNode = {
        id: `${fId}-synthetic-audio-${removeExactDataLink || dataLinkPrefix}`,
        path: `${fId}-synthetic-audio-${removeExactDataLink || dataLinkPrefix}`,
        name: targetSlotVarName || cfg.varKey,
        config: { groupSettings: { dataLink: removeExactDataLink || dataLinkPrefix, useDynamicNaming: false } },
        featureData: featureNode.featureData,
        customisationVars: liveCVars,
        topNode: featureNode,
        hasChildren: true,
        dynamicSubNodes: true,
        subNodes: [],
        subNodesToPreserve: [],
        depth: 1,
        parent: featureNode,
      };
    }
    if (!groupNode) return;

    // For audioStack / pollAudio nodes, bypass getSubNodes entirely — these audio vars
    // live directly in the feature customisation tab data, and the generic node-tree path
    // can miss the correct synthetic addButton node. Splice the live array directly.
    if (!!cfg?.meta?.audioStack || !!cfg?.meta?.pollAudio) {
      try {
        const liveFeatureId = featureNode?.featureData?._id || featureNode?.id;
        const liveCVars = (liveFeatureId && customisationVarsRef.current?.current?.[liveFeatureId])
          || (liveFeatureId && customisationVars?.current?.[liveFeatureId])
          || groupNode.customisationVars;
        groupNode.customisationVars = liveCVars;
        const dataArr = liveCVars?.[cfg.tabKey]?.[cfg.varKey]?.[targetSlotVarName];
        const toggleObj = liveCVars?.[cfg.tabKey]?.[cfg.varKey]?.toggleSFX?.[0];

        if (!Array.isArray(dataArr)) return;
        if (fileIdx >= 0 && fileIdx < dataArr.length) {
          // For flow-managed audio slots, removing the final file should behave like disable.
          // If we splice the last item out, the default seeder value can reappear on refresh.
          if (dataArr.length === 1) {
            if (toggleObj && targetToggleVarName && Object.prototype.hasOwnProperty.call(toggleObj, targetToggleVarName)) {
              toggleObj[targetToggleVarName] = false;
            }
            updateCustomisationValue(
              liveCVars,
              `${cfg.tabKey}.${cfg.varKey}.${targetSlotVarName}.playSFX`,
              false,
              fileIdx,
              null,
              featureNode,
              null,
              null,
              true,
              false,
              true,
            );
            forceUpdateComponent?.();
            return;
          }
          const removedVal = dataArr[fileIdx];
          if (removedVal && undoRedoHook?.recordCustomisationValueDeleted) {
            undoRedoHook.recordCustomisationValueDeleted(groupNode, removedVal, dataArr, fileIdx);
          }
          dataArr.splice(fileIdx, 1);
          if (toggleObj && targetToggleVarName && dataArr.length === 0 && Object.prototype.hasOwnProperty.call(toggleObj, targetToggleVarName)) {
            toggleObj[targetToggleVarName] = false;
          }

          if (Array.isArray(featureNode.featureData?.feature?.customisation)) {
            triggerSaveFeatureData(featureNode.featureData?._id);
          } else {
            console.warn('[NodeEditor] Skipping save — feature document missing from DB for:', featureNode.featureData?._id);
          }
          forceUpdateComponent?.();
        }
      } catch (e) {
        console.warn('[FlowGraphicRemove:audio] Could not remove audio item:', e);
      }
      return;
    }

    try {
      const dynamicSubNodes = getSubNodes(groupNode, []);
      const items = (dynamicSubNodes || []).filter(n => !n.isAddButton);
      const isPollAudio = !!cfg?.meta?.pollAudio;
      const isAudioStack = !!cfg?.meta?.audioStack;
      // isImageOverlay covers ALL imageStack steps (main image files AND overlay image files)
      const isImageOverlay = !!cfg?.meta?.imageStack;
      if (!isPollAudio && !isAudioStack && !isImageOverlay && items.length <= 1) return; // keep at least one for other node types
      const addBtn = dynamicSubNodes.find(n => n.isAddButton);
      // Use the live valsArray length as the source of truth — it may differ from items.length
      // if the canvas and node system are temporarily out of sync (e.g. stale DB data)
      const liveLen = addBtn?.valsArray?.length ?? items.length;

      if (isImageOverlay && liveLen <= 1) {
        // removeVal guards against deleting the final entry; for image overlay we
        // want to allow the array to become empty, so splice directly.
        if (addBtn && fileIdx >= 0 && fileIdx < liveLen) {
          const removedVal = addBtn.valsArray[fileIdx];
          if (removedVal && undoRedoHook?.recordCustomisationValueDeleted) {
            undoRedoHook.recordCustomisationValueDeleted(addBtn, removedVal, addBtn.valsArray, fileIdx);
          }
          addBtn.valsArray.splice(fileIdx, 1);
          triggerSaveFeatureData(addBtn?.featureData?._id);
          forceUpdateComponent?.();
        }
        return;
      }

      if (isPollAudio && liveLen === 1) {
        // Deleting the last file — turn the toggle off AND clear the array so the slot is truly empty.
        // Access toggleSFX directly from customisationVars state using the feature ID.
        const featureId = featureNode?.id;
        const liveCVars = (featureId && customisationVars?.current?.[featureId]) ?? (featureId && customisationVars?.default?.[featureId]);
        const toggleObj = liveCVars?.[cfg.tabKey]?.[cfg.varKey]?.toggleSFX?.[0];
        if (toggleObj && removeToggleVarName && Object.prototype.hasOwnProperty.call(toggleObj, removeToggleVarName)) {
          toggleObj[removeToggleVarName] = false;
        }

        if (addBtn && fileIdx >= 0 && fileIdx < liveLen) {
          // removeVal refuses to delete the final item; for poll audio we
          // want to allow the array to become empty, so perform a targeted
          // splice here and record/save via the existing helpers.
          const removedVal = addBtn.valsArray[fileIdx];
          if (removedVal && undoRedoHook?.recordCustomisationValueDeleted) {
            undoRedoHook.recordCustomisationValueDeleted(addBtn, removedVal, addBtn.valsArray, fileIdx);
          }
          addBtn.valsArray.splice(fileIdx, 1);
          if (Array.isArray(addBtn?.featureData?.feature?.customisation)) {
            triggerSaveFeatureData(addBtn?.featureData?._id);
          } else {
            console.warn('[NodeEditor] Skipping save — feature document missing from DB for:', addBtn?.featureData?._id);
          }
          forceUpdateComponent?.();
        } else {
          if (addBtn && Array.isArray(addBtn?.featureData?.feature?.customisation)) triggerSaveFeatureData(addBtn?.featureData?._id);
          forceUpdateComponent?.();
        }
        return;
      }

      if (isAudioStack && liveLen === 1) {
        if (addBtn && fileIdx >= 0 && fileIdx < liveLen) {
          const removedVal = addBtn.valsArray[fileIdx];
          if (removedVal && undoRedoHook?.recordCustomisationValueDeleted) {
            undoRedoHook.recordCustomisationValueDeleted(addBtn, removedVal, addBtn.valsArray, fileIdx);
          }
          addBtn.valsArray.splice(fileIdx, 1);
          if (Array.isArray(addBtn?.featureData?.feature?.customisation)) {
            triggerSaveFeatureData(addBtn?.featureData?._id);
          } else {
            console.warn('[NodeEditor] Skipping save — feature document missing from DB for:', addBtn?.featureData?._id);
          }
          forceUpdateComponent?.();
        }
        return;
      }

      if (addBtn && fileIdx >= 0 && fileIdx < liveLen) {
        removeVal(addBtn.valsArray, fileIdx, forceUpdateComponent, addBtn);
      }
    } catch (e) {
      console.warn('[FlowGraphicRemove] Could not remove sub-node:', e);
    }
  };

  const getExpandedFlowPollContext = (step, pollIndex) => {
    const cfg = step?.customisationConfig;
    if (!cfg || !leftListNewCustomisationItems) return null;
    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return null;

    const featureId = featureNode.id;
    const liveCVars = (featureId && customisationVars?.current?.[featureId]) ?? (featureId && customisationVars?.default?.[featureId]);
    if (!liveCVars) return null;

    const tab = (liveCVars[cfg.tabKey] ||= {});
    const group = (tab[cfg.varKey] ||= {});
    const varName = cfg.variables?.[0]?.variableName;
    if (!varName) return null;
    const arr = Array.isArray(group[varName]) ? group[varName] : (group[varName] = []);

    while (arr.length <= pollIndex) {
      arr.push({
        type: 'opinion',
        title: `Poll ${arr.length + 1}`,
        question: '',
        answer1: `Option 1`,
        answer2: `Option 2`,
        points1: 0,
        points2: 0,
      });
    }

    const poll = (arr[pollIndex] ||= {});
    return { featureNode, featureId, arr, poll, cfg };
  };

  const getPollAnswerValues = (poll) => {
    const answerKeys = Object.keys(poll || {})
      .filter(k => /^answer\d+$/.test(k))
      .sort((a, b) => Number(a.replace('answer', '')) - Number(b.replace('answer', '')));
    return answerKeys
      .map(k => poll?.[k] ?? '')
      .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
      .slice(0, 10);
  };

  const getPollPointValues = (poll, count) => {
    const pointKeys = Object.keys(poll || {})
      .filter(k => /^points\d+$/.test(k))
      .sort((a, b) => Number(a.replace('points', '')) - Number(b.replace('points', '')));
    const values = pointKeys.map(k => poll?.[k] ?? 0);
    while (values.length < count) values.push(0);
    return values;
  };

  const rewritePollAnswers = (poll, answers, points) => {
    Object.keys(poll || {}).forEach(k => {
      if (/^answer\d+$/.test(k) || /^points\d+$/.test(k)) delete poll[k];
    });
    answers.forEach((answer, i) => {
      poll[`answer${i + 1}`] = answer;
      poll[`points${i + 1}`] = points?.[i] ?? 0;
    });
    poll.answers = [...answers];
  };

  const saveExpandedFlowPollContext = (ctx) => {
    if (!ctx?.featureNode) return;
    triggerSaveFeatureData(ctx.featureNode?.featureData?._id || ctx.featureNode?.id);
    forceUpdateComponent?.();
  };

  const getExpandedFlowFeatureNode = () => {
    if (!leftListNewCustomisationItems) return null;
    // Prefer lookup by specific featureId to avoid ambiguity when two features share the same varKey
    if (expandedFlowFeatureId) {
      const byId = leftListNewCustomisationItems[expandedFlowFeatureId];
      if (byId) return byId;
    }
    return Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature) || null;
  };

  const ensureExpandedFlowFeatureVars = (featureId, featureNode) => {
    if (!customisationVars || !customisationVars.current) return null;
    if (!customisationVars.current[featureId]) {
      const defaultVars = customisationVars?.default?.[featureId]
        || featureNode?.customisationVars
        || {};
      customisationVars.current[featureId] = JSON.parse(JSON.stringify(defaultVars));
    }
    return customisationVars.current[featureId];
  };

  const getExpandedFlowImageArrayContext = (step) => {
    const cfg = step?.customisationConfig;
    const variableName = cfg?.variables?.[0]?.variableName;
    if (!cfg?.tabKey || !cfg?.varKey || !variableName) return null;

    const featureNode = getExpandedFlowFeatureNode();
    if (!featureNode) return null;

    const featureId = featureNode.id;
    const featureVars = ensureExpandedFlowFeatureVars(featureId, featureNode);
    if (!featureVars) return null;

    const tab = (featureVars[cfg.tabKey] ||= {});
    const group = (tab[cfg.varKey] ||= {});
    let arr = group[variableName];
    if (!Array.isArray(arr)) {
      const defaultArr = customisationVars?.default?.[featureId]?.[cfg.tabKey]?.[cfg.varKey]?.[variableName];
      arr = group[variableName] = Array.isArray(defaultArr)
        ? JSON.parse(JSON.stringify(defaultArr))
        : [];
    }

    return { featureNode, featureId, arr };
  };

  const getExpandedFlowStepItems = (step) => {
    const cfg = step?.customisationConfig;
    if (!cfg?.tabKey || !cfg?.varKey) return null;

    const variableName = cfg?.variables?.[0]?.variableName;
    if (!variableName) return null;

    // Use the stored featureId directly (set when the expanded flow was opened)
    // to avoid ambiguity when two features share the same varKey.
    let featureId = expandedFlowFeatureId || null;
    if (!featureId) {
      const expandedFeatureInstance = campaignData?.features?.find(f => f.varKey === expandedFlowFeature);
      featureId = expandedFeatureInstance?._id?.toString?.() || expandedFeatureInstance?._id || null;
    }
    if (!featureId) return null;

    // Use the ref (always current) rather than state (can lag behind in-memory mutations)
    const vars = customisationVarsRef.current;
    const currentFeatureVars = vars?.current?.[featureId] || null;
    const defaultFeatureVars = vars?.default?.[featureId] || null;

    const dataLink = `${cfg.tabKey}.${cfg.varKey}.${variableName}`;

    const currentItems = getGroupData(currentFeatureVars, dataLink, -1);
    if (Array.isArray(currentItems)) return currentItems.filter(item => item != null);

    const defaultItems = getGroupData(defaultFeatureVars, dataLink, -1);
    if (Array.isArray(defaultItems)) return defaultItems.filter(item => item != null);

    return null;
  };

  const saveExpandedFlowImageArrayContext = (ctx) => {
    if (!ctx?.featureNode) return;
    triggerSaveFeatureData(ctx.featureNode?.featureData?._id || ctx.featureId || ctx.featureNode?.id);
    forceUpdateComponent?.();
  };

  const isSinglePollDisruptorField = (triggerField) => (
    triggerField === 'correctDisruptor' || triggerField === 'answersHiddenDisruptor'
  );

  const getPollTriggerValues = (poll, triggerField) => {
    const raw = poll?.[triggerField];
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object') return [raw];
    if (typeof raw === 'string' && raw.trim() !== '') return [{ triggerDisruptorFeatureID: raw }];
    return [];
  };

  const handleFlowPollQuestionChange = (step, pollIndex, value) => {
    const ctx = getExpandedFlowPollContext(step, pollIndex);
    if (!ctx) return;
    ctx.poll.question = value;
    saveExpandedFlowPollContext(ctx);
  };

  const handleFlowPollAnswerChange = (step, pollIndex, answerIndex, value) => {
    const ctx = getExpandedFlowPollContext(step, pollIndex);
    if (!ctx) return;
    const answers = getPollAnswerValues(ctx.poll);
    const points = getPollPointValues(ctx.poll, answers.length);
    while (answers.length <= answerIndex && answers.length < 10) {
      answers.push(`Option ${answers.length + 1}`);
      points.push(0);
    }
    answers[answerIndex] = value;
    rewritePollAnswers(ctx.poll, answers, points);
    saveExpandedFlowPollContext(ctx);
  };

  const handleFlowPollAnswerAdd = (step, pollIndex) => {
    const ctx = getExpandedFlowPollContext(step, pollIndex);
    if (!ctx) return;
    const answers = getPollAnswerValues(ctx.poll);
    if (answers.length >= 10) return;
    const points = getPollPointValues(ctx.poll, answers.length);
    answers.push(`Option ${answers.length + 1}`);
    points.push(0);
    rewritePollAnswers(ctx.poll, answers, points);
    saveExpandedFlowPollContext(ctx);
  };

  const handleFlowPollAnswerRemove = (step, pollIndex, answerIndex) => {
    const ctx = getExpandedFlowPollContext(step, pollIndex);
    if (!ctx) return;
    const answers = getPollAnswerValues(ctx.poll);
    if (answers.length <= 2) return;
    const points = getPollPointValues(ctx.poll, answers.length);
    answers.splice(answerIndex, 1);
    points.splice(answerIndex, 1);
    rewritePollAnswers(ctx.poll, answers, points);
    saveExpandedFlowPollContext(ctx);
  };

  const handleFlowImageFieldChange = (step, imageIndex, field, value) => {
    const ctx = getExpandedFlowImageArrayContext(step);
    if (!ctx || imageIndex < 0 || imageIndex >= ctx.arr.length) return;

    const item = (ctx.arr[imageIndex] ||= {});
    item[field] = value;
    saveExpandedFlowImageArrayContext(ctx);
  };

  const handleFlowFieldChange = (tabKey, varKey, varName, itemIndex, field, value) => {
    if (!leftListNewCustomisationItems || !tabKey || !varKey || !varName || !field) return;

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    const featureId = featureNode.id;
    if (!customisationVars?.current) return;
    if (!customisationVars.current[featureId]) customisationVars.current[featureId] = {};
    const featureVars = customisationVars.current[featureId];
    const tab = (featureVars[tabKey] ||= {});
    const group = (tab[varKey] ||= {});
    const arr = (group[varName] ||= []);

    // Special: reorder operation — move arr[itemIndex] to arr[value]
    if (field === '_reorder_') {
      const fromIdx = itemIndex;
      const toIdx = value;
      if (fromIdx >= 0 && toIdx >= 0 && fromIdx < arr.length && toIdx < arr.length && fromIdx !== toIdx) {
        const [moved] = arr.splice(fromIdx, 1);
        arr.splice(toIdx, 0, moved);
      }
    } else if (field === '_delete_') {
      const fromIdx = itemIndex;
      if (fromIdx >= 0 && fromIdx < arr.length) {
        arr.splice(fromIdx, 1);
      }
    } else {
      while (arr.length <= itemIndex) arr.push({});
      const item = (arr[itemIndex] ||= {});
      item[field] = value;
    }

    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };

    const syncNodeTreeCustomisationVars = (nodeToSync) => {
      if (!nodeToSync || typeof nodeToSync !== 'object') return;
      nodeToSync.customisationVars = featureVars;
      if (Array.isArray(nodeToSync.subNodes)) {
        nodeToSync.subNodes.forEach(syncNodeTreeCustomisationVars);
      }
      if (Array.isArray(nodeToSync.subNodesToPreserve)) {
        nodeToSync.subNodesToPreserve.forEach(syncNodeTreeCustomisationVars);
      }
    };

    syncNodeTreeCustomisationVars(featureNode);

    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);

    if (activeNode) {
      activeNode.customisationVars = featureVars;
      if (activeNode.topNode)  activeNode.topNode.customisationVars = featureVars;
      if (activeNode.parent)   activeNode.parent.customisationVars = featureVars;
    }

    triggerSaveFeatureData(featureId);
    forceUpdateComponent(activeNode || undefined);
  };

  const handleVideoSettingsFieldChange = (field, value) => {
    if (!leftListNewCustomisationItems || !field) return;
    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;
    const featureId = featureNode.id;
    if (!customisationVars?.current) return;
    const existingFeatureVars = customisationVarsRef.current?.current?.[featureId] || customisationVars.current?.[featureId] || {};
    const existingControlsTab = existingFeatureVars['controls'] || {};
    const existingControlsGroup = existingControlsTab['controls'] || {};
    const newControlsGroup = { ...existingControlsGroup, [field]: value };
    const newControlsTab = { ...existingControlsTab, controls: newControlsGroup };
    const newFeatureVars = { ...existingFeatureVars, controls: newControlsTab };
    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: { ...(baseCustomisationVars.current || {}), [featureId]: newFeatureVars },
    };

    const syncNodeTreeCustomisationVars = (nodeToSync) => {
      if (!nodeToSync || typeof nodeToSync !== 'object') return;
      nodeToSync.customisationVars = newFeatureVars;
      if (Array.isArray(nodeToSync.subNodes)) {
        nodeToSync.subNodes.forEach(syncNodeTreeCustomisationVars);
      }
      if (Array.isArray(nodeToSync.subNodesToPreserve)) {
        nodeToSync.subNodesToPreserve.forEach(syncNodeTreeCustomisationVars);
      }
    };

    syncNodeTreeCustomisationVars(featureNode);

    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);

    if (activeNode) {
      activeNode.customisationVars = newFeatureVars;
      if (activeNode.topNode) activeNode.topNode.customisationVars = newFeatureVars;
      if (activeNode.parent) activeNode.parent.customisationVars = newFeatureVars;
    }

    triggerSaveFeatureData(featureId, newFeatureVars);
    forceUpdateComponent(activeNode || undefined);
  };

  const handleFlowPresetFieldChange = (step, presetIndex, field, value) => {
    const cfg = step?.customisationConfig;
    const varName = cfg?.variables?.[0]?.variableName;
    if (!cfg?.tabKey || !cfg?.varKey || !varName || typeof presetIndex !== 'number') return;
    handleFlowFieldChange(cfg.tabKey, cfg.varKey, varName, presetIndex, field, value);
  };

  const handleFlowPresetAdd = (step) => {
    const cfg = step?.customisationConfig;
    const variableName = cfg?.variables?.[0]?.variableName;
    if (!cfg?.tabKey || !cfg?.varKey || !variableName) return;

    const featureNode = getExpandedFlowFeatureNode();
    if (!featureNode) return;

    const featureId = featureNode.id;
    const featureVars = ensureExpandedFlowFeatureVars(featureId, featureNode);
    if (!featureVars) return;

    const tab = (featureVars[cfg.tabKey] ||= {});
    const group = (tab[cfg.varKey] ||= {});
    const arr = (group[variableName] ||= []);
    arr.push({
      presetName: `Preset ${arr.length + 1}`,
      headerText: '',
      mainContent: '',
    });

    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };

    const syncNodeTreeCustomisationVars = (nodeToSync) => {
      if (!nodeToSync || typeof nodeToSync !== 'object') return;
      nodeToSync.customisationVars = featureVars;
      if (Array.isArray(nodeToSync.subNodes)) nodeToSync.subNodes.forEach(syncNodeTreeCustomisationVars);
      if (Array.isArray(nodeToSync.subNodesToPreserve)) nodeToSync.subNodesToPreserve.forEach(syncNodeTreeCustomisationVars);
    };

    syncNodeTreeCustomisationVars(featureNode);

    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);

    if (activeNode) {
      activeNode.customisationVars = featureVars;
      if (activeNode.topNode) activeNode.topNode.customisationVars = featureVars;
      if (activeNode.parent) activeNode.parent.customisationVars = featureVars;
    }

    triggerSaveFeatureData(featureNode?.featureData?._id || featureId);
    forceUpdateComponent?.();
  };

  // Called from DiamondNode toggle: persists a boolean leaf within a customisation array.
  // tabKey/varKey/varName mirror the displayCondition path format; itemIndex is the array index (usually 0).
  const handleFlowBoolChange = (tabKey, varKey, varName, itemIndex, field, value) => {
    handleFlowFieldChange(tabKey, varKey, varName, itemIndex, field, value);
  };

  const handleFlowSlotOptionDisruptorDrop = async (reelIndex, itemIndex, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const di = draggedItemRef.current || draggedItem;
    if (!di) return;

    const droppedVarKey = di.varKey || di.id;
    if (droppedVarKey && droppedVarKey === expandedFlowFeature) {
      toast.error('A feature cannot trigger itself — this would create an infinite loop.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    let newCampaignFeatureId = null;
    if (di.isExistingCampaignFeature) {
      newCampaignFeatureId = di.campaignFeatureId;
    } else {
      try {
        newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, di.label);
      } catch (err) {
        console.error('[SlotOptionDisruptorDrop] Failed to add feature to campaign:', err);
        toast.error('Failed to add feature to campaign');
        return;
      }
    }

    if (!newCampaignFeatureId) return;

    const existingItems = getExpandedFlowStepItems({
      customisationConfig: {
        tabKey: 'setup',
        varKey: `options${reelIndex + 1}`,
        variables: [{ variableName: 'options' }],
      },
    }) || [];
    const existingId = existingItems?.[itemIndex]?.disruptor;
    if (existingId && String(existingId) === String(newCampaignFeatureId)) {
      toast.error('This disruptor is already assigned to that reel image.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    handleFlowFieldChange('setup', `options${reelIndex + 1}`, 'options', itemIndex, 'disruptor', newCampaignFeatureId);
    draggedItemRef.current = null;
    setDraggedItem(null);
    setCurrentAction(ACTION_STATE.IDLE);
  };

  const handleFlowSlotOptionDisruptorRemove = (reelIndex, itemIndex) => {
    handleFlowFieldChange('setup', `options${reelIndex + 1}`, 'options', itemIndex, 'disruptor', '');
  };

  const handleFlowSlotOptionRemove = (reelIndex, itemIndex) => {
    handleFlowFieldChange('setup', `options${reelIndex + 1}`, 'options', itemIndex, '_delete_', true);
  };

  const handleFlowSlotResultAdd = () => {
    const featureNode = getExpandedFlowFeatureNode();
    if (!featureNode) return;

    const featureId = featureNode.id;
    const featureVars = ensureExpandedFlowFeatureVars(featureId, featureNode);
    if (!featureVars) return;

    const tab = (featureVars.setup ||= {});
    const group = (tab.resultTriggers ||= {});
    const arr = (group.setup ||= []);
    arr.push({
      option1: '',
      option2: '',
      option3: '',
      option4: '',
      option5: '',
      triggerDisruptor: '',
    });

    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };

    const syncNodeTreeCustomisationVars = (nodeToSync) => {
      if (!nodeToSync || typeof nodeToSync !== 'object') return;
      nodeToSync.customisationVars = featureVars;
      if (Array.isArray(nodeToSync.subNodes)) nodeToSync.subNodes.forEach(syncNodeTreeCustomisationVars);
      if (Array.isArray(nodeToSync.subNodesToPreserve)) nodeToSync.subNodesToPreserve.forEach(syncNodeTreeCustomisationVars);
    };

    syncNodeTreeCustomisationVars(featureNode);

    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);
    triggerSaveFeatureData(featureNode?.featureData?._id || featureId);
    forceUpdateComponent?.();
  };

  const handleFlowSlotResultRemove = (itemIndex) => {
    handleFlowFieldChange('setup', 'resultTriggers', 'setup', itemIndex, '_delete_', true);
  };

  const handleFlowSlotResultDisruptorRemove = (itemIndex) => {
    handleFlowFieldChange('setup', 'resultTriggers', 'setup', itemIndex, 'triggerDisruptor', '');
  };

  const handleFlowSlotResultDisruptorDrop = async (itemIndex, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const di = draggedItemRef.current || draggedItem;
    if (!di) return;

    const droppedVarKey = di.varKey || di.id;
    if (droppedVarKey && droppedVarKey === expandedFlowFeature) {
      toast.error('A feature cannot trigger itself — this would create an infinite loop.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    let newCampaignFeatureId = null;
    if (di.isExistingCampaignFeature) {
      newCampaignFeatureId = di.campaignFeatureId;
    } else {
      try {
        newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, di.label);
      } catch (err) {
        console.error('[SlotResultDisruptorDrop] Failed to add feature to campaign:', err);
        toast.error('Failed to add feature to campaign');
        return;
      }
    }

    if (!newCampaignFeatureId) return;

    const existingItems = getExpandedFlowStepItems({
      customisationConfig: {
        tabKey: 'setup',
        varKey: 'resultTriggers',
        variables: [{ variableName: 'setup' }],
      },
    }) || [];
    const existingId = existingItems?.[itemIndex]?.triggerDisruptor;
    if (existingId && String(existingId) === String(newCampaignFeatureId)) {
      toast.error('This disruptor is already assigned to that winning result.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    handleFlowFieldChange('setup', 'resultTriggers', 'setup', itemIndex, 'triggerDisruptor', newCampaignFeatureId);
    draggedItemRef.current = null;
    setDraggedItem(null);
    setCurrentAction(ACTION_STATE.IDLE);
  };

  const sanitiseFlowGoalTriggerState = (goalItem, field, nextValue) => {
    const isEmptyTrigger = (triggerValue) => triggerValue === '' || triggerValue === null || triggerValue === undefined;

    if (!goalItem || typeof goalItem !== 'object') return nextValue;

    if (field === 'triggerDisruptorFeatureID' && isEmptyTrigger(nextValue)) {
      goalItem.pauseProgress = false;
      goalItem.endPreviousDisruptors = false;
      return '';
    }

    if ((field === 'pauseProgress' || field === 'endPreviousDisruptors') && nextValue === true && isEmptyTrigger(goalItem.triggerDisruptorFeatureID)) {
      return false;
    }

    return nextValue;
  };

  const handleFlowGoalFieldChange = (step, goalIndex, field, value) => {
    const cfg = step?.customisationConfig;
    if (!leftListNewCustomisationItems || typeof goalIndex !== 'number' || goalIndex < 0 || !cfg?.tabKey || !cfg?.varKey) return;

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    const featureId = featureNode.id;
    if (!customisationVars?.current) return;
    if (!customisationVars.current[featureId]) customisationVars.current[featureId] = {};
    const featureVars = customisationVars.current[featureId];
    const tab = (featureVars[cfg.tabKey] ||= {});
    const group = (tab[cfg.varKey] ||= {});
    const arr = (group.goal ||= []);
    while (arr.length <= goalIndex) arr.push({});
    const item = (arr[goalIndex] ||= {});
    item[field] = sanitiseFlowGoalTriggerState(item, field, value);

    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };
    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);

    if (activeNode) {
      activeNode.customisationVars = featureVars;
      if (activeNode.topNode) activeNode.topNode.customisationVars = featureVars;
      if (activeNode.parent) activeNode.parent.customisationVars = featureVars;
    }

    triggerSaveFeatureData(featureId);
    forceUpdateComponent(activeNode || undefined);
  };

  const handleFlowPollTriggerRemove = (step, pollIndex, triggerField, removeIndex) => {
    const ctx = getExpandedFlowPollContext(step, pollIndex);
    if (!ctx || !triggerField) return;

    if (isSinglePollDisruptorField(triggerField)) {
      ctx.poll[triggerField] = '';
      saveExpandedFlowPollContext(ctx);
      return;
    }

    const arr = getPollTriggerValues(ctx.poll, triggerField);
    if (removeIndex < 0 || removeIndex >= arr.length) return;

    arr.splice(removeIndex, 1);
    if (arr.length > 0) ctx.poll[triggerField] = arr;
    else delete ctx.poll[triggerField];
    saveExpandedFlowPollContext(ctx);
  };

  const handleFlowPollTriggerDrop = async (step, pollIndex, triggerField, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const ctx = getExpandedFlowPollContext(step, pollIndex);
    if (!ctx || !triggerField) return;

    const di = draggedItemRef.current || draggedItem;
    if (!di) return;

    const droppedVarKey = di.varKey || di.id;
    if (droppedVarKey && droppedVarKey === expandedFlowFeature) {
      toast.error('A feature cannot trigger itself — this would create an infinite loop.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    let newCampaignFeatureId = null;
    if (di.isExistingCampaignFeature) {
      newCampaignFeatureId = di.campaignFeatureId;
    } else {
      try {
        newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, di.label);
      } catch (err) {
        console.error('[FlowPollTriggerDrop] Failed to add feature to campaign:', err);
        toast.error('Failed to add feature to campaign');
        return;
      }
    }

    if (!newCampaignFeatureId) return;

    if (isSinglePollDisruptorField(triggerField)) {
      const existingValue = ctx.poll?.[triggerField];
      const existingId = existingValue && typeof existingValue === 'object'
        ? (existingValue.triggerDisruptorFeatureID || existingValue.featureId || existingValue.id)
        : existingValue;

      if (existingId && String(existingId) === String(newCampaignFeatureId)) {
        toast.error('This disruptor is already assigned to this poll trigger.');
        draggedItemRef.current = null;
        setDraggedItem(null);
        setCurrentAction(ACTION_STATE.IDLE);
        return;
      }

      ctx.poll[triggerField] = newCampaignFeatureId;
      saveExpandedFlowPollContext(ctx);

      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    const existingArr = getPollTriggerValues(ctx.poll, triggerField);
    const alreadyPresent = existingArr.some(v => {
      const existingId = v && typeof v === 'object' ? v.triggerDisruptorFeatureID : v;
      return existingId && String(existingId) === String(newCampaignFeatureId);
    });
    if (alreadyPresent) {
      toast.error('This disruptor is already assigned to this poll trigger.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    ctx.poll[triggerField] = [...existingArr, { triggerDisruptorFeatureID: newCampaignFeatureId }];
    saveExpandedFlowPollContext(ctx);

    draggedItemRef.current = null;
    setDraggedItem(null);
    setCurrentAction(ACTION_STATE.IDLE);
  };

  const handleFlowGraphicActivate = (step, graphicIndex) => {
    const cfg = step?.customisationConfig;

    // Find the top-level feature node for the feature currently in the expanded flow view.
    // Fall back to selected/singleton feature when expanded-flow state is stale.
    const featureNode = resolveFlowFeatureNode(step, 'handleFlowGraphicActivate');
    if (!featureNode) return;

    // ── timeline audio early-exit ──────────────────────────────────────────────
    // Audio stack nodes do not map cleanly onto customisation groups in the
    // right sidebar. Route them through the timeline audio editor instead.
    if (cfg?.meta?.pollAudio || cfg?.meta?.audioStack) {
      const slotInfo = graphicIndex && typeof graphicIndex === 'object' && !Array.isArray(graphicIndex) ? graphicIndex : null;
      const slotVarName = slotInfo?.slotVarName ?? null;
      const toggleVarName = slotInfo?.toggleVarName ?? slotVarName;
      const groupVarKey = slotInfo?.groupVarKey ?? cfg?.varKey;
      const selectionMode = slotInfo?.selectionMode ?? null;
      const fileIndex   = slotInfo ? slotInfo.fileIndex : graphicIndex;
      // Some seeded audio entries use their group's toggle (rather than an
      // item-level playSFX flag) to represent the blank state. Remember that
      // state before enabling it so the first Add reuses that entry.
      let audioSlotWasDisabled = false;


      // Set feature context so the right sidebar knows which feature is selected
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);

      // Find the timeline audio config entry that corresponds to this slot
      const findTimelineAudio = (varName) => {
        const dataLinkBase = `${cfg.tabKey}.${groupVarKey}.${varName}`;
        for (const group of (featureNode.config?.timeline?.audioGroups || [])) {
          for (const audio of (group.audios || [])) {
            const af = audio['audio-file'] || '';
            if (af === dataLinkBase || af.startsWith(dataLinkBase + '.')) {
              return { groupData: group, audioData: audio };
            }
          }
        }
        // Fallback: audio files are customisation vars, not timeline audio group entries.
        // Scan the feature's customisation config for an audio module whose field
        // data links start with the expected data-link base.
        const syntheticAudioData = { name: varName };
        let foundAudioField = false;
        for (const group of (featureNode.config?.customisation?.groups ?? [])) {
          for (const child of (group.children ?? [])) {
            for (const field of (child.fields ?? [])) {
              if (field.dataLink && field.dataLink.startsWith(dataLinkBase + '.')) {
                if (field.field === 'customisation-module-audio-file') { syntheticAudioData['audio-file'] = field.dataLink; foundAudioField = true; }
                else if (field.field === 'customisation-module-audio-name') syntheticAudioData['audio-name'] = field.dataLink;
                else if (field.field === 'customisation-module-audio-volume') syntheticAudioData['audio-volume'] = field.dataLink;
                else if (field.field === 'customisation-module-audio-loop') syntheticAudioData['audio-loop'] = field.dataLink;
                else if (field.field === 'customisation-module-audio-enabled') syntheticAudioData['audio-enabled'] = field.dataLink;
                else if (field.field === 'customisation-module-audio-delay') syntheticAudioData['audio-delay'] = field.dataLink;
              }
            }
          }
        }
        if (foundAudioField) {
          return { groupData: { name: featureNode.name || varName }, audioData: syntheticAudioData };
        }
        return null;
      };

      const findToggleHost = (liveCVars) => {
        const groupVars = liveCVars?.[cfg.tabKey]?.[groupVarKey];
        if (groupVars && typeof groupVars === 'object') {
          if (Array.isArray(groupVars?.toggleSFX) && groupVars.toggleSFX[0] && Object.prototype.hasOwnProperty.call(groupVars.toggleSFX[0], toggleVarName)) {
            return groupVars.toggleSFX[0];
          }
          for (const value of Object.values(groupVars)) {
            if (Array.isArray(value) && value[0] && typeof value[0] === 'object' && Object.prototype.hasOwnProperty.call(value[0], toggleVarName)) {
              return value[0];
            }
          }
        }

        const tabVars = liveCVars?.[cfg.tabKey];
        if (!tabVars || typeof tabVars !== 'object') return null;
        for (const value of Object.values(tabVars)) {
          if (!value || typeof value !== 'object') continue;
          if (Array.isArray(value)) {
            const first = value[0];
            if (first && typeof first === 'object' && Object.prototype.hasOwnProperty.call(first, toggleVarName)) {
              return first;
            }
            continue;
          }
          for (const nested of Object.values(value)) {
            if (Array.isArray(nested) && nested[0] && typeof nested[0] === 'object' && Object.prototype.hasOwnProperty.call(nested[0], toggleVarName)) {
              return nested[0];
            }
          }
        }

        return null;
      };

      if (slotVarName && selectionMode !== 'group') {
        const fId = featureNode?.featureData?._id || featureNode?.id;
        const liveCVars = (fId && customisationVarsRef.current?.current?.[fId])
          || (fId && customisationVars?.current?.[fId])
          || (fId && customisationVars?.default?.[fId])
          || {};
        const toggleObj = findToggleHost(liveCVars);
        if (toggleObj && toggleVarName && Object.prototype.hasOwnProperty.call(toggleObj, toggleVarName) && toggleObj[toggleVarName] === false) {
          audioSlotWasDisabled = true;
          toggleObj[toggleVarName] = true;
        }
      }

      if (selectionMode === 'group') {
        if (slotVarName) {
          const match = findTimelineAudio(slotVarName);
          if (match) { activateTimelineAudioGroup({ ...match, selectedFileIndex: null }); return; }
        }
        return;
      }

      if (fileIndex === null) {
        // CTA / "Add another file" — enable the toggle then add a new entry,
        // then open the timeline audio editor so the user can pick the file.
        if (slotVarName) {
          const fId = featureNode?.featureData?._id || featureNode?.id;
          const liveCVars = (fId && customisationVarsRef.current?.current?.[fId])
            || (fId && customisationVars?.current?.[fId])
            || (fId && customisationVars?.default?.[fId])
            || {};

          // Turn toggle on (if it was off)
          const toggleObj = findToggleHost(liveCVars);
          if (toggleObj && toggleVarName && Object.prototype.hasOwnProperty.call(toggleObj, toggleVarName) && toggleObj[toggleVarName] === false) {
            toggleObj[toggleVarName] = true;
          }

          // Add a new vals entry via the seeder's valSchema
          const valsArray = liveCVars?.[cfg.tabKey]?.[cfg.varKey]?.[slotVarName];
          // Re-enable a single disabled file instead of adding a new entry.
          // This applies to both pollAudio-backed and audioStack-backed flow nodes.
          if (Array.isArray(valsArray) && valsArray.length === 1 && (audioSlotWasDisabled || valsArray[0]?.playSFX !== true)) {
            updateCustomisationValue(
              liveCVars,
              `${cfg.tabKey}.${cfg.varKey}.${slotVarName}.playSFX`,
              true,
              0,
              null,
              featureNode,
              null,
              null,
              true,
              false,
              true,
            );
            forceUpdateComponent?.();
            const reEnableMatch = findTimelineAudio(slotVarName);
            if (reEnableMatch) { activateTimelineAudioGroup({ ...reEnableMatch, selectedFileIndex: 0 }); }
            return;
          }

          if (Array.isArray(valsArray)) {
            // Build a minimal node so addVal can record history and save
            const fData = featureNode.featureData || findFeatureData(campaignData || campaignDataProp, featureNode?.id);
            const candidateCustomisationSets = [
              fData?.feature?.customisation,
              featureNode?.featureData?.feature?.customisation,
              findFeatureData(campaignData || campaignDataProp, featureNode?.id)?.feature?.customisation,
            ].filter(Boolean);
            const seederVarConfig = (() => {
              for (const customisationSet of candidateCustomisationSets) {
                const tab = customisationSet?.find(t => t.tabKey === cfg.tabKey);
                const grp = tab?.options?.find(o => o.varKey === cfg.varKey);
                const variable = grp?.variables?.find(v => v.varName === slotVarName);
                if (variable) return variable;
              }
              return null;
            })();

            if (seederVarConfig) {
              const syntheticAddNode = {
                valsArray,
                varSchema: seederVarConfig,
                featureData: fData,
              };
              addVal(valsArray, seederVarConfig.valSchema, fData?.feature, forceUpdateComponent, syntheticAddNode);
              // Audio seed schemas often omit playSFX. New entries must be
              // explicitly enabled so they render as usable tracks (including
              // their delete control), rather than as disabled placeholders.
              const addedAudioEntry = valsArray[valsArray.length - 1];
              if (addedAudioEntry && typeof addedAudioEntry === 'object') {
                addedAudioEntry.playSFX = true;
                triggerSaveFeatureData(featureNode.featureData?._id);
                forceUpdateComponent?.();
              }

            } else {
              const templateEntry = Array.isArray(valsArray) && valsArray.length > 0
                ? JSON.parse(JSON.stringify(valsArray[valsArray.length - 1]))
                : null;
              if (templateEntry) {
                const syntheticAddNode = {
                  valsArray,
                  featureData: fData,
                };
                Object.keys(templateEntry).forEach((prop) => {
                  const value = templateEntry[prop];
                  if (value && typeof value === 'object' && !Array.isArray(value) && (value.type === 'audio' || Object.prototype.hasOwnProperty.call(value, 'filename'))) {
                    templateEntry[prop] = {
                      ...value,
                      filename: null,
                      originalFile: null,
                      srcUrl: null,
                      newFile: undefined,
                    };
                    return;
                  }
                  if (typeof value === 'string' && prop.toLowerCase().includes('filename')) {
                    templateEntry[prop] = '';
                    return;
                  }
                  if (prop === 'playSFX') {
                    templateEntry[prop] = true;
                  }
                });
                templateEntry.playSFX = true;
                valsArray.push(templateEntry);
                if (undoRedoHook?.recordCustomisationValueAdded) {
                  undoRedoHook.recordCustomisationValueAdded(syntheticAddNode, templateEntry, valsArray);
                }
                if (Array.isArray(fData?.feature?.customisation)) {
                  triggerSaveFeatureData(featureNode.featureData?._id);
                } else {
                  console.warn('[NodeEditor] Skipping save — feature document missing from DB for:', featureNode.featureData?._id);
                }
                forceUpdateComponent?.();
                console.warn('[NodeEditor][AudioDebug] addVal-fallback-template-entry', {
                  stepId: step?.stepId,
                  slotVarName,
                  valsLenAfter: valsArray.length,
                });
              } else {
                console.warn('[NodeEditor][AudioDebug] addVal-skipped-no-seederVarConfig', {
                  stepId: step?.stepId,
                  slotVarName,
                });
              }
            }
          }
        }

        // Open the timeline audio editor for this slot
        if (slotVarName) {
          const match = findTimelineAudio(slotVarName);
          if (match) { activateTimelineAudioGroup({ ...match, selectedFileIndex: null }); return; }
        }
      } else {
        // Existing file row clicked — open the timeline audio editor for this slot
        if (slotVarName) {
          const match = findTimelineAudio(slotVarName);
          if (match) { activateTimelineAudioGroup({ ...match, selectedFileIndex: fileIndex }); return; }
        }
      }
      // Outer card clicked with no specific slot — navigate right panel to opensFeatureSettings
      if (!slotVarName) {
        const findSettingsNodeLocal = (nodes, name) => {
          for (const n of (nodes || [])) {
            if (n?.name === name) return n;
            if (n?.subNodes?.length) { const f = findSettingsNodeLocal(n.subNodes, name); if (f) return f; }
          }
          return null;
        };
        const settingsNodeName = step?.opensFeatureSettings;
        const settingsNode = settingsNodeName
          ? findSettingsNodeLocal(featureNode.subNodes || [], settingsNodeName)
          : null;
        const audioTargetNode = settingsNode || featureNode;
        const audioLatestVars = customisationVarsRef.current?.current?.[featureNode.id]
          || customisationVars?.current?.[featureNode.id]
          || featureNode.customisationVars || {};
        audioTargetNode.customisationVars = audioLatestVars;
        if (audioTargetNode.topNode) audioTargetNode.topNode.customisationVars = audioLatestVars;
        if (audioTargetNode.parent) audioTargetNode.parent.customisationVars = audioLatestVars;
        setSelectedFeatureNode(featureNode);
        setSelectedFeatureId(featureNode.id);
        activateNode(audioTargetNode);
      }
      return; // timeline audio handled — don't fall through to generic logic
    }
    // ── end timeline audio early-exit ──────────────────────────────────────────

    const isObjectSelection = graphicIndex && typeof graphicIndex === 'object' && !Array.isArray(graphicIndex);
    const goalSelection = isObjectSelection && Object.prototype.hasOwnProperty.call(graphicIndex, 'goalIndex')
      ? graphicIndex
      : null;
    const itemFieldSelection = isObjectSelection
      && !goalSelection
      && Object.prototype.hasOwnProperty.call(graphicIndex, 'itemIndex')
      ? graphicIndex
      : null;
    const goalIndex = typeof goalSelection?.goalIndex === 'number' ? goalSelection.goalIndex : null;
    const goalField = goalSelection?.field ?? null;
    const itemFieldIndex = typeof itemFieldSelection?.itemIndex === 'number' ? itemFieldSelection.itemIndex : null;
    const itemFieldName = itemFieldSelection?.field ?? null;
    const fieldSettingsSelection = isObjectSelection && !goalSelection && !itemFieldSelection
      && Object.prototype.hasOwnProperty.call(graphicIndex, 'fieldSettings')
      ? graphicIndex
      : null;
    const fieldSettingsName = fieldSettingsSelection?.fieldSettings ?? null;
    const rewardFieldSet = new Set([
      'goalName',
      'goalImageFilename',
      'triggerDisruptorFeatureID',
      'pauseProgress',
      'endPreviousDisruptors',
      'sfxFilename',
    ]);
    const preferredGoalGroupName = goalField && rewardFieldSet.has(goalField) ? 'Rewards' : 'Goals';

    // Generic recursive search helpers
    const findNodeByName = (nodes, name) => {
      for (const n of nodes) {
        if (n.name === name) return n;
        if (n.subNodes?.length) { const f = findNodeByName(n.subNodes, name); if (f) return f; }
      }
      return null;
    };

    // ── fieldSettings early-exit ──────────────────────────────────────────────
    // Used by InfoBoxNode fields to navigate to a specific named settings group.
    if (fieldSettingsName) {
      const fsLatestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars || {};
      const fsTarget = findNodeByName(featureNode.subNodes || [], fieldSettingsName) || featureNode;
      fsTarget.customisationVars = fsLatestVars;
      if (fsTarget.topNode) fsTarget.topNode.customisationVars = fsLatestVars;
      if (fsTarget.parent) fsTarget.parent.customisationVars = fsLatestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(fsTarget);
      return;
    }
    // ── end fieldSettings early-exit ──────────────────────────────────────────

    const nodeContainsField = (node, fieldName) => {
      if (!node || !fieldName) return false;

      const children = Array.isArray(node?.config?.children) ? node.config.children : [];
      for (const child of children) {
        const fields = Array.isArray(child?.fields) ? child.fields : [];
        for (const field of fields) {
          const dataLink = field?.customFieldConfig?.dataLink || field?.dataLink;
          if (typeof dataLink === 'string' && (dataLink === fieldName || dataLink.endsWith(`.${fieldName}`))) {
            return true;
          }
        }
      }

      return false;
    };

    const findNodeContainingField = (node, fieldName) => {
      if (!node || !fieldName) return null;
      if (nodeContainsField(node, fieldName)) return node;
      for (const childNode of (node.subNodes || [])) {
        const found = findNodeContainingField(childNode, fieldName);
        if (found) return found;
      }
      return null;
    };

    const nodeHasModuleChildren = (node) => Array.isArray(node?.config?.children)
      && node.config.children.some(child => child?.type === 'module');

    const findNearestRenderableGroup = (node) => {
      let current = node;
      while (current && !nodeHasModuleChildren(current)) {
        current = current.parent;
      }
      return current || null;
    };

    const findNamedGroupByDataLink = (nodes, name, prefix) => {
      for (const n of nodes) {
        const dl = n.config?.groupSettings?.dataLink;
        if (n.name === name && typeof dl === 'string' && dl.startsWith(prefix)) return n;
        if (n.subNodes?.length) {
          const found = findNamedGroupByDataLink(n.subNodes, name, prefix);
          if (found) return found;
        }
      }
      return null;
    };

    const resolveSubBombTargetNode = () => {
      if (!String(step?.stepId || '').startsWith('sb_')) return null;
      const nodes = featureNode.subNodes || [];

      if (step.stepId === 'sb_allow_explosions_decision') {
        return settingsSubNode
          || findNodeByNameWithModules(nodes, 'Image & Animation')
          || findNodeByName(nodes, 'Image & Animation')
          || findNodeContainingField(featureNode, 'explosionStyleChoice');
      }

      if (cfg?.meta?.subBombDisplaySettings) {
        if (cfg?.varKey === 'headerSettings') {
          const headerGroup = findNodeByName(nodes, 'Header');
          return findNodeByName(headerGroup?.subNodes || [], 'Header Text')
            || findNodeByName(headerGroup?.subNodes || [], 'Header Image')
            || findNearestRenderableGroup(findNodeContainingField(headerGroup || featureNode, 'copy.headerSettings.copy.copy'))
            || findNearestRenderableGroup(findNodeContainingField(headerGroup || featureNode, 'copy.headerSettings.image.filename'))
            || headerGroup
            || groupNode;
        }

        const subTextGroup = findNodeByName(nodes, 'Sub Text');
        return findNodeByName(subTextGroup?.subNodes || [], 'Sub Text Text')
          || findNodeByName(subTextGroup?.subNodes || [], 'Sub Text Image')
          || findNearestRenderableGroup(findNodeContainingField(subTextGroup || featureNode, 'copy.subTextSettings.copy.copy'))
          || findNearestRenderableGroup(findNodeContainingField(subTextGroup || featureNode, 'copy.subTextSettings.image.filename'))
          || subTextGroup
          || groupNode;
      }

      if (cfg?.meta?.subBombSpeedControl) {
        return findNodeByName(nodes, 'Counter')
          || findNearestRenderableGroup(findNodeContainingField(featureNode, 'copy.counterCopy.timing.countSpeed'))
          || groupNode;
      }

      if (cfg?.meta?.audioStack) {
        const targetVarName = slotVarName || cfg?.variables?.[0]?.variableName;
        return (targetVarName && findNodeContainingField(featureNode, targetVarName))
          || findNodeByNameWithModules(nodes, 'Choose Audio')
          || findNodeByName(nodes, 'Choose Audio')
          || groupNode;
      }

      return null;
    };

    // Walk the subNodes tree to find the group whose dataLink starts with "tabKey.varKey"
    const dataLinkPrefix = cfg ? `${cfg.tabKey}.${cfg.varKey}` : null;
    const primaryVariableName = cfg?.variables?.length === 1 ? cfg.variables[0]?.variableName : null;
    const variableDataLink = (dataLinkPrefix && primaryVariableName) ? `${dataLinkPrefix}.${primaryVariableName}` : null;
    // Detect slot-level navigation from pollAudio multi-slot nodes: { slotVarName, fileIndex }
    const slotInfo = isObjectSelection && !goalSelection && !itemFieldSelection ? graphicIndex : null;
    const slotVarName = slotInfo?.slotVarName ?? null;
    const itemIndex = goalSelection
      ? goalIndex
      : (itemFieldSelection ? itemFieldIndex : (slotInfo ? slotInfo.fileIndex : graphicIndex));
    const exactDataLink = (dataLinkPrefix && slotVarName) ? `${dataLinkPrefix}.${slotVarName}` : null;

    const findGroupNode = (nodes, preferredName = null) => {
      let fallbackMatch = null;
      let exactMatch = null;
      for (const n of nodes) {
        const dl = n.config?.groupSettings?.dataLink;
        // When a variableDataLink is available (e.g. "particleOptions.confettiSettings.colours")
        // only accept exact or variableDataLink-prefixed matches.  Do NOT fall through to the
        // broader dataLinkPrefix startsWith check — that would incorrectly match sibling groups
        // that share the same tabKey/varKey (e.g. customImages matching when we want colours).
        const matches = exactDataLink
          ? dl === exactDataLink
          : (variableDataLink ? dl === variableDataLink : false)
            || (!exactDataLink && variableDataLink && dl?.startsWith(variableDataLink))
            || (!exactDataLink && !variableDataLink && dataLinkPrefix && dl?.startsWith(dataLinkPrefix));

        // Track exact variableDataLink matches separately so they defeat the fallback
        if (!exactDataLink && variableDataLink && dl === variableDataLink && !exactMatch) {
          exactMatch = n;
        }

        if (matches) {
          if (preferredName && n.name === preferredName) return n;
          if (!fallbackMatch) fallbackMatch = n;
        }
        if (n.subNodes?.length) {
          const found = findGroupNode(n.subNodes, preferredName);
          if (found) return found;
        }
      }
      // Prefer an exact variableDataLink match over a broader prefix fallback
      return exactMatch || fallbackMatch;
    };

    // ── Winner nodes: navigate tree by name since their groupSettings.dataLink is null ──
    const teamWinnerFilter = cfg?.meta?.teamWinnerFilter;
    if (typeof teamWinnerFilter === 'number') {
      // The workspace calls this group "Winner Options". Older flow code used
      // "Winner Graphics", which silently fell back to the top feature node
      // and left the right sidebar empty.
      const winnerGraphicsNode = findNodeByName(featureNode.subNodes || [], 'Winner Options')
        || findNodeByName(featureNode.subNodes || [], 'Winner Graphics');
      const childNodes = winnerGraphicsNode?.subNodes || [];
      const targetChild = childNodes[teamWinnerFilter] || winnerGraphicsNode || featureNode;

      // The waveform inside a winner card is a shortcut to the SFX controls
      // on that same winning-hand setup. Keeping the editor target identical
      // to the graphic controls makes the toggle and file picker available
      // without adding a detached audio branch to the flow.
      if (graphicIndex?.winnerAudio) {
        setSelectedFeatureNode(featureNode);
        setSelectedFeatureId(featureNode.id);
        activateNode(targetChild);
        // Clicking the empty/custom waveform should open exactly the same
        // audio picker used by the sidebar's Replace Audio button.
        const winnerVarName = cfg?.variables?.[0]?.variableName;
        const audioDataLink = winnerVarName
          ? `${cfg.tabKey}.${cfg.varKey}.${winnerVarName}.audioFilename`
          : null;
        if (winnerVarName && audioDataLink) {
          const currentWinnerVars = customisationVarsRef.current?.current?.[featureNode.id]
            || customisationVars?.current?.[featureNode.id]
            || featureNode.customisationVars
            || {};
          const storedWinnerAudio = currentWinnerVars?.[cfg.tabKey]?.[cfg.varKey]?.[winnerVarName]?.[0]?.audioFilename;
          const winnerAudio = storedWinnerAudio && typeof storedWinnerAudio === 'object'
            ? { ...storedWinnerAudio }
            : storedWinnerAudio ? { filename: storedWinnerAudio, originalFile: storedWinnerAudio } : {};
          // Let the winner settings panel mount before opening its shared
          // uploader. The previous zero-delay call could be overwritten by
          // the panel's own selection update.
          setTimeout(() => {
            slideOutPanelControl.openAudioUploader?.(
              winnerAudio,
              (updatedAudio) => handleFlowFieldChange(cfg.tabKey, cfg.varKey, winnerVarName, 0, 'audioFilename', updatedAudio),
              () => {},
              'Choose Winner Audio',
              { dataLink: audioDataLink },
              100
            );
          }, 100);
        }
        return;
      }

      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(targetChild);
      return;
    }

    const namedGoalsGroupNode = dataLinkPrefix
      ? findNamedGroupByDataLink(featureNode.subNodes || [], 'Goals', dataLinkPrefix)
      : null;
    const namedRewardsGroupNode = dataLinkPrefix
      ? findNamedGroupByDataLink(featureNode.subNodes || [], 'Rewards', dataLinkPrefix)
      : null;

    let groupNode = null;
    if (dataLinkPrefix) {
      if (goalSelection && preferredGoalGroupName === 'Rewards' && namedRewardsGroupNode?.config?.groupSettings?.dataLink?.startsWith(dataLinkPrefix)) {
        groupNode = namedRewardsGroupNode;
      } else if ((!goalSelection || preferredGoalGroupName === 'Goals') && namedGoalsGroupNode?.config?.groupSettings?.dataLink?.startsWith(dataLinkPrefix)) {
        groupNode = namedGoalsGroupNode;
      } else {
        groupNode = findGroupNode(featureNode.subNodes || [], goalSelection ? preferredGoalGroupName : null);
      }
    }

    if (!groupNode && cfg?.meta?.videoStack && variableDataLink) {
      groupNode = findNamedGroupByDataLink(featureNode.subNodes || [], 'Videos', variableDataLink)
        || findGroupNode(featureNode.subNodes || [], 'Videos');
    }

    // For pollAudio steps the audio variables live in a customisation TAB (not in
    // customisation.groups), so findGroupNode returns null. Build a synthetic node
    // that provides the dataLink so getSubNodes can resolve the live data array.
    if (!groupNode && cfg?.meta?.pollAudio && dataLinkPrefix) {
      const fId = featureNode?.id;
      const liveCVars = (fId && customisationVars?.current?.[fId]) ?? (fId && customisationVars?.default?.[fId]) ?? {};
      groupNode = {
        id: `${fId}-synthetic-audio-${exactDataLink || dataLinkPrefix}`,
        path: `${fId}-synthetic-audio-${exactDataLink || dataLinkPrefix}`,
        name: slotVarName || cfg.varKey,
        config: { groupSettings: { dataLink: exactDataLink || dataLinkPrefix, useDynamicNaming: false } },
        featureData: featureNode.featureData,
        customisationVars: liveCVars,
        topNode: featureNode,
        hasChildren: true,
        dynamicSubNodes: true,
        subNodes: [],
        subNodesToPreserve: [],
        depth: 1,
        parent: featureNode,
      };
    }

    // For circle/node-type steps (no cfg → no groupNode), use the opensFeatureSettings
    // value from the step (set in the seeder) to find the named settings subNode.
    // This is fully data-driven — different features/steps can point to different panels.
    // Fallback: some DB feature definitions lose opensFeatureSettings.
    // Keep start node click opening the main Settings panel.
    const inferredParticleSettingsName = cfg?.meta?.particleSettings
      ? (cfg?.varKey === 'confettiSettings'
        ? 'Confetti'
        : (cfg?.varKey === 'fireworksSettings' ? 'Fireworks' : null))
      : (cfg?.meta?.particleTimer
        ? (step?.stepId?.startsWith('fw_') ? 'Fireworks Settings' : 'Confetti Settings')
        : null);
    const settingsNodeName = inferredParticleSettingsName || step?.opensFeatureSettings || (step?.stepId === 'start_node' ? 'Settings' : null);
    const findNodeByNameWithModules = (nodes, targetName) => {
      for (const n of (nodes || [])) {
        if (n?.name === targetName && nodeHasModuleChildren(n)) return n;
        if (n?.subNodes?.length) {
          const found = findNodeByNameWithModules(n.subNodes, targetName);
          if (found) return found;
        }
      }
      return null;
    };
    const settingsNodeCandidates = typeof settingsNodeName === 'string'
      ? Array.from(new Set([
          settingsNodeName,
          settingsNodeName.replace(/\s+Settings$/i, '').trim(),
        ].filter(Boolean)))
      : [];
    const settingsSubNode = settingsNodeName
      ? (typeof settingsNodeName === 'string'
          ? (settingsNodeCandidates
              .map(name => findNodeByNameWithModules(featureNode.subNodes || [], name) || findNodeByName(featureNode.subNodes || [], name))
              .find(Boolean) || null)
          : (featureNode.subNodes || []).find(n => !n.isAddButton))
      : null;

    const explicitSubBombTargetNode = resolveSubBombTargetNode();

    const particleSettingsTarget = cfg?.meta?.particleSettings && settingsNodeName
      ? (typeof settingsNodeName === 'string'
          ? (findNodeByNameWithModules(featureNode.subNodes || [], settingsNodeName)
              || findNodeByName(featureNode.subNodes || [], settingsNodeName))
          : (featureNode.subNodes || []).find(n => !n.isAddButton))
      : null;

    // ── particleSettings early-exit ────────────────────────────────────────────
    // The settings summary card (fw_settings_node / confetti_settings_node) must
    // open a specific settings panel, NOT trigger groupNode add-item logic.
    if (cfg?.meta?.particleSettings) {
      const particleGroupName = cfg?.varKey === 'confettiSettings' ? 'Confetti'
        : cfg?.varKey === 'fireworksSettings' ? 'Fireworks'
        : null;
      // featureNode.subNodes[0] is the outer wrapper group (also named 'Fireworks'/'Confetti')
      // which maps to the top-level Particles group. Inside IT is the real settings group
      // with the same name that contains General Settings, Explosion Settings, etc.
      // We need the INNER match, not the outer wrapper.
      const outerNode = particleGroupName
        ? (findNodeByNameWithModules(featureNode.subNodes || [], particleGroupName) || findNodeByName(featureNode.subNodes || [], particleGroupName))
        : null;
      const innerNode = outerNode
        ? (findNodeByNameWithModules(outerNode.subNodes || [], particleGroupName) || findNodeByName(outerNode.subNodes || [], particleGroupName))
        : null;
      const resolvedParticleNode = innerNode || outerNode || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      resolvedParticleNode.customisationVars = latestVars;
      if (resolvedParticleNode.topNode) resolvedParticleNode.topNode.customisationVars = latestVars;
      if (resolvedParticleNode.parent) resolvedParticleNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedParticleNode);
      return;
    }
    // ── end particleSettings early-exit ────────────────────────────────────────

    // ── particleTimer early-exit ───────────────────────────────────────────────
    // The duration/timer node should open the same settings panel as the start title.
    if (cfg?.meta?.particleTimer) {
      const resolvedNode = (typeof settingsNodeName === 'string'
        ? (settingsNodeCandidates
            .map(name => findNodeByNameWithModules(featureNode.subNodes || [], name) || findNodeByName(featureNode.subNodes || [], name))
            .find(Boolean) || null)
        : null) || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      resolvedNode.customisationVars = latestVars;
      if (resolvedNode.topNode) resolvedNode.topNode.customisationVars = latestVars;
      if (resolvedNode.parent) resolvedNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedNode);
      return;
    }
    // ── end particleTimer early-exit ───────────────────────────────────────────

    // ── videoSettings early-exit ──────────────────────────────────────────────
    // The video settings summary card should navigate to the live-controls group,
    // not the dynamic Videos list.
    if (cfg?.meta?.videoSettings) {
      const targetNames = settingsNodeCandidates.length
        ? settingsNodeCandidates
        : ['Video Controls', 'Controls'];
      const resolvedNode = settingsSubNode || targetNames
        .map(name => findNodeByNameWithModules(featureNode.subNodes || [], name) || findNodeByName(featureNode.subNodes || [], name))
        .find(Boolean)
        || featureNode;
      const currentVarsForVideo = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      const defaultVarsForVideo = customisationVarsRef.current?.default?.[featureNode.id]
        || customisationVars?.default?.[featureNode.id]
        || {};
      const latestVars = { ...currentVarsForVideo };
      for (const tab of Object.keys(defaultVarsForVideo)) {
        if (latestVars[tab] === undefined) {
          latestVars[tab] = { ...defaultVarsForVideo[tab] };
        }
      }
      if (featureNode.id && customisationVarsRef.current?.current) {
        customisationVarsRef.current.current[featureNode.id] = latestVars;
      }
      resolvedNode.customisationVars = latestVars;
      if (resolvedNode.topNode) resolvedNode.topNode.customisationVars = latestVars;
      if (resolvedNode.parent) resolvedNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedNode);
      return;
    }
    // ── end videoSettings early-exit ──────────────────────────────────────────

    // ── countdownSettings early-exit ──────────────────────────────────────────
    // Opening the countdown type selector node should navigate to the
    // "Countdown View" settings group (which contains the "Countdown Type" module).
    if (cfg?.meta?.countdownSettings) {
      const targetName = 'Countdown View';
      const resolvedNode = findNodeByNameWithModules(featureNode.subNodes || [], targetName)
        || findNodeByName(featureNode.subNodes || [], targetName)
        || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      resolvedNode.customisationVars = latestVars;
      if (resolvedNode.topNode) resolvedNode.topNode.customisationVars = latestVars;
      if (resolvedNode.parent) resolvedNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedNode);
      return;
    }
    // ── end countdownSettings early-exit ──────────────────────────────────────

    // ── countdownTimer early-exit ─────────────────────────────────────────────
    // Opening the HMS timer node should navigate to the HMS countdown group.
    if (cfg?.meta?.countdownTimer) {
      const targetName = 'HMS Countdown';
      const resolvedNode = findNodeByNameWithModules(featureNode.subNodes || [], targetName)
        || findNodeByName(featureNode.subNodes || [], targetName)
        || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      resolvedNode.customisationVars = latestVars;
      if (resolvedNode.topNode) resolvedNode.topNode.customisationVars = latestVars;
      if (resolvedNode.parent) resolvedNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedNode);
      return;
    }
    // ── end countdownTimer early-exit ─────────────────────────────────────────

    // ── slotReelsCount early-exit ─────────────────────────────────────────────
    // Clicking the Reels Count flow node (or its inline 1-5 buttons) should open
    // the "Slots Settings" settings group which contains the Reels Count field.
    if (step?.stepId === 'slot_reels_settings') {
      const targetName = 'Slots Settings';
      const resolvedNode = findNodeByNameWithModules(featureNode.subNodes || [], targetName)
        || findNodeByName(featureNode.subNodes || [], targetName)
        || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      resolvedNode.customisationVars = latestVars;
      if (resolvedNode.topNode) resolvedNode.topNode.customisationVars = latestVars;
      if (resolvedNode.parent) resolvedNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedNode);
      return;
    }
    // ── end slotReelsCount early-exit ─────────────────────────────────────────

    // ── slotReels image/panel early-exit ─────────────────────────────────────
    // The Reel Images node is a summary card, but each inner panel/image needs to
    // open the matching Options N group / item on the right rather than the
    // generic Slot Settings group.
    if (cfg?.meta?.slotReels && isObjectSelection && Object.prototype.hasOwnProperty.call(graphicIndex, 'reelIndex')) {
      const reelIndex = typeof graphicIndex?.reelIndex === 'number' ? graphicIndex.reelIndex : null;
      const reelItemIndex = typeof graphicIndex?.itemIndex === 'number' ? graphicIndex.itemIndex : null;
      const addItem = !!graphicIndex?.addItem;
      const optionsVarKey = reelIndex === null ? null : `options${reelIndex + 1}`;
      // Config names the group "Reel N" (inside "Randomizer Data"), not "Options N"
      const targetName = reelIndex === null ? null : `Reel ${reelIndex + 1}`;
      const targetPrefix = optionsVarKey ? `setup.${optionsVarKey}` : null;
      const resolvedGroup = (targetName && targetPrefix)
        ? (findNamedGroupByDataLink(featureNode.subNodes || [], targetName, targetPrefix)
            || findNodeByNameWithModules(featureNode.subNodes || [], targetName)
            || findNodeByName(featureNode.subNodes || [], targetName))
        : null;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};

      let reelTargetNode = resolvedGroup || featureNode;
      if (resolvedGroup && addItem) {
        try {
          resolvedGroup.customisationVars = latestVars;
          if (resolvedGroup.topNode) resolvedGroup.topNode.customisationVars = latestVars;
          const dynamicSubNodes = getSubNodes(resolvedGroup, []);
          const addBtn = (dynamicSubNodes || []).find(n => n.isAddButton);
          if (addBtn) {
            addVal(addBtn.valsArray, addBtn.varSchema?.valSchema, addBtn.featureData?.feature, forceUpdateComponent, addBtn);
            const freshSubNodes = getSubNodes(resolvedGroup, []);
            const freshItems = (freshSubNodes || []).filter(n => !n.isAddButton);
            if (freshItems.length > 0) reelTargetNode = freshItems[freshItems.length - 1];
          }
        } catch (e) {
          console.warn('[slotReels add]', e);
        }
      } else if (resolvedGroup && reelItemIndex !== null) {
        try {
          resolvedGroup.customisationVars = latestVars;
          if (resolvedGroup.topNode) resolvedGroup.topNode.customisationVars = latestVars;
          const subNodes = getSubNodes(resolvedGroup, []);
          const items = (subNodes || []).filter(n => !n.isAddButton);
          if (reelItemIndex < items.length) reelTargetNode = items[reelItemIndex];
        } catch (e) {
          console.warn('[slotReels navigate]', e);
        }
      }

      reelTargetNode.customisationVars = latestVars;
      if (reelTargetNode.topNode) reelTargetNode.topNode.customisationVars = latestVars;
      if (reelTargetNode.parent) reelTargetNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(reelTargetNode);
      return;
    }
    // ── end slotReels image/panel early-exit ─────────────────────────────────

    // ── slotResults combo/group early-exit ───────────────────────────────────
    // The Winning Results node is a summary card over the dynamic Result Triggers
    // group. Each row click must open the matching dynamic result entry on the
    // right rather than falling back to the generic feature node.
    if (cfg?.meta?.slotResults) {
      const targetName = 'Result Triggers';
      const targetPrefix = 'setup.resultTriggers.setup';
      const resolvedGroup = findNamedGroupByDataLink(featureNode.subNodes || [], targetName, targetPrefix)
        || findNodeByNameWithModules(featureNode.subNodes || [], targetName)
        || findNodeByName(featureNode.subNodes || [], targetName)
        || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};

      let resultTargetNode = resolvedGroup;
      if (resolvedGroup && typeof itemIndex === 'number' && itemIndex >= 0) {
        try {
          resolvedGroup.customisationVars = latestVars;
          if (resolvedGroup.topNode) resolvedGroup.topNode.customisationVars = latestVars;
          const subNodes = getSubNodes(resolvedGroup, []);
          const items = (subNodes || []).filter(n => !n.isAddButton);
          if (itemIndex < items.length) resultTargetNode = items[itemIndex];
        } catch (e) {
          console.warn('[slotResults navigate]', e);
        }
      }

      resultTargetNode.customisationVars = latestVars;
      if (resultTargetNode.topNode) resultTargetNode.topNode.customisationVars = latestVars;
      if (resultTargetNode.parent) resultTargetNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resultTargetNode);
      return;
    }
    // ── end slotResults combo/group early-exit ───────────────────────────────

    // ── triviaCards rounds early-exit ────────────────────────────────────────
    // When TriviaCardsNode fires with { roundVarKey, questionIdx },
    // { roundVarKey, add: true }, or { roundVarKey, roundClick: true },
    // navigate to the correct round setup or questions group.
    if (cfg?.meta?.triviaCards && isObjectSelection && graphicIndex?.roundVarKey) {
      const { roundVarKey, questionIdx, add, roundClick, field } = graphicIndex;
      const roundRootDataLink = `setup.${roundVarKey}`;
      const targetDataLink = roundClick
        ? `${roundRootDataLink}.roundSetup`
        : `${roundRootDataLink}.questions`;
      const targetFieldDataLink = roundClick
        ? `${targetDataLink}.roundName`
        : (field ? `${targetDataLink}.${field}` : null);



      const findGroupByDataLink = (nodes, dl) => {
        for (const n of (nodes || [])) {
          if (n.config?.groupSettings?.dataLink === dl) return n;
          const nested = findGroupByDataLink(n.subNodes, dl);
          if (nested) return nested;
        }
        return null;
      };

      const nodeContainsDataLink = (node, dataLinkToMatch) => {
        if (!node || !dataLinkToMatch) return false;
        const directFields = Array.isArray(node?.config?.fields) ? node.config.fields : [];
        for (const fieldConfig of directFields) {
          const dataLink = fieldConfig?.customFieldConfig?.dataLink || fieldConfig?.dataLink;
          if (typeof dataLink === 'string' && dataLink === dataLinkToMatch) {
            return true;
          }
        }
        const children = Array.isArray(node?.config?.children) ? node.config.children : [];
        for (const child of children) {
          const fields = Array.isArray(child?.fields) ? child.fields : [];
          for (const fieldConfig of fields) {
            const dataLink = fieldConfig?.customFieldConfig?.dataLink || fieldConfig?.dataLink;
            if (typeof dataLink === 'string' && dataLink === dataLinkToMatch) {
              return true;
            }
          }
        }
        return false;
      };

      const findNodeContainingDataLink = (node, dataLinkToMatch) => {
        if (!node || !dataLinkToMatch) return null;
        if (nodeContainsDataLink(node, dataLinkToMatch)) return node;
        for (const childNode of (node.subNodes || [])) {
          const found = findNodeContainingDataLink(childNode, dataLinkToMatch);
          if (found) return found;
        }
        return null;
      };

      let roundGroupNode = findGroupByDataLink(featureNode.subNodes || [], targetDataLink);
      if (!roundGroupNode && roundClick && targetFieldDataLink) {
        roundGroupNode = findNodeContainingDataLink(featureNode, targetFieldDataLink);
      }

      if (roundGroupNode) {
        const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
          || customisationVars?.current?.[featureNode.id]
          || featureNode.customisationVars
          || {};
        roundGroupNode.customisationVars = latestVars;
        if (roundGroupNode.topNode) roundGroupNode.topNode.customisationVars = latestVars;

        let targetNode = roundGroupNode;

        if (add) {
          try {
            const subNodes = getSubNodes(roundGroupNode, []);
            const addBtn = (subNodes || []).find((n) => n.isAddButton);
            if (addBtn) {
              addVal(addBtn.valsArray, addBtn.varSchema?.valSchema, addBtn.featureData?.feature, forceUpdateComponent, addBtn);
              const freshSubNodes = getSubNodes(roundGroupNode, []);
              const freshItems = (freshSubNodes || []).filter((n) => !n.isAddButton);
              if (freshItems.length > 0) targetNode = freshItems[freshItems.length - 1];
            }
          } catch (e) {
            console.warn('[triviaCards add navigate]', e);
          }
        } else if (typeof questionIdx === 'number' && questionIdx >= 0) {
          try {
            const subNodes = getSubNodes(roundGroupNode, []);
            const items = (subNodes || []).filter((n) => !n.isAddButton);
            if (questionIdx < items.length) targetNode = items[questionIdx];
          } catch (e) {
            console.warn('[triviaCards navigate]', e);
          }
        }

        if (targetFieldDataLink) {
          targetNode = findNodeContainingDataLink(targetNode, targetFieldDataLink) || targetNode;
        }



        targetNode.customisationVars = latestVars;
        if (targetNode.topNode) targetNode.topNode.customisationVars = latestVars;
        if (targetNode.parent) targetNode.parent.customisationVars = latestVars;
        setSelectedFeatureNode(featureNode);
        setSelectedFeatureId(featureNode.id);
        pendingSidebarFieldDataLinkRef.current = targetFieldDataLink;
        setSelectedSidebarFieldDataLink(targetFieldDataLink || null);

        activateNode(targetNode);
        return;
      }


    }
    // ── end triviaCards rounds early-exit ─────────────────────────────────────


    // Only intercept explicit card clicks. A null itemIndex means the canvas +
    // button was clicked, and that must keep flowing into the generic add-item
    // path below so a new countdown entry is actually created.
    if ((cfg?.meta?.countdownImageStack || cfg?.meta?.countdownTextStack)
      && typeof itemIndex === 'number' && itemIndex >= 0) {
      const panelName = cfg?.meta?.countdownImageStack ? 'Image Options' : 'Text Options';
      // Prefer the dataLink-resolved group; fall back to name search.
      const cdGroupNode = groupNode
        || findNodeByNameWithModules(featureNode.subNodes || [], panelName)
        || findNodeByName(featureNode.subNodes || [], panelName);

      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};

      let cdTargetNode = cdGroupNode || featureNode;

      if (cdGroupNode) {
        try {
          const subNodes = getSubNodes(cdGroupNode, []);
          const items = (subNodes || []).filter(n => !n.isAddButton);
          if (itemIndex < items.length) cdTargetNode = items[itemIndex];
        } catch (e) {
          console.warn('[countdownStack navigate]', e);
        }
      }

      cdTargetNode.customisationVars = latestVars;
      if (cdTargetNode.topNode) cdTargetNode.topNode.customisationVars = latestVars;
      if (cdTargetNode.parent) cdTargetNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(cdTargetNode);
      return;
    }
    // ── end countdownImageStack / countdownTextStack early-exit ───────────────

    // ── Sub Bomb summary/settings cards and audio info early-exit ──────────────
    // These nodes represent existing settings groups. A null activation should
    // open the relevant settings panel, not be treated as an add-item action.
    if (explicitSubBombTargetNode) {
      const resolvedNode = explicitSubBombTargetNode || groupNode || settingsSubNode || featureNode;
      const latestVars = customisationVarsRef.current?.current?.[featureNode.id]
        || customisationVars?.current?.[featureNode.id]
        || featureNode.customisationVars
        || {};
      resolvedNode.customisationVars = latestVars;
      if (resolvedNode.topNode) resolvedNode.topNode.customisationVars = latestVars;
      if (resolvedNode.parent) resolvedNode.parent.customisationVars = latestVars;
      setSelectedFeatureNode(featureNode);
      setSelectedFeatureId(featureNode.id);
      activateNode(resolvedNode);
      return;
    }
    // ── end Sub Bomb summary/settings cards and audio info early-exit ─────────

    const prefersSettingsPanel = !!(cfg && step?.opensFeatureSettings);
    let targetNode = particleSettingsTarget
      || (prefersSettingsPanel ? (settingsSubNode || groupNode) : (groupNode || settingsSubNode))
      || featureNode;

    if (groupNode) {
      try {
        // Ensure groupNode has the latest vars before getSubNodes reads the data
        // array. leftListNewCustomisationItems is only built on initial load so
        // groupNode.customisationVars can be stale after items are added/removed.
        // Merge default + current so tabs that have never been edited (e.g. customVideo
        // before the first save) are still visible via their default values.
        const _freshCurrent = customisationVarsRef.current?.current?.[featureNode.id]
          || customisationVars?.current?.[featureNode.id]
          || groupNode.customisationVars
          || {};
        const _freshDefault = customisationVarsRef.current?.default?.[featureNode.id]
          || customisationVars?.default?.[featureNode.id]
          || {};
        const freshGroupVars = { ..._freshDefault, ..._freshCurrent };
        groupNode.customisationVars = freshGroupVars;
        if (groupNode.topNode) groupNode.topNode.customisationVars = freshGroupVars;

        const dynamicSubNodes = getSubNodes(groupNode, []);

        if (itemIndex === null) {
          // null → add a new entry (CTA click or "Add another file" click)
          const addBtn = (dynamicSubNodes || []).find(n => n.isAddButton);
          if (addBtn) {
            const prevLen = Array.isArray(addBtn.valsArray) ? addBtn.valsArray.length : 0;

            // addVal mutates valsArray in-place, records undo history and triggers save
            addVal(addBtn.valsArray, addBtn.varSchema?.valSchema, addBtn.featureData?.feature, forceUpdateComponent, addBtn);

            if (cfg?.meta?.teamFilter !== undefined && Array.isArray(addBtn.valsArray) && addBtn.valsArray.length > prevLen) {
              const newWaveItem = addBtn.valsArray[addBtn.valsArray.length - 1];
              const teamAssign = cfg?.meta?.teamAssign ?? cfg?.meta?.teamFilter;
              const handFilter = cfg?.meta?.handFilter;
              let patchedWaveDefaults = false;

              if (newWaveItem && typeof newWaveItem === 'object') {
                if (teamAssign !== undefined && newWaveItem.team !== teamAssign) {
                  newWaveItem.team = teamAssign;
                  patchedWaveDefaults = true;
                }

                if (handFilter !== undefined && handFilter !== null && newWaveItem.hand !== handFilter) {
                  newWaveItem.hand = handFilter;
                  patchedWaveDefaults = true;
                }
              }

              if (patchedWaveDefaults) {
                triggerSaveFeatureData(addBtn.featureData?._id || addBtn.featureData?.id);
                forceUpdateComponent?.();
              }
            }

            if (step?.customisationConfig?.meta?.goalsNode && Array.isArray(addBtn.valsArray) && addBtn.valsArray.length > prevLen) {
              const newGoalIndex = addBtn.valsArray.length - 1;
              const newGoal = (addBtn.valsArray[newGoalIndex] ||= {});
              if (!newGoal.goalName || !String(newGoal.goalName).trim()) {
                newGoal.goalName = `Goal ${newGoalIndex + 1}`;
                triggerSaveFeatureData(addBtn.featureData?._id || addBtn.featureData?.id);
                forceUpdateComponent?.();
              }
            }

            // Navigate to the freshly added sub-node
            const freshSubNodes = getSubNodes(groupNode, []);
            const freshItems = (freshSubNodes || []).filter(n => !n.isAddButton);
            if (freshItems.length > 0) {
              targetNode = freshItems[freshItems.length - 1];
            }
          }
        } else if (typeof itemIndex === 'number' && itemIndex >= 0) {
          const items = (dynamicSubNodes || []).filter(n => !n.isAddButton);
          if (itemIndex < items.length) {
            targetNode = items[itemIndex];
          }
        }
      } catch (e) {
        console.warn('[FlowGraphicActivate] Could not resolve sub-node:', e);
      }
    }

    if (goalSelection && targetNode) {
      const targetChildren = targetNode.subNodes || [];
      if (goalField && rewardFieldSet.has(goalField)) {
        const rewardChildName = goalField === 'sfxFilename' ? 'Goal Audio' : 'Reward Setup';
        targetNode = findNodeByName(targetChildren, rewardChildName) || targetNode;
      } else {
        targetNode = findNodeByName(targetChildren, 'Goal Setup') || targetNode;
      }
    }

    if (itemFieldSelection && targetNode) {
      targetNode = findNodeContainingField(targetNode, itemFieldName) || targetNode;
    }

    const _latestCurrent = customisationVarsRef.current?.current?.[featureNode.id]
      || customisationVars?.current?.[featureNode.id]
      || featureNode.customisationVars
      || {};
    const _latestDefault = customisationVarsRef.current?.default?.[featureNode.id]
      || customisationVars?.default?.[featureNode.id]
      || {};
    const latestFeatureVars = { ..._latestDefault, ..._latestCurrent };

    // ── Pre-patch customisationVars for solution-image activation ──────────────
    // The FlowCanvas already called onFlowBoolChange (which sets customisationVars
    // state) before calling onActivateNode via setTimeout. However the React state
    // batch means customisationVars.current still holds the OLD reference at this
    // point. We directly mutate the live object so the sidebar initialises with
    // solutionImageIsPuzzleImage = false on its first render without needing an
    // extra round-trip. The formal state is already updated by handleFlowBoolChange.
    if (
      itemFieldSelection &&
      itemFieldName === 'solutionImage' &&
      itemFieldIndex !== null &&
      cfg?.tabKey && cfg?.varKey && cfg?.variables?.[0]?.variableName
    ) {
      const varName2 = cfg.variables[0].variableName;
      const arr = latestFeatureVars?.[cfg.tabKey]?.[cfg.varKey]?.[varName2];
      if (Array.isArray(arr) && arr[itemFieldIndex]) {
        arr[itemFieldIndex].solutionImageIsPuzzleImage = false;
      }
    }
    // ── end pre-patch ──────────────────────────────────────────────────────────

    if (targetNode && typeof targetNode === 'object') {
      targetNode.customisationVars = latestFeatureVars;
      if (targetNode.topNode) targetNode.topNode.customisationVars = latestFeatureVars;
      if (targetNode.parent) targetNode.parent.customisationVars = latestFeatureVars;
    }

    // Set the feature context (required so EditablePreview / ModuleList renders correctly)
    setSelectedFeatureNode(featureNode);
    setSelectedFeatureId(featureNode.id);

    // Activate the node (highlights it in the sidebar).
    // Do NOT switch mode — stay in EXPANDED_FLOW so the flow canvas stays visible.
    activateNode(targetNode);
  };

  const handleFlowDisruptorActivate = (step) => {
    if (step?.customisationConfig?.meta?.activateAsNode) {
      handleFlowGraphicActivate(step, {
        itemIndex: 0,
        field: step?.customisationConfig?.meta?.activateField || 'featureID',
      });
      return;
    }

    const selectLink = resolveDisruptorSelectDataLink(step);
    if (!leftListNewCustomisationItems || !selectLink) return;

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    // Ensure the feature context is set so ModuleList renders correctly
    setSelectedFeatureNode(featureNode);
    setSelectedFeatureId(featureNode.id);

    if (step?.customisationConfig?.tabKey === 'hands' && step?.customisationConfig?.varKey === 'winningHands') {
      handleFlowGraphicActivate(step, { itemIndex: 0, field: 'featureID' });
      return;
    }

    // Find the matching disruptor config in the workspace timeline config
    let matched = null;
    const disruptorGroups = featureNode?.config?.timeline?.disruptorGroups || [];
    for (const group of disruptorGroups) {
      for (const disruptor of (group?.disruptors || [])) {
        const dl = disruptor?.['disruptor-select'] || disruptor?.disruptorData?.['disruptor-select'];
        if (dl === selectLink) {
          matched = { groupData: group, disruptorData: disruptor };
          break;
        }
      }
      if (matched) break;
    }

    if (matched) {
      activateTimelineDisruptorGroup(matched);
    } else {
      console.warn('[FlowDisruptorActivate] No matching disruptor group found for dataLink:', selectLink);
    }
  };

  const getDraggedItemVarKeys = useCallback((dragItem) => {
    if (!dragItem) return [];

    const keys = new Set();
    const pushKey = (value) => {
      if (typeof value === 'string' && value.trim()) keys.add(value.trim());
    };

    const pushWorkspaceFeatureKey = (value) => {
      if (typeof value !== 'string' || !value.trim()) return;
      const trimmed = value.trim();
      pushKey(trimmed);
      pushKey(WorkspaceConfig?.[trimmed]?.featureKey);
    };

    pushWorkspaceFeatureKey(dragItem.varKey);
    pushWorkspaceFeatureKey(dragItem.id);

    const campaignFeature = dragItem.isExistingCampaignFeature
      ? campaignFeatures.find((feature) => feature?._id === dragItem.campaignFeatureId)
      : null;
    const resolvedFeature = campaignFeature ? resolveCampaignFeatureObject(campaignFeature) : null;

    pushWorkspaceFeatureKey(campaignFeature?.configKey);
    pushWorkspaceFeatureKey(campaignFeature?.varKey);
    pushWorkspaceFeatureKey(resolvedFeature?.varKey);

    return Array.from(keys);
  }, [campaignFeatures, resolveCampaignFeatureObject]);

  const stepAcceptsDraggedItem = useCallback((step, dragItem) => {
    const acceptedVarKeys = step?.customisationConfig?.meta?.acceptedFeatureVarKeys;
    if (!Array.isArray(acceptedVarKeys) || acceptedVarKeys.length === 0) return true;

    const acceptedSet = new Set(
      acceptedVarKeys
        .filter((value) => typeof value === 'string' && value.trim())
        .flatMap((value) => {
          const trimmed = value.trim();
          const workspaceFeatureKey = WorkspaceConfig?.[trimmed]?.featureKey;
          return workspaceFeatureKey && workspaceFeatureKey !== trimmed
            ? [trimmed, workspaceFeatureKey]
            : [trimmed];
        })
    );

    const candidateKeys = getDraggedItemVarKeys(dragItem);
    return candidateKeys.some((value) => acceptedSet.has(value));
  }, [getDraggedItemVarKeys]);

  const resolveFlowDisruptorFieldTarget = useCallback((featureVars, selectLink, options = {}) => {
    if (!featureVars || typeof selectLink !== 'string' || !selectLink.trim()) return null;

    const { createMissing = false, preferArray = false } = options;
    const parts = selectLink.split('.');
    if (parts.length < 2) return null;

    const parentKeys = parts.slice(0, -1);
    const valueKey = parts[parts.length - 1];
    let current = featureVars;

    for (let index = 0; index < parentKeys.length; index++) {
      const key = parentKeys[index];
      const isLeafParent = index === parentKeys.length - 1;
      const nextValue = current?.[key];

      if (nextValue == null) {
        if (!createMissing) return null;
        current[key] = isLeafParent ? (preferArray ? [] : {}) : {};
      } else if (isLeafParent && preferArray && !Array.isArray(nextValue) && typeof nextValue === 'object') {
        current[key] = [nextValue];
      } else if (typeof nextValue !== 'object') {
        if (!createMissing) return null;
        current[key] = isLeafParent ? (preferArray ? [] : {}) : {};
      }

      current = current[key];
    }

    return {
      parent: current,
      valueKey,
      isArrayParent: Array.isArray(current),
    };
  }, []);

  const migrateWaveWinnerDisruptorBindings = useCallback((featureVars) => {
    if (!featureVars || typeof featureVars !== 'object') return false;

    const mappings = [
      {
        oldPath: 'triggerOptions.waveWinnerTriggers.team0WaveStart.triggerDisruptorFeatureID',
        newPath: 'hands.winningHands.team1hand1.featureID',
      },
      {
        oldPath: 'triggerOptions.waveWinnerTriggers.team1WaveStart.triggerDisruptorFeatureID',
        newPath: 'hands.winningHands.team2hand1.featureID',
      },
    ];

    let changed = false;

    for (const mapping of mappings) {
      const oldId = getDisruptorItemsFromDataLink(featureVars, mapping.oldPath)?.[0]?.id;
      if (!oldId) continue;

      const newId = getDisruptorItemsFromDataLink(featureVars, mapping.newPath)?.[0]?.id;
      if (newId) continue;

      const parts = mapping.newPath.split('.');
      let current = featureVars;
      for (let index = 0; index < parts.length - 1; index++) {
        const key = parts[index];
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }

      current[parts[parts.length - 1]] = oldId;
      changed = true;
    }

    return changed;
  }, []);

  useEffect(() => {
    const featureNode = getExpandedFlowFeatureNode();
    const featureId = featureNode?.featureData?._id || featureNode?.id || expandedFlowFeatureId;
    if (!featureId) return;

    const baseCustomisationVars = customisationVarsRef.current || customisationVars;
    const featureVars = baseCustomisationVars?.current?.[featureId];
    if (!featureVars) return;

    const changed = migrateWaveWinnerDisruptorBindings(featureVars);
    if (!changed) return;

    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };

    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);
    triggerSaveFeatureData(featureId, featureVars);
  }, [customisationVars, expandedFlowFeatureId, leftListNewCustomisationItems, migrateWaveWinnerDisruptorBindings]);

  const handleFlowDisruptorRemove = (step, removeIndex) => {
    const selectLink = resolveDisruptorSelectDataLink(step);
    if (!leftListNewCustomisationItems || !selectLink) return;

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    const featureId = featureNode.id;
    if (!customisationVars || !customisationVars.current) return;
    if (!customisationVars.current[featureId]) customisationVars.current[featureId] = {};
    const featureVars = customisationVars.current[featureId];
    const shouldUseArrayShape = Array.isArray(step?.populatedData?.variables?.[0]?.allValues);
    const target = resolveFlowDisruptorFieldTarget(featureVars, selectLink, {
      createMissing: false,
      preferArray: shouldUseArrayShape,
    });
    if (!target) return;

    if (target.isArrayParent) {
      const arr = target.parent;
      if (removeIndex < 0 || removeIndex >= arr.length) return;

      if (arr.length <= 1) {
        arr[0] = { ...(arr[0] || {}), [target.valueKey]: '' };
      } else {
        arr.splice(removeIndex, 1);
      }
    } else if (removeIndex === 0) {
      target.parent[target.valueKey] = '';
    }

    triggerSaveFeatureData(featureId);
    forceUpdateComponent();
  };

  const handleFlowDisruptorDrop = async (step, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const selectLink = resolveDisruptorSelectDataLink(step);
    if (!leftListNewCustomisationItems || !selectLink) return;

    const di = draggedItemRef.current || draggedItem;
    if (!di) return;

    if (!stepAcceptsDraggedItem(step, di)) {
      const acceptedLabel = step?.customisationConfig?.meta?.acceptedFeatureLabel || 'allowed component';
      const labelText = acceptedLabel.toLowerCase() === 'leaderboard'
        ? 'Only Leaderboards can be added here.'
        : `Only ${acceptedLabel} can be added here.`;
      toast.error(labelText);
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    const allowMultiple = step?.customisationConfig?.meta?.allowMultiple ?? step?.customisationConfig?.variables?.[0]?.allowMultiple ?? true;
    const maxMultiples = step?.customisationConfig?.meta?.maxMultiples ?? -1;

    // Guard: prevent self-loops — the feature cannot trigger itself
    const droppedVarKey = di.varKey || di.id;
    if (droppedVarKey && droppedVarKey === expandedFlowFeature) {
      toast.error('A feature cannot trigger itself — this would create an infinite loop.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    // Resolve (or create) the campaign feature ID for the dropped library item
    let newCampaignFeatureId = null;
    if (di.isExistingCampaignFeature) {
      newCampaignFeatureId = di.campaignFeatureId;
    } else {
      const contentLabel = di.label;
      try {
        newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, contentLabel);
      } catch (err) {
        console.error('[FlowDisruptorDrop] Failed to add feature to campaign:', err);
        toast.error('Failed to add feature to campaign');
        return;
      }
    }

    if (!newCampaignFeatureId) return;

    // Guard: prevent duplicates — same disruptor already assigned to this step
    const featureId_check = featureNode.id;
    if (customisationVars?.current?.[featureId_check]) {
      const existingItems = getDisruptorItemsFromDataLink(customisationVars.current[featureId_check], selectLink);
      const alreadyPresent = existingItems.some((item) => String(item.id) === String(newCampaignFeatureId));
      if (alreadyPresent) {
        toast.error('This disruptor is already assigned to this trigger.');
        draggedItemRef.current = null;
        setDraggedItem(null);
        setCurrentAction(ACTION_STATE.IDLE);
        return;
      }
    }

    const featureId = featureNode.id;
    if (!customisationVars || !customisationVars.current) return;
    if (!customisationVars.current[featureId]) customisationVars.current[featureId] = {};
    const featureVars = customisationVars.current[featureId];
    const shouldUseArrayShape = allowMultiple || Array.isArray(step?.populatedData?.variables?.[0]?.allValues);
    const target = resolveFlowDisruptorFieldTarget(featureVars, selectLink, {
      createMissing: true,
      preferArray: shouldUseArrayShape,
    });
    if (!target) return;

    if (!target.isArrayParent) {
      target.parent[target.valueKey] = newCampaignFeatureId;
    } else if (!allowMultiple) {
      target.parent[0] = {
        ...(target.parent[0] || {}),
        [target.valueKey]: newCampaignFeatureId,
      };
    } else {
      const arr = target.parent;
      let filled = false;
      for (let i = 0; i < arr.length; i++) {
        const cur = arr[i]?.[target.valueKey];
        if (!cur || (typeof cur === 'string' && cur.trim() === '')) {
          arr[i] = { ...(arr[i] || {}), [target.valueKey]: newCampaignFeatureId };
          filled = true;
          break;
        }
      }
      if (!filled) {
        if (typeof maxMultiples === 'number' && maxMultiples > 0 && arr.length >= maxMultiples) {
          arr[maxMultiples - 1] = { ...(arr[maxMultiples - 1] || {}), [target.valueKey]: newCampaignFeatureId };
        } else {
          arr.push({ [target.valueKey]: newCampaignFeatureId });
        }
      }
    }

    // Make sure the sidebar is pointed at the relevant editor after assignment.
    if (step?.customisationConfig?.meta?.activateAsNode) {
      handleFlowGraphicActivate(step, {
        itemIndex: 0,
        field: step?.customisationConfig?.meta?.activateField || 'featureID',
      });
    } else {
      handleFlowDisruptorActivate(step);
    }

    triggerSaveFeatureData(featureId);
    forceUpdateComponent();

    // Clean up drag state in case dragend doesn't fire
    draggedItemRef.current = null;
    setDraggedItem(null);
    setCurrentAction(ACTION_STATE.IDLE);
  };

  const handleFlowGoalRewardDisruptorDrop = async (step, goalIndex, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const cfg = step?.customisationConfig;
    if (!leftListNewCustomisationItems || typeof goalIndex !== 'number' || goalIndex < 0 || !cfg?.tabKey || !cfg?.varKey) return;

    const di = draggedItemRef.current || draggedItem;
    if (!di) return;

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    const droppedVarKey = di.varKey || di.id;
    if (droppedVarKey && droppedVarKey === expandedFlowFeature) {
      toast.error('A feature cannot trigger itself — this would create an infinite loop.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    let newCampaignFeatureId = null;
    if (di.isExistingCampaignFeature) {
      newCampaignFeatureId = di.campaignFeatureId;
    } else {
      try {
        newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, di.label);
      } catch (err) {
        console.error('[GoalRewardDisruptorDrop] Failed to add feature to campaign:', err);
        toast.error('Failed to add feature to campaign');
        return;
      }
    }

    if (!newCampaignFeatureId) return;

    const featureId = featureNode.id;
    if (!customisationVars || !customisationVars.current) return;
    if (!customisationVars.current[featureId]) customisationVars.current[featureId] = {};
    const featureVars = customisationVars.current[featureId];
    const tab = (featureVars[cfg.tabKey] ||= {});
    const group = (tab[cfg.varKey] ||= {});
    const arr = (group.goal ||= []);
    while (arr.length <= goalIndex) arr.push({});

    arr[goalIndex] = {
      ...(arr[goalIndex] || {}),
      triggerDisruptorFeatureID: newCampaignFeatureId,
    };

    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };
    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);

    if (activeNode) {
      activeNode.customisationVars = featureVars;
      if (activeNode.topNode) activeNode.topNode.customisationVars = featureVars;
      if (activeNode.parent) activeNode.parent.customisationVars = featureVars;
    }

    handleFlowGraphicActivate(step, { goalIndex, field: 'triggerDisruptorFeatureID' });
    triggerSaveFeatureData(featureId);
    forceUpdateComponent(activeNode || undefined);

    draggedItemRef.current = null;
    setDraggedItem(null);
    setCurrentAction(ACTION_STATE.IDLE);
  };

  const handleFlowTriviaQuestionDisruptorDrop = async (step, roundVarKey, questionIdx, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const di = draggedItemRef.current || draggedItem;
    if (!di) return;

    const featureNode = (expandedFlowFeatureId && leftListNewCustomisationItems[expandedFlowFeatureId])
      || Object.values(leftListNewCustomisationItems).find(n => n.varKey === expandedFlowFeature);
    if (!featureNode) return;

    const droppedVarKey = di.varKey || di.id;
    if (droppedVarKey && droppedVarKey === expandedFlowFeature) {
      toast.error('A feature cannot trigger itself — this would create an infinite loop.');
      draggedItemRef.current = null;
      setDraggedItem(null);
      setCurrentAction(ACTION_STATE.IDLE);
      return;
    }

    let newCampaignFeatureId = null;
    if (di.isExistingCampaignFeature) {
      newCampaignFeatureId = di.campaignFeatureId;
    } else {
      try {
        newCampaignFeatureId = await addFeatureToCampaign(di.varKey || di.id, di.label);
      } catch (err) {
        console.error('[TriviaQuestionDisruptorDrop] Failed to add feature to campaign:', err);
        toast.error('Failed to add feature to campaign');
        return;
      }
    }

    if (!newCampaignFeatureId) return;

    const featureId = featureNode.id;
    if (!customisationVars || !customisationVars.current) return;
    if (!customisationVars.current[featureId]) customisationVars.current[featureId] = {};
    const featureVars = customisationVars.current[featureId];
    const setup = (featureVars.setup ||= {});
    const round = (setup[roundVarKey] ||= {});
    const questions = (round.questions ||= []);
    while (questions.length <= questionIdx) questions.push({});

    questions[questionIdx] = {
      ...(questions[questionIdx] || {}),
      startQuestionDisruptor: newCampaignFeatureId,
    };

    const baseCustomisationVars = customisationVarsRef.current || customisationVars || {};
    const nextCustomisationVars = {
      ...baseCustomisationVars,
      current: {
        ...(baseCustomisationVars.current || {}),
        [featureId]: featureVars,
      },
    };
    customisationVarsRef.current = nextCustomisationVars;
    setCustomisationVars(nextCustomisationVars);

    if (activeNode) {
      activeNode.customisationVars = featureVars;
      if (activeNode.topNode) activeNode.topNode.customisationVars = featureVars;
      if (activeNode.parent) activeNode.parent.customisationVars = featureVars;
    }

    triggerSaveFeatureData(featureId);
    forceUpdateComponent(activeNode || undefined);

    draggedItemRef.current = null;
    setDraggedItem(null);
    setCurrentAction(ACTION_STATE.IDLE);
  };

  return (
    <>
    <div className="timeline-editor-shell">
    <div
      className={`timeline-editor-collaboration-bar v2-workspace-colour-vars${activeEditors.length > 1 ? ' is-visible' : ''}`}
      aria-hidden={activeEditors.length <= 1}
    >
      <div className="timeline-editor-collaboration-copy">
        <p className="timeline-editor-collaboration-label">Campaign editors</p>
        <p className="timeline-editor-collaboration-value">{`${activeEditors.length} people editing now`}</p>
      </div>
      <div className="timeline-editor-collaboration-list" aria-label="Campaign editors currently in this workspace">
        {activeEditors.map((editor) => (
          <div
            key={editor._id}
            className={`timeline-editor-collaboration-pill${editor._id === authContext?.userData?._id ? ' is-self' : ''}`}
            title={getCampaignEditorDisplayName(editor)}
          >
            <ProfilePic size={PROFILE_PIC_SIZES.small} className="timeline-editor-collaboration-avatar" user={editor} />
            <span className="timeline-editor-collaboration-name">{getCampaignEditorDisplayName(editor)}{editor._id === authContext?.userData?._id ? ' (You)' : ''}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="timeline-editor-grid v2-workspace-colour-vars" style={{"--timeline-height": `${timelineHeight}px`, gridTemplateColumns: `${leftSidebarWidth}px 1fr ${RIGHT_SIDEBAR_WIDTH}px`}}>
      {/* Left Tabbed Sidebar */}
      <div className={`timeline-editor-left-sidebar-shell${isResizingLeftSidebar ? ' is-resizing' : ''}`}>
        <LeftSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          layersPanelProps={layersPanelProps}
          libraryItems={libraryItems}
          onLibraryDragStart={handleLibraryDragStart}
          onLibraryDragEnd={handleLibraryDragEnd}
          onMediaDragStart={handleLibraryDragStart}
          onMediaDragEnd={handleLibraryDragEnd}
          onAddFromTemplate={handleAddFeatureFromTemplate}
          onAddFromLocalFile={authContext?.userData?.userLevel >= ACCOUNT_TYPES.koko.level ? handleAddFeatureFromLocalFile : null}
          onExportFeature={authContext?.userData?.userLevel >= ACCOUNT_TYPES.koko.level ? (event, feature) => showPackageDownloadMenu(event, feature) : null}
          onExportCampaign={authContext?.userData?.userLevel >= ACCOUNT_TYPES.koko.level ? (event) => showPackageDownloadMenu(event) : null}
          onDeleteCampaignFeature={handleDeleteCampaignFeature}
          onSelectCampaignFeature={handleCampaignLibraryFeatureSelect}
          onHoverCampaignFeature={handleCampaignLibraryFeatureHover}
          onRenameFeature={handleFeatureNameChange}
          onPlacedComponentSelection={handleFlowComponentSelect}
          featuresList={fullFeaturesList}

          leftListNewCustomisationItems={leftListNewCustomisationItems}
          forceUpdateComponentFunc={forceUpdateComponent}
          activateNode={activateNode}
          activeNode={activeNode}
          selectedFieldDataLink={selectedSidebarFieldDataLink}
          selectedFeatureId={selectedFeatureId}
          selectedFeatureNode={selectedFeatureNode}
          selectedLayersNodeState={selectedLayersNodeState}
          setSelectedFeatureId={setSelectedFeatureId}
          setSelectedFeatureNode={setSelectedFeatureNode}
          setSelectedLayersNodeState={setSelectedLayersNodeState}
          setTimelinePlayheadStep={setTimelinePlayheadStep}
          clearActiveNode={clearActiveNode}

          currentMode={currentMode}
          setCurrentMode={setCurrentMode}

          campaignData={campaignData}
          customisationVars={customisationVars}

          activateStepFunc={activateStep}
        />
        <div
          className="left-sidebar-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize left sidebar"
          onMouseDown={handleLeftSidebarResizeStart}
        >
          <div className="left-sidebar-resize-handle-line" />
        </div>
      </div>

      {showLegacyEditor && legacyEditorFeatureNode &&
        <LegacyCustomisation
          feature={legacyEditorFeatureNode.featureData}
          campaignId={campaignData._id}
          campaignData={campaignData}
          customisationVars={customisationVars}
          onClose={closeLegacyEditor}
          saveFunction={(onSaved) => {triggerSaveFeatureData(legacyEditorFeatureNode.featureData._id, null, null, onSaved);}}

          showTooltipFunc={showTooltip}
          hideTooltipFunc={hideTooltip}
        />
      }

      {!showLegacyEditor && !previewMode &&
        <>
      
          {/* Main Flow Area with Node Editor Canvas and Timeline */}
          <div className="timeline-editor-col flow-area-wrapper" id="flow-area-wrapper">
            {currentMode === MODES.FLOW_EDITOR && 
              <NodeEditorCanvas
                flowAreaRef={flowAreaRef}
                panOffset={panOffset}
                zoom={zoom}
                safeGridWidth={safeGridWidth}
                safeGridHeight={safeGridHeight}
                minX={minX}
                minY={minY}
                gridDots={gridDots}
                connections={connections}
                flowItems={flowItems}
                flowSteps={steps}
                animatingPanels={animatingPanels}
                dragOverPosition={dragOverPosition}
                draggedPanel={draggedPanel}
                hoveredStackingItem={hoveredStackingItemSafe}
                stackingOrder={stackingOrder}
                expandedComponents={expandedComponents}
                dragOverStep={dragOverStep}
                isPanning={isPanning}
                isScrollingRef={isScrollingRef}
                isScrolling={isScrollingState}
                timelineHeight={timelineHeight}
                currentAction={currentAction}
                ACTION_STATE={ACTION_STATE}
                hoveredTimelineComponentId={hoveredTimelineComponentId}
                onMouseDown={panningZoomHandlers.handleMouseDown}
                onFlowAreaDragOver={handleFlowAreaDragOver}
                onFlowAreaDragLeave={handleFlowAreaDragLeave}
                onInsertionDrop={handleInsertionDrop}
                onPanelDragStart={handlePanelDragStart}
                onPanelDragEnd={handlePanelDragEnd}
                onStepNameChange={handleStepNameChange}
                onComponentNameChange={handleComponentNameChange}
                isEditingName={isEditingName}
                setIsEditingName={setIsEditingName}
                onTimelineResizeStart={handleTimelineResizeStart}
                onComponentDragStart={handleComponentDragStart}
                onComponentDragEnd={handleComponentDragEnd}
                onStepContextMenu={handleStepContextMenu}
                onComponentContextMenu={handleComponentContextMenu}
                onComponentClick={handleFlowComponentSelect}
                onEditComponentCanvas={handleCampaignLibraryFeatureSelect}
                onComponentHover={handleFlowPanelComponentHover}
                onBackgroundClick={handleFlowBackgroundClick}
                leftListNewCustomisationItems={leftListNewCustomisationItems}
                campaignData={campaignData}
                customisationVars={customisationVars}
                selectedFeatureId={selectedFeatureId}
                selectedFeatureNode={selectedFeatureNode}
                selectedItem={selectedItem}
                onComponentTimingChange={updateComponentTiming}
                activeFlowLoopEnabled={flows.find((flow) => flow.id === activeFlowId)?.loop === true}
                activeFlowLoopDelay={Math.max(0, Number(flows.find((flow) => flow.id === activeFlowId)?.loopDelay) || 0)}
                onFlowLoopSettingsChange={updateActiveFlowLoopSettings}
                showFlowLoopControl={!isFeatureTemplateCampaign}
                getFlowStepListNodesForPreviewFunc={getFlowStepListNodesForPreview}
                activateStepFunc={activateStep}
                previewRefreshToken={`${forceUpdate}:${forceUpdateModuleList}`}
              />
            }
            {currentMode === MODES.CANVAS_EDITOR &&
              <div
                className="canvas-drop-surface"
                onDragOver={handleCanvasAreaDragOver}
                onDrop={handleCanvasAreaDrop}
              >
                <EditablePreview
                    campaignData={campaignData}
                    customisationVars={customisationVars}
                    selectedFeatureId={selectedFeatureId}
                    selectedFeatureNode={selectedFeatureNode}
                    activeNode={activeNode}
                    focusEditingOnNode={focusEditingOnNode}
                    setFocusEditingOnNode={setFocusEditingOnNode}
                    visualisedNode={activeVisualisedPreviewNode}
                    timelinePlayheadStep={timelinePlayheadStep}
                    timelinePlayheadLabel={timelinePlayheadLabel}
                    activateNodeFunction={activateNode}
                    forceUpdateComponent={forceUpdateComponent}
                    forceUpdate={forceUpdate}
                    forceUpdateModuleListFunc={forceUpdateModuleListFunc}
                    timelineHeight={timelineHeight}
                    layerNodes={getFlowStepListNodesForPreview()}
                />
              </div>
            }
            {currentMode === MODES.EXPANDED_FLOW && expandedFlowFeature && (() => {
              // Pass live in-memory customisationVars so VoteCard thumbnails show
              // the current uploaded image (blob URL) rather than stale DB data.
              // Merge default + current so displayCondition checks (e.g. waveMode)
              // work even when the user hasn't explicitly changed the setting yet
              // (isDefault:true vals only live in .default, not .current).
              // Use the stored featureId to avoid picking wrong instance when two features share varKey
              const expandedFeatureId = expandedFlowFeatureId
                || (() => { const i = campaignData?.features?.find(f => f.varKey === expandedFlowFeature); return i?._id?.toString?.() || i?._id; })();
              // Expanded flow should use the campaign customisation state as the single
              // source of truth. Sidebar node copies can be stale/default-heavy after a
              // reload and incorrectly make disabled audio appear active again.
              const liveCurrentData  = expandedFeatureId
                ? customisationVars?.current?.[expandedFeatureId]
                : null;
              const liveDefaultData  = expandedFeatureId ? customisationVars?.default?.[expandedFeatureId]  : null;
              const liveCustomisationData = (liveCurrentData || liveDefaultData)
                ? { ...liveDefaultData, ...liveCurrentData }
                : null;
              const liveFeatureData = expandedFeatureId
                ? (
                    featureVarsRef.current?.current?.[expandedFeatureId]
                    || featureVars?.current?.[expandedFeatureId]
                    || featureVarsRef.current?.[expandedFeatureId]
                    || featureVars?.[expandedFeatureId]
                    || null
                  )
                : null;

              // Derive which audio slot varName is currently open in the right panel
              // so PollAudioNode can highlight it. E.g. "audio.inGame.voteReceived.filename"
              // → split('.').at(-2) → "voteReceived"
              const activeAudioFile = activeTimelineAudioGroup?.audioData?.['audio-file'] || '';
              const activeAudioSlotVarName = activeAudioFile
                ? (activeAudioFile.split('.').slice(-2, -1)[0] || null)
                : null;

              return (
                <FeatureFlowPage
                  key={`${expandedFlowFeature}:${expandedFeatureId || 'none'}`}
                  embedded={true}
                  campaignId={campaignId}
                  featureInstanceId={expandedFeatureId}
                  featureVarKey={expandedFlowFeature}
                  onResetView={(resetFn) => { expandedFlowResetViewRef.current = resetFn; }}
                  streamId={campaignData?.liveStreams?.[0]?._id || ''}
                  onClose={() => { setCurrentMode(MODES.FLOW_EDITOR); clearActiveNode(); }}
                  onActivateNode={handleFlowGraphicActivate}
                  onRemoveGraphic={handleFlowGraphicRemove}
                  onPollQuestionChange={handleFlowPollQuestionChange}
                  onPollAnswerChange={handleFlowPollAnswerChange}
                  onPollAnswerAdd={handleFlowPollAnswerAdd}
                  onPollAnswerRemove={handleFlowPollAnswerRemove}
                  onImageFieldChange={handleFlowImageFieldChange}
                  onGoalFieldChange={handleFlowGoalFieldChange}
                  onPresetFieldChange={handleFlowPresetFieldChange}
                  onFlowBoolChange={handleFlowBoolChange}
                  onFlowFieldChange={handleFlowFieldChange}
                  getStepItems={getExpandedFlowStepItems}
                  onPollTriggerDrop={handleFlowPollTriggerDrop}
                  onPollTriggerRemove={handleFlowPollTriggerRemove}
                  onActivateDisruptor={handleFlowDisruptorActivate}
                  onDropDisruptor={handleFlowDisruptorDrop}
                  onRemoveDisruptor={handleFlowDisruptorRemove}
                  onGoalRewardDisruptorDrop={handleFlowGoalRewardDisruptorDrop}
                  campaignFeatures={campaignData?.features || []}
                  liveCustomisationData={liveCustomisationData}
                  liveFeatureData={liveFeatureData}
                  activeAudioSlotVarName={activeAudioSlotVarName}
                  onAddPreset={handleFlowPresetAdd}
                  onVideoSettingsChange={handleVideoSettingsFieldChange}
                  onSlotOptionDisruptorDrop={handleFlowSlotOptionDisruptorDrop}
                  onSlotOptionDisruptorRemove={handleFlowSlotOptionDisruptorRemove}
                  onSlotOptionRemove={handleFlowSlotOptionRemove}
                  onSlotResultAdd={handleFlowSlotResultAdd}
                  onSlotResultRemove={handleFlowSlotResultRemove}
                  onSlotResultDisruptorDrop={handleFlowSlotResultDisruptorDrop}
                  onSlotResultDisruptorRemove={handleFlowSlotResultDisruptorRemove}
                  activeNode={activeNode}
                  selectedFieldDataLink={selectedSidebarFieldDataLink}
                  onTriviaQuestionDisruptorDrop={handleFlowTriviaQuestionDisruptorDrop}
                  onSelectPlacedComponent={handleCampaignLibraryFeatureSelect}
                  onClearSelection={() => { clearActiveNode(); setActiveTimelineDisruptorGroup(null); setActiveTimelineAudioGroup(null); }}
                />
              );
            })()}

            {/* Resize handle — hidden in expanded flow mode */}
            {currentMode !== MODES.EXPANDED_FLOW && (
              <div 
                className="timeline-resize-handle"
                onMouseDown={handleTimelineResizeStart}
              >
                <div className="resize-handle-line"></div>
              </div>
            )}
            
            {/* Timeline Section — hidden in expanded flow mode */}
            <div className="timeline-section" style={{ height: `${timelineHeight}px`, display: currentMode === MODES.EXPANDED_FLOW ? 'none' : undefined }}>
              {currentMode === MODES.FLOW_EDITOR && 
                <Timeline 
                  panels={steps}
                  selectedItem={selectedItem}
                  onSelectItem={handleTimelineItemSelect}
                  zoomLevel={zoom}
                  onZoomIn={() => panningZoomHandlers.handleSliderZoom(Math.min(2, parseFloat((zoom + 0.1).toFixed(2))))}
                  onZoomOut={() => panningZoomHandlers.handleSliderZoom(Math.max(0.5, parseFloat((zoom - 0.1).toFixed(2))))}
                  onComponentExpansionChange={handleComponentExpansion}
                  onComponentTimingChange={updateComponentTiming}
                  stackingOrder={stackingOrder}
                  hoveredFeatureId={hoveredStackingItemSafe}
                  hoveredTimelineComponentId={hoveredTimelineComponentId}
                  onClipHover={handleTimelineClipHover}
                  previewFlow={previewFlow}
                />
              }
              {currentMode === MODES.CANVAS_EDITOR &&
                <FeatureTimeline
                    campaignData={campaignData}
                    customisationVars={customisationVars}
                    selectedFeatureId={selectedFeatureId}
                    selectedFeatureNode={selectedFeatureNode}
                    selectedFeature={selectedFeature}
                    activeNode={activeNode}
                    activateNodeFunction={activateNode}
                    visualisedNode={activeVisualisedPreviewNode}
                    timelinePlayheadStep={timelinePlayheadStep}
                    setTimelinePlayheadStep={setTimelinePlayheadStep}
                    activeTimelineAudioGroup={activeTimelineAudioGroup}
                    activateTimelineAudioGroup={activateTimelineAudioGroup}
                    activeTimelineDisruptorGroup={activeTimelineDisruptorGroup}
                    activateTimelineDisruptorGroup={activateTimelineDisruptorGroup}
                    forceUpdateComponent={forceUpdateComponent}
                    forceUpdate={forceUpdate}
                    timelineHeight={timelineHeight}
                    previewThisCustomisation={previewThisCustomisation}
                    clearActiveNode={clearActiveNode}
                    attachLibraryItemToVisualisedGroupNode={attachLibraryItemToVisualisedGroupNode}
                />
              }
            </div>
          </div>

          <SlideoutPanel campaignData={campaignData} showTooltipFunc={showTooltip} hideTooltipFunc={hideTooltip} libraryItems={libraryItems} selectedFeatureNode={selectedFeatureNode} onAddFromTemplate={handleAddFeatureFromTemplate} featuresList={fullFeaturesList} />

          {/* Right Controls */}
          <RightSidebar
            onResetView={() => {
              if (currentMode === MODES.EXPANDED_FLOW) {
                expandedFlowResetViewRef.current?.();
              } else {
                fitViewportToFirstStep();
              }
            }}

            campaignData={campaignData}
            selectedFeatureNode={selectedFeatureNode}
            activeNode={activeNode}
            selectedFieldDataLink={selectedSidebarFieldDataLink}
            activateNodeFunction={activateNode}
            activeTimelineAudioGroup={activeTimelineAudioGroup}
            activeTimelineDisruptorGroup={activeTimelineDisruptorGroup}
            groupConfig={activeNode?.config}
            customisationVars={activeSidebarCustomisationVars}
            fullCustomisationVars={customisationVars}
            openFontSelector={openFontSelector}
            openCustomFontSelector={openCustomFontSelector}
            openTemporaryImageUploadPopup={openTemporaryImageUploadPopup}
            openTemporaryAudioUploadPopup={openTemporaryAudioUploadPopup}
            openClassicCustomisationSubsetPopup={openClassicCustomisationSubsetPopup}
            forceUpdateComponent={forceUpdateComponent}
            forceUpdate={forceUpdate}
            forceUpdateModuleList={forceUpdateModuleList}
            fixSrcUrlsFunc={fixSrcUrls}
            addVal={addVal}
            getCustomisationVarsForFeature={getCustomisationVarsForFeature}

            visibleTriggeredDisruptorId={visibleTriggeredDisruptorId}
            visibleTriggeredDisruptorParamValue={visibleTriggeredDisruptorParamValue}
            setVisibleTriggeredDisruptorId={
              (id, param) => {
                setVisibleTriggeredDisruptorId(id);
                setVisibleTriggeredDisruptorParamValue(param);
              }
            }
          />

          <FloatingToolbar
              timelineHeight={timelineHeight}
              currentMode={currentMode}
              setCurrentMode={setCurrentMode}
              canOpenCanvas={canOpenSelectedFeatureFlow}
              onOpenComponentFlow={handleExpandSelectedFeatureFlow}
              canOpenComponentFlow={canOpenSelectedFeatureFlow}

              undoLastAction={undoLastAction}
              redoLastAction={redoLastAction}
              undoStackLength={undoStack.length}
              redoStackLength={redoStack.length}
              undoStack={undoStack}
              redoStack={redoStack}
          />
        </>
      }
      </div>
    </div>

      {/* Context Menu */}
      {contextMenu && contextMenuStep && (
        <div 
          className="step-context-menu"
          style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            zIndex: 10000,
          }}
        >
          {/* Show different menu based on whether component or step is selected */}
          {contextMenuComponent ? (
            // Component context menu
            <>
              <div 
                className="context-menu-item"
                onClick={handleCopyStep}
                style={{ cursor: 'pointer' }}
              >
                Copy Component
              </div>
              <div 
                className="context-menu-item"
                onClick={(copiedComponent || copiedStep) ? handlePasteStep : undefined}
                style={{
                  cursor: (copiedComponent || copiedStep) ? 'pointer' : 'not-allowed',
                  color: (copiedComponent || copiedStep) ? undefined : 'var(--workspace-faded-header)',
                  opacity: (copiedComponent || copiedStep) ? 1 : 0.4
                }}
              >
                Paste {copiedComponent ? 'Component' : copiedStep ? 'Step' : ''}
              </div>
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleDuplicateStep}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : undefined,
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Duplicate Component
              </div>
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked || contextMenuComponent?.isPrimaryStepItem ? undefined : handleSetPrimaryComponent}
                style={{
                  cursor: contextMenuStep.locked || contextMenuComponent?.isPrimaryStepItem ? 'not-allowed' : 'pointer',
                  color: contextMenuComponent?.isPrimaryStepItem ? 'var(--success-color)' : (contextMenuStep.locked ? 'var(--workspace-faded-header)' : undefined),
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                {contextMenuComponent?.isPrimaryStepItem ? 'Primary Feature in Chain' : 'Set as Primary Feature in Chain'}
              </div>
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={(() => {
                  const component = contextMenuComponent || (contextMenuStep.components && contextMenuStep.components[0]);
                  return canOpenExpandedFlowForComponent(component) ? handleExpandFlow : undefined;
                })()}
                style={{
                  cursor: (() => {
                    const component = contextMenuComponent || (contextMenuStep.components && contextMenuStep.components[0]);
                    return canOpenExpandedFlowForComponent(component) ? 'pointer' : 'not-allowed';
                  })(),
                  color: (() => {
                    const component = contextMenuComponent || (contextMenuStep.components && contextMenuStep.components[0]);
                    return canOpenExpandedFlowForComponent(component) ? 'var(--primary-color)' : 'var(--workspace-faded-header)';
                  })(),
                  opacity: (() => {
                    const component = contextMenuComponent || (contextMenuStep.components && contextMenuStep.components[0]);
                    return canOpenExpandedFlowForComponent(component) ? 1 : 0.4;
                  })()
                }}
              >
                Component Flow
              </div>
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={handleOpenApplyTemplate}
                style={{ cursor: 'pointer' }}
              >
                Apply Template…
              </div>
              {(authContext?.userData?.userLevel || 0) >= ACCOUNT_TYPES.koko.level && (
                <>
                  <div className="context-menu-separator" />
                  <div
                    className="context-menu-item"
                    onClick={handleDownloadContextMenuFeaturePackage}
                    style={{ cursor: 'pointer' }}
                  >
                    Download Feature Package
                  </div>
                </>
              )}
              {canCreateFeatureTemplate && (
                <>
                  <div className="context-menu-separator" />
                  <div 
                    className="context-menu-item"
                    onClick={handleConvertComponentToFeatureTemplate}
                    style={{ cursor: 'pointer' }}
                  >
                    Convert to Feature Template
                  </div>
                </>
              )}
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleDeleteComponent}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : 'var(--error-color)',
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Delete Component
              </div>
            </>
          ) : (
            // Step context menu
            <>
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleRenameStep}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : undefined,
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Rename Step
              </div>
              <div 
                className="context-menu-item"
                onClick={handleCopyStep}
                style={{ cursor: 'pointer' }}
              >
                Copy Step
              </div>
              <div 
                className="context-menu-item"
                onClick={(copiedComponent || copiedStep) ? handlePasteStep : undefined}
                style={{
                  cursor: (copiedComponent || copiedStep) ? 'pointer' : 'not-allowed',
                  color: (copiedComponent || copiedStep) ? undefined : 'var(--workspace-faded-header)',
                  opacity: (copiedComponent || copiedStep) ? 1 : 0.4
                }}
              >
                Paste {copiedComponent ? 'Component' : copiedStep ? 'Step' : ''}
              </div>
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleDuplicateStep}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : undefined,
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Duplicate Step
              </div>
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleMoveToStart}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : undefined,
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Move to Start
              </div>
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleMoveToEnd}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : undefined,
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Move to End
              </div>
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={handleToggleLockStep}
                style={{ cursor: 'pointer' }}
              >
                {contextMenuStep.locked ? 'Unlock Step' : 'Lock Step'}
              </div>
              <div 
                className="context-menu-item"
                onClick={handleExpandFlow}
                style={{ cursor: 'pointer', color: 'var(--primary-color)' }}
              >
                Component Flow
              </div>
              <div className="context-menu-separator" />
              <div 
                className="context-menu-item"
                onClick={contextMenuStep.locked ? undefined : handleDeleteStep}
                style={{
                  cursor: contextMenuStep.locked ? 'not-allowed' : 'pointer',
                  color: contextMenuStep.locked ? 'var(--workspace-faded-header)' : 'var(--error-color)',
                  opacity: contextMenuStep.locked ? 0.4 : 1
                }}
              >
                Delete Step
              </div>
            </>
          )}
        </div>
      )}

                  {showFontSelector &&
                      <FontSelectorPopup
                          closePanelFunc={closeFontSelector}
                          selectFunc={fontSelectedFunc_ref.current}
                          font={currentFont_ref.current}
                          customFonts={campaignData.customFonts}
                          campaignId={campaignData._id}
                      />
                  }
                  {showApplyTemplatePopup && applyTemplateTarget && (
                      <ApplyTemplatePopup
                          campaignId={campaignId}
                          campaignFeatureId={applyTemplateTarget.campaignFeatureId}
                          featureVarKey={applyTemplateTarget.featureVarKey}
                          featureLabel={applyTemplateTarget.featureLabel}
                          hasCustomisations={applyTemplateTarget.hasCustomisations}
                          onClose={() => { setShowApplyTemplatePopup(false); setApplyTemplateTarget(null); }}
                          onApplied={async (updatedCampaignData) => {
                              setShowApplyTemplatePopup(false);
                              setApplyTemplateTarget(null);

                              if (updatedCampaignData) {
                                  const normalizedCampaignData = getNormalizedCampaignOwnerAccountData(updatedCampaignData, campaignDataRef.current);
                                  setCampaignData(normalizedCampaignData);
                                  setCampaignFeatures(normalizedCampaignData.features || []);
                                  setCampaignContext(prev => ({
                                    ...prev,
                                    campaignData: getNormalizedCampaignOwnerAccountData(normalizedCampaignData, prev?.campaignData),
                                  }));
                              }

                            await fetchCampaignData(true);
                              toast.success('Template applied successfully!');
                          }}
                      />
                  )}
                        {showCreateFeatureTemplatePopup && createFeatureTemplateTarget && (
                          <CreateFeatureTemplatePopup
                            campaignId={campaignId}
                            campaignFeatureId={createFeatureTemplateTarget.campaignFeatureId}
                            featureLabel={createFeatureTemplateTarget.featureLabel}
                            beforeCreate={async () => {
                              await forceImmediateCustomisationSave(customisationSaverHook.campaignData, customisationSaverHook.customisationVars);
                            }}
                            onClose={() => {
                              setShowCreateFeatureTemplatePopup(false);
                              setCreateFeatureTemplateTarget(null);
                            }}
                            onCreated={(templateId) => {
                              setShowCreateFeatureTemplatePopup(false);
                              setCreateFeatureTemplateTarget(null);
                              toast.success('Feature template created. Redirecting...');
                              navigate(`/site-admin/templates/${templateId}`);
                            }}
                          />
                        )}
                  {showCustomFontSelector &&
                      <CustomFontsPopup
                          closePanelFunc={closeCustomFontSelector}
                          customFonts={campaignData.customFonts}
                          campaignId={campaignData._id}
                          fullCampaignData={campaignData}
                      />
                  }
                  {showTemporaryImageUploadPopup &&
                      <TemporaryImageUploadPopup
                          closePanelFunc={closeTemporaryImageUploadPopup}
                          varVal={imageFileData}
                          controlLabel={imagePopupLabel}
                          labelNote={imagePopupLabelNote}
                          onComplete={() => {
                              closeTemporaryImageUploadPopup();
                              forceUpdateComponent();
                          }}
                      />
                  }
                  {showTemporaryAudioUploadPopup &&
                      <TemporaryAudioUploadPopup
                          closePanelFunc={closeTemporaryAudioUploadPopup}
                          varVal={audioFileData}
                          controlLabel={audioPopupLabel}
                          onComplete={() => {
                              closeTemporaryAudioUploadPopup();
                              forceUpdateComponent();
                          }}
                      />
                  }
                  {showClassicCustomisationSubsetPopup &&
                      <RestrictedCustomisationPopup
                          closePanelFunc={() => {setShowClassicCustomisationSubsetPopup(false);}}
                          campaignData={campaignData}
                          featureId={selectedFeatureId}
                          customisationVars={customisationVars}
                          basicModeItemData={classicCustomisationSubsetItemData}
                          basicModeCompletedCallback={classicCustomisationSubsetCompletedCallback}
                          saveCustomisationVars={
                              (updatedCustomisationVars, passedInRestriction, basicModeItemData, completedCallback) => {

                                  if (passedInRestriction && passedInRestriction.tab && passedInRestriction.group && passedInRestriction.family && typeof passedInRestriction.itemIndex === 'number') {
                                      // Update the item data in the main customisationVars
                                      const updatedItemData = updatedCustomisationVars[passedInRestriction.tab][passedInRestriction.group][passedInRestriction.family][passedInRestriction.itemIndex];
                                      for (const key in updatedItemData) {
                                          basicModeItemData[key] = updatedItemData[key];
                                      }
                                  }
                                  forceUpdateComponent();
                                  if (completedCallback) {
                                      completedCallback(basicModeItemData);
                                  }
                              }
                          }
                          restriction={classCustomisationSubsetRestriction}
                          updateCustomisationValue={updateCustomisationValue}
                          forceUpdateComponent={forceUpdateComponent}
                          /* width='fit-content'
                          minWidth='60rem'
                          height='fit-content'
                          minHeight='10vh'
                          maxHeight='64vh' */
                          title={classicCustomisationSubsetTitle}
                          tab={classCustomisationSubsetRestriction?.tab}
                          group={classCustomisationSubsetRestriction?.group}
                          family={classCustomisationSubsetRestriction?.family}
                          var={classCustomisationSubsetRestriction?.var}
                          item={classCustomisationSubsetRestriction?.item}
                          itemIndex={classCustomisationSubsetRestriction?.itemIndex}
                          basicMode={true}
                          singleColumn={!!classCustomisationSubsetRestriction?.var}
                      />
                  }

    {previewMode &&
      <PreviewErrorBoundary
        resetKey={`${currentFeature?._id || 'none'}:${previewUpdate}:${previewMode ? 'open' : 'closed'}`}
        onError={handleFeaturePreviewOpenError}
      >
      <FeaturePreview
            campaignId={campaignId}

            currentFeature={currentFeature ?? getFirstFeature(campaignData)}
            titlePostFix={previewPostFix}
            featureVars={featureVars}
            streamId={null}
            customisationVars={customisationVars}
            closeFunc={() => setPreviewMode(false)}

            startControlsExpanded={true/*currentFeature.feature.featureType === 'feature'*/}
            startMaximized={true}
            resizeable={false}
            startPosX={'right'}
            showQuickPlayButton={true}
            autoStart={autoStartPreview}
            showChains={previewChains}
            activeFlowId={activeFlowId}
            flows={flows}
            previewUpdate={previewUpdate}

            _reportFeatureVarsChanged={reportFeatureVarsChanged}
            _reportCustomisationVarsChanged={reportCustomisationVarsChanged}
            
            _sendFeatureCommandFunc={sendFeatureCommand}
            _showTooltip={showTooltip}
            _hideTooltip={hideTooltip}
        />
          </PreviewErrorBoundary>
    }

    {showingFunctionUnavilablePopup &&
        <FunctionUnavailableInPreviewPopup
            closePanelFunc={closeFunctionUnavilablePopup}
        />
    }

    {showingTooltip &&
        <div className="tw-tooltip" style={tooltipPos} dangerouslySetInnerHTML={{__html: tooltipText}}>
        </div>
    }

    </>
  );
}

export default TimelineEditor;
